// Inner page translations for Dashboard, Calculators, Compare, RealEstate, Tax, and Sidebar

export const innerPageTranslations: Record<string, Record<string, string>> = {
  en: {
    // Sidebar
    "sidebar.dashboard": "Dashboard",
    "sidebar.calculators": "Investment Calculators",
    "sidebar.compare": "Product Comparison",
    "sidebar.realEstate": "Real Estate Planner",
    "sidebar.tax": "Tax Planner",
    "sidebar.tagline": "Smart Financial Tools",
    "sidebar.disclaimer": "Data for educational purposes only. Not financial advice.",

    // Dashboard
    "dash.title": "Dashboard",
    "dash.subtitle": "Your financial overview at a glance",
    "dash.portfolioValue": "Portfolio Value",
    "dash.monthlySip": "Monthly SIP",
    "dash.taxSaved": "Tax Saved (FY25)",
    "dash.returns1y": "Returns (1Y)",
    "dash.portfolioGrowth": "Portfolio Growth",
    "dash.last9months": "Last 9 months",
    "dash.quickActions": "Quick Actions",
    "dash.sipCalc": "SIP Calculator",
    "dash.compareMF": "Compare Mutual Funds",
    "dash.emiEstimator": "EMI Estimator",
    "dash.taxOptimizer": "Tax Planner",
    "dash.marketSnapshot": "Market Snapshot",
    "dash.active": "active",
    "dash.miniTools": "Mini Tools — Quick Money Hacks",
    "dash.vikasScore": "Vikas Journey",
    "dash.totalAssets": "Total Assets",
    "dash.totalLiabilities": "Total Liabilities",
    "dash.netInvestment": "Net Investment",
    "dash.dtiRatio": "DTI Ratio",
    "dash.savingsInvestments": "Savings + Investments",
    "dash.annualEmi": "Annual EMI Obligations",
    "dash.monthlyWealth": "Monthly Wealth Building",
    "dash.financialPulse": "Financial Pulse",
    "dash.assetsVsLiabilities": "Assets vs Liabilities — 12 Month Projection",
    "dash.dailySathiTip": "Sathi Tip of the Day",
    "dash.goalTracker": "Goal Tracker",
    "dash.welcome": "Welcome",
    "dash.welcomeSub": "Here's your financial overview for today.",
    "dash.editProfile": "Edit Profile",
    "dash.user": "User",
    "ai.insightTitle": "AI Insight",
    "ai.sipAdvice": "Based on your {income} income, we recommend starting a SIP of {sip}/month to fast-track your {goal}.",
    "ai.dtiWarning": "Your EMI load is {dti}% — consider a Balance Transfer to reduce interest costs.",
    "ai.financialGoal": "financial goal",
    "action.title": "Action Items",
    "action.completeProfile": "Complete your Financial Profile",
    "action.setup": "Set up",
    "action.runTax": "Run Tax Optimization",
    "action.open": "Open",
    "action.rentVsBuy": "Rent vs Buy Analysis",
    "action.analyze": "Analyze",
    "goal.buyHome": "Buy a Home",
    "goal.payDebt": "Pay off Debt",
    "goal.retireEarly": "Retire Early",
    "goal.emergency": "Emergency Fund",
    "goal.childEdu": "Child's Education",
    "goal.wealthGrowth": "Grow Wealth",
    "goal.eta50plus": "50+ years",
    "goal.achieved": "Achieved",
    "goal.basedOn": "Based on",
    "goal.moSIP": "mo SIP",
    "goal.returns": "returns",
    "goal.saved": "saved",
    "goal.target": "Target",
    "goal.custom": "custom",
    "goal.complete": "complete",
    "goal.yrsToGo": "yrs to go",
    "goal.sipNeeded": "SIP needed to reach",
    "goal.yrs": "yrs",
    "goal.month": "month",
    "goal.save": "Save",
    "goal.reset": "Reset",
    "goal.setCustomTarget": "Set custom target",
    "goal.noGoals": "No goals set. Update your profile to track goals.",
    "goal.enterValid": "Enter a valid amount",
    "goal.targetUpdated": "Target updated",
    "goal.resetDefault": "Reset to default",
    "sidebar.catDashboard": "Dashboard",
    "sidebar.catHomeLife": "🏠 Home & Life",
    "sidebar.catFuture": "🔮 Future",
    "sidebar.catSafety": "🛡️ Safety",
    "sidebar.catInvest": "📈 Invest",
    "sidebar.catBanking": "🏦 Banking",
    "sidebar.catAccount": "Account",
    "sidebar.desc.dashboard": "Your financial overview at a glance",
    "sidebar.desc.calculators": "SIP, lump sum & compound interest tools",
    "sidebar.desc.compare": "Compare financial products side by side",
    "sidebar.desc.realEstate": "Plan property purchases & ROI",
    "sidebar.desc.tax": "Optimize your tax savings under old & new regime",
    "sidebar.desc.retirement": "Calculate your retirement corpus & SIP needed",
    "sidebar.desc.rentVsBuy": "Find out if renting or buying is better for you",
    "sidebar.desc.debtKiller": "Create a plan to become debt-free faster",
    "sidebar.desc.stockResearch": "Live market data & stock analysis",
    "sidebar.desc.emiMentor": "Check if you can truly afford a purchase",
    "sidebar.desc.childFuture": "Plan education & future expenses for your child",
    "sidebar.desc.creditCard": "Understand true cost of credit card debt",
    "sidebar.desc.assetComparison": "Compare returns across asset classes",
    "sidebar.desc.profile": "Manage your financial profile & preferences",
    "sidebar.desc.dataRights": "Your data privacy & download options",

    // Mini Tools
    "mini.chaiTitle": "Chai-Sutta SIP ☕",
    "mini.chaiDesc": "What if you invested your daily chai-sutta money?",
    "mini.dailyExpense": "Daily Expense",
    "mini.years": "Investment Period",
    "mini.couldBecome": "Could Become",
    "mini.vampireTitle": "Vampire Spend Audit 🧛",
    "mini.vampireDesc": "Subscriptions silently draining your wealth",
    "mini.10yrCost": "10-Year Opportunity Cost",
    "mini.inflationTitle": "Inflation Time Machine ⏳",
    "mini.inflationDesc": "See what today's prices will cost in the future",
    "mini.todayPrice": "Today's Price (₹)",

    // Calculators
    "calc.title": "Investment Calculators",
    "calc.subtitle": "Plan your wealth with precision",
    "calc.sipCalc": "SIP Calculator",
    "calc.lumpSum": "Lump Sum",
    "calc.monthlyInvestment": "Monthly Investment",
    "calc.investmentAmount": "Investment Amount",
    "calc.expectedReturn": "Expected Return (%)",
    "calc.timePeriod": "Time Period (Years)",
    "calc.yrs": "yrs",
    "calc.totalValue": "Total Value",
    "calc.invested": "Invested",
    "calc.gains": "Gains",

    // Wealth Engine
    "we.pageTitle": "VittaSathi Wealth Engine",
    "we.pageSubtitle": "Grow your wealth with step-up SIP and inflation-adjusted projections",
    "we.wealthEngine": "Wealth Engine",
    "we.monthlyInvestment": "Monthly Investment",
    "we.expectedReturn": "Expected Return (%)",
    "we.tenure": "Investment Tenure (Years)",
    "we.stepUp": "Annual Step-Up",
    "we.perYear": "per year",
    "we.stepUpHint": "Increase your SIP every year as your salary grows",
    "we.inflationToggle": "Show value in today's terms",
    "we.inflationHint": "Discount future value by 6% inflation to see real purchasing power",
    "we.totalValue": "Total Corpus",
    "we.totalValueInflation": "Corpus (Today's Value)",
    "we.wealthGained": "Wealth Gained",
    "we.wealthRatio": "Wealth Ratio",
    "we.interestShare": "of your corpus is pure interest — the power of compounding!",
    "we.sathiSays": "Sathi says:",
    "we.sathiMessage": "Your money is working {X} times harder than you are! Keep the SIP going.",
    "we.growthChart": "Wealth Growth Over Time",
    "we.amountInvested": "Amount Invested",
    "we.compoundingPower": "Power of Compounding!",
    "we.gainsExceed": "Your wealth gained exceeds your total invested amount. Starting early makes a massive difference.",
    "we.stepUpTip": "Step-Up Superstar!",
    "we.stepUpTipDesc": "A 10%+ annual step-up can nearly double your corpus compared to a flat SIP over the same period.",
    "we.caution": "Caution:",
    "we.cautionDesc": "Expecting {rate}% returns in {years} year(s) is risky. Equity markets are volatile short-term.",
    "we.saveResult": "Save Result",
    "we.delayToggle": "What if I wait? (Cost of Delay)",
    "we.delayHint": "See how much wealth you lose by delaying your SIP start",
    "we.delaySliderLabel": "Years of Delay",
    "we.penaltyTitle": "Penalty for Waiting",
    "we.sathiInsight": "Sathi Insight:",
    "we.delayInsight": "By waiting {X} years, you are effectively paying a 'Procrastination Tax' of {PENALTY}. This money could have funded {YEARS} years of your child's education or a luxury retirement home.",
    "we.lostWealth": "Lost Wealth (Delay Penalty)",
    "we.exploreMore": "Explore More Tools",
    "we.assetFD": "Safe FD",
    "we.assetGold": "Traditional Gold",
    "we.assetRealEstate": "Real Estate",
    "we.assetIndex": "Index Funds",
    "we.assetStock": "Stock Investment",
    "we.riskFD": "Capital safe, but barely beats inflation. Purchasing power erodes over time.",
    "we.riskGold": "Hedge against inflation. No regular income. Prices can stagnate for years.",
    "we.riskRealEstate": "Tangible asset with rental income potential. Illiquid, high entry cost. 5+ year horizon.",
    "we.riskIndex": "Historically beats inflation long-term. Expect 20-30% drops in bad years.",
    "we.riskStock": "Highest potential, highest risk. Can lose 40%+ in crashes. Needs 7+ year horizon.",
    "we.opportunityCost": "Opportunity Cost (Wealth Gap)",
    "we.wealthGapDesc": "Difference between Stock Investment & Safe FD over {years} years",
    "we.sathiStockVsFD": "By choosing Stock Investments over FD, your wealth could be {amount} higher after {years} years. That's real compounding power!",
    "we.growthComparison": "Growth Comparison",
    "we.sathiSecret": "The Sathi Secret",
    "we.sathiSecretText": "While FD gives 6.5%, inflation in India is ~6%. Your real growth in an FD is only 0.5% per year. Over decades, your money barely keeps up with rising prices.",

    // Wealth Simulator
    "ws.pageTitle": "VittaSathi Wealth Simulator",
    "ws.pageSubtitle": "Professional-grade SIP, Lumpsum & Hybrid wealth projections",
    "ws.modeSIP": "Monthly SIP",
    "ws.modeLumpsum": "Lumpsum",
    "ws.modeHybrid": "Hybrid (Both)",
    "ws.monthlySIP": "Monthly SIP Amount",
    "ws.lumpsumSeed": "Lumpsum Seed",
    "ws.expectedReturn": "Expected Return (%)",
    "ws.timeHorizon": "Time Horizon (Years)",
    "ws.stepUpSIP": "Step-Up SIP (% p.a.)",
    "ws.stepUpHint": "Increase your SIP every year to match salary growth",
    "ws.inflationToggle": "Show Inflation-Adjusted Value (6%)",
    "ws.inflationHint": "See purchasing power in today's terms",
    "ws.noStepUpWarning": "Sathi, your money grows, but prices grow too. Increase your SIP every year to stay ahead!",
    "ws.finalCorpus": "Final Corpus",
    "ws.totalInvested": "Total Invested",
    "ws.wealthGained": "Wealth Gained",
    "ws.croreClub": "Welcome to the ₹1 Crore Club! 🎉",
    "ws.wealthTrajectory": "Wealth Trajectory",
    "ws.wealthBreakdown": "Wealth Breakdown",
    "ws.futureLifestyle": "Future Lifestyle Note",
    "ws.futureLifestyleDesc": "If you invest this SIP in the Stock Lab for 5 years, you could have {X}!",
    "ws.sendToGoalPlanner": "Send to Goal Planner",
    "ws.disclaimer": "This tool demonstrates the mathematical power of compounding over time. It is an educational projection and not a guarantee of market returns.",

    // Compare
    "compare.title": "Product Comparison",
    "compare.subtitle": "Compare mutual funds, FDs, and insurance plans",
    "compare.fundName": "Fund Name",
    "compare.1yReturn": "1Y Return",
    "compare.3yReturn": "3Y Return",
    "compare.5yReturn": "5Y Return",
    "compare.risk": "Risk",
    "compare.expense": "Expense",
    "compare.disclaimer": "* Past performance does not guarantee future returns. Data is illustrative.",
    "compare.low": "Low",
    "compare.moderate": "Moderate",
    "compare.high": "High",
    "compare.moderateHigh": "Moderate-High",

    // Real Estate
    "re.title": "Real Estate Planner",
    "re.subtitle": "EMI calculator and property planning tools",
    "re.propertyValue": "Property Value",
    "re.downPayment": "Down Payment (%)",
    "re.interestRate": "Interest Rate (%)",
    "re.loanTenure": "Loan Tenure (Years)",
    "re.monthlyEmi": "Monthly EMI",
    "re.loanAmount": "Loan Amount",
    "re.totalInterest": "Total Interest",
    "re.totalPayment": "Total Payment",

    // Tax
    "tax.title": "Tax Planner",
    "tax.subtitle": "Plan and optimize your tax savings under Indian tax law",
    "tax.recommended": "Recommended",
    "tax.alternative": "Alternative",
    "tax.oldRegime": "Old Tax Regime",
    "tax.newRegime": "New Tax Regime",
    "tax.estimatedTax": "Estimated Tax",
    "tax.withDeductions": "With deductions of ₹2,41,000",
    "tax.standardDeduction": "Standard deduction ₹75,000",
    "tax.section80c": "Section 80C – ₹1.5 Lakh",
    "tax.section80d": "Section 80D – Health Insurance",
    "tax.hraExemption": "HRA Exemption",
    "tax.elss": "ELSS Mutual Funds",
    "tax.epf": "EPF Contribution",
    "tax.lifeInsurance": "Life Insurance Premium",
    "tax.selfFamily": "Self & Family Premium",
    "tax.parentsPremium": "Parents Premium",
    "tax.rentPaid": "Rent Paid (Annual)",
    "tax.exemptedAmount": "Exempted Amount",
    "tax.invested": "Invested",
    "tax.autoDeducted": "Auto-deducted",
    "tax.paid": "Paid",
    "tax.claimable": "Claimable",
    "tax.calculated": "Calculated",
    "sidebar.retirement": "Retirement Clock",
    "re.step1Title": "Budgeting",
    "re.step2Title": "ROI Simulator",
    "re.step3Title": "Affordability",
    "re.stampDuty": "Stamp Duty",
    "re.registration": "Registration",
    "re.gst": "GST",
    "re.totalCost": "Total Property Cost",
    "re.nextStep": "Next Step",
    "re.back": "Back",
    "re.roiDesc": "Compare: What if you invested the same amount in Mutual Funds instead of Real Estate?",
    "re.realEstateInv": "Real Estate Investment",
    "re.mutualFundInv": "Mutual Fund Investment",
    "re.rentalYield": "Rental Yield",
    "re.appreciation": "Appreciation",
    "re.propertyAfter20": "Property Value (20 yrs)",
    "re.totalRentalIncome": "Total Rental Income",
    "re.costDeductions": "Cost Deductions",
    "re.netGain": "Net Gain",
    "re.expectedReturn": "Expected Return",
    "re.amountInvested": "Amount Invested",
    "re.valueAfter20": "Value after 20 yrs",
    "re.mfWins": "Mutual Fund wins by",
    "re.reWins": "Real Estate wins by",
    "re.summary": "Summary",
    "re.minIncome": "Min. Monthly Income",
    "re.affordable": "This property is affordable for you!",
    "re.notAffordable": "This property may stretch your budget.",
    "re.emiShouldBe": "EMI should be less than 40% of your monthly income.",
    "re.nextSteps": "Next Steps Guide",
    "re.step1Tip": "Get home loan pre-approval from 2-3 banks",
    "re.step2Tip": "Verify RERA registration of the property",
    "re.step3Tip": "Budget for interior & moving costs (5-10%)",
    "re.step4Tip": "Consider home insurance before possession",
    "re.startOver": "Start Over",
    "re.step4Title": "Loan Eligibility",
    "re.empType": "Employment Type",
    "re.empSalaried": "Salaried",
    "re.empBusiness": "Business Owner",
    "re.empFreelancer": "Freelancer",
    "re.netSalary": "Net Monthly Salary (₹)",
    "re.existingEmis": "Existing EMIs (₹)",
    "re.workExp": "Work Experience",
    "re.maxAllowableEmi": "Maximum Allowable EMI",
    "re.maxLoanAmount": "Maximum Loan Amount",
    "re.requiredLoan": "Required Loan Amount",
    "re.calcBasis": "Calculation Basis",
    "re.eligible": "Eligible — You qualify!",
    "re.borderline": "Borderline — Close to limit",
    "re.notEligible": "Not Eligible — Gap too large",
    "re.eligibleTip": "Your required loan is within your eligibility. You are ready to apply!",
    "re.borderlineTip": "Add a co-applicant like a spouse to increase your limit.",
    "re.notEligibleTip": "Increase your down payment or clear existing debts first.",
    "re.sathiLoanTip": "Sathi Tip: Banks love stability. If you switched jobs in the last 3 months, wait for 3 salary slips before applying!",
    "re.addCoApplicant": "Add Co-applicant Income?",
    "re.coApplicantSalary": "Co-applicant Monthly Salary (₹)",
    "re.coApplicantNote": "Adding a spouse or parent's income can significantly boost your loan eligibility!",
    "ret.title": "Retirement Clock",
    "ret.subtitle": "Plan your retirement with inflation-adjusted calculations",
    "ret.monthlyExpense": "Current Monthly Expenses",
    "ret.currentAge": "Current Age",
    "ret.retireAge": "Retirement Age",
    "ret.inflationRate": "Inflation Rate (%)",
    "ret.sipReturn": "Expected SIP Return (%)",
    "ret.yearsLeft": "Years to Retirement",
    "ret.futureCost": "Future Monthly Cost",
    "ret.month": "month",
    "ret.inflationNote": "Your lifestyle cost adjusted for inflation",
    "ret.corpusNeeded": "Retirement Corpus Needed",
    "ret.sipNeeded": "Monthly SIP Needed",
    "ret.sipNote": "Start this SIP today to retire comfortably",
    "sidebar.rentVsBuy": "Rent vs Buy",
    "sidebar.debtKiller": "Debt-Free Path",
    "sidebar.stockResearch": "Stock Research",
    "sidebar.emiMentor": "Can I Buy It?",
    "sidebar.lifeStages": "Life Stages",
    "sidebar.childFuture": "Child's Future Planner",
    "sidebar.creditCard": "Credit Card Master",
    "sidebar.profile": "My Profile",
    "sidebar.dataRights": "Data Rights",
    "rvb.title": "Rent vs Buy Analyzer",
    "rvb.subtitle": "Should you rent or buy? Compare the true cost over time.",
    "rvb.cityPreset": "City Preset",
    "rvb.custom": "Custom",
    "rvb.taxRegime": "Tax Regime",
    "rvb.oldRegime": "Old Regime",
    "rvb.newRegime": "New Regime",
    "rvb.horizon": "Analysis Period",
    "rvb.yrs": "yrs",
    "rvb.buyPath": "Buy Path",
    "rvb.rentPath": "Rent + Invest Path",
    "rvb.propertyPrice": "Property Price",
    "rvb.downPayment": "Down Payment (%)",
    "rvb.loanRate": "Loan Interest Rate (%)",
    "rvb.loanTenure": "Loan Tenure (Years)",
    "rvb.interiors": "Interior & Furnishing Cost",
    "rvb.monthlyMaintenance": "Monthly Maintenance",
    "rvb.yearlyPropertyTax": "Yearly Property Tax",
    "rvb.appreciationRate": "Property Appreciation (%)",
    "rvb.monthlyRent": "Monthly Rent",
    "rvb.rentIncrease": "Annual Rent Increase (%)",
    "rvb.mfReturn": "Mutual Fund Return (%)",
    "rvb.opportunityCostNote": "Opportunity cost: your down payment invested instead",
    "rvb.investedInMF": "invested in Mutual Funds",
    "rvb.hraNote": "Remember: HRA exemption available only under Old Regime",
    "rvb.monthlyEmi": "Monthly EMI",
    "rvb.recommendation": "Recommendation",
    "rvb.buyingBetter": "Buying builds more wealth",
    "rvb.rentingBetter": "Renting is better for your wealth",
    "rvb.wealthDiff": "Wealth difference",
    "rvb.breakeven": "Breakeven Year",
    "rvb.year": "Year",
    "rvb.neverBreakeven": "Buy never catches up",
    "rvb.wealthComparison": "Net Wealth Comparison",
    "rvb.years": "Years",
    "rvb.buyWealth": "Buy: Net Wealth",
    "rvb.rentWealth": "Rent + Invest: Net Wealth",
    "rvb.taxRulesTitle": "2026 Tax Rules Applied",
    "rvb.taxRule1": "Old Regime: Sec 24b interest deduction up to ₹2L",
    "rvb.taxRule2": "Old Regime: Sec 80C principal deduction up to ₹1.5L",
    "rvb.taxRule3": "New Regime: No deductions for self-occupied property",
    "rvb.assumptions": "Assumptions Used",
    "rvb.assumption1": "Property appreciation",
    "rvb.assumption2": "Rent increase",
    "rvb.assumption3": "MF return (CAGR)",
    "rvb.purposeTitle": "What does this tool do?",
    "rvb.purposeDesc": "This tool compares the true cost of buying a home (EMI, stamp duty, maintenance) against renting and investing the difference in mutual funds. Find out which path builds more wealth over time.",
    "rvb.hiddenCosts": "Hidden Costs (Stamp Duty, Maintenance, Tax)",
    "rvb.stampDuty": "Stamp Duty",
    "rvb.registration": "Registration",
    "rvb.wealthGapChart": "Wealth Gap: Buy vs Rent & Invest",
    "rvb.sathiVerdict": "Sathi's Verdict",
    "rvb.sathiRentAdvice": "Sathi says: Renting is currently saving you {AMT} per month. Invest this in an SIP to build your dream home fund faster!",
    "rvb.sathiBuyAdvice": "Sathi says: At this price, buying is a great long-term asset. You are building real equity!",
    "rvb.totalCostBuy": "Total Cost of Ownership (Buy)",
    "rvb.rentInvestPath": "Rent + Invest Path",
    "rvb.downPaymentStampInteriors": "Down Payment + Stamp Duty + Interiors",
    "rvb.totalEmiPaid": "Total EMI Paid",
    "rvb.interestLabel": "Interest",
    "rvb.taxSavings": "Tax Savings (Sec 24b + 80C)",
    "rvb.totalOutOfPocket": "Total Out-of-Pocket",
    "rvb.propertyValueAtYear": "Property Value at Year",
    "rvb.totalRentPaid": "Total Rent Paid",
    "rvb.downPaymentInvested": "Down Payment Invested in",
    "rvb.indexFund": "Index Fund",
    "rvb.monthlySavingsInvested": "Monthly Savings Invested (EMI difference)",
    "rvb.autoCompounded": "Auto-compounded",
    "rvb.investmentCorpus": "Investment Corpus at Year",
    "rvb.netWealth": "Net Wealth (Corpus − Rent)",
    "rvb.shareWhatsApp": "Share on WhatsApp",
    "rvb.saveResult": "Save Result",
    // Purpose Headers
    "ph.we.title": "What is the Wealth Engine?",
    "ph.we.desc": "This tool helps you see how your monthly SIP grows over time using the power of compounding, step-up increases, and inflation adjustment.",
    "ph.we.tip": "Sathi Tip: Starting early is the single most powerful wealth-building habit you can develop.",
    "ph.dk.title": "What is the Debt-Free Path?",
    "ph.dk.desc": "This tool helps you create a step-by-step plan to pay off all your loans faster using proven strategies like Snowball and Avalanche methods.",
    "ph.dk.tip": "Sathi Tip: Paying even ₹500 extra per month can save you lakhs in interest over time.",
    "ph.re.title": "What is the Real Estate Planner?",
    "ph.re.desc": "Plan your property purchase with EMI calculations, hidden costs breakdown, and a side-by-side comparison with mutual fund investments.",
    "ph.re.tip": "Sathi Tip: Always factor in stamp duty, registration, and maintenance — they add 10-15% to the property price.",
    "ph.rvb.title": "What is the Rent vs Buy Analyzer?",
    "ph.rvb.desc": "Compare the total cost of buying a home vs renting and investing the savings. See which strategy builds more wealth over 20 years.",
    "ph.rvb.tip": "Sathi Tip: Making the right choice today can save you lakhs in the future.",
    "ph.emi.title": "What is the EMI Mentor?",
    "ph.emi.desc": "Before buying on EMI, check if you can truly afford it. This tool analyzes your income, existing EMIs, and savings to give you honest advice.",
    "ph.emi.tip": "Sathi Tip: If your total EMIs exceed 40% of income, you're in the danger zone.",
    "ph.cc.title": "What is the Credit Card Master?",
    "ph.cc.desc": "Understand your credit card debt trap. See how minimum payments keep you in debt forever and calculate your true cost of credit card borrowing.",
    "ph.cc.tip": "Sathi Tip: Always pay the full bill. Minimum payment is the bank's way of earning more from you.",
    "ph.sr.title": "What is the Stock Research Lab?",
    "ph.sr.desc": "Research Indian stocks with key financial metrics, peer comparison, and valuation insights — all in one place.",
    "ph.sr.tip": "Sathi Tip: Never invest in a stock without understanding its business and financials first.",
    "ph.ret.title": "What is the Retirement Clock?",
    "ph.ret.desc": "Calculate how much you need to retire comfortably. Factor in inflation, life expectancy, and your current savings to plan your freedom number.",
    "ph.ret.tip": "Sathi Tip: Inflation is the silent killer of retirement dreams. Plan for 6%+ annual increase.",
    "ph.tax.title": "What is the Tax Planner?",
    "ph.tax.desc": "Track your tax-saving investments under Section 80C, 80D, and HRA. See how much more you can save before the financial year ends.",
    "ph.tax.tip": "Sathi Tip: Don't wait until March. Start tax planning in April itself.",
    "ph.compare.title": "What is the Product Comparison?",
    "ph.compare.desc": "Compare credit cards, savings accounts, and insurance plans side by side. Find the best product for your specific needs.",
    "ph.compare.tip": "Sathi Tip: The cheapest product isn't always the best. Compare total value over 3-5 years.",
    "ph.ls.title": "What is the Life-Stage Toolkit?",
    "ph.ls.desc": "Plan for life's biggest moments — child's education, wedding, medical emergencies. See how inflation affects future costs and prepare today.",
    "ph.ls.tip": "Sathi Tip: Education inflation in India is 10-12% — much higher than general inflation. Start early!",
    "ph.ts.title": "What is the Tax Planner?",
    "ph.ts.desc": "Compare Old vs New tax regime. Input your salary, deductions, and freelance income to see which regime saves you more tax.",
    "ph.ts.tip": "Sathi Tip: If your deductions exceed ₹3.75 Lakhs, the Old Regime is likely better for you.",
    // Multi-Asset Comparison page
    "mac.pageTitle": "Multi-Asset Comparison",
    "mac.pageSubtitle": "Compare how your SIP grows across 5 asset classes",
    "ph.mac.title": "What is the Multi-Asset Comparison?",
    "ph.mac.desc": "This tool compares your monthly SIP growth across FD, Gold, Real Estate, Index Funds, and Stock Investment — helping you see the true opportunity cost of playing it safe.",
    "ph.mac.tip": "Sathi Tip: The difference between FD and Equity over 20 years can be enough to buy a house!",
    "mac.tableTitle": "Wealth Gap Result Table",
    "mac.assetName": "Asset Name",
    "mac.totalInvested": "Total Invested",
    "mac.finalWealth": "Final Wealth",
    "mac.gapVsFD": "Gap vs FD",
    "mac.realReturn": "Real Return",
    "mac.volatility": "Volatility",
    "mac.investmentAmount": "Investment Amount (₹)",
    "mac.frequency": "Investment Frequency",
    "mac.freqMonthly": "Monthly (SIP)",
    "mac.freqQuarterly": "Quarterly",
    "mac.freqHalfYearly": "Half-Yearly",
    "mac.freqYearly": "Yearly",
    "mac.chartTitle": "Growth of {amount} Invested {frequency} over {years} Years",
    "mac.yearlyTip": "Investing a lump sum once a year vs smaller amounts every month can lead to different results due to 'Rupee Cost Averaging'. Try both frequencies to see which works better for you!",
    "sidebar.assetComparison": "Asset Comparison",
  },
  hi: {
    "sidebar.dashboard": "डैशबोर्ड",
    "sidebar.calculators": "निवेश कैलकुलेटर",
    "sidebar.compare": "उत्पाद तुलना",
    "sidebar.realEstate": "रियल एस्टेट प्लानर",
    "sidebar.tax": "टैक्स प्लानर",
    "sidebar.tagline": "स्मार्ट वित्तीय टूल्स",
    "sidebar.disclaimer": "केवल शैक्षिक उद्देश्यों के लिए। वित्तीय सलाह नहीं।",
    "sidebar.catDashboard": "डैशबोर्ड",
    "sidebar.catHomeLife": "🏠 घर और जीवन",
    "sidebar.catFuture": "🔮 भविष्य",
    "sidebar.catSafety": "🛡️ सुरक्षा",
    "sidebar.catInvest": "📈 निवेश",
    "sidebar.catBanking": "🏦 बैंकिंग",
    "sidebar.catAccount": "खाता",
    "sidebar.desc.dashboard": "एक नज़र में आपकी वित्तीय स्थिति",
    "sidebar.desc.calculators": "SIP, एकमुश्त और चक्रवृद्धि ब्याज टूल्स",
    "sidebar.desc.compare": "वित्तीय उत्पादों की तुलना करें",
    "sidebar.desc.realEstate": "संपत्ति खरीद और ROI की योजना बनाएं",
    "sidebar.desc.tax": "पुरानी और नई व्यवस्था में टैक्स बचत अनुकूलित करें",
    "sidebar.desc.retirement": "रिटायरमेंट कॉर्पस और SIP की गणना करें",
    "sidebar.desc.rentVsBuy": "किराया या खरीद — क्या बेहतर है जानें",
    "sidebar.desc.debtKiller": "तेज़ी से कर्ज मुक्त होने की योजना बनाएं",
    "sidebar.desc.stockResearch": "लाइव बाज़ार डेटा और स्टॉक विश्लेषण",
    "sidebar.desc.emiMentor": "जाँचें कि क्या आप सच में खरीद सकते हैं",
    "sidebar.desc.childFuture": "बच्चे की शिक्षा और भविष्य खर्चों की योजना",
    "sidebar.desc.creditCard": "क्रेडिट कार्ड कर्ज की असली लागत समझें",
    "sidebar.desc.assetComparison": "विभिन्न एसेट क्लास में रिटर्न तुलना",
    "sidebar.desc.profile": "अपनी वित्तीय प्रोफ़ाइल प्रबंधित करें",
    "sidebar.desc.dataRights": "आपकी डेटा गोपनीयता और डाउनलोड विकल्प",
    "dash.title": "डैशबोर्ड",
    "dash.subtitle": "आपकी वित्तीय स्थिति एक नज़र में",
    "dash.portfolioValue": "पोर्टफोलियो मूल्य",
    "dash.monthlySip": "मासिक SIP",
    "dash.taxSaved": "टैक्स बचत (FY25)",
    "dash.returns1y": "रिटर्न (1 साल)",
    "dash.portfolioGrowth": "पोर्टफोलियो वृद्धि",
    "dash.last9months": "पिछले 9 महीने",
    "dash.quickActions": "त्वरित कार्य",
    "dash.sipCalc": "SIP कैलकुलेटर",
    "dash.compareMF": "म्यूचुअल फंड तुलना",
    "dash.emiEstimator": "EMI अनुमानक",
    "dash.taxOptimizer": "टैक्स प्लानर",
    "dash.marketSnapshot": "बाज़ार स्नैपशॉट",
    "dash.active": "सक्रिय",
    "dash.miniTools": "मिनी टूल्स — पैसों की त्वरित हैक्स",
    "dash.vikasScore": "विकास यात्रा",
    "dash.totalAssets": "कुल संपत्ति",
    "dash.totalLiabilities": "कुल देनदारियाँ",
    "dash.netInvestment": "शुद्ध निवेश",
    "dash.dtiRatio": "DTI अनुपात",
    "dash.savingsInvestments": "बचत + निवेश",
    "dash.annualEmi": "वार्षिक EMI दायित्व",
    "dash.monthlyWealth": "मासिक संपदा निर्माण",
    "dash.financialPulse": "वित्तीय नब्ज़",
    "dash.assetsVsLiabilities": "संपत्ति vs देनदारी — 12 महीने का अनुमान",
    "dash.dailySathiTip": "आज का साथी सुझाव",
    "dash.goalTracker": "लक्ष्य ट्रैकर",
    "dash.welcome": "स्वागत है",
    "dash.welcomeSub": "आज का आपका वित्तीय अवलोकन यहाँ है।",
    "dash.editProfile": "प्रोफ़ाइल संपादित करें",
    "dash.user": "उपयोगकर्ता",
    "ai.insightTitle": "AI अंतर्दृष्टि",
    "ai.sipAdvice": "आपकी {income} आय के आधार पर, हम आपके {goal} को तेज करने के लिए {sip}/माह SIP शुरू करने की सलाह देते हैं।",
    "ai.dtiWarning": "आपका EMI भार {dti}% है — ब्याज कम करने के लिए बैलेंस ट्रांसफर पर विचार करें।",
    "ai.financialGoal": "वित्तीय लक्ष्य",
    "action.title": "कार्य सूची",
    "action.completeProfile": "अपनी वित्तीय प्रोफ़ाइल पूरी करें",
    "action.setup": "सेटअप",
    "action.runTax": "टैक्स ऑप्टिमाइज़ेशन चलाएँ",
    "action.open": "खोलें",
    "action.rentVsBuy": "किराया बनाम खरीदना विश्लेषण",
    "action.analyze": "विश्लेषण",
    "goal.buyHome": "घर खरीदें",
    "goal.payDebt": "कर्ज़ चुकाएँ",
    "goal.retireEarly": "जल्दी रिटायर हों",
    "goal.emergency": "आपातकालीन निधि",
    "goal.childEdu": "बच्चे की शिक्षा",
    "goal.wealthGrowth": "संपत्ति बढ़ाएँ",
    "goal.eta50plus": "50+ वर्ष",
    "goal.achieved": "प्राप्त",
    "goal.basedOn": "आधारित",
    "goal.moSIP": "मासिक SIP",
    "goal.returns": "रिटर्न",
    "goal.saved": "बचत",
    "goal.target": "लक्ष्य",
    "goal.custom": "कस्टम",
    "goal.complete": "पूर्ण",
    "goal.yrsToGo": "वर्ष शेष",
    "goal.sipNeeded": "लक्ष्य तक पहुँचने के लिए SIP",
    "goal.yrs": "वर्ष",
    "goal.month": "महीना",
    "goal.save": "सहेजें",
    "goal.reset": "रीसेट",
    "goal.setCustomTarget": "कस्टम लक्ष्य सेट करें",
    "goal.noGoals": "कोई लक्ष्य सेट नहीं। लक्ष्य ट्रैक करने के लिए प्रोफ़ाइल अपडेट करें।",
    "goal.enterValid": "सही राशि दर्ज करें",
    "goal.targetUpdated": "लक्ष्य अपडेट हुआ",
    "goal.resetDefault": "डिफ़ॉल्ट पर रीसेट",
    "mini.chaiTitle": "चाय-सुट्टा SIP ☕",
    "mini.chaiDesc": "अगर आप रोज़ चाय-सुट्टा का पैसा निवेश करें तो?",
    "mini.dailyExpense": "रोज़ का खर्च",
    "mini.years": "निवेश अवधि",
    "mini.couldBecome": "बन सकता है",
    "mini.vampireTitle": "वैम्पायर खर्च ऑडिट 🧛",
    "mini.vampireDesc": "सब्सक्रिप्शन चुपचाप आपकी दौलत खा रहे हैं",
    "mini.10yrCost": "10 साल की अवसर लागत",
    "mini.inflationTitle": "महंगाई टाइम मशीन ⏳",
    "mini.inflationDesc": "आज की कीमत भविष्य में कितनी होगी",
    "mini.todayPrice": "आज की कीमत (₹)",
    "calc.subtitle": "सटीकता से अपनी संपत्ति की योजना बनाएं",
    "calc.sipCalc": "SIP कैलकुलेटर",
    "calc.lumpSum": "एकमुश्त",
    "calc.monthlyInvestment": "मासिक निवेश",
    "calc.investmentAmount": "निवेश राशि",
    "calc.expectedReturn": "अपेक्षित रिटर्न (%)",
    "calc.timePeriod": "समय अवधि (वर्ष)",
    "calc.yrs": "वर्ष",
    "calc.totalValue": "कुल मूल्य",
    "calc.invested": "निवेशित",
    "calc.gains": "लाभ",
    "we.pageTitle": "VittaSathi वेल्थ इंजन",
    "we.pageSubtitle": "स्टेप-अप SIP और मुद्रास्फीति-समायोजित अनुमान से अपनी संपत्ति बढ़ाएं",
    "we.wealthEngine": "वेल्थ इंजन",
    "we.monthlyInvestment": "मासिक निवेश",
    "we.expectedReturn": "अपेक्षित रिटर्न (%)",
    "we.tenure": "निवेश अवधि (वर्ष)",
    "we.stepUp": "वार्षिक स्टेप-अप",
    "we.perYear": "प्रति वर्ष",
    "we.stepUpHint": "जैसे-जैसे वेतन बढ़े, हर साल SIP बढ़ाएं",
    "we.inflationToggle": "आज के मूल्य में दिखाएं",
    "we.inflationHint": "भविष्य के मूल्य को 6% मुद्रास्फीति से छूट दें",
    "we.totalValue": "कुल कॉर्पस",
    "we.totalValueInflation": "कॉर्पस (आज का मूल्य)",
    "we.wealthGained": "अर्जित संपत्ति",
    "we.wealthRatio": "संपत्ति अनुपात",
    "we.interestShare": "आपके कॉर्पस का हिस्सा शुद्ध ब्याज है — चक्रवृद्धि की शक्ति!",
    "we.sathiSays": "साथी कहता है:",
    "we.sathiMessage": "आपका पैसा आपसे {X} गुना ज़्यादा मेहनत कर रहा है! SIP जारी रखें।",
    "we.growthChart": "समय के साथ संपत्ति वृद्धि",
    "we.amountInvested": "निवेशित राशि",
    "we.compoundingPower": "चक्रवृद्धि की शक्ति!",
    "we.gainsExceed": "आपकी अर्जित संपत्ति आपकी कुल निवेशित राशि से अधिक है।",
    "we.stepUpTip": "स्टेप-अप सुपरस्टार!",
    "we.stepUpTipDesc": "10%+ वार्षिक स्टेप-अप समान अवधि में फ्लैट SIP की तुलना में कॉर्पस को लगभग दोगुना कर सकता है।",
    "we.caution": "सावधानी:",
    "we.cautionDesc": "{years} वर्षों में {rate}% रिटर्न की उम्मीद जोखिमभरी है।",
    "we.saveResult": "परिणाम सहेजें",
    "we.delayToggle": "क्या होगा अगर मैं रुकूं? (देरी की कीमत)",
    "we.delayHint": "देखें SIP शुरू करने में देरी से कितनी संपत्ति खोते हैं",
    "we.delaySliderLabel": "देरी के वर्ष",
    "we.penaltyTitle": "इंतज़ार की सज़ा",
    "we.sathiInsight": "साथी इनसाइट:",
    "we.delayInsight": "{X} साल इंतज़ार करने पर आप {PENALTY} का 'टालमटोल टैक्स' चुका रहे हैं। यह पैसा आपके बच्चे की {YEARS} साल की शिक्षा या एक शानदार रिटायरमेंट होम के लिए हो सकता था।",
    "we.lostWealth": "खोई हुई संपत्ति (देरी का जुर्माना)",
    "we.exploreMore": "और टूल्स देखें",
    "we.assetFD": "सुरक्षित FD",
    "we.assetGold": "पारंपरिक सोना",
    "we.assetRealEstate": "रियल एस्टेट",
    "we.assetIndex": "इंडेक्स फंड",
    "we.assetStock": "शेयर निवेश",
    "we.riskFD": "पूंजी सुरक्षित, लेकिन मुश्किल से महंगाई को मात देती है।",
    "we.riskGold": "मुद्रास्फीति के खिलाफ सुरक्षा। कोई नियमित आय नहीं।",
    "we.riskRealEstate": "किराये की आय की संभावना वाली मूर्त संपत्ति। 5+ वर्ष का नज़रिया चाहिए।",
    "we.riskIndex": "ऐतिहासिक रूप से लंबी अवधि में मुद्रास्फीति को मात देता है।",
    "we.riskStock": "सबसे अधिक संभावना, सबसे अधिक जोखिम। गिरावट में 40%+ खो सकते हैं।",
    "we.opportunityCost": "अवसर लागत (संपत्ति अंतर)",
    "we.wealthGapDesc": "{years} वर्षों में शेयर निवेश और सुरक्षित FD के बीच अंतर",
    "we.sathiStockVsFD": "FD की जगह शेयर निवेश चुनकर, {years} वर्षों बाद आपकी संपत्ति {amount} अधिक हो सकती है!",
    "we.growthComparison": "वृद्धि तुलना",
    "we.sathiSecret": "साथी का राज़",
    "we.sathiSecretText": "FD 6.5% देती है जबकि भारत में मुद्रास्फीति ~6% है। FD में आपकी वास्तविक वृद्धि केवल 0.5% प्रति वर्ष है।",

    // Wealth Simulator (Hindi)
    "ws.pageTitle": "VittaSathi वेल्थ सिम्युलेटर",
    "ws.pageSubtitle": "प्रोफेशनल-ग्रेड SIP, एकमुश्त और हाइब्रिड संपत्ति अनुमान",
    "ws.modeSIP": "मासिक SIP",
    "ws.modeLumpsum": "एकमुश्त",
    "ws.modeHybrid": "हाइब्रिड (दोनों)",
    "ws.monthlySIP": "मासिक SIP राशि",
    "ws.lumpsumSeed": "एकमुश्त राशि",
    "ws.expectedReturn": "अपेक्षित रिटर्न (%)",
    "ws.timeHorizon": "समय अवधि (वर्ष)",
    "ws.stepUpSIP": "स्टेप-अप SIP (% प्रति वर्ष)",
    "ws.stepUpHint": "वेतन वृद्धि के साथ हर साल SIP बढ़ाएं",
    "ws.inflationToggle": "महंगाई-समायोजित मूल्य दिखाएं (6%)",
    "ws.inflationHint": "आज के मूल्य में क्रय शक्ति देखें",
    "ws.noStepUpWarning": "साथी, आपका पैसा बढ़ रहा है, लेकिन कीमतें भी बढ़ रही हैं। आगे रहने के लिए हर साल SIP बढ़ाएं!",
    "ws.finalCorpus": "अंतिम कॉर्पस",
    "ws.totalInvested": "कुल निवेशित",
    "ws.wealthGained": "अर्जित संपत्ति",
    "ws.croreClub": "₹1 करोड़ क्लब में स्वागत! 🎉",
    "ws.wealthTrajectory": "संपत्ति प्रक्षेपवक्र",
    "ws.wealthBreakdown": "संपत्ति विवरण",
    "ws.futureLifestyle": "भविष्य जीवनशैली नोट",
    "ws.futureLifestyleDesc": "अगर आप इस SIP को स्टॉक लैब में 5 साल के लिए निवेश करें, तो आपके पास {X} हो सकता है!",
    "ws.sendToGoalPlanner": "गोल प्लानर को भेजें",
    "ws.disclaimer": "यह टूल समय के साथ चक्रवृद्धि की गणितीय शक्ति को दर्शाता है। यह शैक्षिक अनुमान है, बाजार रिटर्न की गारंटी नहीं।",

    "compare.title": "उत्पाद तुलना",
    "compare.subtitle": "म्यूचुअल फंड, FD और बीमा योजनाओं की तुलना करें",
    "compare.fundName": "फंड नाम",
    "compare.1yReturn": "1 साल रिटर्न",
    "compare.3yReturn": "3 साल रिटर्न",
    "compare.5yReturn": "5 साल रिटर्न",
    "compare.risk": "जोखिम",
    "compare.expense": "खर्च",
    "compare.disclaimer": "* पिछला प्रदर्शन भविष्य के रिटर्न की गारंटी नहीं देता।",
    "compare.low": "कम",
    "compare.moderate": "मध्यम",
    "compare.high": "उच्च",
    "compare.moderateHigh": "मध्यम-उच्च",
    "re.title": "रियल एस्टेट प्लानर",
    "re.subtitle": "EMI कैलकुलेटर और प्रॉपर्टी प्लानिंग टूल्स",
    "re.propertyValue": "प्रॉपर्टी मूल्य",
    "re.downPayment": "डाउन पेमेंट (%)",
    "re.interestRate": "ब्याज दर (%)",
    "re.loanTenure": "लोन अवधि (वर्ष)",
    "re.monthlyEmi": "मासिक EMI",
    "re.loanAmount": "लोन राशि",
    "re.totalInterest": "कुल ब्याज",
    "re.totalPayment": "कुल भुगतान",
    "tax.title": "टैक्स प्लानर",
    "tax.subtitle": "भारतीय कर कानून के तहत टैक्स बचत की योजना बनाएं",
    "tax.recommended": "अनुशंसित",
    "tax.alternative": "विकल्प",
    "tax.oldRegime": "पुरानी टैक्स रिजीम",
    "tax.newRegime": "नई टैक्स रिजीम",
    "tax.estimatedTax": "अनुमानित टैक्स",
    "tax.withDeductions": "₹2,41,000 की कटौती के साथ",
    "tax.standardDeduction": "मानक कटौती ₹75,000",
    "tax.section80c": "धारा 80C – ₹1.5 लाख",
    "tax.section80d": "धारा 80D – स्वास्थ्य बीमा",
    "tax.hraExemption": "HRA छूट",
    "tax.elss": "ELSS म्यूचुअल फंड",
    "tax.epf": "EPF योगदान",
    "tax.lifeInsurance": "जीवन बीमा प्रीमियम",
    "tax.selfFamily": "स्वयं और परिवार प्रीमियम",
    "tax.parentsPremium": "माता-पिता प्रीमियम",
    "tax.rentPaid": "किराया भुगतान (वार्षिक)",
    "tax.exemptedAmount": "छूट प्राप्त राशि",
    "tax.invested": "निवेशित",
    "tax.autoDeducted": "स्वतः कटौती",
    "tax.paid": "भुगतान",
    "tax.claimable": "दावा योग्य",
    "tax.calculated": "गणना",
    "sidebar.retirement": "रिटायरमेंट क्लॉक",
    "re.step1Title": "बजट",
    "re.step2Title": "ROI सिम्युलेटर",
    "re.step3Title": "किफायत",
    "re.stampDuty": "स्टाम्प ड्यूटी",
    "re.registration": "पंजीकरण",
    "re.gst": "GST",
    "re.totalCost": "कुल संपत्ति लागत",
    "re.nextStep": "अगला कदम",
    "re.back": "वापस",
    "re.roiDesc": "तुलना: अगर आप वही राशि म्यूचुअल फंड में निवेश करते तो?",
    "re.realEstateInv": "रियल एस्टेट निवेश",
    "re.mutualFundInv": "म्यूचुअल फंड निवेश",
    "re.rentalYield": "किराया उपज",
    "re.appreciation": "मूल्य वृद्धि",
    "re.propertyAfter20": "संपत्ति मूल्य (20 वर्ष)",
    "re.totalRentalIncome": "कुल किराया आय",
    "re.costDeductions": "लागत कटौती",
    "re.netGain": "शुद्ध लाभ",
    "re.expectedReturn": "अपेक्षित रिटर्न",
    "re.amountInvested": "निवेशित राशि",
    "re.valueAfter20": "20 वर्ष बाद मूल्य",
    "re.mfWins": "म्यूचुअल फंड जीतता है",
    "re.reWins": "रियल एस्टेट जीतती है",
    "re.summary": "सारांश",
    "re.minIncome": "न्यूनतम मासिक आय",
    "re.affordable": "यह संपत्ति आपके लिए किफायती है!",
    "re.notAffordable": "यह संपत्ति आपके बजट से बाहर हो सकती है।",
    "re.emiShouldBe": "EMI आपकी मासिक आय के 40% से कम होनी चाहिए।",
    "re.nextSteps": "अगले कदम गाइड",
    "re.step1Tip": "2-3 बैंकों से होम लोन प्री-अप्रूवल लें",
    "re.step2Tip": "संपत्ति का RERA पंजीकरण सत्यापित करें",
    "re.step3Tip": "इंटीरियर और स्थानांतरण लागत (5-10%) का बजट रखें",
    "re.step4Tip": "कब्जे से पहले होम इंश्योरेंस पर विचार करें",
    "re.startOver": "फिर से शुरू करें",
    "re.step4Title": "ऋण पात्रता",
    "re.empType": "रोजगार प्रकार",
    "re.empSalaried": "वेतनभोगी",
    "re.empBusiness": "व्यवसाय स्वामी",
    "re.empFreelancer": "फ्रीलांसर",
    "re.netSalary": "शुद्ध मासिक वेतन (₹)",
    "re.existingEmis": "मौजूदा EMI (₹)",
    "re.workExp": "कार्य अनुभव",
    "re.maxAllowableEmi": "अधिकतम अनुमत EMI",
    "re.maxLoanAmount": "अधिकतम ऋण राशि",
    "re.requiredLoan": "आवश्यक ऋण राशि",
    "re.calcBasis": "गणना आधार",
    "re.eligible": "पात्र — आप योग्य हैं!",
    "re.borderline": "सीमा रेखा — सीमा के करीब",
    "re.notEligible": "अपात्र — अंतर बहुत बड़ा",
    "re.eligibleTip": "आपका आवश्यक ऋण आपकी पात्रता में है। आवेदन करने के लिए तैयार हैं!",
    "re.borderlineTip": "सीमा बढ़ाने के लिए पति/पत्नी जैसे सह-आवेदक जोड़ें।",
    "re.notEligibleTip": "अपना डाउन पेमेंट बढ़ाएं या पहले मौजूदा कर्ज चुकाएं।",
    "re.sathiLoanTip": "साथी टिप: बैंक स्थिरता पसंद करते हैं। अगर आपने पिछले 3 महीनों में नौकरी बदली है, तो आवेदन से पहले 3 सैलरी स्लिप का इंतजार करें!",
    "re.addCoApplicant": "सह-आवेदक आय जोड़ें?",
    "re.coApplicantSalary": "सह-आवेदक मासिक वेतन (₹)",
    "re.coApplicantNote": "पति/पत्नी या माता-पिता की आय जोड़ने से आपकी ऋण पात्रता काफी बढ़ सकती है!",
    "ret.title": "रिटायरमेंट क्लॉक",
    "ret.subtitle": "मुद्रास्फीति-समायोजित गणनाओं के साथ रिटायरमेंट की योजना बनाएं",
    "ret.monthlyExpense": "वर्तमान मासिक खर्च",
    "ret.currentAge": "वर्तमान आयु",
    "ret.retireAge": "रिटायरमेंट की आयु",
    "ret.inflationRate": "मुद्रास्फीति दर (%)",
    "ret.sipReturn": "अपेक्षित SIP रिटर्न (%)",
    "ret.yearsLeft": "रिटायरमेंट में बचे वर्ष",
    "ret.futureCost": "भविष्य की मासिक लागत",
    "ret.month": "महीना",
    "ret.inflationNote": "मुद्रास्फीति के लिए समायोजित आपकी जीवनशैली लागत",
    "ret.corpusNeeded": "रिटायरमेंट कॉर्पस आवश्यक",
    "ret.sipNeeded": "मासिक SIP आवश्यक",
    "ret.sipNote": "आरामदायक रिटायरमेंट के लिए आज ही यह SIP शुरू करें",
    "sidebar.rentVsBuy": "किराया vs खरीद",
    "sidebar.debtKiller": "कर्ज-मुक्ति पथ",
    "sidebar.stockResearch": "स्टॉक रिसर्च",
    "sidebar.emiMentor": "क्या मैं इसे खरीदूँ?",
    "sidebar.lifeStages": "जीवन चरण",
    "sidebar.childFuture": "बच्चे का भविष्य प्लानर",
    "sidebar.creditCard": "क्रेडिट कार्ड मास्टर",
    "sidebar.profile": "मेरी प्रोफ़ाइल",
    "sidebar.dataRights": "डेटा अधिकार",
    "rvb.title": "किराया vs खरीद विश्लेषक",
    "rvb.subtitle": "किराया दें या खरीदें? समय के साथ वास्तविक लागत की तुलना करें।",
    "rvb.cityPreset": "शहर प्रीसेट",
    "rvb.custom": "कस्टम",
    "rvb.taxRegime": "कर व्यवस्था",
    "rvb.oldRegime": "पुरानी व्यवस्था",
    "rvb.newRegime": "नई व्यवस्था",
    "rvb.horizon": "विश्लेषण अवधि",
    "rvb.yrs": "वर्ष",
    "rvb.buyPath": "खरीद पथ",
    "rvb.rentPath": "किराया + निवेश पथ",
    "rvb.propertyPrice": "संपत्ति की कीमत",
    "rvb.downPayment": "डाउन पेमेंट (%)",
    "rvb.loanRate": "ऋण ब्याज दर (%)",
    "rvb.loanTenure": "ऋण अवधि (वर्ष)",
    "rvb.interiors": "इंटीरियर और फर्निशिंग लागत",
    "rvb.monthlyMaintenance": "मासिक रखरखाव",
    "rvb.yearlyPropertyTax": "वार्षिक संपत्ति कर",
    "rvb.appreciationRate": "संपत्ति मूल्य वृद्धि (%)",
    "rvb.monthlyRent": "मासिक किराया",
    "rvb.rentIncrease": "वार्षिक किराया वृद्धि (%)",
    "rvb.mfReturn": "म्यूचुअल फंड रिटर्न (%)",
    "rvb.opportunityCostNote": "अवसर लागत: डाउन पेमेंट को निवेश किया जाता",
    "rvb.investedInMF": "म्यूचुअल फंड में निवेशित",
    "rvb.hraNote": "याद रखें: HRA छूट केवल पुरानी व्यवस्था में उपलब्ध",
    "rvb.monthlyEmi": "मासिक EMI",
    "rvb.recommendation": "सिफारिश",
    "rvb.buyingBetter": "खरीदना अधिक संपत्ति बनाता है",
    "rvb.rentingBetter": "किराया आपकी संपत्ति के लिए बेहतर है",
    "rvb.wealthDiff": "संपत्ति अंतर",
    "rvb.breakeven": "ब्रेकइवन वर्ष",
    "rvb.year": "वर्ष",
    "rvb.neverBreakeven": "खरीदना कभी नहीं पकड़ पाता",
    "rvb.wealthComparison": "शुद्ध संपत्ति तुलना",
    "rvb.years": "वर्ष",
    "rvb.buyWealth": "खरीद: शुद्ध संपत्ति",
    "rvb.rentWealth": "किराया + निवेश: शुद्ध संपत्ति",
    "rvb.taxRulesTitle": "2026 कर नियम लागू",
    "rvb.taxRule1": "पुरानी व्यवस्था: धारा 24b ब्याज कटौती ₹2L तक",
    "rvb.taxRule2": "पुरानी व्यवस्था: धारा 80C मूलधन कटौती ₹1.5L तक",
    "rvb.taxRule3": "नई व्यवस्था: स्व-आवासित संपत्ति के लिए कोई कटौती नहीं",
    "rvb.assumptions": "उपयोग की गई धारणाएँ",
    "rvb.assumption1": "संपत्ति मूल्य वृद्धि",
    "rvb.assumption2": "किराया वृद्धि",
    "rvb.assumption3": "MF रिटर्न (CAGR)",
    "rvb.purposeTitle": "यह टूल क्या करता है?",
    "rvb.purposeDesc": "यह टूल घर खरीदने की वास्तविक लागत (EMI, स्टाम्प ड्यूटी, रखरखाव) की तुलना किराये पर रहने और अंतर को म्यूचुअल फंड में निवेश करने से करता है। जानें कौन सा रास्ता अधिक संपत्ति बनाता है।",
    "rvb.hiddenCosts": "छिपी लागत (स्टाम्प ड्यूटी, रखरखाव, कर)",
    "rvb.stampDuty": "स्टाम्प ड्यूटी",
    "rvb.registration": "पंजीकरण",
    "rvb.wealthGapChart": "संपत्ति अंतर: खरीद vs किराया + निवेश",
    "rvb.sathiVerdict": "साथी की राय",
    "rvb.sathiRentAdvice": "साथी कहता है: किराया आपको प्रति माह {AMT} बचा रहा है। इसे SIP में निवेश करें और अपना सपनों का घर जल्दी बनाएं!",
    "rvb.sathiBuyAdvice": "साथी कहता है: इस कीमत पर, खरीदना एक बेहतरीन दीर्घकालिक संपत्ति है। आप वास्तविक इक्विटी बना रहे हैं!",
    "rvb.totalCostBuy": "स्वामित्व की कुल लागत (खरीद)",
    "rvb.rentInvestPath": "किराया + निवेश पथ",
    "rvb.downPaymentStampInteriors": "डाउन पेमेंट + स्टाम्प ड्यूटी + इंटीरियर",
    "rvb.totalEmiPaid": "कुल EMI भुगतान",
    "rvb.interestLabel": "ब्याज",
    "rvb.taxSavings": "कर बचत (धारा 24b + 80C)",
    "rvb.totalOutOfPocket": "कुल जेब से खर्च",
    "rvb.propertyValueAtYear": "वर्ष में संपत्ति मूल्य",
    "rvb.totalRentPaid": "कुल किराया भुगतान",
    "rvb.downPaymentInvested": "डाउन पेमेंट निवेशित",
    "rvb.indexFund": "इंडेक्स फंड",
    "rvb.monthlySavingsInvested": "मासिक बचत निवेशित (EMI अंतर)",
    "rvb.autoCompounded": "ऑटो-कंपाउंडेड",
    "rvb.investmentCorpus": "वर्ष में निवेश कोष",
    "rvb.netWealth": "शुद्ध संपत्ति (कोष − किराया)",
    "rvb.shareWhatsApp": "WhatsApp पर शेयर करें",
    "rvb.saveResult": "परिणाम सहेजें",
    "ph.we.title": "वेल्थ इंजन क्या है?",
    "ph.we.desc": "यह टूल दिखाता है कि चक्रवृद्धि, स्टेप-अप और मुद्रास्फीति समायोजन से आपकी मासिक SIP कैसे बढ़ती है।",
    "ph.we.tip": "साथी टिप: जल्दी शुरू करना सबसे शक्तिशाली संपत्ति-निर्माण आदत है।",
    "ph.dk.title": "ऋण-मुक्ति मार्ग क्या है?",
    "ph.dk.desc": "यह टूल आपको स्नोबॉल और एवलांच जैसी सिद्ध रणनीतियों से तेज़ी से कर्ज़ चुकाने की योजना बनाने में मदद करता है।",
    "ph.dk.tip": "साथी टिप: हर महीने ₹500 अतिरिक्त भुगतान करने से समय के साथ लाखों की बचत हो सकती है।",
    "ph.re.title": "रियल एस्टेट प्लानर क्या है?",
    "ph.re.desc": "EMI गणना, छिपी लागतों का विवरण, और म्यूचुअल फंड निवेश के साथ तुलना से अपनी संपत्ति खरीद की योजना बनाएं।",
    "ph.re.tip": "साथी टिप: स्टाम्प ड्यूटी, पंजीकरण और रखरखाव हमेशा जोड़ें — ये संपत्ति की कीमत में 10-15% जोड़ते हैं।",
    "ph.rvb.title": "किराया बनाम खरीद विश्लेषक क्या है?",
    "ph.rvb.desc": "घर खरीदने की कुल लागत बनाम किराये पर रहकर बचत निवेश करने की तुलना करें। 20 साल में कौन सी रणनीति ज़्यादा संपत्ति बनाती है।",
    "ph.rvb.tip": "साथी टिप: आज सही विकल्प चुनने से भविष्य में लाखों की बचत हो सकती है।",
    "ph.emi.title": "EMI मेंटर क्या है?",
    "ph.emi.desc": "EMI पर खरीदने से पहले, जांचें कि क्या आप वास्तव में इसे वहन कर सकते हैं। यह टूल आपकी आय, मौजूदा EMI और बचत का विश्लेषण करता है।",
    "ph.emi.tip": "साथी टिप: अगर आपकी कुल EMI आय के 40% से अधिक है, तो आप खतरे में हैं।",
    "ph.cc.title": "क्रेडिट कार्ड मास्टर क्या है?",
    "ph.cc.desc": "अपने क्रेडिट कार्ड कर्ज़ जाल को समझें। देखें कि न्यूनतम भुगतान आपको कैसे हमेशा कर्ज़ में रखता है।",
    "ph.cc.tip": "साथी टिप: हमेशा पूरा बिल चुकाएं। न्यूनतम भुगतान बैंक का ज़्यादा कमाने का तरीका है।",
    "ph.sr.title": "स्टॉक रिसर्च लैब क्या है?",
    "ph.sr.desc": "प्रमुख वित्तीय मेट्रिक्स, पीयर तुलना और वैल्यूएशन इनसाइट्स के साथ भारतीय स्टॉक्स का अनुसंधान करें।",
    "ph.sr.tip": "साथी टिप: बिना व्यवसाय और वित्तीय समझ के कभी किसी शेयर में निवेश न करें।",
    "ph.ret.title": "रिटायरमेंट क्लॉक क्या है?",
    "ph.ret.desc": "आराम से रिटायर होने के लिए कितनी राशि चाहिए, यह गणना करें। मुद्रास्फीति, जीवन प्रत्याशा और वर्तमान बचत को शामिल करें।",
    "ph.ret.tip": "साथी टिप: मुद्रास्फीति रिटायरमेंट सपनों का मूक हत्यारा है। 6%+ वार्षिक वृद्धि की योजना बनाएं।",
    "ph.tax.title": "टैक्स प्लानर क्या है?",
    "ph.tax.desc": "धारा 80C, 80D और HRA के तहत अपने कर-बचत निवेशों को ट्रैक करें।",
    "ph.tax.tip": "साथी टिप: मार्च तक इंतज़ार न करें। अप्रैल में ही टैक्स प्लानिंग शुरू करें।",
    "ph.compare.title": "उत्पाद तुलना क्या है?",
    "ph.compare.desc": "क्रेडिट कार्ड, बचत खाते और बीमा योजनाओं की साथ-साथ तुलना करें।",
    "ph.compare.tip": "साथी टिप: सबसे सस्ता उत्पाद हमेशा सबसे अच्छा नहीं होता। 3-5 साल में कुल मूल्य की तुलना करें।",
    "ph.ls.title": "लाइफ-स्टेज टूलकिट क्या है?",
    "ph.ls.desc": "जीवन के बड़े पलों की योजना बनाएं — बच्चे की शिक्षा, शादी, चिकित्सा आपातकाल। देखें कि मुद्रास्फीति भविष्य की लागत कैसे बढ़ाती है।",
    "ph.ls.tip": "साथी टिप: भारत में शिक्षा मुद्रास्फीति 10-12% है — सामान्य मुद्रास्फीति से बहुत अधिक!",
    "ph.ts.title": "टैक्स सिम्युलेटर क्या है?",
    "ph.ts.desc": "पुरानी बनाम नई कर व्यवस्था की तुलना करें। देखें कौन सी व्यवस्था ज़्यादा टैक्स बचाती है।",
    "ph.ts.tip": "साथी टिप: अगर आपकी कटौतियां ₹3.75 लाख से अधिक हैं, तो पुरानी व्यवस्था शायद बेहतर है।",
    "mac.pageTitle": "मल्टी-एसेट तुलना",
    "mac.pageSubtitle": "देखें कि आपकी SIP 5 एसेट क्लास में कैसे बढ़ती है",
    "ph.mac.title": "मल्टी-एसेट तुलना क्या है?",
    "ph.mac.desc": "यह टूल FD, सोना, रियल एस्टेट, इंडेक्स फंड और शेयर निवेश में आपकी मासिक SIP वृद्धि की तुलना करता है।",
    "ph.mac.tip": "साथी टिप: 20 साल में FD और इक्विटी के बीच का अंतर एक घर खरीदने के लिए काफी हो सकता है!",
    "mac.tableTitle": "संपत्ति अंतर परिणाम तालिका",
    "mac.assetName": "एसेट नाम",
    "mac.totalInvested": "कुल निवेश",
    "mac.finalWealth": "अंतिम संपत्ति",
    "mac.gapVsFD": "FD से अंतर",
    "mac.realReturn": "वास्तविक रिटर्न",
    "mac.volatility": "अस्थिरता",
    "mac.investmentAmount": "निवेश राशि (₹)",
    "mac.frequency": "निवेश आवृत्ति",
    "mac.freqMonthly": "मासिक (SIP)",
    "mac.freqQuarterly": "त्रैमासिक",
    "mac.freqHalfYearly": "अर्ध-वार्षिक",
    "mac.freqYearly": "वार्षिक",
    "mac.chartTitle": "{amount} {frequency} निवेश की {years} वर्षों में वृद्धि",
    "mac.yearlyTip": "साल में एक बार एकमुश्त निवेश बनाम हर महीने छोटी राशि — 'रुपी कॉस्ट एवरेजिंग' के कारण परिणाम अलग हो सकते हैं। दोनों आवृत्तियाँ आज़माएं!",
    "sidebar.assetComparison": "एसेट तुलना",
  },
  bn: {
    "sidebar.dashboard": "ড্যাশবোর্ড",
    "sidebar.calculators": "বিনিয়োগ ক্যালকুলেটর",
    "sidebar.compare": "পণ্য তুলনা",
    "sidebar.realEstate": "রিয়েল এস্টেট প্ল্যানার",
    "sidebar.tax": "ট্যাক্স প্ল্যানার",
    "sidebar.tagline": "স্মার্ট আর্থিক টুলস",
    "sidebar.disclaimer": "শুধুমাত্র শিক্ষামূলক উদ্দেশ্যে। আর্থিক পরামর্শ নয়।",
    "sidebar.catDashboard": "ড্যাশবোর্ড",
    "sidebar.catHomeLife": "🏠 বাড়ি ও জীবন",
    "sidebar.catFuture": "🔮 ভবিষ্যৎ",
    "sidebar.catSafety": "🛡️ নিরাপত্তা",
    "sidebar.catInvest": "📈 বিনিয়োগ",
    "sidebar.catBanking": "🏦 ব্যাংকিং",
    "sidebar.catAccount": "অ্যাকাউন্ট",
    "sidebar.desc.dashboard": "এক নজরে আপনার আর্থিক সারসংক্ষেপ",
    "sidebar.desc.calculators": "SIP, একমুঠো ও চক্রবৃদ্ধি সুদের টুলস",
    "sidebar.desc.compare": "আর্থিক পণ্যগুলি পাশাপাশি তুলনা করুন",
    "sidebar.desc.realEstate": "সম্পত্তি কেনা ও ROI পরিকল্পনা করুন",
    "sidebar.desc.tax": "পুরনো ও নতুন কর ব্যবস্থায় সঞ্চয় অপ্টিমাইজ করুন",
    "sidebar.desc.retirement": "অবসর তহবিল ও SIP গণনা করুন",
    "sidebar.desc.rentVsBuy": "ভাড়া নাকি কেনা — কোনটি ভালো জানুন",
    "sidebar.desc.debtKiller": "দ্রুত ঋণমুক্ত হওয়ার পরিকল্পনা করুন",
    "sidebar.desc.stockResearch": "লাইভ বাজার ডেটা ও স্টক বিশ্লেষণ",
    "sidebar.desc.emiMentor": "আপনি সত্যিই কিনতে পারবেন কিনা পরীক্ষা করুন",
    "sidebar.desc.childFuture": "সন্তানের শিক্ষা ও ভবিষ্যৎ খরচের পরিকল্পনা",
    "sidebar.desc.creditCard": "ক্রেডিট কার্ড ঋণের প্রকৃত খরচ বুঝুন",
    "sidebar.desc.assetComparison": "বিভিন্ন সম্পদ শ্রেণিতে রিটার্ন তুলনা",
    "sidebar.desc.profile": "আপনার আর্থিক প্রোফাইল পরিচালনা করুন",
    "sidebar.desc.dataRights": "আপনার ডেটা গোপনীয়তা ও ডাউনলোড বিকল্প",
    "dash.title": "ড্যাশবোর্ড",
    "dash.subtitle": "আপনার আর্থিক পরিস্থিতি এক নজরে",
    "dash.portfolioValue": "পোর্টফোলিও মূল্য",
    "dash.monthlySip": "মাসিক SIP",
    "dash.taxSaved": "ট্যাক্স সঞ্চয় (FY25)",
    "dash.returns1y": "রিটার্ন (১ বছর)",
    "dash.portfolioGrowth": "পোর্টফোলিও বৃদ্ধি",
    "dash.last9months": "গত ৯ মাস",
    "dash.quickActions": "দ্রুত কার্য",
    "dash.sipCalc": "SIP ক্যালকুলেটর",
    "dash.compareMF": "মিউচুয়াল ফান্ড তুলনা",
    "dash.emiEstimator": "EMI অনুমানক",
    "dash.taxOptimizer": "ট্যাক্স প্ল্যানার",
    "dash.marketSnapshot": "বাজার স্ন্যাপশট",
    "dash.active": "সক্রিয়",
    "dash.vikasScore": "বিকাশ যাত্রা",
    "dash.totalAssets": "মোট সম্পদ",
    "dash.totalLiabilities": "মোট দায়",
    "dash.netInvestment": "নিট বিনিয়োগ",
    "dash.dtiRatio": "DTI অনুপাত",
    "dash.savingsInvestments": "সঞ্চয় + বিনিয়োগ",
    "dash.annualEmi": "বার্ষিক EMI দায়",
    "dash.monthlyWealth": "মাসিক সম্পদ গঠন",
    "dash.financialPulse": "আর্থিক পালস",
    "dash.assetsVsLiabilities": "সম্পদ vs দায় — ১২ মাসের অনুমান",
    "dash.dailySathiTip": "আজকের সাথী টিপ",
    "dash.goalTracker": "লক্ষ্য ট্র্যাকার",
    "dash.miniTools": "মিনি টুলস — দ্রুত অর্থ হ্যাক",
    "dash.welcome": "স্বাগতম",
    "dash.welcomeSub": "আজকের আপনার আর্থিক সারসংক্ষেপ এখানে।",
    "dash.editProfile": "প্রোফাইল সম্পাদনা",
    "dash.user": "ব্যবহারকারী",
    "ai.insightTitle": "AI অন্তর্দৃষ্টি",
    "ai.sipAdvice": "আপনার {income} আয়ের ভিত্তিতে, আপনার {goal} দ্রুত করতে {sip}/মাস SIP শুরু করার পরামর্শ দিই।",
    "ai.dtiWarning": "আপনার EMI বোঝা {dti}% — সুদ কমাতে ব্যালেন্স ট্রান্সফার বিবেচনা করুন।",
    "ai.financialGoal": "আর্থিক লক্ষ্য",
    "action.title": "কর্ম তালিকা",
    "action.completeProfile": "আপনার আর্থিক প্রোফাইল সম্পূর্ণ করুন",
    "action.setup": "সেটআপ",
    "action.runTax": "ট্যাক্স অপ্টিমাইজেশন চালান",
    "action.open": "খুলুন",
    "action.rentVsBuy": "ভাড়া বনাম কেনা বিশ্লেষণ",
    "action.analyze": "বিশ্লেষণ",
    "goal.buyHome": "বাড়ি কিনুন",
    "goal.payDebt": "ঋণ পরিশোধ",
    "goal.retireEarly": "তাড়াতাড়ি অবসর",
    "goal.emergency": "জরুরি তহবিল",
    "goal.childEdu": "সন্তানের শিক্ষা",
    "goal.wealthGrowth": "সম্পদ বৃদ্ধি",
    "goal.eta50plus": "50+ বছর",
    "goal.achieved": "অর্জিত",
    "goal.basedOn": "ভিত্তিতে",
    "goal.moSIP": "মাসিক SIP",
    "goal.returns": "রিটার্ন",
    "goal.saved": "সঞ্চয়",
    "goal.target": "লক্ষ্য",
    "goal.custom": "কাস্টম",
    "goal.complete": "সম্পূর্ণ",
    "goal.yrsToGo": "বছর বাকি",
    "goal.sipNeeded": "লক্ষ্যে পৌঁছাতে SIP",
    "goal.yrs": "বছর",
    "goal.month": "মাস",
    "goal.save": "সেভ",
    "goal.reset": "রিসেট",
    "goal.setCustomTarget": "কাস্টম লক্ষ্য সেট করুন",
    "goal.noGoals": "কোনো লক্ষ্য সেট নেই। ট্র্যাক করতে প্রোফাইল আপডেট করুন।",
    "goal.enterValid": "সঠিক পরিমাণ দিন",
    "goal.targetUpdated": "লক্ষ্য আপডেট হয়েছে",
    "goal.resetDefault": "ডিফল্টে রিসেট",
    "calc.title": "বিনিয়োগ ক্যালকুলেটর",
    "calc.subtitle": "নির্ভুলতার সাথে আপনার সম্পদ পরিকল্পনা করুন",
    "calc.sipCalc": "SIP ক্যালকুলেটর",
    "calc.lumpSum": "একমুঠো",
    "calc.monthlyInvestment": "মাসিক বিনিয়োগ",
    "calc.investmentAmount": "বিনিয়োগ পরিমাণ",
    "calc.expectedReturn": "প্রত্যাশিত রিটার্ন (%)",
    "calc.timePeriod": "সময়কাল (বছর)",
    "calc.yrs": "বছর",
    "calc.totalValue": "মোট মূল্য",
    "calc.invested": "বিনিয়োগকৃত",
    "calc.gains": "লাভ",
    "compare.title": "পণ্য তুলনা",
    "compare.subtitle": "মিউচুয়াল ফান্ড, FD এবং বীমা পরিকল্পনার তুলনা করুন",
    "compare.fundName": "ফান্ডের নাম",
    "compare.1yReturn": "১ বছর রিটার্ন",
    "compare.3yReturn": "৩ বছর রিটার্ন",
    "compare.5yReturn": "৫ বছর রিটার্ন",
    "compare.risk": "ঝুঁকি",
    "compare.expense": "খরচ",
    "compare.disclaimer": "* অতীত কর্মক্ষমতা ভবিষ্যত রিটার্নের গ্যারান্টি দেয় না।",
    "compare.low": "কম",
    "compare.moderate": "মাঝারি",
    "compare.high": "উচ্চ",
    "compare.moderateHigh": "মাঝারি-উচ্চ",
    "re.title": "রিয়েল এস্টেট প্ল্যানার",
    "re.subtitle": "EMI ক্যালকুলেটর এবং সম্পত্তি পরিকল্পনা টুলস",
    "re.propertyValue": "সম্পত্তির মূল্য",
    "re.downPayment": "ডাউন পেমেন্ট (%)",
    "re.interestRate": "সুদের হার (%)",
    "re.loanTenure": "ঋণের মেয়াদ (বছর)",
    "re.monthlyEmi": "মাসিক EMI",
    "re.loanAmount": "ঋণের পরিমাণ",
    "re.totalInterest": "মোট সুদ",
    "re.totalPayment": "মোট পরিশোধ",
    "tax.title": "ট্যাক্স প্ল্যানার",
    "tax.subtitle": "ভারতীয় কর আইনের অধীনে কর সঞ্চয়ের পরিকল্পনা করুন",
    "tax.recommended": "সুপারিশকৃত",
    "tax.alternative": "বিকল্প",
    "tax.oldRegime": "পুরানো কর ব্যবস্থা",
    "tax.newRegime": "নতুন কর ব্যবস্থা",
    "tax.estimatedTax": "আনুমানিক কর",
    "tax.withDeductions": "₹২,৪১,০০০ কর্তনসহ",
    "tax.standardDeduction": "মানক কর্তন ₹৭৫,০০০",
    "tax.section80c": "ধারা 80C – ₹১.৫ লাখ",
    "tax.section80d": "ধারা 80D – স্বাস্থ্য বীমা",
    "tax.hraExemption": "HRA ছাড়",
    "tax.elss": "ELSS মিউচুয়াল ফান্ড",
    "tax.epf": "EPF অবদান",
    "tax.lifeInsurance": "জীবন বীমা প্রিমিয়াম",
    "tax.selfFamily": "নিজ ও পরিবার প্রিমিয়াম",
    "tax.parentsPremium": "পিতামাতা প্রিমিয়াম",
    "tax.rentPaid": "ভাড়া প্রদান (বার্ষিক)",
    "tax.exemptedAmount": "ছাড়প্রাপ্ত পরিমাণ",
    "tax.invested": "বিনিয়োগকৃত",
    "tax.autoDeducted": "স্বয়ংক্রিয় কর্তন",
    "tax.paid": "প্রদান",
    "tax.claimable": "দাবিযোগ্য",
    "tax.calculated": "গণনাকৃত",
    "sidebar.retirement": "অবসর ঘড়ি",
    "re.step1Title": "বাজেট",
    "re.step2Title": "ROI সিমুলেটর",
    "re.step3Title": "সামর্থ্য",
    "re.stampDuty": "স্ট্যাম্প ডিউটি",
    "re.registration": "রেজিস্ট্রেশন",
    "re.gst": "GST",
    "re.totalCost": "মোট সম্পত্তি খরচ",
    "re.nextStep": "পরবর্তী ধাপ",
    "re.back": "পিছনে",
    "re.roiDesc": "তুলনা: একই পরিমাণ মিউচুয়াল ফান্ডে বিনিয়োগ করলে কী হতো?",
    "re.realEstateInv": "রিয়েল এস্টেট বিনিয়োগ",
    "re.mutualFundInv": "মিউচুয়াল ফান্ড বিনিয়োগ",
    "re.rentalYield": "ভাড়া ফলন",
    "re.appreciation": "মূল্য বৃদ্ধি",
    "re.propertyAfter20": "সম্পত্তি মূল্য (20 বছর)",
    "re.totalRentalIncome": "মোট ভাড়া আয়",
    "re.costDeductions": "খরচ কর্তন",
    "re.netGain": "নেট লাভ",
    "re.expectedReturn": "প্রত্যাশিত রিটার্ন",
    "re.amountInvested": "বিনিয়োগকৃত পরিমাণ",
    "re.valueAfter20": "20 বছর পরে মূল্য",
    "re.mfWins": "মিউচুয়াল ফান্ড জেতে",
    "re.reWins": "রিয়েল এস্টেট জেতে",
    "re.summary": "সারাংশ",
    "re.minIncome": "ন্যূনতম মাসিক আয়",
    "re.affordable": "এই সম্পত্তি আপনার সাধ্যের মধ্যে!",
    "re.notAffordable": "এই সম্পত্তি আপনার বাজেটের বাইরে হতে পারে।",
    "re.emiShouldBe": "EMI আপনার মাসিক আয়ের 40% এর কম হওয়া উচিত।",
    "re.nextSteps": "পরবর্তী পদক্ষেপ গাইড",
    "re.step1Tip": "2-3 ব্যাংক থেকে হোম লোন প্রি-অ্যাপ্রুভাল নিন",
    "re.step2Tip": "সম্পত্তির RERA রেজিস্ট্রেশন যাচাই করুন",
    "re.step3Tip": "ইন্টেরিয়র ও স্থানান্তর খরচ (5-10%) বাজেট করুন",
    "re.step4Tip": "দখলের আগে হোম ইন্স্যুরেন্স বিবেচনা করুন",
    "re.startOver": "আবার শুরু করুন",
    "re.step4Title": "ঋণ যোগ্যতা",
    "re.empType": "কর্মসংস্থানের ধরন",
    "re.empSalaried": "বেতনভোগী",
    "re.empBusiness": "ব্যবসার মালিক",
    "re.empFreelancer": "ফ্রিল্যান্সার",
    "re.netSalary": "নিট মাসিক বেতন (₹)",
    "re.existingEmis": "বিদ্যমান EMI (₹)",
    "re.workExp": "কাজের অভিজ্ঞতা",
    "re.maxAllowableEmi": "সর্বোচ্চ অনুমোদিত EMI",
    "re.maxLoanAmount": "সর্বোচ্চ ঋণের পরিমাণ",
    "re.requiredLoan": "প্রয়োজনীয় ঋণের পরিমাণ",
    "re.calcBasis": "গণনার ভিত্তি",
    "re.eligible": "যোগ্য — আপনি উত্তীর্ণ!",
    "re.borderline": "সীমারেখা — সীমার কাছে",
    "re.notEligible": "অযোগ্য — ব্যবধান অনেক বেশি",
    "re.eligibleTip": "আপনার প্রয়োজনীয় ঋণ আপনার যোগ্যতার মধ্যে। আবেদন করতে প্রস্তুত!",
    "re.borderlineTip": "সীমা বাড়াতে স্বামী/স্ত্রীকে সহ-আবেদক হিসেবে যোগ করুন।",
    "re.notEligibleTip": "আপনার ডাউন পেমেন্ট বাড়ান বা আগে বিদ্যমান ঋণ শোধ করুন।",
    "re.sathiLoanTip": "সাথী টিপ: ব্যাংক স্থিতিশীলতা পছন্দ করে। গত 3 মাসে চাকরি বদলে থাকলে, আবেদনের আগে 3টি বেতন স্লিপের জন্য অপেক্ষা করুন!",
    "re.addCoApplicant": "সহ-আবেদকের আয় যোগ করুন?",
    "re.coApplicantSalary": "সহ-আবেদকের মাসিক বেতন (₹)",
    "re.coApplicantNote": "স্বামী/স্ত্রী বা পিতামাতার আয় যোগ করলে আপনার ঋণ যোগ্যতা উল্লেখযোগ্যভাবে বাড়তে পারে!",
    "ret.title": "অবসর ঘড়ি",
    "ret.subtitle": "মুদ্রাস্ফীতি-সমন্বিত গণনা সহ অবসর পরিকল্পনা",
    "ret.monthlyExpense": "বর্তমান মাসিক খরচ",
    "ret.currentAge": "বর্তমান বয়স",
    "ret.retireAge": "অবসরের বয়স",
    "ret.inflationRate": "মুদ্রাস্ফীতি হার (%)",
    "ret.sipReturn": "প্রত্যাশিত SIP রিটার্ন (%)",
    "ret.yearsLeft": "অবসরে বাকি বছর",
    "ret.futureCost": "ভবিষ্যতের মাসিক খরচ",
    "ret.month": "মাস",
    "ret.inflationNote": "মুদ্রাস্ফীতির জন্য সমন্বিত জীবনযাত্রার খরচ",
    "ret.corpusNeeded": "অবসর কর্পাস প্রয়োজন",
    "ret.sipNeeded": "মাসিক SIP প্রয়োজন",
    "ret.sipNote": "আরামদায়ক অবসরের জন্য আজই এই SIP শুরু করুন",
    "sidebar.rentVsBuy": "ভাড়া vs কেনা",
    "sidebar.debtKiller": "ঋণ-মুক্তি পথ",
    "sidebar.stockResearch": "স্টক রিসার্চ",
    "sidebar.emiMentor": "আমি কি এটা কিনতে পারি?",
    "sidebar.lifeStages": "জীবন পর্যায়",
    "sidebar.creditCard": "ক্রেডিট কার্ড মাস্টার",
    "sidebar.profile": "আমার প্রোফাইল",
    "sidebar.childFuture": "শিশুর ভবিষ্যৎ পরিকল্পক",
    "sidebar.assetComparison": "সম্পদ তুলনা",
    "sidebar.dataRights": "ডেটা অধিকার",
    "rvb.title": "ভাড়া vs কেনা বিশ্লেষক",
    "rvb.subtitle": "ভাড়া দেবেন নাকি কিনবেন? সময়ের সাথে প্রকৃত খরচ তুলনা করুন।",
    "rvb.cityPreset": "শহর প্রিসেট",
    "rvb.custom": "কাস্টম",
    "rvb.taxRegime": "কর ব্যবস্থা",
    "rvb.oldRegime": "পুরাতন ব্যবস্থা",
    "rvb.newRegime": "নতুন ব্যবস্থা",
    "rvb.horizon": "বিশ্লেষণ সময়কাল",
    "rvb.yrs": "বছর",
    "rvb.buyPath": "কেনার পথ",
    "rvb.rentPath": "ভাড়া + বিনিয়োগ পথ",
    "rvb.propertyPrice": "সম্পত্তির মূল্য",
    "rvb.downPayment": "ডাউন পেমেন্ট (%)",
    "rvb.loanRate": "ঋণ সুদের হার (%)",
    "rvb.loanTenure": "ঋণের মেয়াদ (বছর)",
    "rvb.interiors": "ইন্টেরিয়র খরচ",
    "rvb.monthlyMaintenance": "মাসিক রক্ষণাবেক্ষণ",
    "rvb.yearlyPropertyTax": "বার্ষিক সম্পত্তি কর",
    "rvb.appreciationRate": "সম্পত্তি মূল্যবৃদ্ধি (%)",
    "rvb.monthlyRent": "মাসিক ভাড়া",
    "rvb.rentIncrease": "বার্ষিক ভাড়া বৃদ্ধি (%)",
    "rvb.mfReturn": "মিউচুয়াল ফান্ড রিটার্ন (%)",
    "rvb.opportunityCostNote": "সুযোগ খরচ: আপনার ডাউন পেমেন্ট বিনিয়োগ করলে",
    "rvb.investedInMF": "মিউচুয়াল ফান্ডে বিনিয়োগিত",
    "rvb.hraNote": "মনে রাখবেন: HRA ছাড় শুধু পুরাতন ব্যবস্থায় পাওয়া যায়",
    "rvb.monthlyEmi": "মাসিক EMI",
    "rvb.recommendation": "সুপারিশ",
    "rvb.buyingBetter": "কেনা বেশি সম্পদ তৈরি করে",
    "rvb.rentingBetter": "ভাড়া আপনার সম্পদের জন্য ভালো",
    "rvb.wealthDiff": "সম্পদ পার্থক্য",
    "rvb.breakeven": "ব্রেকইভেন বছর",
    "rvb.year": "বছর",
    "rvb.neverBreakeven": "কেনা কখনই ধরতে পারে না",
    "rvb.wealthComparison": "নিট সম্পদ তুলনা",
    "rvb.years": "বছর",
    "rvb.buyWealth": "কেনা: নিট সম্পদ",
    "rvb.rentWealth": "ভাড়া + বিনিয়োগ: নিট সম্পদ",
    "rvb.taxRulesTitle": "2026 কর নিয়ম প্রযোজ্য",
    "rvb.taxRule1": "পুরাতন: ধারা 24b সুদ কর্তন ₹2L পর্যন্ত",
    "rvb.taxRule2": "পুরাতন: ধারা 80C মূলধন কর্তন ₹1.5L পর্যন্ত",
    "rvb.taxRule3": "নতুন: স্ব-আবাসিত সম্পত্তির জন্য কোনো কর্তন নেই",
    "rvb.assumptions": "ব্যবহৃত অনুমান",
    "rvb.assumption1": "সম্পত্তি মূল্যবৃদ্ধি",
    "rvb.assumption2": "ভাড়া বৃদ্ধি",
    "rvb.assumption3": "MF রিটার্ন (CAGR)",
    "rvb.purposeTitle": "এই টুল কী করে?",
    "rvb.purposeDesc": "এই টুল বাড়ি কেনার প্রকৃত খরচ (EMI, স্ট্যাম্প ড্যুটি, রক্ষণাবেক্ষণ) ভাড়ায় থেকে পার্থক্য মিউচুয়াল ফান্ডে বিনিয়োগ করার সাথে তুলনা করে।",
    "rvb.hiddenCosts": "লুকানো খরচ (স্ট্যাম্প ড্যুটি, রক্ষণাবেক্ষণ, কর)",
    "rvb.stampDuty": "স্ট্যাম্প ড্যুটি",
    "rvb.registration": "নিবন্ধন",
    "rvb.wealthGapChart": "সম্পদ ব্যবধান: কেনা vs ভাড়া + বিনিয়োগ",
    "rvb.sathiVerdict": "সাথীর রায়",
    "rvb.sathiRentAdvice": "সাথী বলে: ভাড়া আপনাকে মাসে {AMT} বাঁচাচ্ছে। এটি SIP-তে বিনিয়োগ করুন!",
    "rvb.sathiBuyAdvice": "সাথী বলে: এই দামে কেনা দুর্দান্ত দীর্ঘমেয়াদী সম্পদ!",
    "rvb.totalCostBuy": "মালিকানার মোট খরচ (কেনা)",
    "rvb.rentInvestPath": "ভাড়া + বিনিয়োগ পথ",
    "rvb.downPaymentStampInteriors": "ডাউন পেমেন্ট + স্ট্যাম্প ড্যুটি + ইন্টেরিয়র",
    "rvb.totalEmiPaid": "মোট EMI প্রদান",
    "rvb.interestLabel": "সুদ",
    "rvb.taxSavings": "কর সঞ্চয় (ধারা 24b + 80C)",
    "rvb.totalOutOfPocket": "মোট পকেট থেকে খরচ",
    "rvb.propertyValueAtYear": "বছরে সম্পত্তির মূল্য",
    "rvb.totalRentPaid": "মোট ভাড়া প্রদান",
    "rvb.downPaymentInvested": "ডাউন পেমেন্ট বিনিয়োগিত",
    "rvb.indexFund": "ইনডেক্স ফান্ড",
    "rvb.monthlySavingsInvested": "মাসিক সঞ্চয় বিনিয়োগিত (EMI পার্থক্য)",
    "rvb.autoCompounded": "অটো-কম্পাউন্ডেড",
    "rvb.investmentCorpus": "বছরে বিনিয়োগ কোষ",
    "rvb.netWealth": "নিট সম্পদ (কোষ − ভাড়া)",
    "rvb.shareWhatsApp": "WhatsApp-এ শেয়ার করুন",
    "rvb.saveResult": "ফলাফল সংরক্ষণ করুন",
    "mac.investmentAmount": "বিনিয়োগের পরিমাণ (₹)",
    "mac.frequency": "বিনিয়োগের ফ্রিকোয়েন্সি",
    "mac.freqMonthly": "মাসিক (SIP)",
    "mac.freqQuarterly": "ত্রৈমাসিক",
    "mac.freqHalfYearly": "অর্ধ-বার্ষিক",
    "mac.freqYearly": "বার্ষিক",
    "mac.chartTitle": "{amount} {frequency} বিনিয়োগের {years} বছরের বৃদ্ধি",
    "mac.yearlyTip": "বছরে একবার একমুঠো বিনিয়োগ বনাম প্রতি মাসে ছোট পরিমাণ — 'রুপি কস্ট অ্যাভারেজিং'-এর কারণে ফলাফল ভিন্ন হতে পারে!",
  },
  te: {
    "sidebar.dashboard": "డాష్‌బోర్డ్",
    "sidebar.calculators": "పెట్టుబడి కాలిక్యులేటర్లు",
    "sidebar.compare": "ఉత్పత్తి పోలిక",
    "sidebar.realEstate": "రియల్ ఎస్టేట్ ప్లానర్",
    "sidebar.tax": "ట్యాక్స్ సెంటర్",
    "sidebar.tagline": "స్మార్ట్ ఆర్థిక టూల్స్",
    "sidebar.disclaimer": "విద్యా ప్రయోజనాల కోసం మాత్రమే. ఆర్థిక సలహా కాదు.",
    "sidebar.catDashboard": "డ్యాష్‌బోర్డ్",
    "sidebar.catHomeLife": "🏠 ఇల్లు & జీవితం",
    "sidebar.catFuture": "🔮 భవిష్యత్తు",
    "sidebar.catSafety": "🛡️ భద్రత",
    "sidebar.catInvest": "📈 పెట్టుబడి",
    "sidebar.catBanking": "🏦 బ్యాంకింగ్",
    "sidebar.catAccount": "ఖాతా",
    "sidebar.desc.dashboard": "మీ ఆర్థిక అవలోకనం ఒక్క చూపులో",
    "sidebar.desc.calculators": "SIP, లంప్ సమ్ & కాంపౌండ్ ఇంట్రెస్ట్ టూల్స్",
    "sidebar.desc.compare": "ఆర్థిక ఉత్పత్తులను పక్కపక్కన పోల్చండి",
    "sidebar.desc.realEstate": "ఆస్తి కొనుగోలు & ROI ప్రణాళిక",
    "sidebar.desc.tax": "పాత & కొత్త పన్ను విధానంలో ఆదా ఆప్టిమైజ్ చేయండి",
    "sidebar.desc.retirement": "రిటైర్మెంట్ కార్పస్ & SIP లెక్కించండి",
    "sidebar.desc.rentVsBuy": "అద్దె లేదా కొనుగోలు — ఏది మెరుగో తెలుసుకోండి",
    "sidebar.desc.debtKiller": "వేగంగా రుణ విముక్తి ప్రణాళిక రూపొందించండి",
    "sidebar.desc.stockResearch": "లైవ్ మార్కెట్ డేటా & స్టాక్ విశ్లేషణ",
    "sidebar.desc.emiMentor": "మీరు నిజంగా కొనగలరా అని తనిఖీ చేయండి",
    "sidebar.desc.childFuture": "పిల్లల విద్య & భవిష్యత్ ఖర్చుల ప్రణాళిక",
    "sidebar.desc.creditCard": "క్రెడిట్ కార్డ్ రుణం నిజమైన ఖర్చు అర్థం చేసుకోండి",
    "sidebar.desc.assetComparison": "వివిధ ఆస్తి తరగతుల్లో రిటర్న్ పోలిక",
    "sidebar.desc.profile": "మీ ఆర్థిక ప్రొఫైల్ నిర్వహించండి",
    "sidebar.desc.dataRights": "మీ డేటా గోప్యత & డౌన్‌లోడ్ ఎంపికలు",
    "dash.title": "డాష్‌బోర్డ్",
    "dash.subtitle": "మీ ఆర్థిక సమీక్ష ఒక చూపులో",
    "dash.portfolioValue": "పోర్ట్‌ఫోలియో విలువ",
    "dash.monthlySip": "నెలవారీ SIP",
    "dash.taxSaved": "ట్యాక్స్ ఆదా (FY25)",
    "dash.returns1y": "రిటర్న్స్ (1 సంవత్సరం)",
    "dash.portfolioGrowth": "పోర్ట్‌ఫోలియో వృద్ధి",
    "dash.last9months": "గత 9 నెలలు",
    "dash.quickActions": "శీఘ్ర చర్యలు",
    "dash.sipCalc": "SIP కాలిక్యులేటర్",
    "dash.compareMF": "మ్యూచువల్ ఫండ్ పోలిక",
    "dash.emiEstimator": "EMI అంచనా",
    "dash.taxOptimizer": "ట్యాక్స్ ప్లానర్",
    "dash.marketSnapshot": "మార్కెట్ స్నాప్‌షాట్",
    "dash.active": "యాక్టివ్",
    "dash.vikasScore": "వికాస్ యాత్ర",
    "dash.totalAssets": "మొత్తం ఆస్తులు",
    "dash.totalLiabilities": "మొత్తం అప్పులు",
    "dash.netInvestment": "నికర పెట్టుబడి",
    "dash.dtiRatio": "DTI నిష్పత్తి",
    "dash.savingsInvestments": "పొదుపు + పెట్టుబడులు",
    "dash.annualEmi": "వార్షిక EMI బాధ్యతలు",
    "dash.monthlyWealth": "నెలవారీ సంపద నిర్మాణం",
    "dash.financialPulse": "ఆర్థిక పల్స్",
    "dash.assetsVsLiabilities": "ఆస్తులు vs అప్పులు — 12 నెలల అంచనా",
    "dash.dailySathiTip": "నేటి సాథీ సూచన",
    "dash.goalTracker": "లక్ష్య ట్రాకర్",
    "dash.miniTools": "మినీ టూల్స్ — త్వరిత డబ్బు హ్యాక్స్",
    "dash.welcome": "స్వాగతం",
    "dash.welcomeSub": "ఈ రోజు మీ ఆర్థిక సారాంశం ఇక్కడ ఉంది.",
    "dash.editProfile": "ప్రొఫైల్ సవరించు",
    "dash.user": "వినియోగదారు",
    "ai.insightTitle": "AI అంతర్దృష్టి",
    "ai.sipAdvice": "మీ {income} ఆదాయం ఆధారంగా, మీ {goal} వేగవంతం చేయడానికి {sip}/నెల SIP ప్రారంభించమని సిఫార్సు చేస్తున్నాము.",
    "ai.dtiWarning": "మీ EMI భారం {dti}% — వడ్డీ తగ్గించడానికి బ్యాలెన్స్ ట్రాన్స్‌ఫర్ పరిగణించండి.",
    "ai.financialGoal": "ఆర్థిక లక్ష్యం",
    "action.title": "చర్య అంశాలు",
    "action.completeProfile": "మీ ఆర్థిక ప్రొఫైల్ పూర్తి చేయండి",
    "action.setup": "సెటప్",
    "action.runTax": "పన్ను ఆప్టిమైజేషన్ నడపండి",
    "action.open": "తెరవండి",
    "action.rentVsBuy": "అద్దె vs కొనుగోలు విశ్లేషణ",
    "action.analyze": "విశ్లేషణ",
    "goal.buyHome": "ఇల్లు కొనండి",
    "goal.payDebt": "అప్పు తీర్చండి",
    "goal.retireEarly": "త్వరగా రిటైర్",
    "goal.emergency": "అత్యవసర నిధి",
    "goal.childEdu": "పిల్లల విద్య",
    "goal.wealthGrowth": "సంపద పెంచండి",
    "goal.eta50plus": "50+ సంవత్సరాలు",
    "goal.achieved": "సాధించారు",
    "goal.basedOn": "ఆధారంగా",
    "goal.moSIP": "నెలవారీ SIP",
    "goal.returns": "రాబడి",
    "goal.saved": "ఆదా",
    "goal.target": "లక్ష్యం",
    "goal.custom": "కస్టమ్",
    "goal.complete": "పూర్తి",
    "goal.yrsToGo": "సం. మిగిలి",
    "goal.sipNeeded": "లక్ష్యం చేరుకోవడానికి SIP",
    "goal.yrs": "సం.",
    "goal.month": "నెల",
    "goal.save": "సేవ్",
    "goal.reset": "రీసెట్",
    "goal.setCustomTarget": "కస్టమ్ లక్ష్యం సెట్ చేయండి",
    "goal.noGoals": "లక్ష్యాలు సెట్ చేయలేదు. ట్రాక్ చేయడానికి ప్రొఫైల్ అప్‌డేట్ చేయండి.",
    "goal.enterValid": "సరైన మొత్తం నమోదు చేయండి",
    "goal.targetUpdated": "లక్ష్యం అప్‌డేట్ అయింది",
    "goal.resetDefault": "డిఫాల్ట్‌కి రీసెట్",
    "calc.title": "పెట్టుబడి కాలిక్యులేటర్లు",
    "calc.subtitle": "ఖచ్చితత్వంతో మీ సంపదను ప్లాన్ చేయండి",
    "calc.sipCalc": "SIP కాలిక్యులేటర్",
    "calc.lumpSum": "ఒకేసారి",
    "calc.monthlyInvestment": "నెలవారీ పెట్టుబడి",
    "calc.investmentAmount": "పెట్టుబడి మొత్తం",
    "calc.expectedReturn": "అంచనా రిటర్న్ (%)",
    "calc.timePeriod": "కాల వ్యవధి (సంవత్సరాలు)",
    "calc.yrs": "సంవత్సరాలు",
    "calc.totalValue": "మొత్తం విలువ",
    "calc.invested": "పెట్టుబడి",
    "calc.gains": "లాభాలు",
    "compare.title": "ఉత్పత్తి పోలిక",
    "compare.subtitle": "మ్యూచువల్ ఫండ్లు, FDలు మరియు బీమా పథకాలను పోల్చండి",
    "compare.fundName": "ఫండ్ పేరు",
    "compare.1yReturn": "1 సం. రిటర్న్",
    "compare.3yReturn": "3 సం. రిటర్న్",
    "compare.5yReturn": "5 సం. రిటర్న్",
    "compare.risk": "రిస్క్",
    "compare.expense": "ఖర్చు",
    "compare.disclaimer": "* గత పనితీరు భవిష్యత్ రిటర్న్‌లకు హామీ ఇవ్వదు.",
    "compare.low": "తక్కువ",
    "compare.moderate": "మధ్యస్థం",
    "compare.high": "ఎక్కువ",
    "compare.moderateHigh": "మధ్యస్థం-ఎక్కువ",
    "re.title": "రియల్ ఎస్టేట్ ప్లానర్",
    "re.subtitle": "EMI కాలిక్యులేటర్ మరియు ఆస్తి ప్లానింగ్ టూల్స్",
    "re.propertyValue": "ఆస్తి విలువ",
    "re.downPayment": "డౌన్ పేమెంట్ (%)",
    "re.interestRate": "వడ్డీ రేటు (%)",
    "re.loanTenure": "లోన్ అవధి (సంవత్సరాలు)",
    "re.monthlyEmi": "నెలవారీ EMI",
    "re.loanAmount": "లోన్ మొత్తం",
    "re.totalInterest": "మొత్తం వడ్డీ",
    "re.totalPayment": "మొత్తం చెల్లింపు",
    "tax.title": "ట్యాక్స్ సెంటర్",
    "tax.subtitle": "భారత పన్ను చట్టం కింద పన్ను ఆదా ప్లాన్ చేయండి",
    "tax.recommended": "సిఫార్సు",
    "tax.alternative": "ప్రత్యామ్నాయం",
    "tax.oldRegime": "పాత ట్యాక్స్ రెజీమ్",
    "tax.newRegime": "కొత్త ట్యాక్స్ రెజీమ్",
    "tax.estimatedTax": "అంచనా పన్ను",
    "tax.withDeductions": "₹2,41,000 తగ్గింపులతో",
    "tax.standardDeduction": "ప్రామాణిక తగ్గింపు ₹75,000",
    "tax.section80c": "సెక్షన్ 80C – ₹1.5 లక్ష",
    "tax.section80d": "సెక్షన్ 80D – ఆరోగ్య బీమా",
    "tax.hraExemption": "HRA మినహాయింపు",
    "tax.elss": "ELSS మ్యూచువల్ ఫండ్లు",
    "tax.epf": "EPF రాయితీ",
    "tax.lifeInsurance": "జీవిత బీమా ప్రీమియం",
    "tax.selfFamily": "స్వయం & కుటుంబ ప్రీమియం",
    "tax.parentsPremium": "తల్లిదండ్రుల ప్రీమియం",
    "tax.rentPaid": "చెల్లించిన అద్దె (వార్షిక)",
    "tax.exemptedAmount": "మినహాయించిన మొత్తం",
    "tax.invested": "పెట్టుబడి",
    "tax.autoDeducted": "స్వయంచాలక కటింపు",
    "tax.paid": "చెల్లించారు",
    "tax.claimable": "దావా చేయగలరు",
    "tax.calculated": "లెక్కించబడింది",
    "sidebar.retirement": "రిటైర్‌మెంట్ క్లాక్",
    "re.step1Title": "బడ్జెట్",
    "re.step2Title": "ROI సిమ్యులేటర్",
    "re.step3Title": "భరించగల సామర్థ్యం",
    "re.stampDuty": "స్టాంప్ డ్యూటీ",
    "re.registration": "రిజిస్ట్రేషన్",
    "re.gst": "GST",
    "re.totalCost": "మొత్తం ఆస్తి ఖర్చు",
    "re.nextStep": "తదుపరి దశ",
    "re.back": "వెనుకకు",
    "re.roiDesc": "పోలిక: అదే మొత్తం మ్యూచువల్ ఫండ్‌లో పెట్టుబడి పెడితే?",
    "re.realEstateInv": "రియల్ ఎస్టేట్ పెట్టుబడి",
    "re.mutualFundInv": "మ్యూచువల్ ఫండ్ పెట్టుబడి",
    "re.rentalYield": "అద్దె రాబడి",
    "re.appreciation": "విలువ పెరుగుదల",
    "re.propertyAfter20": "ఆస్తి విలువ (20 సం.)",
    "re.totalRentalIncome": "మొత్తం అద్దె ఆదాయం",
    "re.costDeductions": "ఖర్చు తగ్గింపులు",
    "re.netGain": "నికర లాభం",
    "re.expectedReturn": "అంచనా రిటర్న్",
    "re.amountInvested": "పెట్టుబడి మొత్తం",
    "re.valueAfter20": "20 సం. తర్వాత విలువ",
    "re.mfWins": "మ్యూచువల్ ఫండ్ గెలుస్తుంది",
    "re.reWins": "రియల్ ఎస్టేట్ గెలుస్తుంది",
    "re.summary": "సారాంశం",
    "re.minIncome": "కనీస నెలవారీ ఆదాయం",
    "re.affordable": "ఈ ఆస్తి మీకు భరించగలది!",
    "re.notAffordable": "ఈ ఆస్తి మీ బడ్జెట్‌కు మించినది.",
    "re.emiShouldBe": "EMI మీ నెలవారీ ఆదాయంలో 40% కంటే తక్కువ ఉండాలి.",
    "re.nextSteps": "తదుపరి దశల గైడ్",
    "re.step1Tip": "2-3 బ్యాంకుల నుండి హోమ్ లోన్ ప్రీ-అప్రూవల్ పొందండి",
    "re.step2Tip": "ఆస్తి RERA రిజిస్ట్రేషన్ ధృవీకరించండి",
    "re.step3Tip": "ఇంటీరియర్ & తరలింపు ఖర్చులు (5-10%) బడ్జెట్ చేయండి",
    "re.step4Tip": "స్వాధీనానికి ముందు హోమ్ ఇన్సూరెన్స్ పరిగణించండి",
    "re.startOver": "మళ్ళీ మొదలు",
    "re.step4Title": "రుణ అర్హత",
    "re.empType": "ఉద్యోగ రకం",
    "re.empSalaried": "జీతం పొందేవారు",
    "re.empBusiness": "వ్యాపార యజమాని",
    "re.empFreelancer": "ఫ్రీలాన్సర్",
    "re.netSalary": "నికర నెలవారీ జీతం (₹)",
    "re.existingEmis": "ఉన్న EMIలు (₹)",
    "re.workExp": "పని అనుభవం",
    "re.maxAllowableEmi": "గరిష్ట అనుమతించదగిన EMI",
    "re.maxLoanAmount": "గరిష్ట రుణ మొత్తం",
    "re.requiredLoan": "అవసరమైన రుణ మొత్తం",
    "re.calcBasis": "లెక్కింపు ఆధారం",
    "re.eligible": "అర్హులు — మీరు అర్హులు!",
    "re.borderline": "అంచున — పరిమితికి దగ్గరగా",
    "re.notEligible": "అనర్హులు — అంతరం చాలా ఎక్కువ",
    "re.eligibleTip": "మీ అవసరమైన రుణం మీ అర్హతలో ఉంది. దరఖాస్తు చేయడానికి సిద్ధం!",
    "re.borderlineTip": "పరిమితిని పెంచడానికి భార్య/భర్తను సహ-దరఖాస్తుదారుగా చేర్చండి.",
    "re.notEligibleTip": "మీ డౌన్ పేమెంట్ పెంచండి లేదా ముందు ఉన్న అప్పులు తీర్చండి.",
    "re.sathiLoanTip": "సాథీ టిప్: బ్యాంకులు స్థిరత్వాన్ని ఇష్టపడతాయి. గత 3 నెలల్లో ఉద్యోగం మారితే, దరఖాస్తుకు ముందు 3 జీతం స్లిప్‌ల కోసం ఆగండి!",
    "re.addCoApplicant": "సహ-దరఖాస్తుదారు ఆదాయం జోడించండి?",
    "re.coApplicantSalary": "సహ-దరఖాస్తుదారు నెలవారీ జీతం (₹)",
    "re.coApplicantNote": "భార్య/భర్త లేదా తల్లిదండ్రుల ఆదాయం జోడిస్తే మీ రుణ అర్హత గణనీయంగా పెరుగుతుంది!",
    "ret.title": "రిటైర్‌మెంట్ క్లాక్",
    "ret.subtitle": "ద్రవ్యోల్బణం-సమీకృత లెక్కలతో రిటైర్‌మెంట్ ప్రణాళిక",
    "ret.monthlyExpense": "ప్రస్తుత నెలవారీ ఖర్చులు",
    "ret.currentAge": "ప్రస్తుత వయసు",
    "ret.retireAge": "రిటైర్‌మెంట్ వయసు",
    "ret.inflationRate": "ద్రవ్యోల్బణ రేటు (%)",
    "ret.sipReturn": "అంచనా SIP రిటర్న్ (%)",
    "ret.yearsLeft": "రిటైర్‌మెంట్‌కు మిగిలిన సంవత్సరాలు",
    "ret.futureCost": "భవిష్యత్ నెలవారీ ఖర్చు",
    "ret.month": "నెల",
    "ret.inflationNote": "ద్రవ్యోల్బణానికి సర్దుబాటు చేసిన జీవనశైలి ఖర్చు",
    "ret.corpusNeeded": "రిటైర్‌మెంట్ కార్పస్ అవసరం",
    "ret.sipNeeded": "నెలవారీ SIP అవసరం",
    "ret.sipNote": "సుఖమైన రిటైర్‌మెంట్ కోసం ఈ SIP ఈరోజే ప్రారంభించండి",
    "sidebar.rentVsBuy": "అద్దె vs కొనుగోలు",
    "sidebar.debtKiller": "అప్పు-విముక్తి మార్గం",
    "sidebar.stockResearch": "స్టాక్ రీసెర్చ్",
    "sidebar.emiMentor": "నేను ఇది కొనగలనా?",
    "sidebar.lifeStages": "జీవిత దశలు",
    "sidebar.creditCard": "క్రెడిట్ కార్డ్ మాస్టర్",
    "sidebar.profile": "నా ప్రొఫైల్",
    "sidebar.childFuture": "పిల్లల భవిష్యత్ ప్లానర్",
    "sidebar.assetComparison": "ఆస్తి పోలిక",
    "sidebar.dataRights": "డేటా హక్కులు",
    "rvb.title": "అద్దె vs కొనుగోలు విశ్లేషకం",
    "rvb.subtitle": "అద్దెకు ఇవ్వాలా లేక కొనాలా? కాలక్రమంలో నిజమైన ఖర్చును పోల్చండి।",
    "rvb.cityPreset": "నగర ప్రీసెట్",
    "rvb.custom": "కస్టమ్",
    "rvb.taxRegime": "పన్ను విధానం",
    "rvb.oldRegime": "పాత విధానం",
    "rvb.newRegime": "కొత్త విధానం",
    "rvb.horizon": "విశ్లేషణ కాలం",
    "rvb.yrs": "సంవత్సరాలు",
    "rvb.buyPath": "కొనుగోలు మార్గం",
    "rvb.rentPath": "అద్దె + పెట్టుబడి మార్గం",
    "rvb.propertyPrice": "ఆస్తి ధర",
    "rvb.downPayment": "డౌన్ పేమెంట్ (%)",
    "rvb.loanRate": "రుణ వడ్డీ రేటు (%)",
    "rvb.loanTenure": "రుణ కాలం (సంవత్సరాలు)",
    "rvb.interiors": "ఇంటీరియర్ ఖర్చు",
    "rvb.monthlyMaintenance": "నెలవారీ నిర్వహణ",
    "rvb.yearlyPropertyTax": "వార్షిక ఆస్తి పన్ను",
    "rvb.appreciationRate": "ఆస్తి విలువ పెరుగుదల (%)",
    "rvb.monthlyRent": "నెలవారీ అద్దె",
    "rvb.rentIncrease": "వార్షిక అద్దె పెరుగుదల (%)",
    "rvb.mfReturn": "మ్యూచువల్ ఫండ్ రిటర్న్ (%)",
    "rvb.opportunityCostNote": "అవకాశ వ్యయం: డౌన్ పేమెంట్ పెట్టుబడి చేస్తే",
    "rvb.investedInMF": "మ్యూచువల్ ఫండ్స్‌లో పెట్టుబడి",
    "rvb.hraNote": "గుర్తుంచుకోండి: HRA మినహాయింపు పాత విధానంలో మాత్రమే",
    "rvb.monthlyEmi": "నెలవారీ EMI",
    "rvb.recommendation": "సిఫారసు",
    "rvb.buyingBetter": "కొనుగోలు ఎక్కువ సంపదను నిర్మిస్తుంది",
    "rvb.rentingBetter": "అద్దె మీ సంపదకు మెరుగు",
    "rvb.wealthDiff": "సంపద తేడా",
    "rvb.breakeven": "బ్రేక్‌ఈవెన్ సంవత్సరం",
    "rvb.year": "సంవత్సరం",
    "rvb.neverBreakeven": "కొనుగోలు ఎప్పటికీ అందుకోదు",
    "rvb.wealthComparison": "నికర సంపద పోలిక",
    "rvb.years": "సంవత్సరాలు",
    "rvb.buyWealth": "కొనుగోలు: నికర సంపద",
    "rvb.rentWealth": "అద్దె + పెట్టుబడి: నికర సంపద",
    "rvb.taxRulesTitle": "2026 పన్ను నిబంధనలు వర్తింపజేయబడ్డాయి",
    "rvb.taxRule1": "పాతది: సెక్షన్ 24b వడ్డీ తగ్గింపు ₹2L వరకు",
    "rvb.taxRule2": "పాతది: సెక్షన్ 80C అసలు తగ్గింపు ₹1.5L వరకు",
    "rvb.taxRule3": "కొత్తది: స్వంత ఆస్తికి తగ్గింపులు లేవు",
    "rvb.assumptions": "ఉపయోగించిన అంచనాలు",
    "rvb.assumption1": "ఆస్తి విలువ పెరుగుదల",
    "rvb.assumption2": "అద్దె పెరుగుదల",
    "rvb.assumption3": "MF రిటర్న్ (CAGR)",
    "rvb.purposeTitle": "ఈ టూల్ ఏమి చేస్తుంది?",
    "rvb.purposeDesc": "ఈ టూల్ ఇల్లు కొనడం యొక్క నిజమైన ఖర్చు (EMI, స్టాంప్ డ్యూటీ, నిర్వహణ) ను అద్దెకు ఉండి తేడాను మ్యూచువల్ ఫండ్‌లో పెట్టుబడి పెట్టడంతో పోలుస్తుంది.",
    "rvb.hiddenCosts": "దాచిన ఖర్చులు (స్టాంప్ డ్యూటీ, నిర్వహణ, పన్ను)",
    "rvb.stampDuty": "స్టాంప్ డ్యూటీ",
    "rvb.registration": "నమోదు",
    "rvb.wealthGapChart": "సంపద అంతరం: కొనుగోలు vs అద్దె + పెట్టుబడి",
    "rvb.sathiVerdict": "సాథీ తీర్పు",
    "rvb.sathiRentAdvice": "సాథీ చెప్తున్నారు: అద్దె మీకు నెలకు {AMT} ఆదా చేస్తోంది. దీన్ని SIPలో పెట్టుబడి పెట్టండి!",
    "rvb.sathiBuyAdvice": "సాథీ చెప్తున్నారు: ఈ ధరలో కొనడం గొప్ప దీర్ఘకాలిక ఆస్తి!",
    "rvb.totalCostBuy": "యాజమాన్య మొత్తం ఖర్చు (కొనుగోలు)",
    "rvb.rentInvestPath": "అద్దె + పెట్టుబడి మార్గం",
    "rvb.downPaymentStampInteriors": "డౌన్ పేమెంట్ + స్టాంప్ డ్యూటీ + ఇంటీరియర్స్",
    "rvb.totalEmiPaid": "మొత్తం EMI చెల్లింపు",
    "rvb.interestLabel": "వడ్డీ",
    "rvb.taxSavings": "పన్ను ఆదా (సెక్షన్ 24b + 80C)",
    "rvb.totalOutOfPocket": "మొత్తం జేబు ఖర్చు",
    "rvb.propertyValueAtYear": "సంవత్సరంలో ఆస్తి విలువ",
    "rvb.totalRentPaid": "మొత్తం అద్దె చెల్లింపు",
    "rvb.downPaymentInvested": "డౌన్ పేమెంట్ పెట్టుబడి",
    "rvb.indexFund": "ఇండెక్స్ ఫండ్",
    "rvb.monthlySavingsInvested": "నెలవారీ ఆదా పెట్టుబడి (EMI తేడా)",
    "rvb.autoCompounded": "ఆటో-కంపౌండెడ్",
    "rvb.investmentCorpus": "సంవత్సరంలో పెట్టుబడి కార్పస్",
    "rvb.netWealth": "నికర సంపద (కార్పస్ − అద్దె)",
    "rvb.shareWhatsApp": "WhatsAppలో షేర్ చేయండి",
    "rvb.saveResult": "ఫలితం సేవ్ చేయండి",
    "mac.investmentAmount": "పెట్టుబడి మొత్తం (₹)",
    "mac.frequency": "పెట్టుబడి ఫ్రీక్వెన్సీ",
    "mac.freqMonthly": "నెలవారీ (SIP)",
    "mac.freqQuarterly": "త్రైమాసిక",
    "mac.freqHalfYearly": "అర్ధ-వార్షిక",
    "mac.freqYearly": "వార్షిక",
    "mac.chartTitle": "{amount} {frequency} పెట్టుబడి {years} సంవత్సరాల వృద్ధి",
    "mac.yearlyTip": "సంవత్సరానికి ఒకసారి పెద్ద మొత్తం పెట్టుబడి vs ప్రతి నెలా చిన్న మొత్తం — 'రూపీ కాస్ట్ ఆవరేజింగ్' కారణంగా ఫలితాలు భిన్నంగా ఉంటాయి!",
  },
  mr: {
    "sidebar.dashboard": "डॅशबोर्ड",
    "sidebar.calculators": "गुंतवणूक कॅल्क्युलेटर",
    "sidebar.compare": "उत्पादन तुलना",
    "sidebar.realEstate": "रिअल इस्टेट प्लॅनर",
    "sidebar.tax": "टॅक्स सेंटर",
    "sidebar.tagline": "स्मार्ट आर्थिक साधने",
    "sidebar.disclaimer": "केवळ शैक्षणिक उद्देशाने. आर्थिक सल्ला नाही.",
    "sidebar.catDashboard": "डॅशबोर्ड",
    "sidebar.catHomeLife": "🏠 घर आणि जीवन",
    "sidebar.catFuture": "🔮 भविष्य",
    "sidebar.catSafety": "🛡️ सुरक्षा",
    "sidebar.catInvest": "📈 गुंतवणूक",
    "sidebar.catBanking": "🏦 बँकिंग",
    "sidebar.catAccount": "खाते",
    "sidebar.desc.dashboard": "एका नजरेत तुमची आर्थिक स्थिती",
    "sidebar.desc.calculators": "SIP, एकरकमी आणि चक्रवाढ व्याज साधने",
    "sidebar.desc.compare": "आर्थिक उत्पादनांची शेजारी शेजारी तुलना करा",
    "sidebar.desc.realEstate": "मालमत्ता खरेदी आणि ROI नियोजन",
    "sidebar.desc.tax": "जुन्या आणि नव्या कर प्रणालीत बचत ऑप्टिमाइझ करा",
    "sidebar.desc.retirement": "निवृत्ती निधी आणि SIP गणना करा",
    "sidebar.desc.rentVsBuy": "भाडे की खरेदी — कोणते चांगले ते जाणा",
    "sidebar.desc.debtKiller": "जलद कर्जमुक्त होण्याचे नियोजन करा",
    "sidebar.desc.stockResearch": "लाइव्ह बाजार डेटा आणि स्टॉक विश्लेषण",
    "sidebar.desc.emiMentor": "तुम्ही खरोखर विकत घेऊ शकता का ते तपासा",
    "sidebar.desc.childFuture": "मुलाचे शिक्षण आणि भविष्यातील खर्चांचे नियोजन",
    "sidebar.desc.creditCard": "क्रेडिट कार्ड कर्जाची खरी किंमत समजून घ्या",
    "sidebar.desc.assetComparison": "विविध मालमत्ता वर्गांमध्ये परताव्याची तुलना",
    "sidebar.desc.profile": "तुमची आर्थिक प्रोफाइल व्यवस्थापित करा",
    "sidebar.desc.dataRights": "तुमची डेटा गोपनीयता आणि डाउनलोड पर्याय",
    "dash.title": "डॅशबोर्ड",
    "dash.subtitle": "तुमची आर्थिक स्थिती एका नजरेत",
    "dash.portfolioValue": "पोर्टफोलिओ मूल्य",
    "dash.monthlySip": "मासिक SIP",
    "dash.taxSaved": "टॅक्स बचत (FY25)",
    "dash.returns1y": "परतावा (1 वर्ष)",
    "dash.portfolioGrowth": "पोर्टफोलिओ वाढ",
    "dash.last9months": "मागील 9 महिने",
    "dash.quickActions": "त्वरित कृती",
    "dash.sipCalc": "SIP कॅल्क्युलेटर",
    "dash.compareMF": "म्यूचुअल फंड तुलना",
    "dash.emiEstimator": "EMI अंदाजक",
    "dash.taxOptimizer": "टॅक्स प्लानर",
    "dash.marketSnapshot": "बाजार स्नॅपशॉट",
    "dash.active": "सक्रिय",
    "dash.vikasScore": "विकास यात्रा",
    "dash.totalAssets": "एकूण मालमत्ता",
    "dash.totalLiabilities": "एकूण दायित्वे",
    "dash.netInvestment": "निव्वळ गुंतवणूक",
    "dash.dtiRatio": "DTI गुणोत्तर",
    "dash.savingsInvestments": "बचत + गुंतवणूक",
    "dash.annualEmi": "वार्षिक EMI दायित्वे",
    "dash.monthlyWealth": "मासिक संपत्ती निर्माण",
    "dash.financialPulse": "आर्थिक नाडी",
    "dash.assetsVsLiabilities": "मालमत्ता vs दायित्वे — 12 महिन्यांचा अंदाज",
    "dash.dailySathiTip": "आजचा साथी सल्ला",
    "dash.goalTracker": "ध्येय ट्रॅकर",
    "dash.miniTools": "मिनी टूल्स — झटपट पैशांच्या युक्त्या",
    "dash.welcome": "स्वागत आहे",
    "dash.welcomeSub": "आजचा तुमचा आर्थिक आढावा येथे आहे.",
    "dash.editProfile": "प्रोफाइल संपादित करा",
    "dash.user": "वापरकर्ता",
    "ai.insightTitle": "AI अंतर्दृष्टी",
    "ai.sipAdvice": "तुमच्या {income} उत्पन्नावर आधारित, तुमचे {goal} जलद करण्यासाठी {sip}/महिना SIP सुरू करण्याची शिफारस करतो.",
    "ai.dtiWarning": "तुमचा EMI भार {dti}% आहे — व्याज कमी करण्यासाठी बॅलन्स ट्रान्सफर विचारा.",
    "ai.financialGoal": "आर्थिक ध्येय",
    "action.title": "कृती यादी",
    "action.completeProfile": "तुमची आर्थिक प्रोफाइल पूर्ण करा",
    "action.setup": "सेटअप",
    "action.runTax": "कर ऑप्टिमायझेशन चालवा",
    "action.open": "उघडा",
    "action.rentVsBuy": "भाडे विरुद्ध खरेदी विश्लेषण",
    "action.analyze": "विश्लेषण",
    "goal.buyHome": "घर खरेदी करा",
    "goal.payDebt": "कर्ज फेडा",
    "goal.retireEarly": "लवकर निवृत्ती",
    "goal.emergency": "आणीबाणी निधी",
    "goal.childEdu": "मुलाचे शिक्षण",
    "goal.wealthGrowth": "संपत्ती वाढवा",
    "goal.eta50plus": "50+ वर्षे",
    "goal.achieved": "साध्य",
    "goal.basedOn": "आधारित",
    "goal.moSIP": "मासिक SIP",
    "goal.returns": "परतावा",
    "goal.saved": "बचत",
    "goal.target": "लक्ष्य",
    "goal.custom": "सानुकूल",
    "goal.complete": "पूर्ण",
    "goal.yrsToGo": "वर्षे शिल्लक",
    "goal.sipNeeded": "लक्ष्यापर्यंत SIP",
    "goal.yrs": "वर्षे",
    "goal.month": "महिना",
    "goal.save": "सेव्ह करा",
    "goal.reset": "रीसेट",
    "goal.setCustomTarget": "सानुकूल लक्ष्य सेट करा",
    "goal.noGoals": "कोणतेही लक्ष्य सेट नाही. ट्रॅक करण्यासाठी प्रोफाइल अपडेट करा.",
    "goal.enterValid": "योग्य रक्कम प्रविष्ट करा",
    "goal.targetUpdated": "लक्ष्य अपडेट झाले",
    "goal.resetDefault": "डिफॉल्टवर रीसेट",
    "calc.title": "गुंतवणूक कॅल्क्युलेटर",
    "calc.subtitle": "अचूकतेने तुमच्या संपत्तीचे नियोजन करा",
    "calc.sipCalc": "SIP कॅल्क्युलेटर",
    "calc.lumpSum": "एकरकमी",
    "calc.monthlyInvestment": "मासिक गुंतवणूक",
    "calc.investmentAmount": "गुंतवणूक रक्कम",
    "calc.expectedReturn": "अपेक्षित परतावा (%)",
    "calc.timePeriod": "कालावधी (वर्षे)",
    "calc.yrs": "वर्षे",
    "calc.totalValue": "एकूण मूल्य",
    "calc.invested": "गुंतवणूक",
    "calc.gains": "नफा",
    "compare.title": "उत्पादन तुलना",
    "compare.subtitle": "म्यूचुअल फंड, FD आणि विमा योजनांची तुलना करा",
    "compare.fundName": "फंड नाव",
    "compare.1yReturn": "1 वर्ष परतावा",
    "compare.3yReturn": "3 वर्ष परतावा",
    "compare.5yReturn": "5 वर्ष परतावा",
    "compare.risk": "जोखीम",
    "compare.expense": "खर्च",
    "compare.disclaimer": "* मागील कामगिरी भविष्यातील परताव्याची हमी देत नाही.",
    "compare.low": "कमी",
    "compare.moderate": "मध्यम",
    "compare.high": "जास्त",
    "compare.moderateHigh": "मध्यम-जास्त",
    "re.title": "रिअल इस्टेट प्लॅनर",
    "re.subtitle": "EMI कॅल्क्युलेटर आणि प्रॉपर्टी प्लॅनिंग साधने",
    "re.propertyValue": "प्रॉपर्टी मूल्य",
    "re.downPayment": "डाउन पेमेंट (%)",
    "re.interestRate": "व्याज दर (%)",
    "re.loanTenure": "कर्ज कालावधी (वर्षे)",
    "re.monthlyEmi": "मासिक EMI",
    "re.loanAmount": "कर्ज रक्कम",
    "re.totalInterest": "एकूण व्याज",
    "re.totalPayment": "एकूण भुगतान",
    "tax.title": "टॅक्स सेंटर",
    "tax.subtitle": "भारतीय कर कायद्यानुसार टॅक्स बचतीचे नियोजन करा",
    "tax.recommended": "शिफारस",
    "tax.alternative": "पर्याय",
    "tax.oldRegime": "जुनी टॅक्स रिजीम",
    "tax.newRegime": "नवीन टॅक्स रिजीम",
    "tax.estimatedTax": "अंदाजित टॅक्स",
    "tax.withDeductions": "₹2,41,000 कपातीसह",
    "tax.standardDeduction": "मानक कपात ₹75,000",
    "tax.section80c": "कलम 80C – ₹1.5 लाख",
    "tax.section80d": "कलम 80D – आरोग्य विमा",
    "tax.hraExemption": "HRA सवलत",
    "tax.elss": "ELSS म्यूचुअल फंड",
    "tax.epf": "EPF योगदान",
    "tax.lifeInsurance": "जीवन विमा प्रीमियम",
    "tax.selfFamily": "स्वतः आणि कुटुंब प्रीमियम",
    "tax.parentsPremium": "पालक प्रीमियम",
    "tax.rentPaid": "भरलेले भाडे (वार्षिक)",
    "tax.exemptedAmount": "सूट प्राप्त रक्कम",
    "tax.invested": "गुंतवणूक",
    "tax.autoDeducted": "स्वयं कपात",
    "tax.paid": "भरलेले",
    "tax.claimable": "दावा करण्यायोग्य",
    "tax.calculated": "गणना",
    "sidebar.retirement": "रिटायरमेंट क्लॉक",
    "re.step1Title": "बजेट",
    "re.step2Title": "ROI सिम्युलेटर",
    "re.step3Title": "परवडणारी किंमत",
    "re.stampDuty": "मुद्रांक शुल्क",
    "re.registration": "नोंदणी",
    "re.gst": "GST",
    "re.totalCost": "एकूण मालमत्ता खर्च",
    "re.nextStep": "पुढील पाऊल",
    "re.back": "मागे",
    "re.roiDesc": "तुलना: तीच रक्कम म्युच्युअल फंडात गुंतवली असती तर?",
    "re.realEstateInv": "रिअल इस्टेट गुंतवणूक",
    "re.mutualFundInv": "म्युच्युअल फंड गुंतवणूक",
    "re.rentalYield": "भाडे उत्पन्न",
    "re.appreciation": "मूल्यवाढ",
    "re.propertyAfter20": "मालमत्ता मूल्य (20 वर्षे)",
    "re.totalRentalIncome": "एकूण भाडे उत्पन्न",
    "re.costDeductions": "खर्च वजावट",
    "re.netGain": "निव्वळ नफा",
    "re.expectedReturn": "अपेक्षित परतावा",
    "re.amountInvested": "गुंतवलेली रक्कम",
    "re.valueAfter20": "20 वर्षांनंतर मूल्य",
    "re.mfWins": "म्युच्युअल फंड जिंकतो",
    "re.reWins": "रिअल इस्टेट जिंकते",
    "re.summary": "सारांश",
    "re.minIncome": "किमान मासिक उत्पन्न",
    "re.affordable": "ही मालमत्ता तुम्हाला परवडणारी आहे!",
    "re.notAffordable": "ही मालमत्ता तुमच्या बजेटच्या बाहेर असू शकते.",
    "re.emiShouldBe": "EMI तुमच्या मासिक उत्पन्नाच्या 40% पेक्षा कमी असावी.",
    "re.nextSteps": "पुढील पावले मार्गदर्शक",
    "re.step1Tip": "2-3 बँकांकडून होम लोन प्री-अप्रूव्हल मिळवा",
    "re.step2Tip": "मालमत्तेची RERA नोंदणी पडताळा",
    "re.step3Tip": "इंटिरिअर आणि स्थलांतर खर्च (5-10%) बजेट करा",
    "re.step4Tip": "ताबा घेण्यापूर्वी होम इन्श्युरन्स विचारात घ्या",
    "re.startOver": "पुन्हा सुरू करा",
    "re.step4Title": "कर्ज पात्रता",
    "re.empType": "रोजगार प्रकार",
    "re.empSalaried": "पगारदार",
    "re.empBusiness": "व्यवसाय मालक",
    "re.empFreelancer": "फ्रीलान्सर",
    "re.netSalary": "निव्वळ मासिक पगार (₹)",
    "re.existingEmis": "विद्यमान EMI (₹)",
    "re.workExp": "कामाचा अनुभव",
    "re.maxAllowableEmi": "जास्तीत जास्त अनुमत EMI",
    "re.maxLoanAmount": "जास्तीत जास्त कर्ज रक्कम",
    "re.requiredLoan": "आवश्यक कर्ज रक्कम",
    "re.calcBasis": "गणना आधार",
    "re.eligible": "पात्र — तुम्ही पात्र आहात!",
    "re.borderline": "सीमारेषा — मर्यादेजवळ",
    "re.notEligible": "अपात्र — अंतर खूप मोठा",
    "re.eligibleTip": "तुमचे आवश्यक कर्ज तुमच्या पात्रतेत आहे. अर्ज करण्यास तयार!",
    "re.borderlineTip": "मर्यादा वाढवण्यासाठी पती/पत्नीला सह-अर्जदार म्हणून जोडा.",
    "re.notEligibleTip": "तुमचे डाउन पेमेंट वाढवा किंवा आधी विद्यमान कर्ज फेडा.",
    "re.sathiLoanTip": "साथी टिप: बँका स्थिरता पसंत करतात. गेल्या 3 महिन्यांत नोकरी बदलली असेल तर, अर्ज करण्यापूर्वी 3 पगार स्लिपची वाट पहा!",
    "re.addCoApplicant": "सह-अर्जदाराचे उत्पन्न जोडा?",
    "re.coApplicantSalary": "सह-अर्जदार मासिक पगार (₹)",
    "re.coApplicantNote": "पती/पत्नी किंवा पालकांचे उत्पन्न जोडल्याने तुमची कर्ज पात्रता लक्षणीयरीत्या वाढू शकते!",
    "ret.title": "रिटायरमेंट क्लॉक",
    "ret.subtitle": "महागाई-समायोजित गणनांसह निवृत्ती नियोजन",
    "ret.monthlyExpense": "सध्याचा मासिक खर्च",
    "ret.currentAge": "सध्याचे वय",
    "ret.retireAge": "निवृत्तीचे वय",
    "ret.inflationRate": "महागाई दर (%)",
    "ret.sipReturn": "अपेक्षित SIP परतावा (%)",
    "ret.yearsLeft": "निवृत्तीला उरलेली वर्षे",
    "ret.futureCost": "भविष्यातील मासिक खर्च",
    "ret.month": "महिना",
    "ret.inflationNote": "महागाईसाठी समायोजित जीवनशैली खर्च",
    "ret.corpusNeeded": "निवृत्ती कॉर्पस आवश्यक",
    "ret.sipNeeded": "मासिक SIP आवश्यक",
    "ret.sipNote": "आरामदायी निवृत्तीसाठी आजच ही SIP सुरू करा",
    "sidebar.rentVsBuy": "भाडे vs खरेदी",
    "sidebar.debtKiller": "कर्ज-मुक्ति पथ",
    "sidebar.stockResearch": "स्टॉक रिसर्च",
    "sidebar.emiMentor": "मी हे विकत घेऊ शकतो का?",
    "sidebar.lifeStages": "जीवन टप्पे",
    "sidebar.creditCard": "क्रेडिट कार्ड मास्टर",
    "sidebar.profile": "माझी प्रोफाइल",
    "sidebar.childFuture": "मुलाचे भविष्य प्लानर",
    "sidebar.assetComparison": "मालमत्ता तुलना",
    "sidebar.dataRights": "डेटा अधिकार",
    "rvb.title": "भाडे vs खरेदी विश्लेषक",
    "rvb.subtitle": "भाड्याने राहा की विकत घ्या? कालांतराने खर्चाची तुलना करा।",
    "rvb.cityPreset": "शहर प्रीसेट",
    "rvb.custom": "कस्टम",
    "rvb.taxRegime": "कर प्रणाली",
    "rvb.oldRegime": "जुनी प्रणाली",
    "rvb.newRegime": "नवी प्रणाली",
    "rvb.horizon": "विश्लेषण कालावधी",
    "rvb.yrs": "वर्षे",
    "rvb.buyPath": "खरेदी मार्ग",
    "rvb.rentPath": "भाडे + गुंतवणूक मार्ग",
    "rvb.propertyPrice": "मालमत्ता किंमत",
    "rvb.downPayment": "डाउन पेमेंट (%)",
    "rvb.loanRate": "कर्ज व्याज दर (%)",
    "rvb.loanTenure": "कर्ज कालावधी (वर्षे)",
    "rvb.interiors": "इंटेरियर खर्च",
    "rvb.monthlyMaintenance": "मासिक देखभाल",
    "rvb.yearlyPropertyTax": "वार्षिक मालमत्ता कर",
    "rvb.appreciationRate": "मालमत्ता मूल्यवाढ (%)",
    "rvb.monthlyRent": "मासिक भाडे",
    "rvb.rentIncrease": "वार्षिक भाडेवाढ (%)",
    "rvb.mfReturn": "म्युच्युअल फंड रिटर्न (%)",
    "rvb.opportunityCostNote": "संधी खर्च: डाउन पेमेंट गुंतवणूक केली असती तर",
    "rvb.investedInMF": "म्युच्युअल फंडात गुंतवणूक",
    "rvb.hraNote": "लक्षात ठेवा: HRA सूट फक्त जुन्या प्रणालीत उपलब्ध",
    "rvb.monthlyEmi": "मासिक EMI",
    "rvb.recommendation": "शिफारस",
    "rvb.buyingBetter": "खरेदी अधिक संपत्ती निर्माण करते",
    "rvb.rentingBetter": "भाडे तुमच्या संपत्तीसाठी चांगले",
    "rvb.wealthDiff": "संपत्ती फरक",
    "rvb.breakeven": "ब्रेकइवन वर्ष",
    "rvb.year": "वर्ष",
    "rvb.neverBreakeven": "खरेदी कधीच पकडू शकत नाही",
    "rvb.wealthComparison": "निव्वळ संपत्ती तुलना",
    "rvb.years": "वर्षे",
    "rvb.buyWealth": "खरेदी: निव्वळ संपत्ती",
    "rvb.rentWealth": "भाडे + गुंतवणूक: निव्वळ संपत्ती",
    "rvb.taxRulesTitle": "2026 कर नियम लागू",
    "rvb.taxRule1": "जुनी: कलम 24b व्याज वजावट ₹2L पर्यंत",
    "rvb.taxRule2": "जुनी: कलम 80C मूळ वजावट ₹1.5L पर्यंत",
    "rvb.taxRule3": "नवी: स्व-निवासी मालमत्तेसाठी वजावट नाही",
    "rvb.assumptions": "वापरलेल्या गृहीतके",
    "rvb.assumption1": "मालमत्ता मूल्यवाढ",
    "rvb.assumption2": "भाडेवाढ",
    "rvb.assumption3": "MF रिटर्न (CAGR)",
    "rvb.purposeTitle": "हे टूल काय करते?",
    "rvb.purposeDesc": "हे टूल घर खरेदीची खरी किंमत (EMI, स्टॅम्प ड्युटी, देखभाल) भाड्याने राहून फरक म्युच्युअल फंडात गुंतवणूक करण्याशी तुलना करते.",
    "rvb.hiddenCosts": "छुपे खर्च (स्टॅम्प ड्युटी, देखभाल, कर)",
    "rvb.stampDuty": "स्टॅम्प ड्युटी",
    "rvb.registration": "नोंदणी",
    "rvb.wealthGapChart": "संपत्ती अंतर: खरेदी vs भाडे + गुंतवणूक",
    "rvb.sathiVerdict": "साथीचा निर्णय",
    "rvb.sathiRentAdvice": "साथी म्हणतो: भाडे तुम्हाला दरमहा {AMT} वाचवत आहे. हे SIP मध्ये गुंतवा!",
    "rvb.sathiBuyAdvice": "साथी म्हणतो: या किमतीत खरेदी करणे उत्तम दीर्घकालीन संपत्ती आहे!",
    "rvb.totalCostBuy": "मालकीची एकूण किंमत (खरेदी)",
    "rvb.rentInvestPath": "भाडे + गुंतवणूक मार्ग",
    "rvb.downPaymentStampInteriors": "डाउन पेमेंट + स्टॅम्प ड्युटी + इंटिरिअर",
    "rvb.totalEmiPaid": "एकूण EMI भरली",
    "rvb.interestLabel": "व्याज",
    "rvb.taxSavings": "कर बचत (कलम 24b + 80C)",
    "rvb.totalOutOfPocket": "एकूण खिशातून खर्च",
    "rvb.propertyValueAtYear": "वर्षी मालमत्ता मूल्य",
    "rvb.totalRentPaid": "एकूण भाडे भरले",
    "rvb.downPaymentInvested": "डाउन पेमेंट गुंतवणूक",
    "rvb.indexFund": "इंडेक्स फंड",
    "rvb.monthlySavingsInvested": "मासिक बचत गुंतवणूक (EMI फरक)",
    "rvb.autoCompounded": "ऑटो-कंपाउंडेड",
    "rvb.investmentCorpus": "वर्षी गुंतवणूक कोष",
    "rvb.netWealth": "निव्वळ संपत्ती (कोष − भाडे)",
    "rvb.shareWhatsApp": "WhatsApp वर शेअर करा",
    "rvb.saveResult": "निकाल जतन करा",
    "mac.investmentAmount": "गुंतवणूक रक्कम (₹)",
    "mac.frequency": "गुंतवणूक वारंवारता",
    "mac.freqMonthly": "मासिक (SIP)",
    "mac.freqQuarterly": "त्रैमासिक",
    "mac.freqHalfYearly": "सहामाही",
    "mac.freqYearly": "वार्षिक",
    "mac.chartTitle": "{amount} {frequency} गुंतवणुकीची {years} वर्षांत वाढ",
    "mac.yearlyTip": "वर्षातून एकदा मोठी रक्कम गुंतवणूक vs दरमहा लहान रक्कम — 'रुपी कॉस्ट ऍव्हरेजिंग' मुळे निकाल वेगवेगळे असू शकतात!",
  },
  ta: {
    "sidebar.dashboard": "டாஷ்போர்ட்",
    "sidebar.calculators": "முதலீட்டு கால்குலேட்டர்கள்",
    "sidebar.compare": "தயாரிப்பு ஒப்பீடு",
    "sidebar.realEstate": "ரியல் எஸ்டேட் பிளானர்",
    "sidebar.tax": "வரி மையம்",
    "sidebar.tagline": "ஸ்மார்ட் நிதி கருவிகள்",
    "sidebar.disclaimer": "கல்வி நோக்கங்களுக்கு மட்டும். நிதி ஆலோசனை அல்ல.",
    "sidebar.catDashboard": "டாஷ்போர்டு",
    "sidebar.catHomeLife": "🏠 வீடு & வாழ்க்கை",
    "sidebar.catFuture": "🔮 எதிர்காலம்",
    "sidebar.catSafety": "🛡️ பாதுகாப்பு",
    "sidebar.catInvest": "📈 முதலீடு",
    "sidebar.catBanking": "🏦 வங்கி",
    "sidebar.catAccount": "கணக்கு",
    "sidebar.desc.dashboard": "ஒரு பார்வையில் உங்கள் நிதி நிலை",
    "sidebar.desc.calculators": "SIP, மொத்த முதலீடு & கூட்டு வட்டி கருவிகள்",
    "sidebar.desc.compare": "நிதி தயாரிப்புகளை அருகருகே ஒப்பிடுங்கள்",
    "sidebar.desc.realEstate": "சொத்து வாங்குதல் & ROI திட்டமிடுங்கள்",
    "sidebar.desc.tax": "பழைய & புதிய வரி முறையில் சேமிப்பு மேம்படுத்துங்கள்",
    "sidebar.desc.retirement": "ஓய்வூதிய நிதி & SIP கணக்கிடுங்கள்",
    "sidebar.desc.rentVsBuy": "வாடகை அல்லது வாங்குதல் — எது சிறந்தது அறியுங்கள்",
    "sidebar.desc.debtKiller": "விரைவாக கடன் இல்லாமல் ஆக திட்டமிடுங்கள்",
    "sidebar.desc.stockResearch": "நேரடி சந்தை தரவு & பங்கு பகுப்பாய்வு",
    "sidebar.desc.emiMentor": "நீங்கள் உண்மையில் வாங்க முடியுமா என சரிபார்க்கவும்",
    "sidebar.desc.childFuture": "குழந்தையின் கல்வி & எதிர்கால செலவுகள் திட்டமிடுங்கள்",
    "sidebar.desc.creditCard": "கிரெடிட் கார்டு கடனின் உண்மையான செலவைப் புரிந்துகொள்ளுங்கள்",
    "sidebar.desc.assetComparison": "பல்வேறு சொத்து வகைகளில் வருவாய் ஒப்பீடு",
    "sidebar.desc.profile": "உங்கள் நிதி சுயவிவரத்தை நிர்வகியுங்கள்",
    "sidebar.desc.dataRights": "உங்கள் தரவு தனியுரிமை & பதிவிறக்க விருப்பங்கள்",
    "dash.title": "டாஷ்போர்ட்",
    "dash.subtitle": "உங்கள் நிதி நிலை ஒரு பார்வையில்",
    "dash.portfolioValue": "போர்ட்ஃபோலியோ மதிப்பு",
    "dash.monthlySip": "மாதாந்திர SIP",
    "dash.taxSaved": "வரி சேமிப்பு (FY25)",
    "dash.returns1y": "வருமானம் (1 ஆண்டு)",
    "dash.portfolioGrowth": "போர்ட்ஃபோலியோ வளர்ச்சி",
    "dash.last9months": "கடந்த 9 மாதங்கள்",
    "dash.quickActions": "விரைவு செயல்கள்",
    "dash.sipCalc": "SIP கால்குலேட்டர்",
    "dash.compareMF": "மியூச்சுவல் ஃபண்ட் ஒப்பீடு",
    "dash.emiEstimator": "EMI மதிப்பீடு",
    "dash.taxOptimizer": "வரி திட்டமிடுபவர்",
    "dash.marketSnapshot": "சந்தை ஸ்னாப்ஷாட்",
    "dash.active": "செயலில்",
    "dash.vikasScore": "விகாஸ் பயணம்",
    "dash.totalAssets": "மொத்த சொத்துக்கள்",
    "dash.totalLiabilities": "மொத்த கடன்கள்",
    "dash.netInvestment": "நிகர முதலீடு",
    "dash.dtiRatio": "DTI விகிதம்",
    "dash.savingsInvestments": "சேமிப்பு + முதலீடுகள்",
    "dash.annualEmi": "வருடாந்திர EMI கடமைகள்",
    "dash.monthlyWealth": "மாதாந்திர செல்வ உருவாக்கம்",
    "dash.financialPulse": "நிதி துடிப்பு",
    "dash.assetsVsLiabilities": "சொத்துக்கள் vs கடன்கள் — 12 மாத கணிப்பு",
    "dash.dailySathiTip": "இன்றைய சாதி குறிப்பு",
    "dash.goalTracker": "இலக்கு கண்காணிப்பான்",
    "dash.miniTools": "மினி கருவிகள் — விரைவு பண ஹேக்ஸ்",
    "dash.welcome": "வரவேற்கிறோம்",
    "dash.welcomeSub": "இன்றைய உங்கள் நிதி கண்ணோட்டம் இங்கே.",
    "dash.editProfile": "சுயவிவரம் திருத்து",
    "dash.user": "பயனர்",
    "ai.insightTitle": "AI நுண்ணறிவு",
    "ai.sipAdvice": "உங்கள் {income} வருமானத்தின் அடிப்படையில், உங்கள் {goal} வேகப்படுத்த {sip}/மாதம் SIP தொடங்க பரிந்துரைக்கிறோம்.",
    "ai.dtiWarning": "உங்கள் EMI சுமை {dti}% — வட்டி குறைக்க பேலன்ஸ் டிரான்ஸ்ஃபர் பரிசீலியுங்கள்.",
    "ai.financialGoal": "நிதி இலக்கு",
    "action.title": "செயல் பட்டியல்",
    "action.completeProfile": "உங்கள் நிதி சுயவிவரத்தை முடிக்கவும்",
    "action.setup": "அமை",
    "action.runTax": "வரி மேம்படுத்தல் இயக்கவும்",
    "action.open": "திற",
    "action.rentVsBuy": "வாடகை vs வாங்குதல் பகுப்பாய்வு",
    "action.analyze": "பகுப்பாய்வு",
    "goal.buyHome": "வீடு வாங்கு",
    "goal.payDebt": "கடன் அடை",
    "goal.retireEarly": "சீக்கிரம் ஓய்வு",
    "goal.emergency": "அவசர நிதி",
    "goal.childEdu": "குழந்தை கல்வி",
    "goal.wealthGrowth": "செல்வம் வளர்",
    "goal.eta50plus": "50+ ஆண்டுகள்",
    "goal.achieved": "அடைந்தது",
    "goal.basedOn": "அடிப்படையில்",
    "goal.moSIP": "மாத SIP",
    "goal.returns": "வருமானம்",
    "goal.saved": "சேமிப்பு",
    "goal.target": "இலக்கு",
    "goal.custom": "தனிப்பயன்",
    "goal.complete": "முடிந்தது",
    "goal.yrsToGo": "ஆண்டுகள் மீதம்",
    "goal.sipNeeded": "இலக்கை அடைய SIP",
    "goal.yrs": "ஆண்டு",
    "goal.month": "மாதம்",
    "goal.save": "சேமி",
    "goal.reset": "ரீசெட்",
    "goal.setCustomTarget": "தனிப்பயன் இலக்கு அமை",
    "goal.noGoals": "இலக்குகள் இல்லை. கண்காணிக்க சுயவிவரத்தை புதுப்பிக்கவும்.",
    "goal.enterValid": "சரியான தொகை உள்ளிடவும்",
    "goal.targetUpdated": "இலக்கு புதுப்பிக்கப்பட்டது",
    "goal.resetDefault": "இயல்புநிலைக்கு ரீசெட்",
    "calc.title": "முதலீட்டு கால்குலேட்டர்கள்",
    "calc.subtitle": "துல்லியமாக உங்கள் செல்வத்தை திட்டமிடுங்கள்",
    "calc.sipCalc": "SIP கால்குலேட்டர்",
    "calc.lumpSum": "ஒட்டுமொத்தம்",
    "calc.monthlyInvestment": "மாதாந்திர முதலீடு",
    "calc.investmentAmount": "முதலீட்டு தொகை",
    "calc.expectedReturn": "எதிர்பார்க்கப்படும் வருமானம் (%)",
    "calc.timePeriod": "கால அளவு (ஆண்டுகள்)",
    "calc.yrs": "ஆண்டுகள்",
    "calc.totalValue": "மொத்த மதிப்பு",
    "calc.invested": "முதலீடு",
    "calc.gains": "லாபம்",
    "compare.title": "தயாரிப்பு ஒப்பீடு",
    "compare.subtitle": "மியூச்சுவல் ஃபண்டுகள், FD மற்றும் காப்பீட்டு திட்டங்களை ஒப்பிடுங்கள்",
    "compare.fundName": "ஃபண்ட் பெயர்",
    "compare.1yReturn": "1 ஆண்டு வருமானம்",
    "compare.3yReturn": "3 ஆண்டு வருமானம்",
    "compare.5yReturn": "5 ஆண்டு வருமானம்",
    "compare.risk": "ரிஸ்க்",
    "compare.expense": "செலவு",
    "compare.disclaimer": "* கடந்த கால செயல்திறன் எதிர்கால வருமானத்திற்கு உத்தரவாதம் அல்ல.",
    "compare.low": "குறைவு",
    "compare.moderate": "மிதம்",
    "compare.high": "அதிகம்",
    "compare.moderateHigh": "மிதம்-அதிகம்",
    "re.title": "ரியல் எஸ்டேட் பிளானர்",
    "re.subtitle": "EMI கால்குலேட்டர் மற்றும் சொத்து திட்டமிடல் கருவிகள்",
    "re.propertyValue": "சொத்து மதிப்பு",
    "re.downPayment": "டவுன் பேமெண்ட் (%)",
    "re.interestRate": "வட்டி விகிதம் (%)",
    "re.loanTenure": "கடன் காலம் (ஆண்டுகள்)",
    "re.monthlyEmi": "மாதாந்திர EMI",
    "re.loanAmount": "கடன் தொகை",
    "re.totalInterest": "மொத்த வட்டி",
    "re.totalPayment": "மொத்த செலுத்தல்",
    "tax.title": "வரி மையம்",
    "tax.subtitle": "இந்திய வரி சட்டத்தின் கீழ் வரி சேமிப்பை திட்டமிடுங்கள்",
    "tax.recommended": "பரிந்துரை",
    "tax.alternative": "மாற்று",
    "tax.oldRegime": "பழைய வரி முறை",
    "tax.newRegime": "புதிய வரி முறை",
    "tax.estimatedTax": "மதிப்பிடப்பட்ட வரி",
    "tax.withDeductions": "₹2,41,000 கழிவுகளுடன்",
    "tax.standardDeduction": "நிலையான கழிவு ₹75,000",
    "tax.section80c": "பிரிவு 80C – ₹1.5 லட்சம்",
    "tax.section80d": "பிரிவு 80D – சுகாதார காப்பீடு",
    "tax.hraExemption": "HRA விலக்கு",
    "tax.elss": "ELSS மியூச்சுவல் ஃபண்டுகள்",
    "tax.epf": "EPF பங்களிப்பு",
    "tax.lifeInsurance": "ஆயுள் காப்பீட்டு பிரீமியம்",
    "tax.selfFamily": "சுய & குடும்ப பிரீமியம்",
    "tax.parentsPremium": "பெற்றோர் பிரீமியம்",
    "tax.rentPaid": "செலுத்திய வாடகை (ஆண்டு)",
    "tax.exemptedAmount": "விலக்குப் பெற்ற தொகை",
    "tax.invested": "முதலீடு",
    "tax.autoDeducted": "தானியங்கி கழிவு",
    "tax.paid": "செலுத்தியது",
    "tax.claimable": "கோரக்கூடியது",
    "tax.calculated": "கணக்கிடப்பட்டது",
    "sidebar.retirement": "ஓய்வு கடிகாரம்",
    "re.step1Title": "பட்ஜெட்",
    "re.step2Title": "ROI சிமுலேட்டர்",
    "re.step3Title": "தாங்கும் திறன்",
    "re.stampDuty": "முத்திரை வரி",
    "re.registration": "பதிவு",
    "re.gst": "GST",
    "re.totalCost": "மொத்த சொத்து செலவு",
    "re.nextStep": "அடுத்த படி",
    "re.back": "பின்செல்",
    "re.roiDesc": "ஒப்பீடு: அதே தொகையை மியூச்சுவல் ஃபண்டில் முதலீடு செய்திருந்தால்?",
    "re.realEstateInv": "ரியல் எஸ்டேட் முதலீடு",
    "re.mutualFundInv": "மியூச்சுவல் ஃபண்ட் முதலீடு",
    "re.rentalYield": "வாடகை வருமானம்",
    "re.appreciation": "மதிப்பு உயர்வு",
    "re.propertyAfter20": "சொத்து மதிப்பு (20 ஆண்டுகள்)",
    "re.totalRentalIncome": "மொத்த வாடகை வருமானம்",
    "re.costDeductions": "செலவு கழிப்புகள்",
    "re.netGain": "நிகர லாபம்",
    "re.expectedReturn": "எதிர்பார்க்கப்படும் வருமானம்",
    "re.amountInvested": "முதலீடு செய்த தொகை",
    "re.valueAfter20": "20 ஆண்டுகளுக்குப் பிறகு மதிப்பு",
    "re.mfWins": "மியூச்சுவல் ஃபண்ட் வெற்றி",
    "re.reWins": "ரியல் எஸ்டேட் வெற்றி",
    "re.summary": "சுருக்கம்",
    "re.minIncome": "குறைந்தபட்ச மாத வருமானம்",
    "re.affordable": "இந்த சொத்து உங்களுக்கு ஏற்றது!",
    "re.notAffordable": "இந்த சொத்து உங்கள் பட்ஜெட்டை மீறலாம்.",
    "re.emiShouldBe": "EMI உங்கள் மாத வருமானத்தின் 40%-க்கும் குறைவாக இருக்க வேண்டும்.",
    "re.nextSteps": "அடுத்த படிகள் வழிகாட்டி",
    "re.step1Tip": "2-3 வங்கிகளிடம் வீட்டுக்கடன் முன்அனுமதி பெறுங்கள்",
    "re.step2Tip": "சொத்தின் RERA பதிவை சரிபார்க்கவும்",
    "re.step3Tip": "உள்ளமைப்பு & இடமாற்ற செலவுகளை (5-10%) பட்ஜெட் செய்யுங்கள்",
    "re.step4Tip": "உரிமை பெறும் முன் வீட்டு காப்பீடு பரிசீலியுங்கள்",
    "re.startOver": "மீண்டும் தொடங்கு",
    "re.step4Title": "கடன் தகுதி",
    "re.empType": "வேலை வகை",
    "re.empSalaried": "சம்பளம் பெறுபவர்",
    "re.empBusiness": "வணிக உரிமையாளர்",
    "re.empFreelancer": "ஃப்ரீலான்சர்",
    "re.netSalary": "நிகர மாத சம்பளம் (₹)",
    "re.existingEmis": "தற்போதைய EMIகள் (₹)",
    "re.workExp": "பணி அனுபவம்",
    "re.maxAllowableEmi": "அதிகபட்ச அனுமதிக்கப்பட்ட EMI",
    "re.maxLoanAmount": "அதிகபட்ச கடன் தொகை",
    "re.requiredLoan": "தேவையான கடன் தொகை",
    "re.calcBasis": "கணக்கீட்டு அடிப்படை",
    "re.eligible": "தகுதியானது — நீங்கள் தகுதியானவர்!",
    "re.borderline": "எல்லையில் — வரம்புக்கு அருகில்",
    "re.notEligible": "தகுதியற்றது — இடைவெளி மிக அதிகம்",
    "re.eligibleTip": "உங்கள் தேவையான கடன் உங்கள் தகுதிக்குள் உள்ளது. விண்ணப்பிக்க தயார்!",
    "re.borderlineTip": "வரம்பை அதிகரிக்க கணவர்/மனைவியை இணை விண்ணப்பதாரராக சேர்க்கவும்.",
    "re.notEligibleTip": "உங்கள் டவுன் பேமென்ட் அதிகரிக்கவும் அல்லது முதலில் இருக்கும் கடன்களை அடைக்கவும்.",
    "re.sathiLoanTip": "சாதி டிப்: வங்கிகள் நிலைத்தன்மையை விரும்புகின்றன. கடந்த 3 மாதங்களில் வேலை மாறியிருந்தால், விண்ணப்பிக்கும் முன் 3 சம்பள சீட்டுகளுக்கு காத்திருங்கள்!",
    "re.addCoApplicant": "இணை விண்ணப்பதாரர் வருமானம் சேர்க்கவா?",
    "re.coApplicantSalary": "இணை விண்ணப்பதாரர் மாத சம்பளம் (₹)",
    "re.coApplicantNote": "கணவர்/மனைவி அல்லது பெற்றோரின் வருமானம் சேர்ப்பது உங்கள் கடன் தகுதியை கணிசமாக அதிகரிக்கும்!",
    "ret.title": "ஓய்வு கடிகாரம்",
    "ret.subtitle": "பணவீக்க-சரிசெய்யப்பட்ட கணக்கீடுகளுடன் ஓய்வு திட்டமிடல்",
    "ret.monthlyExpense": "தற்போதைய மாத செலவுகள்",
    "ret.currentAge": "தற்போதைய வயது",
    "ret.retireAge": "ஓய்வு வயது",
    "ret.inflationRate": "பணவீக்க விகிதம் (%)",
    "ret.sipReturn": "எதிர்பார்க்கப்படும் SIP வருமானம் (%)",
    "ret.yearsLeft": "ஓய்வுக்கு மீதமுள்ள ஆண்டுகள்",
    "ret.futureCost": "எதிர்கால மாத செலவு",
    "ret.month": "மாதம்",
    "ret.inflationNote": "பணவீக்கத்திற்கு சரிசெய்யப்பட்ட வாழ்க்கை முறை செலவு",
    "ret.corpusNeeded": "ஓய்வு கார்பஸ் தேவை",
    "ret.sipNeeded": "மாதாந்திர SIP தேவை",
    "ret.sipNote": "வசதியான ஓய்வுக்கு இன்றே இந்த SIP-ஐ தொடங்குங்கள்",
    "sidebar.rentVsBuy": "வாடகை vs வாங்குதல்",
    "sidebar.debtKiller": "கடன்-விடுதலை பாதை",
    "sidebar.stockResearch": "பங்கு ஆராய்ச்சி",
    "sidebar.emiMentor": "நான் இதை வாங்கலாமா?",
    "sidebar.lifeStages": "வாழ்க்கை நிலைகள்",
    "sidebar.creditCard": "கிரெடிட் கார்டு மாஸ்டர்",
    "sidebar.profile": "எனது சுயவிவரம்",
    "sidebar.childFuture": "குழந்தையின் எதிர்கால திட்டமிடல்",
    "sidebar.assetComparison": "சொத்து ஒப்பீடு",
    "sidebar.dataRights": "தரவு உரிமைகள்",
    "rvb.title": "வாடகை vs வாங்குதல் பகுப்பாய்வி",
    "rvb.subtitle": "வாடகைக்கா வாங்கவா? காலப்போக்கில் உண்மையான செலவை ஒப்பிடுங்கள்.",
    "rvb.cityPreset": "நகர ப்ரீசெட்",
    "rvb.custom": "தனிப்பயன்",
    "rvb.taxRegime": "வரி முறை",
    "rvb.oldRegime": "பழைய முறை",
    "rvb.newRegime": "புதிய முறை",
    "rvb.horizon": "பகுப்பாய்வு காலம்",
    "rvb.yrs": "ஆண்டுகள்",
    "rvb.buyPath": "வாங்கும் பாதை",
    "rvb.rentPath": "வாடகை + முதலீட்டு பாதை",
    "rvb.propertyPrice": "சொத்து விலை",
    "rvb.downPayment": "முன்பணம் (%)",
    "rvb.loanRate": "கடன் வட்டி விகிதம் (%)",
    "rvb.loanTenure": "கடன் காலம் (ஆண்டுகள்)",
    "rvb.interiors": "உள்அலங்கார செலவு",
    "rvb.monthlyMaintenance": "மாத பராமரிப்பு",
    "rvb.yearlyPropertyTax": "ஆண்டு சொத்து வரி",
    "rvb.appreciationRate": "சொத்து மதிப்பேற்றம் (%)",
    "rvb.monthlyRent": "மாத வாடகை",
    "rvb.rentIncrease": "ஆண்டு வாடகை உயர்வு (%)",
    "rvb.mfReturn": "மியூச்சுவல் ஃபண்ட் வருமானம் (%)",
    "rvb.opportunityCostNote": "வாய்ப்பு செலவு: முன்பணம் முதலீடு செய்யப்பட்டிருந்தால்",
    "rvb.investedInMF": "மியூச்சுவல் ஃபண்டில் முதலீடு",
    "rvb.hraNote": "நினைவில் கொள்ளுங்கள்: HRA விலக்கு பழைய முறையில் மட்டுமே",
    "rvb.monthlyEmi": "மாத EMI",
    "rvb.recommendation": "பரிந்துரை",
    "rvb.buyingBetter": "வாங்குவது அதிக செல்வத்தை உருவாக்கும்",
    "rvb.rentingBetter": "வாடகை உங்கள் செல்வத்திற்கு சிறந்தது",
    "rvb.wealthDiff": "செல்வ வேறுபாடு",
    "rvb.breakeven": "சரிநிலை ஆண்டு",
    "rvb.year": "ஆண்டு",
    "rvb.neverBreakeven": "வாங்குதல் ஒருபோதும் பிடிக்காது",
    "rvb.wealthComparison": "நிகர செல்வ ஒப்பீடு",
    "rvb.years": "ஆண்டுகள்",
    "rvb.buyWealth": "வாங்குதல்: நிகர செல்வம்",
    "rvb.rentWealth": "வாடகை + முதலீடு: நிகர செல்வம்",
    "rvb.taxRulesTitle": "2026 வரி விதிகள் பயன்படுத்தப்பட்டன",
    "rvb.taxRule1": "பழையது: பிரிவு 24b வட்டி கழிவு ₹2L வரை",
    "rvb.taxRule2": "பழையது: பிரிவு 80C அசல் கழிவு ₹1.5L வரை",
    "rvb.taxRule3": "புதியது: சொந்த சொத்துக்கு கழிவுகள் இல்லை",
    "rvb.assumptions": "பயன்படுத்திய அனுமானங்கள்",
    "rvb.assumption1": "சொத்து மதிப்பேற்றம்",
    "rvb.assumption2": "வாடகை உயர்வு",
    "rvb.assumption3": "MF வருமானம் (CAGR)",
    "rvb.purposeTitle": "இந்தக் கருவி என்ன செய்கிறது?",
    "rvb.purposeDesc": "இந்தக் கருவி வீடு வாங்குவதின் உண்மையான செலவை (EMI, முத்திரை வரி, பராமரிப்பு) வாடகையில் இருந்து வேறுபாட்டை மியூச்சுவல் ஃபண்டில் முதலீடு செய்வதுடன் ஒப்பிடுகிறது.",
    "rvb.hiddenCosts": "மறைந்த செலவுகள் (முத்திரை வரி, பராமரிப்பு, வரி)",
    "rvb.stampDuty": "முத்திரை வரி",
    "rvb.registration": "பதிவு",
    "rvb.wealthGapChart": "செல்வ இடைவெளி: வாங்குதல் vs வாடகை + முதலீடு",
    "rvb.sathiVerdict": "சாதி தீர்ப்பு",
    "rvb.sathiRentAdvice": "சாதி சொல்கிறார்: வாடகை உங்களுக்கு மாதம் {AMT} சேமிக்கிறது. இதை SIPல் முதலீடு செய்யுங்கள்!",
    "rvb.sathiBuyAdvice": "சாதி சொல்கிறார்: இந்த விலையில் வாங்குவது சிறந்த நீண்டகால சொத்து!",
    "rvb.totalCostBuy": "உரிமையின் மொத்த செலவு (வாங்குதல்)",
    "rvb.rentInvestPath": "வாடகை + முதலீட்டு வழி",
    "rvb.downPaymentStampInteriors": "முன்பணம் + முத்திரை வரி + உள்ளமைப்பு",
    "rvb.totalEmiPaid": "மொத்த EMI செலுத்தியது",
    "rvb.interestLabel": "வட்டி",
    "rvb.taxSavings": "வரி சேமிப்பு (பிரிவு 24b + 80C)",
    "rvb.totalOutOfPocket": "மொத்த பாக்கெட் செலவு",
    "rvb.propertyValueAtYear": "ஆண்டில் சொத்து மதிப்பு",
    "rvb.totalRentPaid": "மொத்த வாடகை செலுத்தியது",
    "rvb.downPaymentInvested": "முன்பணம் முதலீடு",
    "rvb.indexFund": "இண்டெக்ஸ் ஃபண்ட்",
    "rvb.monthlySavingsInvested": "மாதாந்திர சேமிப்பு முதலீடு (EMI வேறுபாடு)",
    "rvb.autoCompounded": "ஆட்டோ-கூட்டு வட்டி",
    "rvb.investmentCorpus": "ஆண்டில் முதலீட்டு நிதி",
    "rvb.netWealth": "நிகர செல்வம் (நிதி − வாடகை)",
    "rvb.shareWhatsApp": "WhatsAppல் பகிரவும்",
    "rvb.saveResult": "முடிவைச் சேமிக்கவும்",
    "mac.investmentAmount": "முதலீடு தொகை (₹)",
    "mac.frequency": "முதலீடு அதிர்வெண்",
    "mac.freqMonthly": "மாதாந்திர (SIP)",
    "mac.freqQuarterly": "காலாண்டு",
    "mac.freqHalfYearly": "அரையாண்டு",
    "mac.freqYearly": "வருடாந்திர",
    "mac.chartTitle": "{amount} {frequency} முதலீட்டின் {years} ஆண்டுகள் வளர்ச்சி",
    "mac.yearlyTip": "ஆண்டுக்கு ஒருமுறை பெரிய தொகை முதலீடு vs மாதம்தோறும் சிறிய தொகை — 'ரூபி காஸ்ட் அவரேஜிங்' காரணமாக முடிவுகள் வேறுபடலாம்!",
  },
  gu: {
    "sidebar.dashboard": "ડૅશબોર્ડ",
    "sidebar.calculators": "રોકાણ કેલ્ક્યુલેટર",
    "sidebar.compare": "ઉત્પાદન સરખામણી",
    "sidebar.realEstate": "રિયલ એસ્ટેટ પ્લાનર",
    "sidebar.tax": "ટેક્સ સેન્ટર",
    "sidebar.tagline": "સ્માર્ટ નાણાકીય ટૂલ્સ",
    "sidebar.disclaimer": "ફક્ત શૈક્ષણિક હેતુ માટે. નાણાકીય સલાહ નથી.",
    "sidebar.catDashboard": "ડેશબોર્ડ",
    "sidebar.catHomeLife": "🏠 ઘર અને જીવન",
    "sidebar.catFuture": "🔮 ભવિષ્ય",
    "sidebar.catSafety": "🛡️ સુરક્ષા",
    "sidebar.catInvest": "📈 રોકાણ",
    "sidebar.catBanking": "🏦 બેંકિંગ",
    "sidebar.catAccount": "ખાતું",
    "sidebar.desc.dashboard": "એક નજરમાં તમારી નાણાકીય સ્થિતિ",
    "sidebar.desc.calculators": "SIP, એકમુઠ્ઠ અને ચક્રવૃદ્ધિ વ્યાજ ટૂલ્સ",
    "sidebar.desc.compare": "નાણાકીય ઉત્પાદનોની બાજુ બાજુ સરખામણી",
    "sidebar.desc.realEstate": "સંપત્તિ ખરીદી અને ROI આયોજન",
    "sidebar.desc.tax": "જૂની અને નવી કર યોજનામાં બચત ઑપ્ટિમાઇઝ કરો",
    "sidebar.desc.retirement": "નિવૃત્તિ કોર્પસ અને SIP ગણતરી",
    "sidebar.desc.rentVsBuy": "ભાડે કે ખરીદી — શું વધુ સારું છે જાણો",
    "sidebar.desc.debtKiller": "ઝડપથી દેવું મુક્ત થવાનું આયોજન",
    "sidebar.desc.stockResearch": "લાઇવ બજાર ડેટા અને સ્ટોક વિશ્લેષણ",
    "sidebar.desc.emiMentor": "તમે ખરેખર ખરીદી કરી શકો છો કે નહીં તપાસો",
    "sidebar.desc.childFuture": "બાળકના શિક્ષણ અને ભાવિ ખર્ચનું આયોજન",
    "sidebar.desc.creditCard": "ક્રેડિટ કાર્ડ દેવાનો સાચો ખર્ચ સમજો",
    "sidebar.desc.assetComparison": "વિવિધ એસેટ ક્લાસમાં રિટર્ન સરખામણી",
    "sidebar.desc.profile": "તમારી નાણાકીય પ્રોફાઇલ મેનેજ કરો",
    "sidebar.desc.dataRights": "તમારી ડેટા ગોપનીયતા અને ડાઉનલોડ વિકલ્પો",
    "dash.title": "ડૅશબોર્ડ",
    "dash.subtitle": "તમારી નાણાકીય સ્થિતિ એક નજરમાં",
    "dash.portfolioValue": "પોર્ટફોલિયો મૂલ્ય",
    "dash.monthlySip": "માસિક SIP",
    "dash.taxSaved": "ટેક્સ બચત (FY25)",
    "dash.returns1y": "વળતર (1 વર્ષ)",
    "dash.portfolioGrowth": "પોર્ટફોલિયો વૃદ્ધિ",
    "dash.last9months": "છેલ્લા 9 મહિના",
    "dash.quickActions": "ઝડપી ક્રિયાઓ",
    "dash.sipCalc": "SIP કેલ્ક્યુલેટર",
    "dash.compareMF": "મ્યુચ્યુઅલ ફંડ સરખામણી",
    "dash.emiEstimator": "EMI અંદાજ",
    "dash.taxOptimizer": "ટેક્સ પ્લાનર",
    "dash.marketSnapshot": "બજાર સ્નેપશોટ",
    "dash.active": "સક્રિય",
    "dash.vikasScore": "વિકાસ યાત્રા",
    "dash.totalAssets": "કુલ સંપત્તિ",
    "dash.totalLiabilities": "કુલ જવાબદારીઓ",
    "dash.netInvestment": "ચોખ્ખું રોકાણ",
    "dash.dtiRatio": "DTI ગુણોત્તર",
    "dash.savingsInvestments": "બચત + રોકાણ",
    "dash.annualEmi": "વાર્ષિક EMI જવાબદારીઓ",
    "dash.monthlyWealth": "માસિક સંપત્તિ નિર્માણ",
    "dash.financialPulse": "નાણાકીય પલ્સ",
    "dash.assetsVsLiabilities": "સંપત્તિ vs જવાબદારીઓ — 12 મહિનાનો અંદાજ",
    "dash.dailySathiTip": "આજની સાથી ટિપ",
    "dash.goalTracker": "લક્ષ્ય ટ્રેકર",
    "dash.miniTools": "મિની ટૂલ્સ — ઝડપી પૈસા હેક્સ",
    "dash.welcome": "સ્વાગત છે",
    "dash.welcomeSub": "આજનો તમારો નાણાકીય ઝલક અહીં છે.",
    "dash.editProfile": "પ્રોફાઇલ સંપાદિત કરો",
    "dash.user": "વપરાશકર્તા",
    "ai.insightTitle": "AI આંતરદૃષ્ટિ",
    "ai.sipAdvice": "તમારી {income} આવકના આધારે, તમારા {goal} ઝડપી બનાવવા {sip}/મહિનો SIP શરૂ કરવાની ભલામણ કરીએ છીએ.",
    "ai.dtiWarning": "તમારો EMI ભાર {dti}% છે — વ્યાજ ઘટાડવા બેલેન્સ ટ્રાન્સફર વિચારો.",
    "ai.financialGoal": "નાણાકીય લક્ષ્ય",
    "action.title": "કાર્ય સૂચિ",
    "action.completeProfile": "તમારી નાણાકીય પ્રોફાઇલ પૂર્ણ કરો",
    "action.setup": "સેટઅપ",
    "action.runTax": "ટેક્સ ઓપ્ટિમાઇઝેશન ચલાવો",
    "action.open": "ખોલો",
    "action.rentVsBuy": "ભાડું vs ખરીદી વિશ્લેષણ",
    "action.analyze": "વિશ્લેષણ",
    "goal.buyHome": "ઘર ખરીદો",
    "goal.payDebt": "દેવું ચૂકવો",
    "goal.retireEarly": "વહેલા નિવૃત્તિ",
    "goal.emergency": "ઇમરજન્સી ફંડ",
    "goal.childEdu": "બાળકનું શિક્ષણ",
    "goal.wealthGrowth": "સંપત્તિ વધારો",
    "goal.eta50plus": "50+ વર્ષ",
    "goal.achieved": "પ્રાપ્ત",
    "goal.basedOn": "આધારિત",
    "goal.moSIP": "માસિક SIP",
    "goal.returns": "વળતર",
    "goal.saved": "બચત",
    "goal.target": "લક્ષ્ય",
    "goal.custom": "કસ્ટમ",
    "goal.complete": "પૂર્ણ",
    "goal.yrsToGo": "વર્ષ બાકી",
    "goal.sipNeeded": "લક્ષ્ય સુધી SIP",
    "goal.yrs": "વર્ષ",
    "goal.month": "મહિનો",
    "goal.save": "સાચવો",
    "goal.reset": "રીસેટ",
    "goal.setCustomTarget": "કસ્ટમ લક્ષ્ય સેટ કરો",
    "goal.noGoals": "કોઈ લક્ષ્ય સેટ નથી. ટ્રેક કરવા પ્રોફાઈલ અપડેટ કરો.",
    "goal.enterValid": "યોગ્ય રકમ દાખલ કરો",
    "goal.targetUpdated": "લક્ષ્ય અપડેટ થયું",
    "goal.resetDefault": "ડિફોલ્ટ પર રીસેટ",
    "calc.title": "રોકાણ કેલ્ક્યુલેટર",
    "calc.subtitle": "ચોકસાઈ સાથે તમારી સંપત્તિનું આયોજન કરો",
    "calc.sipCalc": "SIP કેલ્ક્યુલેટર",
    "calc.lumpSum": "એકસાથે",
    "calc.monthlyInvestment": "માસિક રોકાણ",
    "calc.investmentAmount": "રોકાણ રકમ",
    "calc.expectedReturn": "અપેક્ષિત વળતર (%)",
    "calc.timePeriod": "સમયગાળો (વર્ષ)",
    "calc.yrs": "વર્ષ",
    "calc.totalValue": "કુલ મૂલ્ય",
    "calc.invested": "રોકાણ",
    "calc.gains": "નફો",
    "compare.title": "ઉત્પાદન સરખામણી",
    "compare.subtitle": "મ્યુચ્યુઅલ ફંડ, FD અને વીમા યોજનાઓની સરખામણી કરો",
    "compare.fundName": "ફંડ નામ",
    "compare.1yReturn": "1 વર્ષ વળતર",
    "compare.3yReturn": "3 વર્ષ વળતર",
    "compare.5yReturn": "5 વર્ષ વળતર",
    "compare.risk": "જોખમ",
    "compare.expense": "ખર્ચ",
    "compare.disclaimer": "* ભૂતકાળના પ્રદર્શન ભવિષ્યના વળતરની ગેરંટી આપતા નથી.",
    "compare.low": "ઓછું",
    "compare.moderate": "મધ્યમ",
    "compare.high": "વધુ",
    "compare.moderateHigh": "મધ્યમ-વધુ",
    "re.title": "રિયલ એસ્ટેટ પ્લાનર",
    "re.subtitle": "EMI કેલ્ક્યુલેટર અને પ્રોપર્ટી પ્લાનિંગ ટૂલ્સ",
    "re.propertyValue": "પ્રોપર્ટી મૂલ્ય",
    "re.downPayment": "ડાઉન પેમેન્ટ (%)",
    "re.interestRate": "વ્યાજ દર (%)",
    "re.loanTenure": "લોન અવધિ (વર્ષ)",
    "re.monthlyEmi": "માસિક EMI",
    "re.loanAmount": "લોન રકમ",
    "re.totalInterest": "કુલ વ્યાજ",
    "re.totalPayment": "કુલ ચુકવણી",
    "tax.title": "ટેક્સ સેન્ટર",
    "tax.subtitle": "ભારતીય કર કાયદા હેઠળ ટેક્સ બચતનું આયોજન કરો",
    "tax.recommended": "ભલામણ",
    "tax.alternative": "વિકલ્પ",
    "tax.oldRegime": "જૂની ટેક્સ રિજીમ",
    "tax.newRegime": "નવી ટેક્સ રિજીમ",
    "tax.estimatedTax": "અંદાજિત ટેક્સ",
    "tax.withDeductions": "₹2,41,000 કપાત સાથે",
    "tax.standardDeduction": "માનક કપાત ₹75,000",
    "tax.section80c": "કલમ 80C – ₹1.5 લાખ",
    "tax.section80d": "કલમ 80D – સ્વાસ્થ્ય વીમો",
    "tax.hraExemption": "HRA છૂટ",
    "tax.elss": "ELSS મ્યુચ્યુઅલ ફંડ",
    "tax.epf": "EPF યોગદાન",
    "tax.lifeInsurance": "જીવન વીમા પ્રીમિયમ",
    "tax.selfFamily": "સ્વયં અને કુટુંબ પ્રીમિયમ",
    "tax.parentsPremium": "માતા-પિતા પ્રીમિયમ",
    "tax.rentPaid": "ભાડું ચૂકવ્યું (વાર્ષિક)",
    "tax.exemptedAmount": "છૂટ મળેલ રકમ",
    "tax.invested": "રોકાણ",
    "tax.autoDeducted": "સ્વયં કપાત",
    "tax.paid": "ચૂકવ્યું",
    "tax.claimable": "દાવા યોગ્ય",
    "tax.calculated": "ગણતરી",
    "sidebar.retirement": "નિવૃત્તિ ઘડિયાળ",
    "re.step1Title": "બજેટ",
    "re.step2Title": "ROI સિમ્યુલેટર",
    "re.step3Title": "પરવડે તેવી",
    "re.stampDuty": "સ્ટેમ્પ ડ્યુટી",
    "re.registration": "નોંધણી",
    "re.gst": "GST",
    "re.totalCost": "કુલ મિલકત ખર્ચ",
    "re.nextStep": "આગલું પગલું",
    "re.back": "પાછા",
    "re.roiDesc": "સરખામણી: એ જ રકમ મ્યુચ્યુઅલ ફંડમાં રોકી હોત તો?",
    "re.realEstateInv": "રિયલ એસ્ટેટ રોકાણ",
    "re.mutualFundInv": "મ્યુચ્યુઅલ ફંડ રોકાણ",
    "re.rentalYield": "ભાડાની ઉપજ",
    "re.appreciation": "મૂલ્ય વૃદ્ધિ",
    "re.propertyAfter20": "મિલકત મૂલ્ય (20 વર્ષ)",
    "re.totalRentalIncome": "કુલ ભાડાની આવક",
    "re.costDeductions": "ખર્ચ કપાત",
    "re.netGain": "ચોખ્ખો નફો",
    "re.expectedReturn": "અપેક્ષિત વળતર",
    "re.amountInvested": "રોકાયેલી રકમ",
    "re.valueAfter20": "20 વર્ષ પછીનું મૂલ્ય",
    "re.mfWins": "મ્યુચ્યુઅલ ફંડ જીતે છે",
    "re.reWins": "રિયલ એસ્ટેટ જીતે છે",
    "re.summary": "સારાંશ",
    "re.minIncome": "ન્યૂનતમ માસિક આવક",
    "re.affordable": "આ મિલકત તમને પરવડે છે!",
    "re.notAffordable": "આ મિલકત તમારા બજેટની બહાર હોઈ શકે.",
    "re.emiShouldBe": "EMI તમારી માસિક આવકના 40% કરતા ઓછી હોવી જોઈએ.",
    "re.nextSteps": "આગલા પગલાં માર્ગદર્શિકા",
    "re.step1Tip": "2-3 બેંકોમાંથી હોમ લોન પ્રી-અપ્રૂવલ મેળવો",
    "re.step2Tip": "મિલકતની RERA નોંધણી ચકાસો",
    "re.step3Tip": "ઇન્ટિરિયર અને સ્થળાંતર ખર્ચ (5-10%) બજેટ કરો",
    "re.step4Tip": "કબજા પહેલાં હોમ ઇન્શ્યોરન્સ વિચારો",
    "re.startOver": "ફરી શરૂ કરો",
    "re.step4Title": "લોન પાત્રતા",
    "re.empType": "રોજગાર પ્રકાર",
    "re.empSalaried": "પગારદાર",
    "re.empBusiness": "વ્યવસાય માલિક",
    "re.empFreelancer": "ફ્રીલાન્સર",
    "re.netSalary": "ચોખ્ખો માસિક પગાર (₹)",
    "re.existingEmis": "હાલની EMI (₹)",
    "re.workExp": "કામનો અનુભવ",
    "re.maxAllowableEmi": "મહત્તમ અનુમતિત EMI",
    "re.maxLoanAmount": "મહત્તમ લોન રકમ",
    "re.requiredLoan": "જરૂરી લોન રકમ",
    "re.calcBasis": "ગણતરી આધાર",
    "re.eligible": "પાત્ર — તમે લાયક છો!",
    "re.borderline": "સીમારેખા — મર્યાદાની નજીક",
    "re.notEligible": "અપાત્ર — અંતર ઘણું વધારે",
    "re.eligibleTip": "તમારી જરૂરી લોન તમારી પાત્રતામાં છે. અરજી કરવા તૈયાર!",
    "re.borderlineTip": "મર્યાદા વધારવા પતિ/પત્નીને સહ-અરજદાર તરીકે ઉમેરો.",
    "re.notEligibleTip": "તમારું ડાઉન પેમેન્ટ વધારો અથવા પહેલાં હાલનું દેવું ચૂકવો.",
    "re.sathiLoanTip": "સાથી ટિપ: બેંકો સ્થિરતા પસંદ કરે છે. છેલ્લા 3 મહિનામાં નોકરી બદલી હોય તો, અરજી પહેલાં 3 પગાર સ્લિપની રાહ જુઓ!",
    "re.addCoApplicant": "સહ-અરજદારની આવક ઉમેરો?",
    "re.coApplicantSalary": "સહ-અરજદાર માસિક પગાર (₹)",
    "re.coApplicantNote": "પતિ/પત્ની અથવા માતાપિતાની આવક ઉમેરવાથી તમારી લોન પાત્રતા નોંધપાત્ર રીતે વધી શકે છે!",
    "ret.title": "નિવૃત્તિ ઘડિયાળ",
    "ret.subtitle": "ફુગાવો-સમાયોજિત ગણતરીઓ સાથે નિવૃત્તિ આયોજન",
    "ret.monthlyExpense": "વર્તમાન માસિક ખર્ચ",
    "ret.currentAge": "વર્તમાન ઉંમર",
    "ret.retireAge": "નિવૃત્તિ ઉંમર",
    "ret.inflationRate": "ફુગાવો દર (%)",
    "ret.sipReturn": "અપેક્ષિત SIP વળતર (%)",
    "ret.yearsLeft": "નિવૃત્તિમાં બાકી વર્ષો",
    "ret.futureCost": "ભવિષ્યનો માસિક ખર્ચ",
    "ret.month": "મહિનો",
    "ret.inflationNote": "ફુગાવા માટે સમાયોજિત જીવનશૈલી ખર્ચ",
    "ret.corpusNeeded": "નિવૃત્તિ કોર્પસ જરૂરી",
    "ret.sipNeeded": "માસિક SIP જરૂરી",
    "ret.sipNote": "આરામદાયક નિવૃત્તિ માટે આજે જ આ SIP શરૂ કરો",
    "sidebar.rentVsBuy": "ભાડું vs ખરીદી",
    "sidebar.debtKiller": "દેવું-મુક્તિ માર્ગ",
    "sidebar.stockResearch": "સ્ટોક રિસર્ચ",
    "sidebar.emiMentor": "શું હું આ ખરીદી શકું?",
    "sidebar.lifeStages": "જીવન તબક્કા",
    "sidebar.creditCard": "ક્રેડિટ કાર્ડ માસ્ટર",
    "sidebar.profile": "મારી પ્રોफાઇલ",
    "sidebar.childFuture": "બાળકનું ભવિષ્ય પ્લાનર",
    "sidebar.assetComparison": "એસેટ સરખામણી",
    "sidebar.dataRights": "ડેટા અધિકારો",
    "rvb.title": "ભાડું vs ખરીદી વિશ્લેષક",
    "rvb.subtitle": "ભાડે રહો કે ખરીદો? સમય સાથે વાસ્તવિક ખર્ચની તુલના કરો.",
    "rvb.cityPreset": "શહેર પ્રીસેટ",
    "rvb.custom": "કસ્ટમ",
    "rvb.taxRegime": "કર વ્યવસ્થા",
    "rvb.oldRegime": "જૂની વ્યવસ્થા",
    "rvb.newRegime": "નવી વ્યવસ્થા",
    "rvb.horizon": "વિશ્લેષણ સમયગાળો",
    "rvb.yrs": "વર્ષ",
    "rvb.buyPath": "ખરીદી માર્ગ",
    "rvb.rentPath": "ભાડું + રોકાણ માર્ગ",
    "rvb.propertyPrice": "મિલકત કિંમત",
    "rvb.downPayment": "ડાઉન પેમેન્ટ (%)",
    "rvb.loanRate": "લોન વ્યાજ દર (%)",
    "rvb.loanTenure": "લોન મુદત (વર્ષ)",
    "rvb.interiors": "ઇન્ટીરિયર ખર્ચ",
    "rvb.monthlyMaintenance": "માસિક જાળવણી",
    "rvb.yearlyPropertyTax": "વાર્ષિક મિલકત કર",
    "rvb.appreciationRate": "મિલકત મૂલ્ય વૃદ્ધિ (%)",
    "rvb.monthlyRent": "માસિક ભાડું",
    "rvb.rentIncrease": "વાર્ષિક ભાડા વૃદ્ધિ (%)",
    "rvb.mfReturn": "મ્યુચ્યુઅલ ફંડ રિટર્ન (%)",
    "rvb.opportunityCostNote": "તક ખર્ચ: ડાઉન પેમેન્ટ રોકાણ કરાયું હોત તો",
    "rvb.investedInMF": "મ્યુચ્યુઅલ ફંડમાં રોકાણ",
    "rvb.hraNote": "યાદ રાખો: HRA મુક્તિ ફક્ત જૂની વ્યવસ્થામાં ઉપલબ્ધ",
    "rvb.monthlyEmi": "માસિક EMI",
    "rvb.recommendation": "ભલામણ",
    "rvb.buyingBetter": "ખરીદવું વધુ સંપત્તિ બનાવે છે",
    "rvb.rentingBetter": "ભાડું તમારી સંપત્તિ માટે સારું છે",
    "rvb.wealthDiff": "સંપત્તિ તફાવત",
    "rvb.breakeven": "બ્રેકઇવન વર્ષ",
    "rvb.year": "વર્ષ",
    "rvb.neverBreakeven": "ખરીદી ક્યારેય પકડી શકતી નથી",
    "rvb.wealthComparison": "ચોખ્ખી સંપત્તિ તુલના",
    "rvb.years": "વર્ષ",
    "rvb.buyWealth": "ખરીદી: ચોખ્ખી સંપત્તિ",
    "rvb.rentWealth": "ભાડું + રોકાણ: ચોખ્ખી સંપત્તિ",
    "rvb.taxRulesTitle": "2026 કર નિયમો લાગુ",
    "rvb.taxRule1": "જૂની: કલમ 24b વ્યાજ કપાત ₹2L સુધી",
    "rvb.taxRule2": "જૂની: કલમ 80C મૂળ કપાત ₹1.5L સુધી",
    "rvb.taxRule3": "નવી: સ્વ-રહેઠાણ મિલકત માટે કોઈ કપાત નથી",
    "rvb.assumptions": "વપરાયેલી ધારણાઓ",
    "rvb.assumption1": "મિલકત મૂલ્ય વૃદ્ધિ",
    "rvb.assumption2": "ભાડા વૃદ્ધિ",
    "rvb.assumption3": "MF રિટર્ન (CAGR)",
    "rvb.purposeTitle": "આ ટૂલ શું કરે છે?",
    "rvb.purposeDesc": "આ ટૂલ ઘર ખરીદવાની સાચી કિંમત (EMI, સ્ટેમ્પ ડ્યુટી, જાળવણી) ને ભાડે રહીને ફરક મ્યુચ્યુઅલ ફંડમાં રોકાણ કરવા સાથે સરખાવે છે.",
    "rvb.hiddenCosts": "છુપાયેલા ખર્ચ (સ્ટેમ્પ ડ્યુટી, જાળવણી, કર)",
    "rvb.stampDuty": "સ્ટેમ્પ ડ્યુટી",
    "rvb.registration": "નોંધણી",
    "rvb.wealthGapChart": "સંપત્તિ અંતર: ખરીદી vs ભાડું + રોકાણ",
    "rvb.sathiVerdict": "સાથીનો ચુકાદો",
    "rvb.sathiRentAdvice": "સાથી કહે છે: ભાડું તમને દર મહિને {AMT} બચાવી રહ્યું છે. આ SIPમાં રોકાણ કરો!",
    "rvb.sathiBuyAdvice": "સાથી કહે છે: આ કિંમતે ખરીદવું ઉત્તમ લાંબા ગાળાની સંપત્તિ છે!",
    "rvb.totalCostBuy": "માલિકીની કુલ કિંમત (ખરીદી)",
    "rvb.rentInvestPath": "ભાડું + રોકાણ માર્ગ",
    "rvb.downPaymentStampInteriors": "ડાઉન પેમેન્ટ + સ્ટેમ્પ ડ્યુટી + ઇન્ટીરિયર",
    "rvb.totalEmiPaid": "કુલ EMI ચૂકવ્યું",
    "rvb.interestLabel": "વ્યાજ",
    "rvb.taxSavings": "કર બચત (કલમ 24b + 80C)",
    "rvb.totalOutOfPocket": "કુલ ખિસ્સામાંથી ખર્ચ",
    "rvb.propertyValueAtYear": "વર્ષમાં મિલકત મૂલ્ય",
    "rvb.totalRentPaid": "કુલ ભાડું ચૂકવ્યું",
    "rvb.downPaymentInvested": "ડાઉન પેમેન્ટ રોકાણ",
    "rvb.indexFund": "ઇન્ડેક્સ ફંડ",
    "rvb.monthlySavingsInvested": "માસિક બચત રોકાણ (EMI ફરક)",
    "rvb.autoCompounded": "ઓટો-કંપાઉન્ડેડ",
    "rvb.investmentCorpus": "વર્ષમાં રોકાણ કોષ",
    "rvb.netWealth": "ચોખ્ખી સંપત્તિ (કોષ − ભાડું)",
    "rvb.shareWhatsApp": "WhatsApp પર શેર કરો",
    "rvb.saveResult": "પરિણામ સાચવો",
    "mac.investmentAmount": "રોકાણ રકમ (₹)",
    "mac.frequency": "રોકાણ આવર્તન",
    "mac.freqMonthly": "માસિક (SIP)",
    "mac.freqQuarterly": "ત્રિમાસિક",
    "mac.freqHalfYearly": "અર્ધ-વાર્ષિક",
    "mac.freqYearly": "વાર્ષિક",
    "mac.chartTitle": "{amount} {frequency} રોકાણની {years} વર્ષોમાં વૃદ્ધિ",
    "mac.yearlyTip": "વર્ષમાં એક વખત મોટી રકમ રોકાણ vs દર મહિને નાની રકમ — 'રૂપી કોસ્ટ એવરેજિંગ' ને કારણે પરિણામ અલગ હોઈ શકે!",
  },
  kn: {
    "sidebar.dashboard": "ಡ್ಯಾಶ್‌ಬೋರ್ಡ್",
    "sidebar.calculators": "ಹೂಡಿಕೆ ಕ್ಯಾಲ್ಕುಲೇಟರ್‌ಗಳು",
    "sidebar.compare": "ಉತ್ಪನ್ನ ಹೋಲಿಕೆ",
    "sidebar.realEstate": "ರಿಯಲ್ ಎಸ್ಟೇಟ್ ಪ್ಲ್ಯಾನರ್",
    "sidebar.tax": "ತೆರಿಗೆ ಕೇಂದ್ರ",
    "sidebar.tagline": "ಸ್ಮಾರ್ಟ್ ಹಣಕಾಸು ಟೂಲ್ಸ್",
    "sidebar.disclaimer": "ಶೈಕ್ಷಣಿಕ ಉದ್ದೇಶಗಳಿಗಾಗಿ ಮಾತ್ರ. ಹಣಕಾಸು ಸಲಹೆ ಅಲ್ಲ.",
    "sidebar.catDashboard": "ಡ್ಯಾಶ್‌ಬೋರ್ಡ್",
    "sidebar.catHomeLife": "🏠 ಮನೆ ಮತ್ತು ಜೀವನ",
    "sidebar.catFuture": "🔮 ಭವಿಷ್ಯ",
    "sidebar.catSafety": "🛡️ ಸುರಕ್ಷತೆ",
    "sidebar.catInvest": "📈 ಹೂಡಿಕೆ",
    "sidebar.catBanking": "🏦 ಬ್ಯಾಂಕಿಂಗ್",
    "sidebar.catAccount": "ಖಾತೆ",
    "sidebar.desc.dashboard": "ಒಂದೇ ನೋಟದಲ್ಲಿ ನಿಮ್ಮ ಹಣಕಾಸಿನ ಅವಲೋಕನ",
    "sidebar.desc.calculators": "SIP, ಏಕಮುಷ್ಟಿ ಮತ್ತು ಚಕ್ರವೃದ್ಧಿ ಬಡ್ಡಿ ಪರಿಕರಗಳು",
    "sidebar.desc.compare": "ಹಣಕಾಸಿನ ಉತ್ಪನ್ನಗಳನ್ನು ಪಕ್ಕಪಕ್ಕ ಹೋಲಿಸಿ",
    "sidebar.desc.realEstate": "ಆಸ್ತಿ ಖರೀದಿ ಮತ್ತು ROI ಯೋಜನೆ",
    "sidebar.desc.tax": "ಹಳೆಯ ಮತ್ತು ಹೊಸ ತೆರಿಗೆ ವ್ಯವಸ್ಥೆಯಲ್ಲಿ ಉಳಿತಾಯ ಅತ್ಯುತ್ತಮಗೊಳಿಸಿ",
    "sidebar.desc.retirement": "ನಿವೃತ್ತಿ ಕಾರ್ಪಸ್ ಮತ್ತು SIP ಲೆಕ್ಕ ಹಾಕಿ",
    "sidebar.desc.rentVsBuy": "ಬಾಡಿಗೆ ಅಥವಾ ಖರೀದಿ — ಯಾವುದು ಉತ್ತಮ ತಿಳಿಯಿರಿ",
    "sidebar.desc.debtKiller": "ವೇಗವಾಗಿ ಸಾಲ ಮುಕ್ತರಾಗುವ ಯೋಜನೆ ಮಾಡಿ",
    "sidebar.desc.stockResearch": "ಲೈವ್ ಮಾರುಕಟ್ಟೆ ಡೇಟಾ ಮತ್ತು ಷೇರು ವಿಶ್ಲೇಷಣೆ",
    "sidebar.desc.emiMentor": "ನೀವು ನಿಜವಾಗಿಯೂ ಖರೀದಿಸಬಹುದೇ ಎಂದು ಪರಿಶೀಲಿಸಿ",
    "sidebar.desc.childFuture": "ಮಕ್ಕಳ ಶಿಕ್ಷಣ ಮತ್ತು ಭವಿಷ್ಯದ ಖರ್ಚುಗಳ ಯೋಜನೆ",
    "sidebar.desc.creditCard": "ಕ್ರೆಡಿಟ್ ಕಾರ್ಡ್ ಸಾಲದ ನಿಜ ವೆಚ್ಚ ಅರ್ಥಮಾಡಿಕೊಳ್ಳಿ",
    "sidebar.desc.assetComparison": "ವಿವಿಧ ಆಸ್ತಿ ವರ್ಗಗಳಲ್ಲಿ ರಿಟರ್ನ್ ಹೋಲಿಕೆ",
    "sidebar.desc.profile": "ನಿಮ್ಮ ಹಣಕಾಸಿನ ಪ್ರೊಫೈಲ್ ನಿರ್ವಹಿಸಿ",
    "sidebar.desc.dataRights": "ನಿಮ್ಮ ಡೇಟಾ ಗೌಪ್ಯತೆ ಮತ್ತು ಡೌನ್‌ಲೋಡ್ ಆಯ್ಕೆಗಳು",
    "dash.title": "ಡ್ಯಾಶ್‌ಬೋರ್ಡ್",
    "dash.subtitle": "ನಿಮ್ಮ ಹಣಕಾಸು ಸ್ಥಿತಿ ಒಂದು ನೋಟದಲ್ಲಿ",
    "dash.portfolioValue": "ಪೋರ್ಟ್‌ಫೋಲಿಯೊ ಮೌಲ್ಯ",
    "dash.monthlySip": "ಮಾಸಿಕ SIP",
    "dash.taxSaved": "ತೆರಿಗೆ ಉಳಿತಾಯ (FY25)",
    "dash.returns1y": "ಲಾಭ (1 ವರ್ಷ)",
    "dash.portfolioGrowth": "ಪೋರ್ಟ್‌ಫೋಲಿಯೊ ಬೆಳವಣಿಗೆ",
    "dash.last9months": "ಕಳೆದ 9 ತಿಂಗಳು",
    "dash.quickActions": "ತ್ವರಿತ ಕ್ರಿಯೆಗಳು",
    "dash.sipCalc": "SIP ಕ್ಯಾಲ್ಕುಲೇಟರ್",
    "dash.compareMF": "ಮ್ಯೂಚುಯಲ್ ಫಂಡ್ ಹೋಲಿಕೆ",
    "dash.emiEstimator": "EMI ಅಂದಾಜು",
    "dash.taxOptimizer": "ತೆರಿಗೆ ಯೋಜಕ",
    "dash.marketSnapshot": "ಮಾರುಕಟ್ಟೆ ಸ್ನ್ಯಾಪ್‌ಶಾಟ್",
    "dash.active": "ಸಕ್ರಿಯ",
    "dash.vikasScore": "ವಿಕಾಸ ಪಯಣ",
    "dash.totalAssets": "ಒಟ್ಟು ಆಸ್ತಿಗಳು",
    "dash.totalLiabilities": "ಒಟ್ಟು ಹೊಣೆಗಾರಿಕೆಗಳು",
    "dash.netInvestment": "ನಿವ್ವಳ ಹೂಡಿಕೆ",
    "dash.dtiRatio": "DTI ಅನುಪಾತ",
    "dash.savingsInvestments": "ಉಳಿತಾಯ + ಹೂಡಿಕೆಗಳು",
    "dash.annualEmi": "ವಾರ್ಷಿಕ EMI ಬಾಧ್ಯತೆಗಳು",
    "dash.monthlyWealth": "ಮಾಸಿಕ ಸಂಪತ್ತು ನಿರ್ಮಾಣ",
    "dash.financialPulse": "ಹಣಕಾಸಿನ ಪಲ್ಸ್",
    "dash.assetsVsLiabilities": "ಆಸ್ತಿಗಳು vs ಹೊಣೆಗಾರಿಕೆಗಳು — 12 ತಿಂಗಳ ಅಂದಾಜು",
    "dash.dailySathiTip": "ಇಂದಿನ ಸಾಥಿ ಸಲಹೆ",
    "dash.goalTracker": "ಗುರಿ ಟ್ರ್ಯಾಕರ್",
    "dash.miniTools": "ಮಿನಿ ಟೂಲ್ಸ್ — ತ್ವರಿತ ಹಣದ ಹ್ಯಾಕ್ಸ್",
    "dash.welcome": "ಸ್ವಾಗತ",
    "dash.welcomeSub": "ಇಂದಿನ ನಿಮ್ಮ ಹಣಕಾಸಿನ ಅವಲೋಕನ ಇಲ್ಲಿದೆ.",
    "dash.editProfile": "ಪ್ರೊಫೈಲ್ ಸಂಪಾದಿಸಿ",
    "dash.user": "ಬಳಕೆದಾರ",
    "ai.insightTitle": "AI ಒಳನೋಟ",
    "ai.sipAdvice": "ನಿಮ್ಮ {income} ಆದಾಯದ ಆಧಾರದಲ್ಲಿ, ನಿಮ್ಮ {goal} ವೇಗಗೊಳಿಸಲು {sip}/ತಿಂಗಳು SIP ಪ್ರಾರಂಭಿಸಲು ಶಿಫಾರಸು ಮಾಡುತ್ತೇವೆ.",
    "ai.dtiWarning": "ನಿಮ್ಮ EMI ಹೊರೆ {dti}% — ಬಡ್ಡಿ ಕಡಿಮೆ ಮಾಡಲು ಬ್ಯಾಲೆನ್ಸ್ ಟ್ರಾನ್ಸ್‌ಫರ್ ಪರಿಗಣಿಸಿ.",
    "ai.financialGoal": "ಆರ್ಥಿಕ ಗುರಿ",
    "action.title": "ಕ್ರಿಯಾ ಪಟ್ಟಿ",
    "action.completeProfile": "ನಿಮ್ಮ ಆರ್ಥಿಕ ಪ್ರೊಫೈಲ್ ಪೂರ್ಣಗೊಳಿಸಿ",
    "action.setup": "ಸೆಟಪ್",
    "action.runTax": "ತೆರಿಗೆ ಆಪ್ಟಿಮೈಸೇಶನ್ ಚಲಾಯಿಸಿ",
    "action.open": "ತೆರೆಯಿರಿ",
    "action.rentVsBuy": "ಬಾಡಿಗೆ vs ಖರೀದಿ ವಿಶ್ಲೇಷಣೆ",
    "action.analyze": "ವಿಶ್ಲೇಷಣೆ",
    "goal.buyHome": "ಮನೆ ಖರೀದಿಸಿ",
    "goal.payDebt": "ಸಾಲ ತೀರಿಸಿ",
    "goal.retireEarly": "ಬೇಗ ನಿವೃತ್ತಿ",
    "goal.emergency": "ತುರ್ತು ನಿಧಿ",
    "goal.childEdu": "ಮಕ್ಕಳ ಶಿಕ್ಷಣ",
    "goal.wealthGrowth": "ಸಂಪತ್ತು ಬೆಳೆಸಿ",
    "goal.eta50plus": "50+ ವರ್ಷ",
    "goal.achieved": "ಸಾಧಿಸಲಾಗಿದೆ",
    "goal.basedOn": "ಆಧಾರಿತ",
    "goal.moSIP": "ಮಾಸಿಕ SIP",
    "goal.returns": "ಆದಾಯ",
    "goal.saved": "ಉಳಿತಾಯ",
    "goal.target": "ಗುರಿ",
    "goal.custom": "ಕಸ್ಟಮ್",
    "goal.complete": "ಪೂರ್ಣ",
    "goal.yrsToGo": "ವರ್ಷ ಬಾಕಿ",
    "goal.sipNeeded": "ಗುರಿ ತಲುಪಲು SIP",
    "goal.yrs": "ವರ್ಷ",
    "goal.month": "ತಿಂಗಳು",
    "goal.save": "ಉಳಿಸಿ",
    "goal.reset": "ರೀಸೆಟ್",
    "goal.setCustomTarget": "ಕಸ್ಟಮ್ ಗುರಿ ಹೊಂದಿಸಿ",
    "goal.noGoals": "ಯಾವುದೇ ಗುರಿ ಹೊಂದಿಸಿಲ್ಲ. ಗುರಿಗಳನ್ನು ಟ್ರ್ಯಾಕ್ ಮಾಡಲು ಪ್ರೊಫೈಲ್ ನವೀಕರಿಸಿ.",
    "goal.enterValid": "ಸರಿಯಾದ ಮೊತ್ತ ನಮೂದಿಸಿ",
    "goal.targetUpdated": "ಗುರಿ ನವೀಕರಿಸಲಾಗಿದೆ",
    "goal.resetDefault": "ಡೀಫಾಲ್ಟ್‌ಗೆ ರೀಸೆಟ್",
    "calc.title": "ಹೂಡಿಕೆ ಕ್ಯಾಲ್ಕುಲೇಟರ್‌ಗಳು",
    "calc.subtitle": "ನಿಖರತೆಯಿಂದ ನಿಮ್ಮ ಸಂಪತ್ತನ್ನು ಯೋಜಿಸಿ",
    "calc.sipCalc": "SIP ಕ್ಯಾಲ್ಕುಲೇಟರ್",
    "calc.lumpSum": "ಒಮ್ಮೆಲೇ",
    "calc.monthlyInvestment": "ಮಾಸಿಕ ಹೂಡಿಕೆ",
    "calc.investmentAmount": "ಹೂಡಿಕೆ ಮೊತ್ತ",
    "calc.expectedReturn": "ನಿರೀಕ್ಷಿತ ಲಾಭ (%)",
    "calc.timePeriod": "ಸಮಯಾವಧಿ (ವರ್ಷಗಳು)",
    "calc.yrs": "ವರ್ಷಗಳು",
    "calc.totalValue": "ಒಟ್ಟು ಮೌಲ್ಯ",
    "calc.invested": "ಹೂಡಿಕೆ",
    "calc.gains": "ಲಾಭ",
    "we.pageTitle": "VittaSathi ವೆಲ್ತ್ ಎಂಜಿನ್",
    "we.pageSubtitle": "ಸ್ಟೆಪ್-ಅಪ್ SIP ಮತ್ತು ಹಣದುಬ್ಬರ-ಸರಿಹೊಂದಿಸಿದ ಅಂದಾಜುಗಳೊಂದಿಗೆ ನಿಮ್ಮ ಸಂಪತ್ತನ್ನು ಬೆಳೆಸಿ",
    "we.wealthEngine": "ವೆಲ್ತ್ ಎಂಜಿನ್",
    "we.monthlyInvestment": "ಮಾಸಿಕ ಹೂಡಿಕೆ",
    "we.expectedReturn": "ನಿರೀಕ್ಷಿತ ಆದಾಯ (%)",
    "we.tenure": "ಹೂಡಿಕೆ ಅವಧಿ (ವರ್ಷಗಳು)",
    "we.stepUp": "ವಾರ್ಷಿಕ ಸ್ಟೆಪ್-ಅಪ್",
    "we.perYear": "ಪ್ರತಿ ವರ್ಷ",
    "we.stepUpHint": "ಸಂಬಳ ಹೆಚ್ಚಾದಂತೆ ಪ್ರತಿ ವರ್ಷ SIP ಹೆಚ್ಚಿಸಿ",
    "we.inflationToggle": "ಇಂದಿನ ಮೌಲ್ಯದಲ್ಲಿ ತೋರಿಸಿ",
    "we.inflationHint": "ನಿಜವಾದ ಕೊಳ್ಳುವ ಶಕ್ತಿಯನ್ನು ನೋಡಲು 6% ಹಣದುಬ್ಬರದಿಂದ ರಿಯಾಯಿತಿ",
    "we.totalValue": "ಒಟ್ಟು ಕಾರ್ಪಸ್",
    "we.totalValueInflation": "ಕಾರ್ಪಸ್ (ಇಂದಿನ ಮೌಲ್ಯ)",
    "we.wealthGained": "ಗಳಿಸಿದ ಸಂಪತ್ತು",
    "we.wealthRatio": "ಸಂಪತ್ತು ಅನುಪಾತ",
    "we.interestShare": "ನಿಮ್ಮ ಕಾರ್ಪಸ್‌ನ ಭಾಗ ಶುದ್ಧ ಬಡ್ಡಿ — ಚಕ್ರವೃದ್ಧಿಯ ಶಕ್ತಿ!",
    "we.sathiSays": "ಸಾಥಿ ಹೇಳುತ್ತಾರೆ:",
    "we.sathiMessage": "ನಿಮ್ಮ ಹಣ ನಿಮಗಿಂತ {X} ಪಟ್ಟು ಹೆಚ್ಚು ಕೆಲಸ ಮಾಡುತ್ತಿದೆ! SIP ಮುಂದುವರಿಸಿ.",
    "we.growthChart": "ಕಾಲಾನಂತರ ಸಂಪತ್ತು ಬೆಳವಣಿಗೆ",
    "we.amountInvested": "ಹೂಡಿಕೆ ಮಾಡಿದ ಮೊತ್ತ",
    "we.compoundingPower": "ಚಕ್ರವೃದ್ಧಿಯ ಶಕ್ತಿ!",
    "we.gainsExceed": "ನಿಮ್ಮ ಗಳಿಸಿದ ಸಂಪತ್ತು ನಿಮ್ಮ ಒಟ್ಟು ಹೂಡಿಕೆಯನ್ನು ಮೀರಿದೆ.",
    "we.stepUpTip": "ಸ್ಟೆಪ್-ಅಪ್ ಸೂಪರ್‌ಸ್ಟಾರ್!",
    "we.stepUpTipDesc": "10%+ ವಾರ್ಷಿಕ ಸ್ಟೆಪ್-ಅಪ್ ಅದೇ ಅವಧಿಯಲ್ಲಿ ಫ್ಲಾಟ್ SIP ಗೆ ಹೋಲಿಸಿದರೆ ಕಾರ್ಪಸ್ ಅನ್ನು ದ್ವಿಗುಣಗೊಳಿಸಬಹುದು.",
    "we.caution": "ಎಚ್ಚರಿಕೆ:",
    "we.cautionDesc": "{years} ವರ್ಷಗಳಲ್ಲಿ {rate}% ಆದಾಯ ನಿರೀಕ್ಷಿಸುವುದು ಅಪಾಯಕಾರಿ.",
    "we.saveResult": "ಫಲಿತಾಂಶ ಉಳಿಸಿ",
    "we.delayToggle": "ನಾನು ಕಾದರೆ ಏನಾಗುತ್ತೆ? (ವಿಳಂಬದ ಬೆಲೆ)",
    "we.delayHint": "SIP ಪ್ರಾರಂಭಿಸಲು ವಿಳಂಬ ಮಾಡಿದರೆ ಎಷ್ಟು ಸಂಪತ್ತು ಕಳೆದುಕೊಳ್ಳುತ್ತೀರಿ ನೋಡಿ",
    "we.delaySliderLabel": "ವಿಳಂಬದ ವರ್ಷಗಳು",
    "we.penaltyTitle": "ಕಾಯುವ ದಂಡ",
    "we.sathiInsight": "ಸಾಥಿ ಇನ್‌ಸೈಟ್:",
    "we.delayInsight": "{X} ವರ್ಷ ಕಾಯುವುದರಿಂದ ನೀವು {PENALTY} 'ಮುಂದೂಡಿಕೆ ತೆರಿಗೆ' ಕಟ್ಟುತ್ತಿದ್ದೀರಿ. ಈ ಹಣ ನಿಮ್ಮ ಮಗುವಿನ {YEARS} ವರ್ಷದ ಶಿಕ್ಷಣ ಅಥವಾ ಐಷಾರಾಮಿ ನಿವೃತ್ತಿ ಮನೆಗೆ ಆಗಬಹುದಿತ್ತು.",
    "we.lostWealth": "ಕಳೆದ ಸಂಪತ್ತು (ವಿಳಂಬ ದಂಡ)",
    "we.exploreMore": "ಇನ್ನಷ್ಟು ಟೂಲ್ಸ್ ನೋಡಿ",
    "we.assetFD": "ಸುರಕ್ಷಿತ FD",
    "we.assetGold": "ಸಾಂಪ್ರದಾಯಿಕ ಚಿನ್ನ",
    "we.assetRealEstate": "ರಿಯಲ್ ಎಸ್ಟೇಟ್",
    "we.assetIndex": "ಇಂಡೆಕ್ಸ್ ಫಂಡ್ಸ್",
    "we.assetStock": "ಷೇರು ಹೂಡಿಕೆ",
    "we.riskFD": "ಬಂಡವಾಳ ಸುರಕ್ಷಿತ, ಆದರೆ ಹಣದುಬ್ಬರವನ್ನು ಕಷ್ಟದಿಂದ ಮೀರುತ್ತದೆ.",
    "we.riskGold": "ಹಣದುಬ್ಬರದ ವಿರುದ್ಧ ರಕ್ಷಣೆ. ನಿಯಮಿತ ಆದಾಯವಿಲ್ಲ.",
    "we.riskRealEstate": "ಬಾಡಿಗೆ ಆದಾಯದ ಸಾಮರ್ಥ್ಯವಿರುವ ಮೂರ್ತ ಆಸ್ತಿ. 5+ ವರ್ಷಗಳ ದೃಷ್ಟಿಕೋನ ಬೇಕು.",
    "we.riskIndex": "ಐತಿಹಾಸಿಕವಾಗಿ ದೀರ್ಘಾವಧಿಯಲ್ಲಿ ಹಣದುಬ್ಬರವನ್ನು ಮೀರುತ್ತದೆ.",
    "we.riskStock": "ಅತ್ಯಧಿಕ ಸಾಮರ್ಥ್ಯ, ಅತ್ಯಧಿಕ ಅಪಾಯ. ಕುಸಿತದಲ್ಲಿ 40%+ ಕಳೆದುಕೊಳ್ಳಬಹುದು.",
    "we.opportunityCost": "ಅವಕಾಶ ವೆಚ್ಚ (ಸಂಪತ್ತು ಅಂತರ)",
    "we.wealthGapDesc": "{years} ವರ್ಷಗಳಲ್ಲಿ ಷೇರು ಹೂಡಿಕೆ ಮತ್ತು ಸುರಕ್ಷಿತ FD ನಡುವಿನ ವ್ಯತ್ಯಾಸ",
    "we.sathiStockVsFD": "FD ಬದಲು ಷೇರು ಹೂಡಿಕೆ ಆಯ್ಕೆ ಮಾಡಿ, {years} ವರ್ಷಗಳ ನಂತರ ನಿಮ್ಮ ಸಂಪತ್ತು {amount} ಹೆಚ್ಚು ಆಗಬಹುದು!",
    "we.growthComparison": "ಬೆಳವಣಿಗೆ ಹೋಲಿಕೆ",
    "we.sathiSecret": "ಸಾಥಿ ರಹಸ್ಯ",
    "we.sathiSecretText": "FD 6.5% ನೀಡುತ್ತದೆ ಆದರೆ ಭಾರತದಲ್ಲಿ ಹಣದುಬ್ಬರ ~6%. FD ಯಲ್ಲಿ ನಿಮ್ಮ ನಿಜವಾದ ಬೆಳವಣಿಗೆ ವರ್ಷಕ್ಕೆ ಕೇವಲ 0.5%.",

    // Wealth Simulator (Kannada)
    "ws.pageTitle": "VittaSathi ವೆಲ್ತ್ ಸಿಮ್ಯುಲೇಟರ್",
    "ws.pageSubtitle": "ಪ್ರೊಫೆಶನಲ್-ಗ್ರೇಡ್ SIP, ಏಕಮುಷ್ಟಿ ಮತ್ತು ಹೈಬ್ರಿಡ್ ಸಂಪತ್ತು ಅಂದಾಜುಗಳು",
    "ws.modeSIP": "ಮಾಸಿಕ SIP",
    "ws.modeLumpsum": "ಏಕಮುಷ್ಟಿ",
    "ws.modeHybrid": "ಹೈಬ್ರಿಡ್ (ಎರಡೂ)",
    "ws.monthlySIP": "ಮಾಸಿಕ SIP ಮೊತ್ತ",
    "ws.lumpsumSeed": "ಏಕಮುಷ್ಟಿ ಮೊತ್ತ",
    "ws.expectedReturn": "ನಿರೀಕ್ಷಿತ ರಿಟರ್ನ್ (%)",
    "ws.timeHorizon": "ಸಮಯ ಅವಧಿ (ವರ್ಷಗಳು)",
    "ws.stepUpSIP": "ಸ್ಟೆಪ್-ಅಪ್ SIP (% ವಾರ್ಷಿಕ)",
    "ws.stepUpHint": "ವೇತನ ಬೆಳವಣಿಗೆಗೆ ಅನುಗುಣವಾಗಿ ಪ್ರತಿ ವರ್ಷ SIP ಹೆಚ್ಚಿಸಿ",
    "ws.inflationToggle": "ಹಣದುಬ್ಬರ-ಸರಿಹೊಂದಿಸಿದ ಮೌಲ್ಯ ತೋರಿಸಿ (6%)",
    "ws.inflationHint": "ಇಂದಿನ ನಿಯಮಗಳಲ್ಲಿ ಕೊಳ್ಳುವ ಶಕ್ತಿ ನೋಡಿ",
    "ws.noStepUpWarning": "ಸಾಥಿ, ನಿಮ್ಮ ಹಣ ಬೆಳೆಯುತ್ತಿದೆ, ಆದರೆ ಬೆಲೆಗಳೂ ಬೆಳೆಯುತ್ತವೆ. ಮುಂದಿರಲು ಪ್ರತಿ ವರ್ಷ SIP ಹೆಚ್ಚಿಸಿ!",
    "ws.finalCorpus": "ಅಂತಿಮ ಕಾರ್ಪಸ್",
    "ws.totalInvested": "ಒಟ್ಟು ಹೂಡಿಕೆ",
    "ws.wealthGained": "ಗಳಿಸಿದ ಸಂಪತ್ತು",
    "ws.croreClub": "₹1 ಕೋಟಿ ಕ್ಲಬ್‌ಗೆ ಸ್ವಾಗತ! 🎉",
    "ws.wealthTrajectory": "ಸಂಪತ್ತು ಪಥ",
    "ws.wealthBreakdown": "ಸಂಪತ್ತು ವಿವರ",
    "ws.futureLifestyle": "ಭವಿಷ್ಯದ ಜೀವನಶೈಲಿ ಟಿಪ್ಪಣಿ",
    "ws.futureLifestyleDesc": "ಈ SIP ಅನ್ನು ಸ್ಟಾಕ್ ಲ್ಯಾಬ್‌ನಲ್ಲಿ 5 ವರ್ಷ ಹೂಡಿಕೆ ಮಾಡಿದರೆ, ನಿಮ್ಮ ಬಳಿ {X} ಇರಬಹುದು!",
    "ws.sendToGoalPlanner": "ಗೋಲ್ ಪ್ಲಾನರ್‌ಗೆ ಕಳುಹಿಸಿ",
    "ws.disclaimer": "ಈ ಟೂಲ್ ಸಮಯದ ಜೊತೆ ಚಕ್ರವಡ್ಡಿಯ ಗಣಿತದ ಶಕ್ತಿಯನ್ನು ತೋರಿಸುತ್ತದೆ. ಇದು ಶೈಕ್ಷಣಿಕ ಅಂದಾಜು, ಮಾರುಕಟ್ಟೆ ರಿಟರ್ನ್ಸ್ ಖಾತ್ರಿ ಅಲ್ಲ.",

    "compare.title": "ಉತ್ಪನ್ನ ಹೋಲಿಕೆ",
    "compare.subtitle": "ಮ್ಯೂಚುಯಲ್ ಫಂಡ್‌ಗಳು, FD ಮತ್ತು ವಿಮಾ ಯೋಜನೆಗಳನ್ನು ಹೋಲಿಸಿ",
    "compare.fundName": "ಫಂಡ್ ಹೆಸರು",
    "compare.1yReturn": "1 ವರ್ಷ ಲಾಭ",
    "compare.3yReturn": "3 ವರ್ಷ ಲಾಭ",
    "compare.5yReturn": "5 ವರ್ಷ ಲಾಭ",
    "compare.risk": "ಅಪಾಯ",
    "compare.expense": "ವೆಚ್ಚ",
    "compare.disclaimer": "* ಹಿಂದಿನ ಕಾರ್ಯಕ್ಷಮತೆ ಭವಿಷ್ಯದ ಲಾಭಕ್ಕೆ ಖಾತರಿ ನೀಡುವುದಿಲ್ಲ.",
    "compare.low": "ಕಡಿಮೆ",
    "compare.moderate": "ಮಧ್ಯಮ",
    "compare.high": "ಹೆಚ್ಚು",
    "compare.moderateHigh": "ಮಧ್ಯಮ-ಹೆಚ್ಚು",
    "re.title": "ರಿಯಲ್ ಎಸ್ಟೇಟ್ ಪ್ಲ್ಯಾನರ್",
    "re.subtitle": "EMI ಕ್ಯಾಲ್ಕುಲೇಟರ್ ಮತ್ತು ಆಸ್ತಿ ಯೋಜನೆ ಟೂಲ್ಸ್",
    "re.propertyValue": "ಆಸ್ತಿ ಮೌಲ್ಯ",
    "re.downPayment": "ಡೌನ್ ಪೇಮೆಂಟ್ (%)",
    "re.interestRate": "ಬಡ್ಡಿ ದರ (%)",
    "re.loanTenure": "ಸಾಲ ಅವಧಿ (ವರ್ಷಗಳು)",
    "re.monthlyEmi": "ಮಾಸಿಕ EMI",
    "re.loanAmount": "ಸಾಲ ಮೊತ್ತ",
    "re.totalInterest": "ಒಟ್ಟು ಬಡ್ಡಿ",
    "re.totalPayment": "ಒಟ್ಟು ಪಾವತಿ",
    "tax.title": "ತೆರಿಗೆ ಕೇಂದ್ರ",
    "tax.subtitle": "ಭಾರತೀಯ ತೆರಿಗೆ ಕಾನೂನಿನಡಿ ತೆರಿಗೆ ಉಳಿತಾಯ ಯೋಜಿಸಿ",
    "tax.recommended": "ಶಿಫಾರಸು",
    "tax.alternative": "ಪರ್ಯಾಯ",
    "tax.oldRegime": "ಹಳೆಯ ತೆರಿಗೆ ರೆಜೀಮ್",
    "tax.newRegime": "ಹೊಸ ತೆರಿಗೆ ರೆಜೀಮ್",
    "tax.estimatedTax": "ಅಂದಾಜು ತೆರಿಗೆ",
    "tax.withDeductions": "₹2,41,000 ಕಡಿತಗಳೊಂದಿಗೆ",
    "tax.standardDeduction": "ಪ್ರಮಾಣಿತ ಕಡಿತ ₹75,000",
    "tax.section80c": "ಸೆಕ್ಷನ್ 80C – ₹1.5 ಲಕ್ಷ",
    "tax.section80d": "ಸೆಕ್ಷನ್ 80D – ಆರೋಗ್ಯ ವಿಮೆ",
    "tax.hraExemption": "HRA ವಿನಾಯಿತಿ",
    "tax.elss": "ELSS ಮ್ಯೂಚುಯಲ್ ಫಂಡ್‌ಗಳು",
    "tax.epf": "EPF ಕೊಡುಗೆ",
    "tax.lifeInsurance": "ಜೀವ ವಿಮೆ ಪ್ರೀಮಿಯಂ",
    "tax.selfFamily": "ಸ್ವಂತ ಮತ್ತು ಕುಟುಂಬ ಪ್ರೀಮಿಯಂ",
    "tax.parentsPremium": "ಪೋಷಕರ ಪ್ರೀಮಿಯಂ",
    "tax.rentPaid": "ಪಾವತಿಸಿದ ಬಾಡಿಗೆ (ವಾರ್ಷಿಕ)",
    "tax.exemptedAmount": "ವಿನಾಯಿತಿ ಪಡೆದ ಮೊತ್ತ",
    "tax.invested": "ಹೂಡಿಕೆ",
    "tax.autoDeducted": "ಸ್ವಯಂ ಕಡಿತ",
    "tax.paid": "ಪಾವತಿಸಲಾಗಿದೆ",
    "tax.claimable": "ಕ್ಲೇಮ್ ಮಾಡಬಹುದು",
    "tax.calculated": "ಲೆಕ್ಕ ಹಾಕಲಾಗಿದೆ",
    "sidebar.retirement": "ನಿವೃತ್ತಿ ಗಡಿಯಾರ",
    "re.step1Title": "ಬಜೆಟ್",
    "re.step2Title": "ROI ಸಿಮ್ಯುಲೇಟರ್",
    "re.step3Title": "ಕೈಗೆಟುಕುವಿಕೆ",
    "re.stampDuty": "ಮುದ್ರಾಂಕ ಶುಲ್ಕ",
    "re.registration": "ನೋಂದಣಿ",
    "re.gst": "GST",
    "re.totalCost": "ಒಟ್ಟು ಆಸ್ತಿ ವೆಚ್ಚ",
    "re.nextStep": "ಮುಂದಿನ ಹಂತ",
    "re.back": "ಹಿಂದೆ",
    "re.roiDesc": "ಹೋಲಿಕೆ: ಅದೇ ಮೊತ್ತವನ್ನು ಮ್ಯೂಚುವಲ್ ಫಂಡ್‌ನಲ್ಲಿ ಹೂಡಿಕೆ ಮಾಡಿದ್ದರೆ?",
    "re.realEstateInv": "ರಿಯಲ್ ಎಸ್ಟೇಟ್ ಹೂಡಿಕೆ",
    "re.mutualFundInv": "ಮ್ಯೂಚುವಲ್ ಫಂಡ್ ಹೂಡಿಕೆ",
    "re.rentalYield": "ಬಾಡಿಗೆ ಆದಾಯ",
    "re.appreciation": "ಮೌಲ್ಯ ಹೆಚ್ಚಳ",
    "re.propertyAfter20": "ಆಸ್ತಿ ಮೌಲ್ಯ (20 ವರ್ಷ)",
    "re.totalRentalIncome": "ಒಟ್ಟು ಬಾಡಿಗೆ ಆದಾಯ",
    "re.costDeductions": "ವೆಚ್ಚ ಕಡಿತಗಳು",
    "re.netGain": "ನಿವ್ವಳ ಲಾಭ",
    "re.expectedReturn": "ನಿರೀಕ್ಷಿತ ಆದಾಯ",
    "re.amountInvested": "ಹೂಡಿಕೆ ಮೊತ್ತ",
    "re.valueAfter20": "20 ವರ್ಷಗಳ ನಂತರ ಮೌಲ್ಯ",
    "re.mfWins": "ಮ್ಯೂಚುವಲ್ ಫಂಡ್ ಗೆಲ್ಲುತ್ತದೆ",
    "re.reWins": "ರಿಯಲ್ ಎಸ್ಟೇಟ್ ಗೆಲ್ಲುತ್ತದೆ",
    "re.summary": "ಸಾರಾಂಶ",
    "re.minIncome": "ಕನಿಷ್ಠ ಮಾಸಿಕ ಆದಾಯ",
    "re.affordable": "ಈ ಆಸ್ತಿ ನಿಮಗೆ ಕೈಗೆಟುಕುವಂಥದ್ದು!",
    "re.notAffordable": "ಈ ಆಸ್ತಿ ನಿಮ್ಮ ಬಜೆಟ್ ಮೀರಬಹುದು.",
    "re.emiShouldBe": "EMI ನಿಮ್ಮ ಮಾಸಿಕ ಆದಾಯದ 40% ಕ್ಕಿಂತ ಕಡಿಮೆ ಇರಬೇಕು.",
    "re.nextSteps": "ಮುಂದಿನ ಹಂತಗಳ ಮಾರ್ಗದರ್ಶಿ",
    "re.step1Tip": "2-3 ಬ್ಯಾಂಕ್‌ಗಳಿಂದ ಹೋಮ್ ಲೋನ್ ಪೂರ್ವ-ಅನುಮೋದನೆ ಪಡೆಯಿರಿ",
    "re.step2Tip": "ಆಸ್ತಿಯ RERA ನೋಂದಣಿ ಪರಿಶೀಲಿಸಿ",
    "re.step3Tip": "ಇಂಟೀರಿಯರ್ ಮತ್ತು ಸ್ಥಳಾಂತರ ವೆಚ್ಚಗಳನ್ನು (5-10%) ಬಜೆಟ್ ಮಾಡಿ",
    "re.step4Tip": "ಸ್ವಾಧೀನಕ್ಕೆ ಮುಂಚೆ ಹೋಮ್ ಇನ್ಶೂರೆನ್ಸ್ ಪರಿಗಣಿಸಿ",
    "re.startOver": "ಮತ್ತೆ ಪ್ರಾರಂಭಿಸಿ",
    "re.step4Title": "ಸಾಲ ಅರ್ಹತೆ",
    "re.empType": "ಉದ್ಯೋಗ ವಿಧ",
    "re.empSalaried": "ವೇತನಗಾರ",
    "re.empBusiness": "ವ್ಯಾಪಾರ ಮಾಲಿಕ",
    "re.empFreelancer": "ಫ್ರೀಲ್ಯಾನ್ಸರ್",
    "re.netSalary": "ನಿವ್ವಳ ಮಾಸಿಕ ಸಂಬಳ (₹)",
    "re.existingEmis": "ಅಸ್ತಿತ್ವದ EMIಗಳು (₹)",
    "re.workExp": "ಕೆಲಸದ ಅನುಭವ",
    "re.maxAllowableEmi": "ಗರಿಷ್ಠ ಅನುಮತಿಸಲಾದ EMI",
    "re.maxLoanAmount": "ಗರಿಷ್ಠ ಸಾಲ ಮೊತ್ತ",
    "re.requiredLoan": "ಅಗತ್ಯ ಸಾಲ ಮೊತ್ತ",
    "re.calcBasis": "ಲೆಕ್ಕಾಚಾರ ಆಧಾರ",
    "re.eligible": "ಅರ್ಹ — ನೀವು ಅರ್ಹರು!",
    "re.borderline": "ಅಂಚಿನಲ್ಲಿ — ಮಿತಿಗೆ ಹತ್ತಿರ",
    "re.notEligible": "ಅನರ್ಹ — ಅಂತರ ತುಂಬಾ ಹೆಚ್ಚು",
    "re.eligibleTip": "ನಿಮ್ಮ ಅಗತ್ಯ ಸಾಲ ನಿಮ್ಮ ಅರ್ಹತೆಯಲ್ಲಿ ಇದೆ. ಅರ್ಜಿ ಸಲ್ಲಿಸಲು ಸಿದ್ಧ!",
    "re.borderlineTip": "ಮಿತಿ ಹೆಚ್ಚಿಸಲು ಪತಿ/ಪತ್ನಿಯನ್ನು ಸಹ-ಅರ್ಜಿದಾರರಾಗಿ ಸೇರಿಸಿ.",
    "re.notEligibleTip": "ನಿಮ್ಮ ಡೌನ್ ಪೇಮೆಂಟ್ ಹೆಚ್ಚಿಸಿ ಅಥವಾ ಮೊದಲು ಅಸ್ತಿತ್ವದ ಸಾಲಗಳನ್ನು ತೀರಿಸಿ.",
    "re.sathiLoanTip": "ಸಾಥಿ ಟಿಪ್: ಬ್ಯಾಂಕ್‌ಗಳು ಸ್ಥಿರತೆಯನ್ನು ಇಷ್ಟಪಡುತ್ತವೆ. ಕಳೆದ 3 ತಿಂಗಳಲ್ಲಿ ಕೆಲಸ ಬದಲಾಯಿಸಿದ್ದರೆ, ಅರ್ಜಿ ಮೊದಲು 3 ಸಂಬಳ ಸ್ಲಿಪ್‌ಗಳಿಗಾಗಿ ಕಾಯಿರಿ!",
    "re.addCoApplicant": "ಸಹ-ಅರ್ಜಿದಾರರ ಆದಾಯ ಸೇರಿಸಿ?",
    "re.coApplicantSalary": "ಸಹ-ಅರ್ಜಿದಾರರ ಮಾಸಿಕ ಸಂಬಳ (₹)",
    "re.coApplicantNote": "ಪತಿ/ಪತ್ನಿ ಅಥವಾ ಪೋಷಕರ ಆದಾಯ ಸೇರಿಸುವುದರಿಂದ ನಿಮ್ಮ ಸಾಲ ಅರ್ಹತೆ ಗಮನಾರ್ಹವಾಗಿ ಹೆಚ್ಚಾಗಬಹುದು!",
    "ret.title": "ನಿವೃತ್ತಿ ಗಡಿಯಾರ",
    "ret.subtitle": "ಹಣದುಬ್ಬರ-ಸರಿಹೊಂದಿಸಿದ ಲೆಕ್ಕಾಚಾರಗಳೊಂದಿಗೆ ನಿವೃತ್ತಿ ಯೋಜನೆ",
    "ret.monthlyExpense": "ಪ್ರಸ್ತುತ ಮಾಸಿಕ ಖರ್ಚುಗಳು",
    "ret.currentAge": "ಪ್ರಸ್ತುತ ವಯಸ್ಸು",
    "ret.retireAge": "ನಿವೃತ್ತಿ ವಯಸ್ಸು",
    "ret.inflationRate": "ಹಣದುಬ್ಬರ ದರ (%)",
    "ret.sipReturn": "ನಿರೀಕ್ಷಿತ SIP ಆದಾಯ (%)",
    "ret.yearsLeft": "ನಿವೃತ್ತಿಗೆ ಉಳಿದ ವರ್ಷಗಳು",
    "ret.futureCost": "ಭವಿಷ್ಯದ ಮಾಸಿಕ ವೆಚ್ಚ",
    "ret.month": "ತಿಂಗಳು",
    "ret.inflationNote": "ಹಣದುಬ್ಬರಕ್ಕೆ ಸರಿಹೊಂದಿಸಿದ ಜೀವನಶೈಲಿ ವೆಚ್ಚ",
    "ret.corpusNeeded": "ನಿವೃತ್ತಿ ಕಾರ್ಪಸ್ ಅಗತ್ಯ",
    "ret.sipNeeded": "ಮಾಸಿಕ SIP ಅಗತ್ಯ",
    "ret.sipNote": "ಆರಾಮದಾಯಕ ನಿವೃತ್ತಿಗಾಗಿ ಇಂದೇ ಈ SIP ಪ್ರಾರಂಭಿಸಿ",
    "sidebar.rentVsBuy": "ಬಾಡಿಗೆ vs ಖರೀದಿ",
    "sidebar.debtKiller": "ಸಾಲದಿಂದ ಮುಕ್ತಿ",
    "sidebar.stockResearch": "ಸ್ಟಾಕ್ ಸಂಶೋಧನೆ",
    "sidebar.emiMentor": "ನಾನು ಇದನ್ನು ಖರೀದಿಸಬಹುದೇ?",
    "sidebar.lifeStages": "ಜೀವನ ಹಂತಗಳು",
    "sidebar.creditCard": "ಕ್ರೆಡಿಟ್ ಕಾರ್ಡ್ ಮಾಸ್ಟರ್",
    "sidebar.profile": "ನನ್ನ ಪ್ರೊಫೈಲ್",
    "sidebar.childFuture": "ಮಕ್ಕಳ ಭವಿಷ್ಯ ಯೋಜಕ",
    "sidebar.dataRights": "ಡೇಟಾ ಹಕ್ಕುಗಳು",
    "rvb.title": "ಬಾಡಿಗೆ vs ಖರೀದಿ ವಿಶ್ಲೇಷಕ",
    "rvb.subtitle": "ಬಾಡಿಗೆಗೆ ಇರಬೇಕಾ ಅಥವಾ ಖರೀದಿಸಬೇಕಾ? ಕಾಲಾನಂತರದ ನಿಜವಾದ ವೆಚ್ಚವನ್ನು ಹೋಲಿಸಿ.",
    "rvb.cityPreset": "ನಗರ ಪ್ರೀಸೆಟ್",
    "rvb.custom": "ಕಸ್ಟಮ್",
    "rvb.taxRegime": "ತೆರಿಗೆ ವ್ಯವಸ್ಥೆ",
    "rvb.oldRegime": "ಹಳೆಯ ವ್ಯವಸ್ಥೆ",
    "rvb.newRegime": "ಹೊಸ ವ್ಯವಸ್ಥೆ",
    "rvb.horizon": "ವಿಶ್ಲೇಷಣೆ ಅವಧಿ",
    "rvb.yrs": "ವರ್ಷಗಳು",
    "rvb.buyPath": "ಖರೀದಿ ಮಾರ್ಗ",
    "rvb.rentPath": "ಬಾಡಿಗೆ + ಹೂಡಿಕೆ ಮಾರ್ಗ",
    "rvb.propertyPrice": "ಆಸ್ತಿ ಬೆಲೆ",
    "rvb.downPayment": "ಡೌನ್ ಪೇಮೆಂಟ್ (%)",
    "rvb.loanRate": "ಸಾಲ ಬಡ್ಡಿ ದರ (%)",
    "rvb.loanTenure": "ಸಾಲದ ಅವಧಿ (ವರ್ಷಗಳು)",
    "rvb.interiors": "ಇಂಟೀರಿಯರ್ ವೆಚ್ಚ",
    "rvb.monthlyMaintenance": "ಮಾಸಿಕ ನಿರ್ವಹಣೆ",
    "rvb.yearlyPropertyTax": "ವಾರ್ಷಿಕ ಆಸ್ತಿ ತೆರಿಗೆ",
    "rvb.appreciationRate": "ಆಸ್ತಿ ಮೌಲ್ಯ ಏರಿಕೆ (%)",
    "rvb.monthlyRent": "ಮಾಸಿಕ ಬಾಡಿಗೆ",
    "rvb.rentIncrease": "ವಾರ್ಷಿಕ ಬಾಡಿಗೆ ಏರಿಕೆ (%)",
    "rvb.mfReturn": "ಮ್ಯೂಚುವಲ್ ಫಂಡ್ ರಿಟರ್ನ್ (%)",
    "rvb.opportunityCostNote": "ಅವಕಾಶ ವೆಚ್ಚ: ಡೌನ್ ಪೇಮೆಂಟ್ ಹೂಡಿಕೆ ಮಾಡಿದ್ದರೆ",
    "rvb.investedInMF": "ಮ್ಯೂಚುವಲ್ ಫಂಡ್‌ಗಳಲ್ಲಿ ಹೂಡಿಕೆ",
    "rvb.hraNote": "ನೆನಪಿಡಿ: HRA ವಿನಾಯಿತಿ ಹಳೆಯ ವ್ಯವಸ್ಥೆಯಲ್ಲಿ ಮಾತ್ರ",
    "rvb.monthlyEmi": "ಮಾಸಿಕ EMI",
    "rvb.recommendation": "ಶಿಫಾರಸು",
    "rvb.buyingBetter": "ಖರೀದಿ ಹೆಚ್ಚು ಸಂಪತ್ತು ನಿರ್ಮಿಸುತ್ತದೆ",
    "rvb.rentingBetter": "ಬಾಡಿಗೆ ನಿಮ್ಮ ಸಂಪತ್ತಿಗೆ ಉತ್ತಮ",
    "rvb.wealthDiff": "ಸಂಪತ್ತು ವ್ಯತ್ಯಾಸ",
    "rvb.breakeven": "ಬ್ರೇಕ್‌ಈವನ್ ವರ್ಷ",
    "rvb.year": "ವರ್ಷ",
    "rvb.neverBreakeven": "ಖರೀದಿ ಎಂದಿಗೂ ಹಿಡಿಯಲಾಗುವುದಿಲ್ಲ",
    "rvb.wealthComparison": "ನಿವ್ವಳ ಸಂಪತ್ತು ಹೋಲಿಕೆ",
    "rvb.years": "ವರ್ಷಗಳು",
    "rvb.buyWealth": "ಖರೀದಿ: ನಿವ್ವಳ ಸಂಪತ್ತು",
    "rvb.rentWealth": "ಬಾಡಿಗೆ + ಹೂಡಿಕೆ: ನಿವ್ವಳ ಸಂಪತ್ತು",
    "rvb.taxRulesTitle": "2026 ತೆರಿಗೆ ನಿಯಮಗಳು ಅನ್ವಯಿಸಲಾಗಿದೆ",
    "rvb.taxRule1": "ಹಳೆಯ: ಸೆಕ್ಷನ್ 24b ಬಡ್ಡಿ ಕಡಿತ ₹2L ವರೆಗೆ",
    "rvb.taxRule2": "ಹಳೆಯ: ಸೆಕ್ಷನ್ 80C ಮೂಲ ಕಡಿತ ₹1.5L ವರೆಗೆ",
    "rvb.taxRule3": "ಹೊಸ: ಸ್ವಂತ ಆಸ್ತಿಗೆ ಕಡಿತಗಳಿಲ್ಲ",
    "rvb.assumptions": "ಬಳಸಿದ ಊಹೆಗಳು",
    "rvb.assumption1": "ಆಸ್ತಿ ಮೌಲ್ಯ ಏರಿಕೆ",
    "rvb.assumption2": "ಬಾಡಿಗೆ ಏರಿಕೆ",
    "rvb.assumption3": "MF ರಿಟರ್ನ್ (CAGR)",
    "rvb.purposeTitle": "ಈ ಟೂಲ್ ಏನು ಮಾಡುತ್ತದೆ?",
    "rvb.purposeDesc": "ಈ ಟೂಲ್ ಮನೆ ಖರೀದಿಸುವ ನಿಜವಾದ ವೆಚ್ಚ (EMI, ಮುದ್ರಾಂಕ ಶುಲ್ಕ, ನಿರ್ವಹಣೆ) ಅನ್ನು ಬಾಡಿಗೆಯಲ್ಲಿ ಇದ್ದು ವ್ಯತ್ಯಾಸವನ್ನು ಮ್ಯೂಚುವಲ್ ಫಂಡ್‌ನಲ್ಲಿ ಹೂಡಿಕೆ ಮಾಡುವುದರೊಂದಿಗೆ ಹೋಲಿಸುತ್ತದೆ.",
    "rvb.hiddenCosts": "ಗುಪ್ತ ವೆಚ್ಚಗಳು (ಮುದ್ರಾಂಕ ಶುಲ್ಕ, ನಿರ್ವಹಣೆ, ತೆರಿಗೆ)",
    "rvb.stampDuty": "ಮುದ್ರಾಂಕ ಶುಲ್ಕ",
    "rvb.registration": "ನೋಂದಣಿ",
    "rvb.wealthGapChart": "ಸಂಪತ್ತು ಅಂತರ: ಖರೀದಿ vs ಬಾಡಿಗೆ + ಹೂಡಿಕೆ",
    "rvb.sathiVerdict": "ಸಾಥಿಯ ತೀರ್ಪು",
    "rvb.sathiRentAdvice": "ಸಾಥಿ ಹೇಳುತ್ತಾರೆ: ಬಾಡಿಗೆ ನಿಮಗೆ ತಿಂಗಳಿಗೆ {AMT} ಉಳಿಸುತ್ತಿದೆ. ಇದನ್ನು SIPನಲ್ಲಿ ಹೂಡಿಕೆ ಮಾಡಿ!",
    "rvb.sathiBuyAdvice": "ಸಾಥಿ ಹೇಳುತ್ತಾರೆ: ಈ ಬೆಲೆಯಲ್ಲಿ ಖರೀದಿಸುವುದು ಅತ್ಯುತ್ತಮ ದೀರ್ಘಕಾಲಿಕ ಆಸ್ತಿ!",
    "rvb.totalCostBuy": "ಮಾಲೀಕತ್ವದ ಒಟ್ಟು ವೆಚ್ಚ (ಖರೀದಿ)",
    "rvb.rentInvestPath": "ಬಾಡಿಗೆ + ಹೂಡಿಕೆ ಮಾರ್ಗ",
    "rvb.downPaymentStampInteriors": "ಮುಂಗಡ ಹಣ + ಮುದ್ರಾಂಕ ಶುಲ್ಕ + ಒಳಾಂಗಣ",
    "rvb.totalEmiPaid": "ಒಟ್ಟು EMI ಪಾವತಿ",
    "rvb.interestLabel": "ಬಡ್ಡಿ",
    "rvb.taxSavings": "ತೆರಿಗೆ ಉಳಿತಾಯ (ಸೆಕ್ಷನ್ 24b + 80C)",
    "rvb.totalOutOfPocket": "ಒಟ್ಟು ಜೇಬಿನಿಂದ ಖರ್ಚು",
    "rvb.propertyValueAtYear": "ವರ್ಷದಲ್ಲಿ ಆಸ್ತಿ ಮೌಲ್ಯ",
    "rvb.totalRentPaid": "ಒಟ್ಟು ಬಾಡಿಗೆ ಪಾವತಿ",
    "rvb.downPaymentInvested": "ಮುಂಗಡ ಹಣ ಹೂಡಿಕೆ",
    "rvb.indexFund": "ಇಂಡೆಕ್ಸ್ ಫಂಡ್",
    "rvb.monthlySavingsInvested": "ಮಾಸಿಕ ಉಳಿತಾಯ ಹೂಡಿಕೆ (EMI ವ್ಯತ್ಯಾಸ)",
    "rvb.autoCompounded": "ಆಟೋ-ಕಂಪೌಂಡ್",
    "rvb.investmentCorpus": "ವರ್ಷದಲ್ಲಿ ಹೂಡಿಕೆ ಕಾರ್ಪಸ್",
    "rvb.netWealth": "ನಿವ್ವಳ ಸಂಪತ್ತು (ಕಾರ್ಪಸ್ − ಬಾಡಿಗೆ)",
    "rvb.shareWhatsApp": "WhatsAppನಲ್ಲಿ ಹಂಚಿಕೊಳ್ಳಿ",
    "rvb.saveResult": "ಫಲಿತಾಂಶ ಉಳಿಸಿ",
    "ph.we.title": "ವೆಲ್ತ್ ಎಂಜಿನ್ ಎಂದರೇನು?",
    "ph.we.desc": "ಈ ಟೂಲ್ ಚಕ್ರವೃದ್ಧಿ, ಸ್ಟೆಪ್-ಅಪ್ ಮತ್ತು ಹಣದುಬ್ಬರ ಸರಿಹೊಂದಿಕೆಯಿಂದ ನಿಮ್ಮ ಮಾಸಿಕ SIP ಹೇಗೆ ಬೆಳೆಯುತ್ತದೆ ಎಂದು ತೋರಿಸುತ್ತದೆ.",
    "ph.we.tip": "ಸಾಥಿ ಟಿಪ್: ಬೇಗ ಪ್ರಾರಂಭಿಸುವುದು ಸಂಪತ್ತು ನಿರ್ಮಿಸಲು ಅತ್ಯಂತ ಶಕ್ತಿಶಾಲಿ ಅಭ್ಯಾಸ.",
    "ph.dk.title": "ಸಾಲ-ಮುಕ್ತ ಮಾರ್ಗ ಎಂದರೇನು?",
    "ph.dk.desc": "ಈ ಟೂಲ್ ಸ್ನೋಬಾಲ್ ಮತ್ತು ಅವಲಾಂಚ್ ರಣನೀತಿಗಳನ್ನು ಬಳಸಿ ಎಲ್ಲಾ ಸಾಲಗಳನ್ನು ವೇಗವಾಗಿ ತೀರಿಸಲು ಹಂತ-ಹಂತದ ಯೋಜನೆ ರಚಿಸಲು ಸಹಾಯ ಮಾಡುತ್ತದೆ.",
    "ph.dk.tip": "ಸಾಥಿ ಟಿಪ್: ಪ್ರತಿ ತಿಂಗಳು ₹500 ಹೆಚ್ಚುವರಿ ಪಾವತಿಯು ಕಾಲಾನಂತರ ಲಕ್ಷಗಳ ಬಡ್ಡಿ ಉಳಿಸಬಹುದು.",
    "ph.re.title": "ರಿಯಲ್ ಎಸ್ಟೇಟ್ ಪ್ಲಾನರ್ ಎಂದರೇನು?",
    "ph.re.desc": "EMI ಲೆಕ್ಕಾಚಾರ, ಗುಪ್ತ ವೆಚ್ಚಗಳ ವಿವರ, ಮತ್ತು ಮ್ಯೂಚುಯಲ್ ಫಂಡ್ ಹೂಡಿಕೆಯೊಂದಿಗೆ ಹೋಲಿಕೆ ಮಾಡಿ ನಿಮ್ಮ ಆಸ್ತಿ ಖರೀದಿಯನ್ನು ಯೋಜಿಸಿ.",
    "ph.re.tip": "ಸಾಥಿ ಟಿಪ್: ಸ್ಟ್ಯಾಂಪ್ ಡ್ಯೂಟಿ, ನೋಂದಣಿ ಮತ್ತು ನಿರ್ವಹಣೆಯನ್ನು ಯಾವಾಗಲೂ ಲೆಕ್ಕಹಾಕಿ — ಇವು ಬೆಲೆಗೆ 10-15% ಸೇರಿಸುತ್ತವೆ.",
    "ph.rvb.title": "ಬಾಡಿಗೆ ಬನಾಮ ಖರೀದಿ ವಿಶ್ಲೇಷಕ ಎಂದರೇನು?",
    "ph.rvb.desc": "ಮನೆ ಖರೀದಿಸುವ ಒಟ್ಟು ವೆಚ್ಚ ಬನಾಮ ಬಾಡಿಗೆಯಲ್ಲಿದ್ದು ಉಳಿತಾಯ ಹೂಡಿಕೆ ಮಾಡುವುದನ್ನು ಹೋಲಿಸಿ.",
    "ph.rvb.tip": "ಸಾಥಿ ಟಿಪ್: ಇಂದು ಸರಿಯಾದ ಆಯ್ಕೆ ಮಾಡುವುದರಿಂದ ಭವಿಷ್ಯದಲ್ಲಿ ಲಕ್ಷಗಳ ಉಳಿತಾಯ ಆಗಬಹುದು.",
    "ph.emi.title": "EMI ಮೆಂಟರ್ ಎಂದರೇನು?",
    "ph.emi.desc": "EMI ಮೇಲೆ ಖರೀದಿಸುವ ಮೊದಲು, ನೀವು ನಿಜವಾಗಿಯೂ ಅದನ್ನು ಭರಿಸಬಹುದೇ ಎಂದು ಪರಿಶೀಲಿಸಿ.",
    "ph.emi.tip": "ಸಾಥಿ ಟಿಪ್: ನಿಮ್ಮ ಒಟ್ಟು EMI ಆದಾಯದ 40% ಮೀರಿದರೆ, ನೀವು ಅಪಾಯದ ವಲಯದಲ್ಲಿದ್ದೀರಿ.",
    "ph.cc.title": "ಕ್ರೆಡಿಟ್ ಕಾರ್ಡ್ ಮಾಸ್ಟರ್ ಎಂದರೇನು?",
    "ph.cc.desc": "ನಿಮ್ಮ ಕ್ರೆಡಿಟ್ ಕಾರ್ಡ್ ಸಾಲದ ಬಲೆಯನ್ನು ಅರ್ಥಮಾಡಿಕೊಳ್ಳಿ. ಕನಿಷ್ಠ ಪಾವತಿ ನಿಮ್ಮನ್ನು ಹೇಗೆ ಶಾಶ್ವತವಾಗಿ ಸಾಲದಲ್ಲಿ ಇಡುತ್ತದೆ ನೋಡಿ.",
    "ph.cc.tip": "ಸಾಥಿ ಟಿಪ್: ಯಾವಾಗಲೂ ಪೂರ್ಣ ಬಿಲ್ ಪಾವತಿಸಿ. ಕನಿಷ್ಠ ಪಾವತಿ ಬ್ಯಾಂಕ್ ಹೆಚ್ಚು ಗಳಿಸುವ ಮಾರ್ಗ.",
    "ph.sr.title": "ಷೇರು ಸಂಶೋಧನಾ ಲ್ಯಾಬ್ ಎಂದರೇನು?",
    "ph.sr.desc": "ಪ್ರಮುಖ ಹಣಕಾಸು ಮೆಟ್ರಿಕ್ಸ್, ಪೀರ್ ಹೋಲಿಕೆ, ಮತ್ತು ಮೌಲ್ಯಮಾಪನ ಒಳನೋಟಗಳೊಂದಿಗೆ ಭಾರತೀಯ ಷೇರುಗಳನ್ನು ಸಂಶೋಧಿಸಿ.",
    "ph.sr.tip": "ಸಾಥಿ ಟಿಪ್: ವ್ಯವಹಾರ ಮತ್ತು ಹಣಕಾಸು ಅರ್ಥಮಾಡಿಕೊಳ್ಳದೆ ಎಂದಿಗೂ ಷೇರಿನಲ್ಲಿ ಹೂಡಿಕೆ ಮಾಡಬೇಡಿ.",
    "ph.ret.title": "ನಿವೃತ್ತಿ ಗಡಿಯಾರ ಎಂದರೇನು?",
    "ph.ret.desc": "ಆರಾಮದಾಯಕವಾಗಿ ನಿವೃತ್ತರಾಗಲು ಎಷ್ಟು ಬೇಕು ಎಂದು ಲೆಕ್ಕ ಹಾಕಿ. ಹಣದುಬ್ಬರ, ಜೀವಿತಾವಧಿ ಮತ್ತು ಪ್ರಸ್ತುತ ಉಳಿತಾಯವನ್ನು ಸೇರಿಸಿ.",
    "ph.ret.tip": "ಸಾಥಿ ಟಿಪ್: ಹಣದುಬ್ಬರ ನಿವೃತ್ತಿ ಕನಸುಗಳ ಮೂಕ ಕೊಲೆಗಾರ. 6%+ ವಾರ್ಷಿಕ ಹೆಚ್ಚಳಕ್ಕೆ ಯೋಜಿಸಿ.",
    "ph.tax.title": "ತೆರಿಗೆ ಯೋಜಕ ಎಂದರೇನು?",
    "ph.tax.desc": "ಸೆಕ್ಷನ್ 80C, 80D ಮತ್ತು HRA ಅಡಿಯಲ್ಲಿ ನಿಮ್ಮ ತೆರಿಗೆ-ಉಳಿತಾಯ ಹೂಡಿಕೆಗಳನ್ನು ಟ್ರ್ಯಾಕ್ ಮಾಡಿ.",
    "ph.tax.tip": "ಸಾಥಿ ಟಿಪ್: ಮಾರ್ಚ್ ವರೆಗೆ ಕಾಯಬೇಡಿ. ಏಪ್ರಿಲ್‌ನಲ್ಲಿಯೇ ತೆರಿಗೆ ಯೋಜನೆ ಪ್ರಾರಂಭಿಸಿ.",
    "ph.compare.title": "ಉತ್ಪನ್ನ ಹೋಲಿಕೆ ಎಂದರೇನು?",
    "ph.compare.desc": "ಕ್ರೆಡಿಟ್ ಕಾರ್ಡ್, ಉಳಿತಾಯ ಖಾತೆಗಳು ಮತ್ತು ವಿಮಾ ಯೋಜನೆಗಳನ್ನು ಅಕ್ಕಪಕ್ಕ ಹೋಲಿಸಿ.",
    "ph.compare.tip": "ಸಾಥಿ ಟಿಪ್: ಅಗ್ಗದ ಉತ್ಪನ್ನ ಯಾವಾಗಲೂ ಅತ್ಯುತ್ತಮವಲ್ಲ. 3-5 ವರ್ಷಗಳಲ್ಲಿ ಒಟ್ಟು ಮೌಲ್ಯವನ್ನು ಹೋಲಿಸಿ.",
    "ph.ls.title": "ಲೈಫ್-ಸ್ಟೇಜ್ ಟೂಲ್‌ಕಿಟ್ ಎಂದರೇನು?",
    "ph.ls.desc": "ಜೀವನದ ದೊಡ್ಡ ಕ್ಷಣಗಳಿಗೆ ಯೋಜಿಸಿ — ಮಗುವಿನ ಶಿಕ್ಷಣ, ಮದುವೆ, ವೈದ್ಯಕೀಯ ತುರ್ತು.",
    "ph.ls.tip": "ಸಾಥಿ ಟಿಪ್: ಭಾರತದಲ್ಲಿ ಶಿಕ್ಷಣ ಹಣದುಬ್ಬರ 10-12% — ಸಾಮಾನ್ಯ ಹಣದುಬ್ಬರಕ್ಕಿಂತ ಹೆಚ್ಚು!",
    "ph.ts.title": "ತೆರಿಗೆ ಸಿಮ್ಯುಲೇಟರ್ ಎಂದರೇನು?",
    "ph.ts.desc": "ಹಳೆಯ ಬನಾಮ ಹೊಸ ತೆರಿಗೆ ವ್ಯವಸ್ಥೆಯನ್ನು ಹೋಲಿಸಿ. ಯಾವ ವ್ಯವಸ್ಥೆ ಹೆಚ್ಚು ತೆರಿಗೆ ಉಳಿಸುತ್ತದೆ ನೋಡಿ.",
    "ph.ts.tip": "ಸಾಥಿ ಟಿಪ್: ನಿಮ್ಮ ಕಡಿತಗಳು ₹3.75 ಲಕ್ಷಕ್ಕಿಂತ ಹೆಚ್ಚಿದ್ದರೆ, ಹಳೆಯ ವ್ಯವಸ್ಥೆ ಬಹುಶಃ ಉತ್ತಮ.",
    "mac.pageTitle": "ಮಲ್ಟಿ-ಆಸ್ತಿ ಹೋಲಿಕೆ",
    "mac.pageSubtitle": "ನಿಮ್ಮ SIP 5 ಆಸ್ತಿ ವರ್ಗಗಳಲ್ಲಿ ಹೇಗೆ ಬೆಳೆಯುತ್ತದೆ ನೋಡಿ",
    "ph.mac.title": "ಮಲ್ಟಿ-ಆಸ್ತಿ ಹೋಲಿಕೆ ಎಂದರೇನು?",
    "ph.mac.desc": "ಈ ಟೂಲ್ FD, ಚಿನ್ನ, ರಿಯಲ್ ಎಸ್ಟೇಟ್, ಇಂಡೆಕ್ಸ್ ಫಂಡ್ಸ್ ಮತ್ತು ಷೇರು ಹೂಡಿಕೆಯಲ್ಲಿ ನಿಮ್ಮ ಮಾಸಿಕ SIP ಬೆಳವಣಿಗೆಯನ್ನು ಹೋಲಿಸುತ್ತದೆ.",
    "ph.mac.tip": "ಸಾಥಿ ಟಿಪ್: 20 ವರ್ಷಗಳಲ್ಲಿ FD ಮತ್ತು ಈಕ್ವಿಟಿ ನಡುವಿನ ವ್ಯತ್ಯಾಸ ಮನೆ ಖರೀದಿಸಲು ಸಾಕಾಗಬಹುದು!",
    "mac.tableTitle": "ಸಂಪತ್ತು ಅಂತರ ಫಲಿತಾಂಶ ಕೋಷ್ಟಕ",
    "mac.assetName": "ಆಸ್ತಿ ಹೆಸರು",
    "mac.totalInvested": "ಒಟ್ಟು ಹೂಡಿಕೆ",
    "mac.finalWealth": "ಅಂತಿಮ ಸಂಪತ್ತು",
    "mac.gapVsFD": "FD ಗಿಂತ ಅಂತರ",
    "mac.realReturn": "ನಿಜವಾದ ಆದಾಯ",
    "mac.volatility": "ಅಸ್ಥಿರತೆ",
    "mac.investmentAmount": "ಹೂಡಿಕೆ ಮೊತ್ತ (₹)",
    "mac.frequency": "ಹೂಡಿಕೆ ಆವರ್ತನ",
    "mac.freqMonthly": "ಮಾಸಿಕ (SIP)",
    "mac.freqQuarterly": "ತ್ರೈಮಾಸಿಕ",
    "mac.freqHalfYearly": "ಅರ್ಧ ವಾರ್ಷಿಕ",
    "mac.freqYearly": "ವಾರ್ಷಿಕ",
    "mac.chartTitle": "{amount} {frequency} ಹೂಡಿಕೆಯ {years} ವರ್ಷಗಳ ಬೆಳವಣಿಗೆ",
    "mac.yearlyTip": "ವರ್ಷಕ್ಕೊಮ್ಮೆ ಒಂದೇ ಬಾರಿ ಹೂಡಿಕೆ ಮಾಡುವುದು ಬನಾಮ ಪ್ರತಿ ತಿಂಗಳು ಸಣ್ಣ ಮೊತ್ತ — 'ರೂಪಿ ಕಾಸ್ಟ್ ಅವರೇಜಿಂಗ್' ಕಾರಣ ಫಲಿತಾಂಶ ಬೇರೆ ಆಗಬಹುದು. ಎರಡೂ ಆವರ್ತನಗಳನ್ನು ಪ್ರಯತ್ನಿಸಿ!",
    "sidebar.assetComparison": "ಆಸ್ತಿ ಹೋಲಿಕೆ",
  },
  ml: {
    "sidebar.dashboard": "ഡാഷ്‌ബോർഡ്",
    "sidebar.calculators": "നിക്ഷേപ കാൽക്കുലേറ്ററുകൾ",
    "sidebar.compare": "ഉൽപ്പന്ന താരതമ്യം",
    "sidebar.realEstate": "റിയൽ എസ്റ്റേറ്റ് പ്ലാനർ",
    "sidebar.tax": "നികുതി കേന്ദ്രം",
    "sidebar.tagline": "സ്മാർട്ട് ധനകാര്യ ടൂളുകൾ",
    "sidebar.disclaimer": "വിദ്യാഭ്യാസ ആവശ്യങ്ങൾക്ക് മാത്രം. ധനകാര്യ ഉപദേശമല്ല.",
    "sidebar.catDashboard": "ഡാഷ്‌ബോർഡ്",
    "sidebar.catHomeLife": "🏠 വീടും ജീവിതവും",
    "sidebar.catFuture": "🔮 ഭാവി",
    "sidebar.catSafety": "🛡️ സുരക്ഷ",
    "sidebar.catInvest": "📈 നിക്ഷേപം",
    "sidebar.catBanking": "🏦 ബാങ്കിംഗ്",
    "sidebar.catAccount": "അക്കൗണ്ട്",
    "sidebar.desc.dashboard": "ഒറ്റ നോട്ടത്തിൽ നിങ്ങളുടെ സാമ്പത്തിക അവലോകനം",
    "sidebar.desc.calculators": "SIP, ലംപ് സം & സംയുക്ത പലിശ ഉപകരണങ്ങൾ",
    "sidebar.desc.compare": "സാമ്പത്തിക ഉൽപ്പന്നങ്ങൾ അരികിലരികിൽ താരതമ്യം ചെയ്യൂ",
    "sidebar.desc.realEstate": "വസ്തു വാങ്ങൽ & ROI ആസൂത്രണം",
    "sidebar.desc.tax": "പഴയ & പുതിയ നികുതി വ്യവസ്ഥയിൽ ലാഭം ഒപ്റ്റിമൈസ് ചെയ്യൂ",
    "sidebar.desc.retirement": "വിരമിക്കൽ കോർപ്പസ് & SIP കണക്കാക്കൂ",
    "sidebar.desc.rentVsBuy": "വാടക അല്ലെങ്കിൽ വാങ്ങൽ — ഏത് നല്ലതെന്ന് അറിയൂ",
    "sidebar.desc.debtKiller": "വേഗത്തിൽ കടമുക്തരാകാൻ ആസൂത്രണം ചെയ്യൂ",
    "sidebar.desc.stockResearch": "ലൈവ് മാർക്കറ്റ് ഡാറ്റ & സ്റ്റോക്ക് വിശകലനം",
    "sidebar.desc.emiMentor": "നിങ്ങൾക്ക് ശരിക്കും വാങ്ങാൻ കഴിയുമോ എന്ന് പരിശോധിക്കുക",
    "sidebar.desc.childFuture": "കുട്ടിയുടെ വിദ്യാഭ്യാസ & ഭാവി ചെലവുകളുടെ ആസൂത്രണം",
    "sidebar.desc.creditCard": "ക്രെഡിറ്റ് കാർഡ് കടത്തിന്റെ യഥാർത്ഥ ചെലവ് മനസ്സിലാക്കൂ",
    "sidebar.desc.assetComparison": "വിവിധ ആസ്തി വിഭാഗങ്ങളിലെ വരുമാന താരതമ്യം",
    "sidebar.desc.profile": "നിങ്ങളുടെ സാമ്പത്തിക പ്രൊഫൈൽ നിയന്ത്രിക്കൂ",
    "sidebar.desc.dataRights": "നിങ്ങളുടെ ഡാറ്റ സ്വകാര്യത & ഡൗൺലോഡ് ഓപ്ഷനുകൾ",
    "dash.title": "ഡാഷ്‌ബോർഡ്",
    "dash.subtitle": "നിങ്ങളുടെ ധനകാര്യ സ്ഥിതി ഒറ്റനോട്ടത്തിൽ",
    "dash.portfolioValue": "പോർട്ട്‌ഫോളിയോ മൂല്യം",
    "dash.monthlySip": "മാസ SIP",
    "dash.taxSaved": "നികുതി ലാഭം (FY25)",
    "dash.returns1y": "ലാഭം (1 വർഷം)",
    "dash.portfolioGrowth": "പോർട്ട്‌ഫോളിയോ വളർച്ച",
    "dash.last9months": "കഴിഞ്ഞ 9 മാസം",
    "dash.quickActions": "ദ്രുത പ്രവർത്തനങ്ങൾ",
    "dash.sipCalc": "SIP കാൽക്കുലേറ്റർ",
    "dash.compareMF": "മ്യൂച്ചൽ ഫണ്ട് താരതമ്യം",
    "dash.emiEstimator": "EMI കണക്ക്",
    "dash.taxOptimizer": "നികുതി ആസൂത്രകൻ",
    "dash.marketSnapshot": "വിപണി സ്നാപ്പ്‌ഷോട്ട്",
    "dash.active": "സജീവം",
    "dash.vikasScore": "വികാസ് യാത്ര",
    "dash.totalAssets": "മൊത്തം ആസ്തികൾ",
    "dash.totalLiabilities": "മൊത്തം ബാധ്യതകൾ",
    "dash.netInvestment": "നിക്ഷേപം (നെറ്റ്)",
    "dash.dtiRatio": "DTI അനുപാതം",
    "dash.savingsInvestments": "സേവിംഗ്സ് + നിക്ഷേപങ്ങൾ",
    "dash.annualEmi": "വാർഷിക EMI ബാധ്യതകൾ",
    "dash.monthlyWealth": "മാസ സമ്പത്ത് നിർമ്മാണം",
    "dash.financialPulse": "ഫിനാൻഷ്യൽ പൾസ്",
    "dash.assetsVsLiabilities": "ആസ്തികൾ vs ബാധ്യതകൾ — 12 മാസ പ്രവചനം",
    "dash.dailySathiTip": "ഇന്നത്തെ സാഥി ടിപ്പ്",
    "dash.goalTracker": "ലക്ഷ്യ ട്രാക്കർ",
    "dash.miniTools": "മിനി ടൂൾസ് — ദ്രുത മണി ഹാക്ക്സ്",
    "dash.welcome": "സ്വാഗതം",
    "dash.welcomeSub": "ഇന്നത്തെ നിങ്ങളുടെ സാമ്പത്തിക അവലോകനം.",
    "dash.editProfile": "പ്രൊഫൈൽ എഡിറ്റ്",
    "dash.user": "ഉപയോക്താവ്",
    "ai.insightTitle": "AI ഉൾക്കാഴ്ച",
    "ai.sipAdvice": "നിങ്ങളുടെ {income} വരുമാനത്തിന്റെ അടിസ്ഥാനത്തിൽ, നിങ്ങളുടെ {goal} വേഗത്തിലാക്കാൻ {sip}/മാസം SIP ആരംഭിക്കാൻ ശുപാർശ ചെയ്യുന്നു.",
    "ai.dtiWarning": "നിങ്ങളുടെ EMI ഭാരം {dti}% — പലിശ കുറയ്ക്കാൻ ബാലൻസ് ട്രാൻസ്ഫർ പരിഗണിക്കൂ.",
    "ai.financialGoal": "സാമ്പത്തിക ലക്ഷ്യം",
    "action.title": "കർമ്മ പട്ടിക",
    "action.completeProfile": "നിങ്ങളുടെ സാമ്പത്തിക പ്രൊഫൈൽ പൂർത്തിയാക്കൂ",
    "action.setup": "സെറ്റപ്പ്",
    "action.runTax": "നികുതി ഒപ്റ്റിമൈസേഷൻ നടത്തൂ",
    "action.open": "തുറക്കൂ",
    "action.rentVsBuy": "വാടക vs വാങ്ങൽ വിശകലനം",
    "action.analyze": "വിശകലനം",
    "goal.buyHome": "വീട് വാങ്ങുക",
    "goal.payDebt": "കടം തീർക്കുക",
    "goal.retireEarly": "നേരത്തെ വിരമിക്കുക",
    "goal.emergency": "അടിയന്തര ഫണ്ട്",
    "goal.childEdu": "കുട്ടിയുടെ വിദ്യാഭ്യാസം",
    "goal.wealthGrowth": "സമ്പത്ത് വളർത്തുക",
    "goal.eta50plus": "50+ വർഷം",
    "goal.achieved": "നേടി",
    "goal.basedOn": "അടിസ്ഥാനത്തിൽ",
    "goal.moSIP": "മാസ SIP",
    "goal.returns": "വരുമാനം",
    "goal.saved": "സേവ്",
    "goal.target": "ലക്ഷ്യം",
    "goal.custom": "ഇഷ്ടാനുസൃത",
    "goal.complete": "പൂർത്തി",
    "goal.yrsToGo": "വർഷം ബാക്കി",
    "goal.sipNeeded": "ലക്ഷ്യത്തിലെത്താൻ SIP",
    "goal.yrs": "വർഷം",
    "goal.month": "മാസം",
    "goal.save": "സേവ്",
    "goal.reset": "റീസെറ്റ്",
    "goal.setCustomTarget": "ഇഷ്ടാനുസൃത ലക്ഷ്യം സജ്ജമാക്കുക",
    "goal.noGoals": "ലക്ഷ്യങ്ങളൊന്നുമില്ല. ട്രാക്ക് ചെയ്യാൻ പ്രൊഫൈൽ അപ്ഡേറ്റ് ചെയ്യുക.",
    "goal.enterValid": "ശരിയായ തുക നൽകുക",
    "goal.targetUpdated": "ലക്ഷ്യം അപ്ഡേറ്റ് ചെയ്തു",
    "goal.resetDefault": "ഡിഫോൾട്ടിലേക്ക് റീസെറ്റ്",
    "calc.title": "നിക്ഷേപ കാൽക്കുലേറ്ററുകൾ",
    "calc.subtitle": "കൃത്യതയോടെ നിങ്ങളുടെ സമ്പത്ത് ആസൂത്രണം ചെയ്യൂ",
    "calc.sipCalc": "SIP കാൽക്കുലേറ്റർ",
    "calc.lumpSum": "ഒറ്റത്തവണ",
    "calc.monthlyInvestment": "മാസ നിക്ഷേപം",
    "calc.investmentAmount": "നിക്ഷേപ തുക",
    "calc.expectedReturn": "പ്രതീക്ഷിത ലാഭം (%)",
    "calc.timePeriod": "കാലയളവ് (വർഷം)",
    "calc.yrs": "വർഷം",
    "calc.totalValue": "ആകെ മൂല്യം",
    "calc.invested": "നിക്ഷേപം",
    "calc.gains": "ലാഭം",
    "compare.title": "ഉൽപ്പന്ന താരതമ്യം",
    "compare.subtitle": "മ്യൂച്ചൽ ഫണ്ടുകൾ, FD, ഇൻഷുറൻസ് പ്ലാനുകൾ താരതമ്യം ചെയ്യൂ",
    "compare.fundName": "ഫണ്ട് പേര്",
    "compare.1yReturn": "1 വർഷ ലാഭം",
    "compare.3yReturn": "3 വർഷ ലാഭം",
    "compare.5yReturn": "5 വർഷ ലാഭം",
    "compare.risk": "റിസ്ക്",
    "compare.expense": "ചെലവ്",
    "compare.disclaimer": "* മുൻ പ്രകടനം ഭാവി ലാഭത്തിന് ഗ്യാരണ്ടി നൽകുന്നില്ല.",
    "compare.low": "കുറവ്",
    "compare.moderate": "മധ്യമം",
    "compare.high": "കൂടുതൽ",
    "compare.moderateHigh": "മധ്യമം-കൂടുതൽ",
    "re.title": "റിയൽ എസ്റ്റേറ്റ് പ്ലാനർ",
    "re.subtitle": "EMI കാൽക്കുലേറ്റർ, സ്വത്ത് ആസൂത്രണ ടൂളുകൾ",
    "re.propertyValue": "സ്വത്ത് മൂല്യം",
    "re.downPayment": "ഡൗൺ പേമെന്റ് (%)",
    "re.interestRate": "പലിശ നിരക്ക് (%)",
    "re.loanTenure": "ലോൺ കാലാവധി (വർഷം)",
    "re.monthlyEmi": "മാസ EMI",
    "re.loanAmount": "ലോൺ തുക",
    "re.totalInterest": "ആകെ പലിശ",
    "re.totalPayment": "ആകെ പേയ്‌മെന്റ്",
    "tax.title": "നികുതി കേന്ദ്രം",
    "tax.subtitle": "ഇന്ത്യൻ നികുതി നിയമപ്രകാരം നികുതി ലാഭം ആസൂത്രണം ചെയ്യൂ",
    "tax.recommended": "ശുപാർശ",
    "tax.alternative": "ബദൽ",
    "tax.oldRegime": "പഴയ നികുതി രീതി",
    "tax.newRegime": "പുതിയ നികുതി രീതി",
    "tax.estimatedTax": "കണക്കാക്കിയ നികുതി",
    "tax.withDeductions": "₹2,41,000 കിഴിവുകളോടെ",
    "tax.standardDeduction": "സ്റ്റാൻഡേർഡ് കിഴിവ് ₹75,000",
    "tax.section80c": "സെക്ഷൻ 80C – ₹1.5 ലക്ഷം",
    "tax.section80d": "സെക്ഷൻ 80D – ആരോഗ്യ ഇൻഷുറൻസ്",
    "tax.hraExemption": "HRA ഇളവ്",
    "tax.elss": "ELSS മ്യൂച്ചൽ ഫണ്ടുകൾ",
    "tax.epf": "EPF സംഭാവന",
    "tax.lifeInsurance": "ജീവിത ഇൻഷുറൻസ് പ്രീമിയം",
    "tax.selfFamily": "സ്വന്തം & കുടുംബ പ്രീമിയം",
    "tax.parentsPremium": "മാതാപിതാക്കളുടെ പ്രീമിയം",
    "tax.rentPaid": "നൽകിയ വാടക (വാർഷിക)",
    "tax.exemptedAmount": "ഇളവ് ലഭിച്ച തുക",
    "tax.invested": "നിക്ഷേപം",
    "tax.autoDeducted": "സ്വയം കിഴിവ്",
    "tax.paid": "നൽകി",
    "tax.claimable": "ക്ലെയിം ചെയ്യാവുന്നത്",
    "tax.calculated": "കണക്കാക്കിയത്",
    "sidebar.retirement": "വിരമിക്കൽ ക്ലോക്ക്",
    "re.step1Title": "ബജറ്റ്",
    "re.step2Title": "ROI സിമുലേറ്റർ",
    "re.step3Title": "താങ്ങാനാവുന്ന നിലവാരം",
    "re.stampDuty": "സ്റ്റാമ്പ് ഡ്യൂട്ടി",
    "re.registration": "രജിസ്ട്രേഷൻ",
    "re.gst": "GST",
    "re.totalCost": "ആകെ വസ്തു ചെലവ്",
    "re.nextStep": "അടുത്ത ഘട്ടം",
    "re.back": "പിന്നിലേക്ക്",
    "re.roiDesc": "താരതമ്യം: അതേ തുക മ്യൂച്ചൽ ഫണ്ടിൽ നിക്ഷേപിച്ചിരുന്നെങ്കിൽ?",
    "re.realEstateInv": "റിയൽ എസ്റ്റേറ്റ് നിക്ഷേപം",
    "re.mutualFundInv": "മ്യൂച്ചൽ ഫണ്ട് നിക്ഷേപം",
    "re.rentalYield": "വാടക വരുമാനം",
    "re.appreciation": "മൂല്യവർദ്ധനവ്",
    "re.propertyAfter20": "വസ്തു മൂല്യം (20 വർഷം)",
    "re.totalRentalIncome": "ആകെ വാടക വരുമാനം",
    "re.costDeductions": "ചെലവ് കുറവുകൾ",
    "re.netGain": "അറ്റ ലാഭം",
    "re.expectedReturn": "പ്രതീക്ഷിത വരുമാനം",
    "re.amountInvested": "നിക്ഷേപിച്ച തുക",
    "re.valueAfter20": "20 വർഷത്തിന് ശേഷം മൂല്യം",
    "re.mfWins": "മ്യൂച്ചൽ ഫണ്ട് ജയിക്കുന്നു",
    "re.reWins": "റിയൽ എസ്റ്റേറ്റ് ജയിക്കുന്നു",
    "re.summary": "സംഗ്രഹം",
    "re.minIncome": "ഏറ്റവും കുറഞ്ഞ പ്രതിമാസ വരുമാനം",
    "re.affordable": "ഈ വസ്തു നിങ്ങൾക്ക് താങ്ങാനാവുന്നതാണ്!",
    "re.notAffordable": "ഈ വസ്തു നിങ്ങളുടെ ബജറ്റിന് അപ്പുറമാകാം.",
    "re.emiShouldBe": "EMI നിങ്ങളുടെ പ്രതിമാസ വരുമാനത്തിന്റെ 40%-ൽ കുറവായിരിക്കണം.",
    "re.nextSteps": "അടുത്ത ഘട്ടങ്ങളുടെ ഗൈഡ്",
    "re.step1Tip": "2-3 ബാങ്കുകളിൽ നിന്ന് ഹോം ലോൺ പ്രീ-അപ്രൂവൽ നേടുക",
    "re.step2Tip": "വസ്തുവിന്റെ RERA രജിസ്ട്രേഷൻ പരിശോധിക്കുക",
    "re.step3Tip": "ഇന്റീരിയർ & മാറ്റ ചെലവുകൾ (5-10%) ബജറ്റ് ചെയ്യുക",
    "re.step4Tip": "കൈവശാവകാശത്തിന് മുമ്പ് ഹോം ഇൻഷുറൻസ് പരിഗണിക്കുക",
    "re.startOver": "വീണ്ടും ആരംഭിക്കുക",
    "re.step4Title": "ലോൺ യോഗ്യത",
    "re.empType": "തൊഴിൽ തരം",
    "re.empSalaried": "ശമ്പളക്കാരൻ",
    "re.empBusiness": "ബിസിനസ് ഉടമ",
    "re.empFreelancer": "ഫ്രീലാൻസർ",
    "re.netSalary": "നെറ്റ് പ്രതിമാസ ശമ്പളം (₹)",
    "re.existingEmis": "നിലവിലുള്ള EMIകൾ (₹)",
    "re.workExp": "പ്രവൃത്തി പരിചയം",
    "re.maxAllowableEmi": "പരമാവധി അനുവദനീയ EMI",
    "re.maxLoanAmount": "പരമാവധി ലോൺ തുക",
    "re.requiredLoan": "ആവശ്യമായ ലോൺ തുക",
    "re.calcBasis": "കണക്കുകൂട്ടൽ അടിസ്ഥാനം",
    "re.eligible": "യോഗ്യം — നിങ്ങൾ യോഗ്യരാണ്!",
    "re.borderline": "അതിരിൽ — പരിധിക്ക് അടുത്ത്",
    "re.notEligible": "അയോഗ്യം — വ്യത്യാസം വളരെ വലുത്",
    "re.eligibleTip": "നിങ്ങളുടെ ആവശ്യമായ ലോൺ യോഗ്യതയ്ക്കുള്ളിലാണ്. അപേക്ഷിക്കാൻ തയ്യാർ!",
    "re.borderlineTip": "പരിധി വർദ്ധിപ്പിക്കാൻ ഭർത്താവ്/ഭാര്യയെ സഹ-അപേക്ഷകനായി ചേർക്കുക.",
    "re.notEligibleTip": "ഡൗൺ പേമെന്റ് വർദ്ധിപ്പിക്കുക അല്ലെങ്കിൽ ആദ്യം നിലവിലുള്ള കടങ്ങൾ അടയ്ക്കുക.",
    "re.sathiLoanTip": "സാഥി ടിപ്: ബാങ്കുകൾ സ്ഥിരത ഇഷ്ടപ്പെടുന്നു. കഴിഞ്ഞ 3 മാസത്തിനുള്ളിൽ ജോലി മാറിയിട്ടുണ്ടെങ്കിൽ, അപേക്ഷിക്കുന്നതിന് മുമ്പ് 3 ശമ്പള സ്ലിപ്പുകൾക്കായി കാത്തിരിക്കുക!",
    "re.addCoApplicant": "സഹ-അപേക്ഷകന്റെ വരുമാനം ചേർക്കണോ?",
    "re.coApplicantSalary": "സഹ-അപേക്ഷകന്റെ പ്രതിമാസ ശമ്പളം (₹)",
    "re.coApplicantNote": "ഭർത്താവ്/ഭാര്യ അല്ലെങ്കിൽ രക്ഷിതാവിന്റെ വരുമാനം ചേർക്കുന്നത് നിങ്ങളുടെ ലോൺ യോഗ്യത ഗണ്യമായി വർദ്ധിപ്പിക്കും!",
    "ret.title": "വിരമിക്കൽ ക്ലോക്ക്",
    "ret.subtitle": "പണപ്പെരുപ്പം-ക്രമീകരിച്ച കണക്കുകൂട്ടലുകളോടെ വിരമിക്കൽ ആസൂത്രണം",
    "ret.monthlyExpense": "നിലവിലെ പ്രതിമാസ ചെലവുകൾ",
    "ret.currentAge": "ഇപ്പോഴത്തെ പ്രായം",
    "ret.retireAge": "വിരമിക്കൽ പ്രായം",
    "ret.inflationRate": "പണപ്പെരുപ്പ നിരക്ക് (%)",
    "ret.sipReturn": "പ്രതീക്ഷിത SIP വരുമാനം (%)",
    "ret.yearsLeft": "വിരമിക്കലിന് ബാക്കിയുള്ള വർഷങ്ങൾ",
    "ret.futureCost": "ഭാവി പ്രതിമാസ ചെലവ്",
    "ret.month": "മാസം",
    "ret.inflationNote": "പണപ്പെരുപ്പത്തിന് ക്രമീകരിച്ച ജീവിതശൈലി ചെലവ്",
    "ret.corpusNeeded": "വിരമിക്കൽ കോർപ്പസ് ആവശ്യം",
    "ret.sipNeeded": "പ്രതിമാസ SIP ആവശ്യം",
    "ret.sipNote": "സുഖകരമായ വിരമിക്കലിന് ഇന്ന് തന്നെ ഈ SIP ആരംഭിക്കുക",
    "sidebar.rentVsBuy": "വാടക vs വാങ്ങൽ",
    "sidebar.debtKiller": "കടം-മോചന മാർഗം",
    "sidebar.stockResearch": "സ്റ്റോക്ക് റിസർച്ച്",
    "sidebar.emiMentor": "എനിക്ക് ഇത് വാങ്ങാൻ കഴിയുമോ?",
    "sidebar.lifeStages": "ജീവിത ഘട്ടങ്ങൾ",
    "sidebar.creditCard": "ക്രെഡിറ്റ് കാർഡ് മാസ്റ്റർ",
    "sidebar.profile": "എന്റെ പ്രൊഫൈൽ",
    "sidebar.childFuture": "കുട്ടിയുടെ ഭാവി പ്ലാനർ",
    "sidebar.assetComparison": "ആസ്തി താരതമ്യം",
    "sidebar.dataRights": "ഡാറ്റ അവകാശങ്ങൾ",
    "rvb.title": "വാടക vs വാങ്ങൽ വിശകലനം",
    "rvb.subtitle": "വാടകയ്ക്ക് താമസിക്കണോ വാങ്ങണോ? കാലക്രമേണ യഥാർത്ഥ ചെലവ് താരതമ്യം ചെയ്യുക.",
    "rvb.cityPreset": "നഗര പ്രീസെറ്റ്",
    "rvb.custom": "ഇഷ്ടാനുസൃതം",
    "rvb.taxRegime": "നികുതി വ്യവസ്ഥ",
    "rvb.oldRegime": "പഴയ വ്യവസ്ഥ",
    "rvb.newRegime": "പുതിയ വ്യവസ്ഥ",
    "rvb.horizon": "വിശകലന കാലയളവ്",
    "rvb.yrs": "വർഷം",
    "rvb.buyPath": "വാങ്ങൽ പാത",
    "rvb.rentPath": "വാടക + നിക്ഷേപ പാത",
    "rvb.propertyPrice": "സ്വത്ത് വില",
    "rvb.downPayment": "ഡൗൺ പേയ്മെന്റ് (%)",
    "rvb.loanRate": "വായ്പ പലിശ നിരക്ക് (%)",
    "rvb.loanTenure": "വായ്പ കാലാവധി (വർഷം)",
    "rvb.interiors": "ഇന്റീരിയർ ചെലവ്",
    "rvb.monthlyMaintenance": "മാസ പരിപാലനം",
    "rvb.yearlyPropertyTax": "വാർഷിക സ്വത്ത് നികുതി",
    "rvb.appreciationRate": "സ്വത്ത് മൂല്യ വർദ്ധന (%)",
    "rvb.monthlyRent": "മാസ വാടക",
    "rvb.rentIncrease": "വാർഷിക വാടക വർദ്ധന (%)",
    "rvb.mfReturn": "മ്യൂച്ചൽ ഫണ്ട് റിട്ടേൺ (%)",
    "rvb.opportunityCostNote": "അവസര ചെലവ്: ഡൗൺ പേയ്മെന്റ് നിക്ഷേപിച്ചിരുന്നെങ്കിൽ",
    "rvb.investedInMF": "മ്യൂച്ചൽ ഫണ്ടിൽ നിക്ഷേപിച്ചത്",
    "rvb.hraNote": "ഓർക്കുക: HRA ഇളവ് പഴയ വ്യവസ്ഥയിൽ മാത്രം",
    "rvb.monthlyEmi": "മാസ EMI",
    "rvb.recommendation": "ശുപാർശ",
    "rvb.buyingBetter": "വാങ്ങുന്നത് കൂടുതൽ സമ്പത്ത് സൃഷ്ടിക്കുന്നു",
    "rvb.rentingBetter": "വാടക നിങ്ങളുടെ സമ്പത്തിന് മികച്ചതാണ്",
    "rvb.wealthDiff": "സമ്പത്ത് വ്യത്യാസം",
    "rvb.breakeven": "ബ്രേക്ക്ഈവൻ വർഷം",
    "rvb.year": "വർഷം",
    "rvb.neverBreakeven": "വാങ്ങൽ ഒരിക്കലും പിടിക്കില്ല",
    "rvb.wealthComparison": "നെറ്റ് സമ്പത്ത് താരതമ്യം",
    "rvb.years": "വർഷം",
    "rvb.buyWealth": "വാങ്ങൽ: നെറ്റ് സമ്പത്ത്",
    "rvb.rentWealth": "വാടക + നിക്ഷേപം: നെറ്റ് സമ്പത്ത്",
    "rvb.taxRulesTitle": "2026 നികുതി നിയമങ്ങൾ പ്രയോഗിച്ചു",
    "rvb.taxRule1": "പഴയത്: സെക്ഷൻ 24b പലിശ കിഴിവ് ₹2L വരെ",
    "rvb.taxRule2": "പഴയത്: സെക്ഷൻ 80C മൂലധന കിഴിവ് ₹1.5L വരെ",
    "rvb.taxRule3": "പുതിയത്: സ്വന്തം സ്വത്തിന് കിഴിവുകളില്ല",
    "rvb.assumptions": "ഉപയോഗിച്ച അനുമാനങ്ങൾ",
    "rvb.assumption1": "സ്വത്ത് മൂല്യ വർദ്ധന",
    "rvb.assumption2": "വാടക വർദ്ധന",
    "rvb.assumption3": "MF റിട്ടേൺ (CAGR)",
    "rvb.purposeTitle": "ഈ ടൂൾ എന്താണ് ചെയ്യുന്നത്?",
    "rvb.purposeDesc": "ഈ ടൂൾ വീട് വാങ്ങുന്നതിന്റെ യഥാർത്ഥ ചെലവ് (EMI, സ്റ്റാമ്പ് ഡ്യൂട്ടി, പരിപാലനം) വാടകയ്ക്ക് താമസിച്ച് വ്യത്യാസം മ്യൂച്ച്വൽ ഫണ്ടിൽ നിക്ഷേപിക്കുന്നതുമായി താരതമ്യം ചെയ്യുന്നു.",
    "rvb.hiddenCosts": "മറഞ്ഞ ചെലവുകൾ (സ്റ്റാമ്പ് ഡ്യൂട്ടി, പരിപാലനം, നികുതി)",
    "rvb.stampDuty": "സ്റ്റാമ്പ് ഡ്യൂട്ടി",
    "rvb.registration": "രജിസ്ട്രേഷൻ",
    "rvb.wealthGapChart": "സമ്പത്ത് അന്തരം: വാങ്ങൽ vs വാടക + നിക്ഷേപം",
    "rvb.sathiVerdict": "സാഥിയുടെ വിധി",
    "rvb.sathiRentAdvice": "സാഥി പറയുന്നു: വാടക നിങ്ങൾക്ക് മാസം {AMT} ലാഭിക്കുന്നു. ഇത് SIPൽ നിക്ഷേപിക്കൂ!",
    "rvb.sathiBuyAdvice": "സാഥി പറയുന്നു: ഈ വിലയിൽ വാങ്ങുന്നത് മികച്ച ദീർഘകാല ആസ്തിയാണ്!",
    "rvb.totalCostBuy": "ഉടമസ്ഥതയുടെ മൊത്ത ചെലവ് (വാങ്ങൽ)",
    "rvb.rentInvestPath": "വാടക + നിക്ഷേപ പാത",
    "rvb.downPaymentStampInteriors": "ഡൗൺ പേയ്‌മെന്റ് + സ്റ്റാമ്പ് ഡ്യൂട്ടി + ഇന്റീരിയർ",
    "rvb.totalEmiPaid": "മൊത്തം EMI അടച്ചത്",
    "rvb.interestLabel": "പലിശ",
    "rvb.taxSavings": "നികുതി ലാഭം (സെക്ഷൻ 24b + 80C)",
    "rvb.totalOutOfPocket": "മൊത്തം പോക്കറ്റ് ചെലവ്",
    "rvb.propertyValueAtYear": "വർഷത്തിൽ സ്വത്ത് മൂല്യം",
    "rvb.totalRentPaid": "മൊത്തം വാടക അടച്ചത്",
    "rvb.downPaymentInvested": "ഡൗൺ പേയ്‌മെന്റ് നിക്ഷേപം",
    "rvb.indexFund": "ഇൻഡെക്സ് ഫണ്ട്",
    "rvb.monthlySavingsInvested": "പ്രതിമാസ സമ്പാദ്യ നിക്ഷേപം (EMI വ്യത്യാസം)",
    "rvb.autoCompounded": "ഓട്ടോ-കമ്പൗണ്ട്",
    "rvb.investmentCorpus": "വർഷത്തിൽ നിക്ഷേപ കോർപ്പസ്",
    "rvb.netWealth": "നെറ്റ് സമ്പത്ത് (കോർപ്പസ് − വാടക)",
    "rvb.shareWhatsApp": "WhatsAppൽ പങ്കിടുക",
    "rvb.saveResult": "ഫലം സേവ് ചെയ്യുക",
    "mac.investmentAmount": "നിക്ഷേപ തുക (₹)",
    "mac.frequency": "നിക്ഷേപ ആവൃത്തി",
    "mac.freqMonthly": "പ്രതിമാസം (SIP)",
    "mac.freqQuarterly": "ത്രൈമാസികം",
    "mac.freqHalfYearly": "അർദ്ധ-വാർഷികം",
    "mac.freqYearly": "വാർഷികം",
    "mac.chartTitle": "{amount} {frequency} നിക്ഷേപത്തിന്റെ {years} വർഷത്തെ വളർച്ച",
    "mac.yearlyTip": "വർഷത്തിൽ ഒരിക്കൽ വലിയ തുക നിക്ഷേപം vs എല്ലാ മാസവും ചെറിയ തുക — 'രൂപി കോസ്റ്റ് ആവറേജിംഗ്' കാരണം ഫലങ്ങൾ വ്യത്യസ്തമാകാം!",
  },
  pa: {
    "sidebar.dashboard": "ਡੈਸ਼ਬੋਰਡ",
    "sidebar.calculators": "ਨਿਵੇਸ਼ ਕੈਲਕੁਲੇਟਰ",
    "sidebar.compare": "ਉਤਪਾਦ ਤੁਲਨਾ",
    "sidebar.realEstate": "ਰੀਅਲ ਅਸਟੇਟ ਪਲੈਨਰ",
    "sidebar.tax": "ਟੈਕਸ ਸੈਂਟਰ",
    "sidebar.tagline": "ਸਮਾਰਟ ਵਿੱਤੀ ਟੂਲਸ",
    "sidebar.disclaimer": "ਸਿਰਫ਼ ਸਿੱਖਿਆ ਦੇ ਉਦੇਸ਼ ਲਈ। ਵਿੱਤੀ ਸਲਾਹ ਨਹੀਂ।",
    "sidebar.catDashboard": "ਡੈਸ਼ਬੋਰਡ",
    "sidebar.catHomeLife": "🏠 ਘਰ ਅਤੇ ਜੀਵਨ",
    "sidebar.catFuture": "🔮 ਭਵਿੱਖ",
    "sidebar.catSafety": "🛡️ ਸੁਰੱਖਿਆ",
    "sidebar.catInvest": "📈 ਨਿਵੇਸ਼",
    "sidebar.catBanking": "🏦 ਬੈਂਕਿੰਗ",
    "sidebar.catAccount": "ਖਾਤਾ",
    "sidebar.desc.dashboard": "ਇੱਕ ਝਲਕ ਵਿੱਚ ਤੁਹਾਡਾ ਵਿੱਤੀ ਸਾਰ",
    "sidebar.desc.calculators": "SIP, ਇੱਕਮੁਸ਼ਤ ਅਤੇ ਚੱਕਰਵਿੱਧੀ ਵਿਆਜ ਟੂਲ",
    "sidebar.desc.compare": "ਵਿੱਤੀ ਉਤਪਾਦਾਂ ਦੀ ਨਾਲੋ ਨਾਲ ਤੁਲਨਾ",
    "sidebar.desc.realEstate": "ਜਾਇਦਾਦ ਖਰੀਦ ਅਤੇ ROI ਯੋਜਨਾ",
    "sidebar.desc.tax": "ਪੁਰਾਣੀ ਅਤੇ ਨਵੀਂ ਟੈਕਸ ਪ੍ਰਣਾਲੀ ਵਿੱਚ ਬਚਤ ਅਨੁਕੂਲ ਬਣਾਓ",
    "sidebar.desc.retirement": "ਰਿਟਾਇਰਮੈਂਟ ਕਾਰਪਸ ਅਤੇ SIP ਗਣਨਾ",
    "sidebar.desc.rentVsBuy": "ਕਿਰਾਏ ਜਾਂ ਖਰੀਦ — ਕੀ ਬਿਹਤਰ ਹੈ ਜਾਣੋ",
    "sidebar.desc.debtKiller": "ਤੇਜ਼ੀ ਨਾਲ ਕਰਜ਼ ਮੁਕਤ ਹੋਣ ਦੀ ਯੋਜਨਾ",
    "sidebar.desc.stockResearch": "ਲਾਈਵ ਬਾਜ਼ਾਰ ਡੇਟਾ ਅਤੇ ਸਟਾਕ ਵਿਸ਼ਲੇਸ਼ਣ",
    "sidebar.desc.emiMentor": "ਜਾਂਚੋ ਕਿ ਕੀ ਤੁਸੀਂ ਸੱਚਮੁੱਚ ਖਰੀਦ ਸਕਦੇ ਹੋ",
    "sidebar.desc.childFuture": "ਬੱਚੇ ਦੀ ਸਿੱਖਿਆ ਅਤੇ ਭਵਿੱਖ ਖਰਚਿਆਂ ਦੀ ਯੋਜਨਾ",
    "sidebar.desc.creditCard": "ਕ੍ਰੈਡਿਟ ਕਾਰਡ ਕਰਜ਼ੇ ਦੀ ਅਸਲ ਲਾਗਤ ਸਮਝੋ",
    "sidebar.desc.assetComparison": "ਵੱਖ-ਵੱਖ ਸੰਪੱਤੀ ਸ਼੍ਰੇਣੀਆਂ ਵਿੱਚ ਰਿਟਰਨ ਤੁਲਨਾ",
    "sidebar.desc.profile": "ਆਪਣੀ ਵਿੱਤੀ ਪ੍ਰੋਫਾਈਲ ਪ੍ਰਬੰਧਿਤ ਕਰੋ",
    "sidebar.desc.dataRights": "ਤੁਹਾਡੀ ਡੇਟਾ ਗੋਪਨੀਯਤਾ ਅਤੇ ਡਾਊਨਲੋਡ ਵਿਕਲਪ",
    "dash.title": "ਡੈਸ਼ਬੋਰਡ",
    "dash.subtitle": "ਤੁਹਾਡੀ ਵਿੱਤੀ ਸਥਿਤੀ ਇੱਕ ਨਜ਼ਰ ਵਿੱਚ",
    "dash.portfolioValue": "ਪੋਰਟਫੋਲੀਓ ਮੁੱਲ",
    "dash.monthlySip": "ਮਹੀਨਾਵਾਰ SIP",
    "dash.taxSaved": "ਟੈਕਸ ਬੱਚਤ (FY25)",
    "dash.returns1y": "ਮੁਨਾਫ਼ਾ (1 ਸਾਲ)",
    "dash.portfolioGrowth": "ਪੋਰਟਫੋਲੀਓ ਵਾਧਾ",
    "dash.last9months": "ਪਿਛਲੇ 9 ਮਹੀਨੇ",
    "dash.quickActions": "ਤੇਜ਼ ਕਾਰਵਾਈਆਂ",
    "dash.sipCalc": "SIP ਕੈਲਕੁਲੇਟਰ",
    "dash.compareMF": "ਮਿਊਚੁਅਲ ਫੰਡ ਤੁਲਨਾ",
    "dash.emiEstimator": "EMI ਅੰਦਾਜ਼ਾ",
    "dash.taxOptimizer": "ਟੈਕਸ ਪਲਾਨਰ",
    "dash.marketSnapshot": "ਬਾਜ਼ਾਰ ਸਨੈਪਸ਼ਾਟ",
    "dash.active": "ਸਰਗਰਮ",
    "dash.vikasScore": "ਵਿਕਾਸ ਯਾਤਰਾ",
    "dash.totalAssets": "ਕੁੱਲ ਜਾਇਦਾਦ",
    "dash.totalLiabilities": "ਕੁੱਲ ਦੇਣਦਾਰੀਆਂ",
    "dash.netInvestment": "ਸ਼ੁੱਧ ਨਿਵੇਸ਼",
    "dash.dtiRatio": "DTI ਅਨੁਪਾਤ",
    "dash.savingsInvestments": "ਬੱਚਤ + ਨਿਵੇਸ਼",
    "dash.annualEmi": "ਸਾਲਾਨਾ EMI ਜ਼ਿੰਮੇਵਾਰੀਆਂ",
    "dash.monthlyWealth": "ਮਹੀਨਾਵਾਰ ਧਨ ਨਿਰਮਾਣ",
    "dash.financialPulse": "ਵਿੱਤੀ ਪਲਸ",
    "dash.assetsVsLiabilities": "ਜਾਇਦਾਦ vs ਦੇਣਦਾਰੀ — 12 ਮਹੀਨੇ ਦਾ ਅਨੁਮਾਨ",
    "dash.dailySathiTip": "ਅੱਜ ਦੀ ਸਾਥੀ ਟਿੱਪ",
    "dash.goalTracker": "ਟੀਚਾ ਟ੍ਰੈਕਰ",
    "dash.miniTools": "ਮਿੰਨੀ ਟੂਲ — ਤੇਜ਼ ਪੈਸੇ ਹੈਕ",
    "dash.welcome": "ਜੀ ਆਇਆਂ ਨੂੰ",
    "dash.welcomeSub": "ਅੱਜ ਦਾ ਤੁਹਾਡਾ ਵਿੱਤੀ ਸਾਰ ਇੱਥੇ ਹੈ।",
    "dash.editProfile": "ਪ੍ਰੋਫਾਈਲ ਸੰਪਾਦਿਤ ਕਰੋ",
    "dash.user": "ਉਪਭੋਗਤਾ",
    "ai.insightTitle": "AI ਸੂਝ",
    "ai.sipAdvice": "ਤੁਹਾਡੀ {income} ਆਮਦਨ ਦੇ ਆਧਾਰ 'ਤੇ, ਤੁਹਾਡੇ {goal} ਤੇਜ਼ ਕਰਨ ਲਈ {sip}/ਮਹੀਨਾ SIP ਸ਼ੁਰੂ ਕਰਨ ਦੀ ਸਿਫ਼ਾਰਿਸ਼ ਕਰਦੇ ਹਾਂ।",
    "ai.dtiWarning": "ਤੁਹਾਡਾ EMI ਬੋਝ {dti}% ਹੈ — ਵਿਆਜ ਘਟਾਉਣ ਲਈ ਬੈਲੰਸ ਟ੍ਰਾਂਸਫਰ ਬਾਰੇ ਸੋਚੋ।",
    "ai.financialGoal": "ਵਿੱਤੀ ਟੀਚਾ",
    "action.title": "ਕਾਰਵਾਈ ਸੂਚੀ",
    "action.completeProfile": "ਆਪਣੀ ਵਿੱਤੀ ਪ੍ਰੋਫਾਈਲ ਪੂਰੀ ਕਰੋ",
    "action.setup": "ਸੈੱਟਅੱਪ",
    "action.runTax": "ਟੈਕਸ ਔਪਟੀਮਾਈਜ਼ੇਸ਼ਨ ਚਲਾਓ",
    "action.open": "ਖੋਲ੍ਹੋ",
    "action.rentVsBuy": "ਕਿਰਾਇਆ ਬਨਾਮ ਖਰੀਦ ਵਿਸ਼ਲੇਸ਼ਣ",
    "action.analyze": "ਵਿਸ਼ਲੇਸ਼ਣ",
    "goal.buyHome": "ਘਰ ਖਰੀਦੋ",
    "goal.payDebt": "ਕਰਜ਼ਾ ਚੁਕਾਓ",
    "goal.retireEarly": "ਜਲਦੀ ਰਿਟਾਇਰ",
    "goal.emergency": "ਐਮਰਜੈਂਸੀ ਫੰਡ",
    "goal.childEdu": "ਬੱਚੇ ਦੀ ਸਿੱਖਿਆ",
    "goal.wealthGrowth": "ਧਨ ਵਧਾਓ",
    "goal.eta50plus": "50+ ਸਾਲ",
    "goal.achieved": "ਪ੍ਰਾਪਤ",
    "goal.basedOn": "ਅਧਾਰਿਤ",
    "goal.moSIP": "ਮਹੀਨਾਵਾਰ SIP",
    "goal.returns": "ਰਿਟਰਨ",
    "goal.saved": "ਬੱਚਤ",
    "goal.target": "ਟੀਚਾ",
    "goal.custom": "ਕਸਟਮ",
    "goal.complete": "ਪੂਰਾ",
    "goal.yrsToGo": "ਸਾਲ ਬਾਕੀ",
    "goal.sipNeeded": "ਟੀਚੇ ਤੱਕ SIP",
    "goal.yrs": "ਸਾਲ",
    "goal.month": "ਮਹੀਨਾ",
    "goal.save": "ਸੇਵ",
    "goal.reset": "ਰੀਸੈੱਟ",
    "goal.setCustomTarget": "ਕਸਟਮ ਟੀਚਾ ਸੈੱਟ ਕਰੋ",
    "goal.noGoals": "ਕੋਈ ਟੀਚਾ ਸੈੱਟ ਨਹੀਂ। ਟ੍ਰੈਕ ਕਰਨ ਲਈ ਪ੍ਰੋਫਾਈਲ ਅੱਪਡੇਟ ਕਰੋ।",
    "goal.enterValid": "ਸਹੀ ਰਕਮ ਦਰਜ ਕਰੋ",
    "goal.targetUpdated": "ਟੀਚਾ ਅੱਪਡੇਟ ਹੋਇਆ",
    "goal.resetDefault": "ਡਿਫਾਲਟ ਤੇ ਰੀਸੈੱਟ",
    "calc.title": "ਨਿਵੇਸ਼ ਕੈਲਕੁਲੇਟਰ",
    "calc.subtitle": "ਸਹੀ ਤਰੀਕੇ ਨਾਲ ਆਪਣੀ ਦੌਲਤ ਦੀ ਯੋਜਨਾ ਬਣਾਓ",
    "calc.sipCalc": "SIP ਕੈਲਕੁਲੇਟਰ",
    "calc.lumpSum": "ਇੱਕਮੁਸ਼ਤ",
    "calc.monthlyInvestment": "ਮਹੀਨਾਵਾਰ ਨਿਵੇਸ਼",
    "calc.investmentAmount": "ਨਿਵੇਸ਼ ਰਕਮ",
    "calc.expectedReturn": "ਅੰਦਾਜ਼ਨ ਮੁਨਾਫ਼ਾ (%)",
    "calc.timePeriod": "ਸਮਾਂ (ਸਾਲ)",
    "calc.yrs": "ਸਾਲ",
    "calc.totalValue": "ਕੁੱਲ ਮੁੱਲ",
    "calc.invested": "ਨਿਵੇਸ਼",
    "calc.gains": "ਮੁਨਾਫ਼ਾ",
    "compare.title": "ਉਤਪਾਦ ਤੁਲਨਾ",
    "compare.subtitle": "ਮਿਊਚੁਅਲ ਫੰਡ, FD ਅਤੇ ਬੀਮਾ ਯੋਜਨਾਵਾਂ ਦੀ ਤੁਲਨਾ ਕਰੋ",
    "compare.fundName": "ਫੰਡ ਨਾਮ",
    "compare.1yReturn": "1 ਸਾਲ ਮੁਨਾਫ਼ਾ",
    "compare.3yReturn": "3 ਸਾਲ ਮੁਨਾਫ਼ਾ",
    "compare.5yReturn": "5 ਸਾਲ ਮੁਨਾਫ਼ਾ",
    "compare.risk": "ਜੋਖ਼ਮ",
    "compare.expense": "ਖ਼ਰਚ",
    "compare.disclaimer": "* ਪਿਛਲੀ ਕਾਰਗੁਜ਼ਾਰੀ ਭਵਿੱਖ ਦੇ ਮੁਨਾਫ਼ੇ ਦੀ ਗਾਰੰਟੀ ਨਹੀਂ।",
    "compare.low": "ਘੱਟ",
    "compare.moderate": "ਦਰਮਿਆਨਾ",
    "compare.high": "ਜ਼ਿਆਦਾ",
    "compare.moderateHigh": "ਦਰਮਿਆਨਾ-ਜ਼ਿਆਦਾ",
    "re.title": "ਰੀਅਲ ਅਸਟੇਟ ਪਲੈਨਰ",
    "re.subtitle": "EMI ਕੈਲਕੁਲੇਟਰ ਅਤੇ ਪ੍ਰਾਪਰਟੀ ਯੋਜਨਾ ਟੂਲਸ",
    "re.propertyValue": "ਪ੍ਰਾਪਰਟੀ ਮੁੱਲ",
    "re.downPayment": "ਡਾਊਨ ਪੇਮੈਂਟ (%)",
    "re.interestRate": "ਵਿਆਜ ਦਰ (%)",
    "re.loanTenure": "ਲੋਨ ਅਵਧੀ (ਸਾਲ)",
    "re.monthlyEmi": "ਮਹੀਨਾਵਾਰ EMI",
    "re.loanAmount": "ਲੋਨ ਰਕਮ",
    "re.totalInterest": "ਕੁੱਲ ਵਿਆਜ",
    "re.totalPayment": "ਕੁੱਲ ਭੁਗਤਾਨ",
    "tax.title": "ਟੈਕਸ ਸੈਂਟਰ",
    "tax.subtitle": "ਭਾਰਤੀ ਟੈਕਸ ਕਾਨੂੰਨ ਅਧੀਨ ਟੈਕਸ ਬੱਚਤ ਦੀ ਯੋਜਨਾ ਬਣਾਓ",
    "tax.recommended": "ਸਿਫ਼ਾਰਸ਼",
    "tax.alternative": "ਬਦਲ",
    "tax.oldRegime": "ਪੁਰਾਣੀ ਟੈਕਸ ਰਿਜੀਮ",
    "tax.newRegime": "ਨਵੀਂ ਟੈਕਸ ਰਿਜੀਮ",
    "tax.estimatedTax": "ਅੰਦਾਜ਼ਨ ਟੈਕਸ",
    "tax.withDeductions": "₹2,41,000 ਕਟੌਤੀਆਂ ਨਾਲ",
    "tax.standardDeduction": "ਮਿਆਰੀ ਕਟੌਤੀ ₹75,000",
    "tax.section80c": "ਧਾਰਾ 80C – ₹1.5 ਲੱਖ",
    "tax.section80d": "ਧਾਰਾ 80D – ਸਿਹਤ ਬੀਮਾ",
    "tax.hraExemption": "HRA ਛੋਟ",
    "tax.elss": "ELSS ਮਿਊਚੁਅਲ ਫੰਡ",
    "tax.epf": "EPF ਯੋਗਦਾਨ",
    "tax.lifeInsurance": "ਜੀਵਨ ਬੀਮਾ ਪ੍ਰੀਮੀਅਮ",
    "tax.selfFamily": "ਸਵੈ ਅਤੇ ਪਰਿਵਾਰ ਪ੍ਰੀਮੀਅਮ",
    "tax.parentsPremium": "ਮਾਪਿਆਂ ਦਾ ਪ੍ਰੀਮੀਅਮ",
    "tax.rentPaid": "ਦਿੱਤਾ ਕਿਰਾਇਆ (ਸਾਲਾਨਾ)",
    "tax.exemptedAmount": "ਛੋਟ ਵਾਲੀ ਰਕਮ",
    "tax.invested": "ਨਿਵੇਸ਼",
    "tax.autoDeducted": "ਸਵੈ ਕਟੌਤੀ",
    "tax.paid": "ਭੁਗਤਾਨ",
    "tax.claimable": "ਦਾਅਵਾ ਯੋਗ",
    "tax.calculated": "ਗਣਨਾ",
    "sidebar.retirement": "ਰਿਟਾਇਰਮੈਂਟ ਕਲਾਕ",
    "re.step1Title": "ਬਜਟ",
    "re.step2Title": "ROI ਸਿਮੂਲੇਟਰ",
    "re.step3Title": "ਸਮਰੱਥਾ",
    "re.stampDuty": "ਸਟੈਂਪ ਡਿਊਟੀ",
    "re.registration": "ਰਜਿਸਟ੍ਰੇਸ਼ਨ",
    "re.gst": "GST",
    "re.totalCost": "ਕੁੱਲ ਜਾਇਦਾਦ ਖਰਚ",
    "re.nextStep": "ਅਗਲਾ ਕਦਮ",
    "re.back": "ਪਿੱਛੇ",
    "re.roiDesc": "ਤੁਲਨਾ: ਉਹੀ ਰਕਮ ਮਿਊਚੁਅਲ ਫੰਡ ਵਿੱਚ ਲਗਾਈ ਹੁੰਦੀ ਤਾਂ?",
    "re.realEstateInv": "ਰੀਅਲ ਅਸਟੇਟ ਨਿਵੇਸ਼",
    "re.mutualFundInv": "ਮਿਊਚੁਅਲ ਫੰਡ ਨਿਵੇਸ਼",
    "re.rentalYield": "ਕਿਰਾਏ ਦੀ ਉਪਜ",
    "re.appreciation": "ਮੁੱਲ ਵਾਧਾ",
    "re.propertyAfter20": "ਜਾਇਦਾਦ ਮੁੱਲ (20 ਸਾਲ)",
    "re.totalRentalIncome": "ਕੁੱਲ ਕਿਰਾਏ ਦੀ ਆਮਦਨ",
    "re.costDeductions": "ਖਰਚ ਕਟੌਤੀਆਂ",
    "re.netGain": "ਸ਼ੁੱਧ ਲਾਭ",
    "re.expectedReturn": "ਅਨੁਮਾਨਿਤ ਵਾਪਸੀ",
    "re.amountInvested": "ਨਿਵੇਸ਼ ਕੀਤੀ ਰਕਮ",
    "re.valueAfter20": "20 ਸਾਲ ਬਾਅਦ ਮੁੱਲ",
    "re.mfWins": "ਮਿਊਚੁਅਲ ਫੰਡ ਜਿੱਤਦਾ ਹੈ",
    "re.reWins": "ਰੀਅਲ ਅਸਟੇਟ ਜਿੱਤਦੀ ਹੈ",
    "re.summary": "ਸਾਰ",
    "re.minIncome": "ਘੱਟੋ-ਘੱਟ ਮਾਸਿਕ ਆਮਦਨ",
    "re.affordable": "ਇਹ ਜਾਇਦਾਦ ਤੁਹਾਡੇ ਲਈ ਕਿਫਾਇਤੀ ਹੈ!",
    "re.notAffordable": "ਇਹ ਜਾਇਦਾਦ ਤੁਹਾਡੇ ਬਜਟ ਤੋਂ ਬਾਹਰ ਹੋ ਸਕਦੀ ਹੈ.",
    "re.emiShouldBe": "EMI ਤੁਹਾਡੀ ਮਾਸਿਕ ਆਮਦਨ ਦੇ 40% ਤੋਂ ਘੱਟ ਹੋਣੀ ਚਾਹੀਦੀ ਹੈ.",
    "re.nextSteps": "ਅਗਲੇ ਕਦਮ ਗਾਈਡ",
    "re.step1Tip": "2-3 ਬੈਂਕਾਂ ਤੋਂ ਹੋਮ ਲੋਨ ਪ੍ਰੀ-ਅਪ੍ਰੂਵਲ ਲਵੋ",
    "re.step2Tip": "ਜਾਇਦਾਦ ਦੀ RERA ਰਜਿਸਟ੍ਰੇਸ਼ਨ ਜਾਂਚੋ",
    "re.step3Tip": "ਇੰਟੀਰੀਅਰ ਅਤੇ ਸ਼ਿਫਟਿੰਗ ਖਰਚ (5-10%) ਬਜਟ ਕਰੋ",
    "re.step4Tip": "ਕਬਜ਼ੇ ਤੋਂ ਪਹਿਲਾਂ ਹੋਮ ਇੰਸ਼ੋਰੈਂਸ ਬਾਰੇ ਸੋਚੋ",
    "re.startOver": "ਦੁਬਾਰਾ ਸ਼ੁਰੂ ਕਰੋ",
    "re.step4Title": "ਕਰਜ਼ ਯੋਗਤਾ",
    "re.empType": "ਰੋਜ਼ਗਾਰ ਦੀ ਕਿਸਮ",
    "re.empSalaried": "ਤਨਖ਼ਾਹਦਾਰ",
    "re.empBusiness": "ਕਾਰੋਬਾਰ ਮਾਲਕ",
    "re.empFreelancer": "ਫ੍ਰੀਲਾਂਸਰ",
    "re.netSalary": "ਸ਼ੁੱਧ ਮਹੀਨਾਵਾਰ ਤਨਖ਼ਾਹ (₹)",
    "re.existingEmis": "ਮੌਜੂਦਾ EMI (₹)",
    "re.workExp": "ਕੰਮ ਦਾ ਤਜਰਬਾ",
    "re.maxAllowableEmi": "ਵੱਧ ਤੋਂ ਵੱਧ ਮਨਜ਼ੂਰ EMI",
    "re.maxLoanAmount": "ਵੱਧ ਤੋਂ ਵੱਧ ਕਰਜ਼ ਰਕਮ",
    "re.requiredLoan": "ਲੋੜੀਂਦੀ ਕਰਜ਼ ਰਕਮ",
    "re.calcBasis": "ਗਣਨਾ ਆਧਾਰ",
    "re.eligible": "ਯੋਗ — ਤੁਸੀਂ ਯੋਗ ਹੋ!",
    "re.borderline": "ਸੀਮਾ ਰੇਖਾ — ਹੱਦ ਦੇ ਨੇੜੇ",
    "re.notEligible": "ਅਯੋਗ — ਅੰਤਰ ਬਹੁਤ ਵੱਡਾ",
    "re.eligibleTip": "ਤੁਹਾਡਾ ਲੋੜੀਂਦਾ ਕਰਜ਼ ਤੁਹਾਡੀ ਯੋਗਤਾ ਵਿੱਚ ਹੈ। ਅਰਜ਼ੀ ਦੇਣ ਲਈ ਤਿਆਰ!",
    "re.borderlineTip": "ਹੱਦ ਵਧਾਉਣ ਲਈ ਪਤੀ/ਪਤਨੀ ਨੂੰ ਸਹ-ਅਰਜ਼ੀਦਾਰ ਵਜੋਂ ਜੋੜੋ.",
    "re.notEligibleTip": "ਡਾਊਨ ਪੇਮੈਂਟ ਵਧਾਓ ਜਾਂ ਪਹਿਲਾਂ ਮੌਜੂਦਾ ਕਰਜ਼ੇ ਚੁਕਾਓ.",
    "re.sathiLoanTip": "ਸਾਥੀ ਟਿਪ: ਬੈਂਕ ਸਥਿਰਤਾ ਪਸੰਦ ਕਰਦੇ ਹਨ। ਪਿਛਲੇ 3 ਮਹੀਨਿਆਂ ਵਿੱਚ ਨੌਕਰੀ ਬਦਲੀ ਹੈ ਤਾਂ, ਅਰਜ਼ੀ ਤੋਂ ਪਹਿਲਾਂ 3 ਤਨਖ਼ਾਹ ਸਲਿੱਪਾਂ ਦੀ ਉਡੀਕ ਕਰੋ!",
    "re.addCoApplicant": "ਸਹ-ਅਰਜ਼ੀਦਾਰ ਦੀ ਆਮਦਨ ਜੋੜੋ?",
    "re.coApplicantSalary": "ਸਹ-ਅਰਜ਼ੀਦਾਰ ਮਹੀਨਾਵਾਰ ਤਨਖ਼ਾਹ (₹)",
    "re.coApplicantNote": "ਪਤੀ/ਪਤਨੀ ਜਾਂ ਮਾਪਿਆਂ ਦੀ ਆਮਦਨ ਜੋੜਨ ਨਾਲ ਤੁਹਾਡੀ ਕਰਜ਼ ਯੋਗਤਾ ਕਾਫ਼ੀ ਵਧ ਸਕਦੀ ਹੈ!",
    "ret.title": "ਰਿਟਾਇਰਮੈਂਟ ਕਲਾਕ",
    "ret.subtitle": "ਮਹਿੰਗਾਈ-ਸਮਾਯੋਜਿਤ ਗਣਨਾਵਾਂ ਨਾਲ ਰਿਟਾਇਰਮੈਂਟ ਯੋਜਨਾ",
    "ret.monthlyExpense": "ਮੌਜੂਦਾ ਮਾਸਿਕ ਖਰਚ",
    "ret.currentAge": "ਮੌਜੂਦਾ ਉਮਰ",
    "ret.retireAge": "ਰਿਟਾਇਰਮੈਂਟ ਉਮਰ",
    "ret.inflationRate": "ਮਹਿੰਗਾਈ ਦਰ (%)",
    "ret.sipReturn": "ਅਨੁਮਾਨਿਤ SIP ਵਾਪਸੀ (%)",
    "ret.yearsLeft": "ਰਿਟਾਇਰਮੈਂਟ ਲਈ ਬਾਕੀ ਸਾਲ",
    "ret.futureCost": "ਭਵਿੱਖ ਦਾ ਮਾਸਿਕ ਖਰਚ",
    "ret.month": "ਮਹੀਨਾ",
    "ret.inflationNote": "ਮਹਿੰਗਾਈ ਲਈ ਸਮਾਯੋਜਿਤ ਜੀਵਨ ਸ਼ੈਲੀ ਖਰਚ",
    "ret.corpusNeeded": "ਰਿਟਾਇਰਮੈਂਟ ਕਾਰਪਸ ਲੋੜੀਂਦਾ",
    "ret.sipNeeded": "ਮਾਸਿਕ SIP ਲੋੜੀਂਦੀ",
    "ret.sipNote": "ਆਰਾਮਦਾਇਕ ਰਿਟਾਇਰਮੈਂਟ ਲਈ ਅੱਜ ਹੀ ਇਹ SIP ਸ਼ੁਰੂ ਕਰੋ",
    "sidebar.rentVsBuy": "ਕਿਰਾਇਆ vs ਖਰੀਦ",
    "sidebar.debtKiller": "ਕਰਜ਼-ਮੁਕਤੀ ਮਾਰਗ",
    "sidebar.stockResearch": "ਸਟਾਕ ਖੋਜ",
    "sidebar.emiMentor": "ਕੀ ਮੈਂ ਇਹ ਖਰੀਦ ਸਕਦਾ ਹਾਂ?",
    "sidebar.lifeStages": "ਜੀਵਨ ਪੜਾਅ",
    "sidebar.creditCard": "ਕ੍ਰੈਡਿਟ ਕਾਰਡ ਮਾਸਟਰ",
    "sidebar.profile": "ਮੇਰੀ ਪ੍ਰੋਫਾਈਲ",
    "sidebar.childFuture": "ਬੱਚੇ ਦਾ ਭਵਿੱਖ ਪਲੈਨਰ",
    "sidebar.assetComparison": "ਸੰਪੱਤੀ ਤੁਲਨਾ",
    "sidebar.dataRights": "ਡੇਟਾ ਅਧਿਕਾਰ",
    "rvb.title": "ਕਿਰਾਇਆ vs ਖਰੀਦ ਵਿਸ਼ਲੇਸ਼ਕ",
    "rvb.subtitle": "ਕਿਰਾਏ 'ਤੇ ਰਹੋ ਜਾਂ ਖਰੀਦੋ? ਸਮੇਂ ਦੇ ਨਾਲ ਅਸਲ ਖਰਚੇ ਦੀ ਤੁਲਨਾ ਕਰੋ।",
    "rvb.cityPreset": "ਸ਼ਹਿਰ ਪ੍ਰੀਸੈੱਟ",
    "rvb.custom": "ਕਸਟਮ",
    "rvb.taxRegime": "ਟੈਕਸ ਪ੍ਰਣਾਲੀ",
    "rvb.oldRegime": "ਪੁਰਾਣੀ ਪ੍ਰਣਾਲੀ",
    "rvb.newRegime": "ਨਵੀਂ ਪ੍ਰਣਾਲੀ",
    "rvb.horizon": "ਵਿਸ਼ਲੇਸ਼ਣ ਸਮਾਂ",
    "rvb.yrs": "ਸਾਲ",
    "rvb.buyPath": "ਖਰੀਦ ਮਾਰਗ",
    "rvb.rentPath": "ਕਿਰਾਇਆ + ਨਿਵੇਸ਼ ਮਾਰਗ",
    "rvb.propertyPrice": "ਜਾਇਦਾਦ ਕੀਮਤ",
    "rvb.downPayment": "ਡਾਊਨ ਪੇਮੈਂਟ (%)",
    "rvb.loanRate": "ਕਰਜ਼ ਵਿਆਜ ਦਰ (%)",
    "rvb.loanTenure": "ਕਰਜ਼ ਮਿਆਦ (ਸਾਲ)",
    "rvb.interiors": "ਇੰਟੀਰੀਅਰ ਖਰਚਾ",
    "rvb.monthlyMaintenance": "ਮਹੀਨਾਵਾਰ ਰੱਖ-ਰਖਾਅ",
    "rvb.yearlyPropertyTax": "ਸਾਲਾਨਾ ਜਾਇਦਾਦ ਟੈਕਸ",
    "rvb.appreciationRate": "ਜਾਇਦਾਦ ਮੁੱਲ ਵਾਧਾ (%)",
    "rvb.monthlyRent": "ਮਹੀਨਾਵਾਰ ਕਿਰਾਇਆ",
    "rvb.rentIncrease": "ਸਾਲਾਨਾ ਕਿਰਾਇਆ ਵਾਧਾ (%)",
    "rvb.mfReturn": "ਮਿਊਚੁਅਲ ਫੰਡ ਰਿਟਰਨ (%)",
    "rvb.opportunityCostNote": "ਮੌਕੇ ਦੀ ਕੀਮਤ: ਡਾਊਨ ਪੇਮੈਂਟ ਨਿਵੇਸ਼ ਕੀਤਾ ਹੁੰਦਾ ਤਾਂ",
    "rvb.investedInMF": "ਮਿਊਚੁਅਲ ਫੰਡ ਵਿੱਚ ਨਿਵੇਸ਼",
    "rvb.hraNote": "ਯਾਦ ਰੱਖੋ: HRA ਛੋਟ ਸਿਰਫ਼ ਪੁਰਾਣੀ ਪ੍ਰਣਾਲੀ ਵਿੱਚ",
    "rvb.monthlyEmi": "ਮਹੀਨਾਵਾਰ EMI",
    "rvb.recommendation": "ਸਿਫਾਰਸ਼",
    "rvb.buyingBetter": "ਖਰੀਦਣਾ ਵੱਧ ਦੌਲਤ ਬਣਾਉਂਦਾ ਹੈ",
    "rvb.rentingBetter": "ਕਿਰਾਇਆ ਤੁਹਾਡੀ ਦੌਲਤ ਲਈ ਬਿਹਤਰ ਹੈ",
    "rvb.wealthDiff": "ਦੌਲਤ ਫ਼ਰਕ",
    "rvb.breakeven": "ਬ੍ਰੇਕਈਵਨ ਸਾਲ",
    "rvb.year": "ਸਾਲ",
    "rvb.neverBreakeven": "ਖਰੀਦ ਕਦੇ ਨਹੀਂ ਫੜ ਸਕਦੀ",
    "rvb.wealthComparison": "ਸ਼ੁੱਧ ਦੌਲਤ ਤੁਲਨਾ",
    "rvb.years": "ਸਾਲ",
    "rvb.buyWealth": "ਖਰੀਦ: ਸ਼ੁੱਧ ਦੌਲਤ",
    "rvb.rentWealth": "ਕਿਰਾਇਆ + ਨਿਵੇਸ਼: ਸ਼ੁੱਧ ਦੌਲਤ",
    "rvb.taxRulesTitle": "2026 ਟੈਕਸ ਨਿਯਮ ਲਾਗੂ",
    "rvb.taxRule1": "ਪੁਰਾਣੀ: ਧਾਰਾ 24b ਵਿਆਜ ਕਟੌਤੀ ₹2L ਤੱਕ",
    "rvb.taxRule2": "ਪੁਰਾਣੀ: ਧਾਰਾ 80C ਮੂਲ ਕਟੌਤੀ ₹1.5L ਤੱਕ",
    "rvb.taxRule3": "ਨਵੀਂ: ਸਵੈ-ਨਿਵਾਸ ਜਾਇਦਾਦ ਲਈ ਕੋਈ ਕਟੌਤੀ ਨਹੀਂ",
    "rvb.assumptions": "ਵਰਤੀਆਂ ਧਾਰਨਾਵਾਂ",
    "rvb.assumption1": "ਜਾਇਦਾਦ ਮੁੱਲ ਵਾਧਾ",
    "rvb.assumption2": "ਕਿਰਾਇਆ ਵਾਧਾ",
    "rvb.assumption3": "MF ਰਿਟਰਨ (CAGR)",
    "rvb.purposeTitle": "ਇਹ ਟੂਲ ਕੀ ਕਰਦਾ ਹੈ?",
    "rvb.purposeDesc": "ਇਹ ਟੂਲ ਘਰ ਖਰੀਦਣ ਦੀ ਅਸਲ ਲਾਗਤ (EMI, ਸਟੈਂਪ ਡਿਊਟੀ, ਰੱਖ-ਰਖਾਅ) ਨੂੰ ਕਿਰਾਏ 'ਤੇ ਰਹਿ ਕੇ ਫਰਕ ਮਿਊਚਲ ਫੰਡ ਵਿੱਚ ਨਿਵੇਸ਼ ਕਰਨ ਨਾਲ ਤੁਲਨਾ ਕਰਦਾ ਹੈ।",
    "rvb.hiddenCosts": "ਲੁਕੇ ਹੋਏ ਖਰਚੇ (ਸਟੈਂਪ ਡਿਊਟੀ, ਰੱਖ-ਰਖਾਅ, ਟੈਕਸ)",
    "rvb.stampDuty": "ਸਟੈਂਪ ਡਿਊਟੀ",
    "rvb.registration": "ਰਜਿਸਟ੍ਰੇਸ਼ਨ",
    "rvb.wealthGapChart": "ਦੌਲਤ ਅੰਤਰ: ਖਰੀਦ vs ਕਿਰਾਇਆ + ਨਿਵੇਸ਼",
    "rvb.sathiVerdict": "ਸਾਥੀ ਦਾ ਫੈਸਲਾ",
    "rvb.sathiRentAdvice": "ਸਾਥੀ ਕਹਿੰਦਾ ਹੈ: ਕਿਰਾਇਆ ਤੁਹਾਨੂੰ ਮਹੀਨੇ ਵਿੱਚ {AMT} ਬਚਾ ਰਿਹਾ ਹੈ। ਇਸ ਨੂੰ SIP ਵਿੱਚ ਨਿਵੇਸ਼ ਕਰੋ!",
    "rvb.sathiBuyAdvice": "ਸਾਥੀ ਕਹਿੰਦਾ ਹੈ: ਇਸ ਕੀਮਤ 'ਤੇ ਖਰੀਦਣਾ ਵਧੀਆ ਲੰਬੇ ਸਮੇਂ ਦੀ ਜਾਇਦਾਦ ਹੈ!",
    "rvb.totalCostBuy": "ਮਾਲਕੀ ਦੀ ਕੁੱਲ ਲਾਗਤ (ਖਰੀਦ)",
    "rvb.rentInvestPath": "ਕਿਰਾਇਆ + ਨਿਵੇਸ਼ ਰਾਹ",
    "rvb.downPaymentStampInteriors": "ਡਾਊਨ ਪੇਮੈਂਟ + ਸਟੈਂਪ ਡਿਊਟੀ + ਇੰਟੀਰੀਅਰ",
    "rvb.totalEmiPaid": "ਕੁੱਲ EMI ਅਦਾ",
    "rvb.interestLabel": "ਵਿਆਜ",
    "rvb.taxSavings": "ਟੈਕਸ ਬਚਤ (ਧਾਰਾ 24b + 80C)",
    "rvb.totalOutOfPocket": "ਕੁੱਲ ਜੇਬ ਤੋਂ ਖਰਚ",
    "rvb.propertyValueAtYear": "ਸਾਲ ਵਿੱਚ ਜਾਇਦਾਦ ਮੁੱਲ",
    "rvb.totalRentPaid": "ਕੁੱਲ ਕਿਰਾਇਆ ਅਦਾ",
    "rvb.downPaymentInvested": "ਡਾਊਨ ਪੇਮੈਂਟ ਨਿਵੇਸ਼",
    "rvb.indexFund": "ਇੰਡੈਕਸ ਫੰਡ",
    "rvb.monthlySavingsInvested": "ਮਹੀਨਾਵਾਰ ਬਚਤ ਨਿਵੇਸ਼ (EMI ਫਰਕ)",
    "rvb.autoCompounded": "ਆਟੋ-ਕੰਪਾਊਂਡ",
    "rvb.investmentCorpus": "ਸਾਲ ਵਿੱਚ ਨਿਵੇਸ਼ ਕੋਸ਼",
    "rvb.netWealth": "ਸ਼ੁੱਧ ਦੌਲਤ (ਕੋਸ਼ − ਕਿਰਾਇਆ)",
    "rvb.shareWhatsApp": "WhatsApp 'ਤੇ ਸਾਂਝਾ ਕਰੋ",
    "rvb.saveResult": "ਨਤੀਜਾ ਸੁਰੱਖਿਅਤ ਕਰੋ",
    "mac.investmentAmount": "ਨਿਵੇਸ਼ ਰਕਮ (₹)",
    "mac.frequency": "ਨਿਵੇਸ਼ ਬਾਰੰਬਾਰਤਾ",
    "mac.freqMonthly": "ਮਹੀਨਾਵਾਰ (SIP)",
    "mac.freqQuarterly": "ਤਿਮਾਹੀ",
    "mac.freqHalfYearly": "ਅਰਧ-ਸਾਲਾਨਾ",
    "mac.freqYearly": "ਸਾਲਾਨਾ",
    "mac.chartTitle": "{amount} {frequency} ਨਿਵੇਸ਼ ਦੀ {years} ਸਾਲਾਂ ਵਿੱਚ ਵਾਧਾ",
    "mac.yearlyTip": "ਸਾਲ ਵਿੱਚ ਇੱਕ ਵਾਰ ਵੱਡੀ ਰਕਮ ਨਿਵੇਸ਼ ਬਨਾਮ ਹਰ ਮਹੀਨੇ ਛੋਟੀ ਰਕਮ — 'ਰੁਪੀ ਕੌਸਟ ਔਸਤ' ਕਾਰਨ ਨਤੀਜੇ ਵੱਖ ਹੋ ਸਕਦੇ ਹਨ!",
  },
  or: {
    "sidebar.dashboard": "ଡ୍ୟାସବୋର୍ଡ",
    "sidebar.calculators": "ନିବେଶ କ୍ୟାଲ୍କୁଲେଟର",
    "sidebar.compare": "ଉତ୍ପାଦ ତୁଳନା",
    "sidebar.realEstate": "ରିଆଲ ଇଷ୍ଟେଟ ପ୍ଲାନର",
    "sidebar.tax": "ଟ୍ୟାକ୍ସ ସେଣ୍ଟର",
    "sidebar.tagline": "ସ୍ମାର୍ଟ ଆର୍ଥିକ ଟୁଲ୍ସ",
    "sidebar.disclaimer": "କେବଳ ଶିକ୍ଷାମୂଳକ ଉଦ୍ଦେଶ୍ୟ ପାଇଁ। ଆର୍ଥିକ ପରାମର୍ଶ ନୁହେଁ।",
    "sidebar.catDashboard": "ଡ୍ୟାସବୋର୍ଡ",
    "sidebar.catHomeLife": "🏠 ଘର ଓ ଜୀବନ",
    "sidebar.catFuture": "🔮 ଭବିଷ୍ୟତ",
    "sidebar.catSafety": "🛡️ ସୁରକ୍ଷା",
    "sidebar.catInvest": "📈 ନିବେଶ",
    "sidebar.catBanking": "🏦 ବ୍ୟାଙ୍କିଂ",
    "sidebar.catAccount": "ଖାତା",
    "sidebar.desc.dashboard": "ଏକ ଦୃଷ୍ଟିରେ ଆପଣଙ୍କ ଆର୍ଥିକ ସାରାଂଶ",
    "sidebar.desc.calculators": "SIP, ଏକକାଳୀନ ଓ ଚକ୍ରବୃଦ୍ଧି ସୁଧ ସାଧନ",
    "sidebar.desc.compare": "ଆର୍ଥିକ ଉତ୍ପାଦଗୁଡିକୁ ପାଖାପାଖି ତୁଳନା କରନ୍ତୁ",
    "sidebar.desc.realEstate": "ସମ୍ପତ୍ତି କ୍ରୟ ଓ ROI ଯୋଜନା",
    "sidebar.desc.tax": "ପୁରୁଣା ଓ ନୂଆ କର ବ୍ୟବସ୍ଥାରେ ସଞ୍ଚୟ ଅପ୍ଟିମାଇଜ୍ କରନ୍ତୁ",
    "sidebar.desc.retirement": "ଅବସର ନିଧି ଓ SIP ଗଣନା କରନ୍ତୁ",
    "sidebar.desc.rentVsBuy": "ଭଡ଼ା ବା କ୍ରୟ — କେଉଁଟି ଭଲ ଜାଣନ୍ତୁ",
    "sidebar.desc.debtKiller": "ଶୀଘ୍ର ଋଣମୁକ୍ତ ହେବାର ଯୋଜନା କରନ୍ତୁ",
    "sidebar.desc.stockResearch": "ଲାଇଭ ବଜାର ଡାଟା ଓ ଷ୍ଟକ ବିଶ୍ଲେଷଣ",
    "sidebar.desc.emiMentor": "ଆପଣ ସତରେ କିଣି ପାରିବେ କି ନାହିଁ ଯାଞ୍ଚ କରନ୍ତୁ",
    "sidebar.desc.childFuture": "ପିଲାର ଶିକ୍ଷା ଓ ଭବିଷ୍ୟତ ଖର୍ଚ୍ଚର ଯୋଜନା",
    "sidebar.desc.creditCard": "କ୍ରେଡିଟ୍ କାର୍ଡ ଋଣର ପ୍ରକୃତ ଖର୍ଚ୍ଚ ବୁଝନ୍ତୁ",
    "sidebar.desc.assetComparison": "ବିଭିନ୍ନ ଆସେଟ ଶ୍ରେଣୀରେ ରିଟର୍ଣ୍ଣ ତୁଳନା",
    "sidebar.desc.profile": "ଆପଣଙ୍କ ଆର୍ଥିକ ପ୍ରୋଫାଇଲ ପରିଚାଳନା କରନ୍ତୁ",
    "sidebar.desc.dataRights": "ଆପଣଙ୍କ ଡାଟା ଗୋପନୀୟତା ଓ ଡାଉନଲୋଡ ବିକଳ୍ପ",
    "dash.title": "ଡ୍ୟାସବୋର୍ଡ",
    "dash.subtitle": "ଆପଣଙ୍କ ଆର୍ଥିକ ସ୍ଥିତି ଏକ ନଜରରେ",
    "dash.portfolioValue": "ପୋର୍ଟଫୋଲିଓ ମୂଲ୍ୟ",
    "dash.monthlySip": "ମାସିକ SIP",
    "dash.taxSaved": "ଟ୍ୟାକ୍ସ ସଞ୍ଚୟ (FY25)",
    "dash.returns1y": "ରିଟର୍ଣ୍ଣ (1 ବର୍ଷ)",
    "dash.portfolioGrowth": "ପୋର୍ଟଫୋଲିଓ ବୃଦ୍ଧି",
    "dash.last9months": "ଗତ 9 ମାସ",
    "dash.quickActions": "ଶୀଘ୍ର କାର୍ଯ୍ୟ",
    "dash.sipCalc": "SIP କ୍ୟାଲ୍କୁଲେଟର",
    "dash.compareMF": "ମ୍ୟୁଚୁଆଲ ଫଣ୍ଡ ତୁଳନା",
    "dash.emiEstimator": "EMI ଅନୁମାନ",
    "dash.taxOptimizer": "ଟ୍ୟାକ୍ସ ପ୍ଲାନର୍",
    "dash.marketSnapshot": "ବଜାର ସ୍ନାପଶଟ",
    "dash.active": "ସକ୍ରିୟ",
    "dash.vikasScore": "ବିକାଶ ଯାତ୍ରା",
    "dash.totalAssets": "ମୋଟ ସମ୍ପତ୍ତି",
    "dash.totalLiabilities": "ମୋଟ ଦାୟିତ୍ୱ",
    "dash.netInvestment": "ନିଟ ବିନିଯୋଗ",
    "dash.dtiRatio": "DTI ଅନୁପାତ",
    "dash.savingsInvestments": "ସଞ୍ଚୟ + ବିନିଯୋଗ",
    "dash.annualEmi": "ବାର୍ଷିକ EMI ଦାୟିତ୍ୱ",
    "dash.monthlyWealth": "ମାସିକ ସମ୍ପଦ ନିର୍ମାଣ",
    "dash.financialPulse": "ଆର୍ଥିକ ପଲ୍ସ",
    "dash.assetsVsLiabilities": "ସମ୍ପତ୍ତି vs ଦାୟିତ୍ୱ — ୧୨ ମାସର ଅନୁମାନ",
    "dash.dailySathiTip": "ଆଜିର ସାଥୀ ଟିପ୍",
    "dash.goalTracker": "ଲକ୍ଷ୍ୟ ଟ୍ରାକର",
    "dash.miniTools": "ମିନି ଟୁଲ୍ସ — ଦ୍ରୁତ ଟଙ୍କା ହ୍ୟାକ୍ସ",
    "dash.welcome": "ସ୍ୱାଗତ",
    "dash.welcomeSub": "ଆଜିର ଆପଣଙ୍କ ଆର୍ଥିକ ସାରାଂଶ ଏଠାରେ.",
    "dash.editProfile": "ପ୍ରୋଫାଇଲ ସଂପାଦନା",
    "dash.user": "ଉପଭୋକ୍ତା",
    "ai.insightTitle": "AI ଅନ୍ତର୍ଦୃଷ୍ଟି",
    "ai.sipAdvice": "ଆପଣଙ୍କ {income} ଆୟ ଆଧାରରେ, ଆପଣଙ୍କ {goal} ଶୀଘ୍ର କରିବା ପାଇଁ {sip}/ମାସ SIP ଆରମ୍ଭ କରିବାକୁ ପରାମର୍ଶ ଦେଉଛୁ.",
    "ai.dtiWarning": "ଆପଣଙ୍କ EMI ଭାର {dti}% — ସୁଧ କମାଇବାକୁ ବ୍ୟାଲାନ୍ସ ଟ୍ରାନ୍ସଫର୍ ବିଚାର କରନ୍ତୁ.",
    "ai.financialGoal": "ଆର୍ଥିକ ଲକ୍ଷ୍ୟ",
    "action.title": "କାର୍ଯ୍ୟ ତାଲିକା",
    "action.completeProfile": "ଆପଣଙ୍କ ଆର୍ଥିକ ପ୍ରୋଫାଇଲ୍ ସମ୍ପୂର୍ଣ୍ଣ କରନ୍ତୁ",
    "action.setup": "ସେଟଅପ୍",
    "action.runTax": "ଟ୍ୟାକ୍ସ ଅପ୍ଟିମାଇଜେସନ୍ ଚଲାନ୍ତୁ",
    "action.open": "ଖୋଲନ୍ତୁ",
    "action.rentVsBuy": "ଭଡ଼ା ବନାମ କ୍ରୟ ବିଶ୍ଲେଷଣ",
    "action.analyze": "ବିଶ୍ଲେଷଣ",
    "goal.buyHome": "ଘର କିଣନ୍ତୁ",
    "goal.payDebt": "ଋଣ ଶୋଧନ",
    "goal.retireEarly": "ଜଲ୍ଦି ଅବସର",
    "goal.emergency": "ଜରୁରୀ ନିଧି",
    "goal.childEdu": "ପିଲାଙ୍କ ଶିକ୍ଷା",
    "goal.wealthGrowth": "ସମ୍ପଦ ବୃଦ୍ଧି",
    "goal.eta50plus": "50+ ବର୍ଷ",
    "goal.achieved": "ହାସଲ",
    "goal.basedOn": "ଆଧାରରେ",
    "goal.moSIP": "ମାସିକ SIP",
    "goal.returns": "ରିଟର୍ନ",
    "goal.saved": "ସଞ୍ଚୟ",
    "goal.target": "ଲକ୍ଷ୍ୟ",
    "goal.custom": "କଷ୍ଟମ",
    "goal.complete": "ସମ୍ପୂର୍ଣ୍ଣ",
    "goal.yrsToGo": "ବର୍ଷ ବାକି",
    "goal.sipNeeded": "ଲକ୍ଷ୍ୟରେ ପହଞ୍ଚିବା SIP",
    "goal.yrs": "ବର୍ଷ",
    "goal.month": "ମାସ",
    "goal.save": "ସେଭ୍",
    "goal.reset": "ରିସେଟ",
    "goal.setCustomTarget": "କଷ୍ଟମ ଲକ୍ଷ୍ୟ ସେଟ୍",
    "goal.noGoals": "କୌଣସି ଲକ୍ଷ୍ୟ ସେଟ୍ ନାହିଁ। ଟ୍ରାକ୍ କରିବାକୁ ପ୍ରୋଫାଇଲ୍ ଅପଡେଟ କରନ୍ତୁ।",
    "goal.enterValid": "ସଠିକ ପରିମାଣ ଦିଅନ୍ତୁ",
    "goal.targetUpdated": "ଲକ୍ଷ୍ୟ ଅପଡେଟ ହେଲା",
    "goal.resetDefault": "ଡିଫଲ୍ଟକୁ ରିସେଟ",
    "calc.title": "ନିବେଶ କ୍ୟାଲ୍କୁଲେଟର",
    "calc.subtitle": "ସଠିକତାରେ ଆପଣଙ୍କ ସମ୍ପତ୍ତିର ଯୋଜନା କରନ୍ତୁ",
    "calc.sipCalc": "SIP କ୍ୟାଲ୍କୁଲେଟର",
    "calc.lumpSum": "ଏକକାଳୀନ",
    "calc.monthlyInvestment": "ମାସିକ ନିବେଶ",
    "calc.investmentAmount": "ନିବେଶ ରାଶି",
    "calc.expectedReturn": "ଆଶାନୁରୂପ ରିଟର୍ଣ୍ଣ (%)",
    "calc.timePeriod": "ସମୟ ଅବଧି (ବର୍ଷ)",
    "calc.yrs": "ବର୍ଷ",
    "calc.totalValue": "ମୋଟ ମୂଲ୍ୟ",
    "calc.invested": "ନିବେଶ",
    "calc.gains": "ଲାଭ",
    "compare.title": "ଉତ୍ପାଦ ତୁଳନା",
    "compare.subtitle": "ମ୍ୟୁଚୁଆଲ ଫଣ୍ଡ, FD ଏବଂ ବୀମା ଯୋଜନା ତୁଳନା କରନ୍ତୁ",
    "compare.fundName": "ଫଣ୍ଡ ନାମ",
    "compare.1yReturn": "1 ବର୍ଷ ରିଟର୍ଣ୍ଣ",
    "compare.3yReturn": "3 ବର୍ଷ ରିଟର୍ଣ୍ଣ",
    "compare.5yReturn": "5 ବର୍ଷ ରିଟର୍ଣ୍ଣ",
    "compare.risk": "ଝୁଙ୍କି",
    "compare.expense": "ଖର୍ଚ୍ଚ",
    "compare.disclaimer": "* ଅତୀତ କାର୍ଯ୍ୟଦକ୍ଷତା ଭବିଷ୍ୟତ ରିଟର୍ଣ୍ଣର ଗ୍ୟାରେଣ୍ଟି ଦିଏ ନାହିଁ।",
    "compare.low": "କମ",
    "compare.moderate": "ମଧ୍ୟମ",
    "compare.high": "ଅଧିକ",
    "compare.moderateHigh": "ମଧ୍ୟମ-ଅଧିକ",
    "re.title": "ରିଆଲ ଇଷ୍ଟେଟ ପ୍ଲାନର",
    "re.subtitle": "EMI କ୍ୟାଲ୍କୁଲେଟର ଏବଂ ସମ୍ପତ୍ତି ଯୋଜନା ଟୁଲ୍ସ",
    "re.propertyValue": "ସମ୍ପତ୍ତି ମୂଲ୍ୟ",
    "re.downPayment": "ଡାଉନ ପେମେଣ୍ଟ (%)",
    "re.interestRate": "ସୁଧ ହାର (%)",
    "re.loanTenure": "ଋଣ ଅବଧି (ବର୍ଷ)",
    "re.monthlyEmi": "ମାସିକ EMI",
    "re.loanAmount": "ଋଣ ରାଶି",
    "re.totalInterest": "ମୋଟ ସୁଧ",
    "re.totalPayment": "ମୋଟ ଭୁଗତାନ",
    "tax.title": "ଟ୍ୟାକ୍ସ ସେଣ୍ଟର",
    "tax.subtitle": "ଭାରତୀୟ ଟ୍ୟାକ୍ସ ଆଇନ ଅନୁସାରେ ଟ୍ୟାକ୍ସ ସଞ୍ଚୟ ଯୋଜନା କରନ୍ତୁ",
    "tax.recommended": "ସୁପାରିଶ",
    "tax.alternative": "ବିକଳ୍ପ",
    "tax.oldRegime": "ପୁରୁଣା ଟ୍ୟାକ୍ସ ରିଜିମ",
    "tax.newRegime": "ନୂଆ ଟ୍ୟାକ୍ସ ରିଜିମ",
    "tax.estimatedTax": "ଆନୁମାନିକ ଟ୍ୟାକ୍ସ",
    "tax.withDeductions": "₹2,41,000 କଟୌତି ସହ",
    "tax.standardDeduction": "ମାନକ କଟୌତି ₹75,000",
    "tax.section80c": "ଧାରା 80C – ₹1.5 ଲକ୍ଷ",
    "tax.section80d": "ଧାରା 80D – ସ୍ୱାସ୍ଥ୍ୟ ବୀମା",
    "tax.hraExemption": "HRA ଛାଡ଼",
    "tax.elss": "ELSS ମ୍ୟୁଚୁଆଲ ଫଣ୍ଡ",
    "tax.epf": "EPF ଯୋଗଦାନ",
    "tax.lifeInsurance": "ଜୀବନ ବୀମା ପ୍ରିମିୟମ",
    "tax.selfFamily": "ସ୍ୱୟଂ ଓ ପରିବାର ପ୍ରିମିୟମ",
    "tax.parentsPremium": "ପିତାମାତା ପ୍ରିମିୟମ",
    "tax.rentPaid": "ଦିଆଯାଇଥିବା ଭଡ଼ା (ବାର୍ଷିକ)",
    "tax.exemptedAmount": "ଛାଡ଼ ପ୍ରାପ୍ତ ରାଶି",
    "tax.invested": "ନିବେଶ",
    "tax.autoDeducted": "ସ୍ୱୟଂ କଟୌତି",
    "tax.paid": "ଭୁଗତାନ",
    "tax.claimable": "ଦାବି ଯୋଗ୍ୟ",
    "tax.calculated": "ଗଣନା",
    "sidebar.retirement": "ଅବସର ଘଣ୍ଟା",
    "re.step1Title": "ବଜେଟ",
    "re.step2Title": "ROI ସିମ୍ୟୁଲେଟର",
    "re.step3Title": "ସାମର୍ଥ୍ୟ",
    "re.stampDuty": "ଷ୍ଟାମ୍ପ ଡ୍ୟୁଟି",
    "re.registration": "ପଞ୍ଜୀକରଣ",
    "re.gst": "GST",
    "re.totalCost": "ମୋଟ ସମ୍ପତ୍ତି ଖର୍ଚ୍ଚ",
    "re.nextStep": "ପରବର୍ତ୍ତୀ ପଦକ୍ଷେପ",
    "re.back": "ପଛକୁ",
    "re.roiDesc": "ତୁଳନା: ସମାନ ରାଶି ମ୍ୟୁଚୁଆଲ ଫଣ୍ଡରେ ନିବେଶ କରିଥିଲେ?",
    "re.realEstateInv": "ରିଏଲ ଏଷ୍ଟେଟ ନିବେଶ",
    "re.mutualFundInv": "ମ୍ୟୁଚୁଆଲ ଫଣ୍ଡ ନିବେଶ",
    "re.rentalYield": "ଭଡ଼ା ଉପଜ",
    "re.appreciation": "ମୂଲ୍ୟ ବୃଦ୍ଧି",
    "re.propertyAfter20": "ସମ୍ପତ୍ତି ମୂଲ୍ୟ (20 ବର୍ଷ)",
    "re.totalRentalIncome": "ମୋଟ ଭଡ଼ା ଆୟ",
    "re.costDeductions": "ଖର୍ଚ୍ଚ କଟକଣା",
    "re.netGain": "ନିଟ ଲାଭ",
    "re.expectedReturn": "ଆଶାନୁରୂପ ରିଟର୍ନ",
    "re.amountInvested": "ନିବେଶ ରାଶି",
    "re.valueAfter20": "20 ବର୍ଷ ପରେ ମୂଲ୍ୟ",
    "re.mfWins": "ମ୍ୟୁଚୁଆଲ ଫଣ୍ଡ ଜିତେ",
    "re.reWins": "ରିଏଲ ଏଷ୍ଟେଟ ଜିତେ",
    "re.summary": "ସାରାଂଶ",
    "re.minIncome": "ସର୍ବନିମ୍ନ ମାସିକ ଆୟ",
    "re.affordable": "ଏହି ସମ୍ପତ୍ତି ଆପଣଙ୍କ ପାଇଁ ସାମର୍ଥ୍ୟ ମଧ୍ୟରେ!",
    "re.notAffordable": "ଏହି ସମ୍ପତ୍ତି ଆପଣଙ୍କ ବଜେଟ ବାହାରେ ହୋଇପାରେ.",
    "re.emiShouldBe": "EMI ଆପଣଙ୍କ ମାସିକ ଆୟର 40% ରୁ କମ ହେବା ଉଚିତ.",
    "re.nextSteps": "ପରବର୍ତ୍ତୀ ପଦକ୍ଷେପ ଗାଇଡ",
    "re.step1Tip": "2-3 ବ୍ୟାଙ୍କରୁ ହୋମ ଲୋନ ପ୍ରି-ଅପ୍ରୁଭାଲ ପାଆନ୍ତୁ",
    "re.step2Tip": "ସମ୍ପତ୍ତିର RERA ପଞ୍ଜୀକରଣ ଯାଞ୍ଚ କରନ୍ତୁ",
    "re.step3Tip": "ଇଣ୍ଟେରିଅର ଓ ସ୍ଥାନାନ୍ତର ଖର୍ଚ୍ଚ (5-10%) ବଜେଟ କରନ୍ତୁ",
    "re.step4Tip": "ଦଖଲ ପୂର୍ବରୁ ହୋମ ଇନ୍ସୁରାନ୍ସ ବିଚାର କରନ୍ତୁ",
    "re.startOver": "ପୁଣି ଆରମ୍ଭ କରନ୍ତୁ",
    "re.step4Title": "ଋଣ ଯୋଗ୍ୟତା",
    "re.empType": "କର୍ମସଂସ୍ଥାନ ପ୍ରକାର",
    "re.empSalaried": "ଦରମା ପାଉଥିବା",
    "re.empBusiness": "ବ୍ୟବସାୟ ମାଲିକ",
    "re.empFreelancer": "ଫ୍ରିଲାନ୍ସର",
    "re.netSalary": "ନିଟ ମାସିକ ଦରମା (₹)",
    "re.existingEmis": "ବିଦ୍ୟମାନ EMI (₹)",
    "re.workExp": "କାମ ଅନୁଭବ",
    "re.maxAllowableEmi": "ସର୍ବାଧିକ ଅନୁମୋଦିତ EMI",
    "re.maxLoanAmount": "ସର୍ବାଧିକ ଋଣ ରାଶି",
    "re.requiredLoan": "ଆବଶ୍ୟକ ଋଣ ରାଶି",
    "re.calcBasis": "ଗଣନା ଆଧାର",
    "re.eligible": "ଯୋଗ୍ୟ — ଆପଣ ଯୋଗ୍ୟ!",
    "re.borderline": "ସୀମାରେଖା — ସୀମା ନିକଟ",
    "re.notEligible": "ଅଯୋଗ୍ୟ — ଅନ୍ତର ବହୁତ ବଡ଼",
    "re.eligibleTip": "ଆପଣଙ୍କ ଆବଶ୍ୟକ ଋଣ ଆପଣଙ୍କ ଯୋଗ୍ୟତା ମଧ୍ୟରେ। ଆବେଦନ କରିବାକୁ ପ୍ରସ୍ତୁତ!",
    "re.borderlineTip": "ସୀମା ବଢ଼ାଇବା ପାଇଁ ସ୍ୱାମୀ/ସ୍ତ୍ରୀଙ୍କୁ ସହ-ଆବେଦକ ଭାବରେ ଯୋଡ଼ନ୍ତୁ.",
    "re.notEligibleTip": "ଡାଉନ ପେମେଣ୍ଟ ବଢ଼ାନ୍ତୁ ବା ପ୍ରଥମେ ବିଦ୍ୟମାନ ଋଣ ପରିଶୋଧ କରନ୍ତୁ.",
    "re.sathiLoanTip": "ସାଥୀ ଟିପ୍: ବ୍ୟାଙ୍କ ସ୍ଥିରତା ପସନ୍ଦ କରେ। ଗତ 3 ମାସରେ ଚାକିରୀ ବଦଳାଇଥିଲେ, ଆବେଦନ ପୂର୍ବରୁ 3 ଦରମା ସ୍ଲିପ୍ ପାଇଁ ଅପେକ୍ଷା କରନ୍ତୁ!",
    "re.addCoApplicant": "ସହ-ଆବେଦକଙ୍କ ଆୟ ଯୋଡ଼ନ୍ତୁ?",
    "re.coApplicantSalary": "ସହ-ଆବେଦକ ମାସିକ ଦରମା (₹)",
    "re.coApplicantNote": "ସ୍ୱାମୀ/ସ୍ତ୍ରୀ ବା ପିତାମାତାଙ୍କ ଆୟ ଯୋଡ଼ିଲେ ଆପଣଙ୍କ ଋଣ ଯୋଗ୍ୟତା ଯଥେଷ୍ଟ ବଢ଼ିପାରିବ!",
    "ret.title": "ଅବସର ଘଣ୍ଟା",
    "ret.subtitle": "ମୁଦ୍ରାସ୍ଫୀତି-ସମନ୍ୱିତ ଗଣନା ସହ ଅବସର ଯୋଜନା",
    "ret.monthlyExpense": "ବର୍ତ୍ତମାନ ମାସିକ ଖର୍ଚ୍ଚ",
    "ret.currentAge": "ବର୍ତ୍ତମାନ ବୟସ",
    "ret.retireAge": "ଅବସର ବୟସ",
    "ret.inflationRate": "ମୁଦ୍ରାସ୍ଫୀତି ହାର (%)",
    "ret.sipReturn": "ଆଶାନୁରୂପ SIP ରିଟର୍ନ (%)",
    "ret.yearsLeft": "ଅବସରରେ ବାକି ବର୍ଷ",
    "ret.futureCost": "ଭବିଷ୍ୟତର ମାସିକ ଖର୍ଚ୍ଚ",
    "ret.month": "ମାସ",
    "ret.inflationNote": "ମୁଦ୍ରାସ୍ଫୀତି ପାଇଁ ସମନ୍ୱିତ ଜୀବନଶୈଳୀ ଖର୍ଚ୍ଚ",
    "ret.corpusNeeded": "ଅବସର କର୍ପସ ଆବଶ୍ୟକ",
    "ret.sipNeeded": "ମାସିକ SIP ଆବଶ୍ୟକ",
    "ret.sipNote": "ଆରାମଦାୟକ ଅବସର ପାଇଁ ଆଜିଠାରୁ ଏହି SIP ଆରମ୍ଭ କରନ୍ତୁ",
    "sidebar.rentVsBuy": "ଭଡ଼ା vs କ୍ରୟ",
    "sidebar.debtKiller": "ଋଣ-ମୁକ୍ତି ପଥ",
    "sidebar.stockResearch": "ଷ୍ଟକ ରିସର୍ଚ",
    "sidebar.emiMentor": "ମୁଁ ଏହା କିଣି ପାରିବି କି?",
    "sidebar.lifeStages": "ଜୀବନ ପର୍ଯ୍ୟାୟ",
    "sidebar.creditCard": "କ୍ରେଡିଟ କାର୍ଡ ମାଷ୍ଟର",
    "sidebar.profile": "ମୋ ପ୍ରୋଫାଇଲ",
    "sidebar.childFuture": "ପିଲାର ଭବିଷ୍ୟତ ପ୍ଲାନର",
    "sidebar.assetComparison": "ଆସେଟ ତୁଳନା",
    "sidebar.dataRights": "ଡେଟା ଅଧିକାର",
    "rvb.title": "ଭଡ଼ା vs କ୍ରୟ ବିଶ୍ଳେଷକ",
    "rvb.subtitle": "ଭଡ଼ାରେ ରୁହନ୍ତୁ ନା କିଣନ୍ତୁ? ସମୟ ସହ ପ୍ରକୃତ ଖର୍ଚ୍ଚ ତୁଳନା କରନ୍ତୁ।",
    "rvb.cityPreset": "ସହର ପ୍ରିସେଟ୍",
    "rvb.custom": "କଷ୍ଟମ",
    "rvb.taxRegime": "କର ବ୍ୟବସ୍ଥା",
    "rvb.oldRegime": "ପୁରୁଣା ବ୍ୟବସ୍ଥା",
    "rvb.newRegime": "ନୂଆ ବ୍ୟବସ୍ଥା",
    "rvb.horizon": "ବିଶ୍ଳେଷଣ ଅବଧି",
    "rvb.yrs": "ବର୍ଷ",
    "rvb.buyPath": "କ୍ରୟ ପଥ",
    "rvb.rentPath": "ଭଡ଼ା + ବିନିଯୋଗ ପଥ",
    "rvb.propertyPrice": "ସମ୍ପତ୍ତି ମୂଲ୍ୟ",
    "rvb.downPayment": "ଡାଉନ୍ ପେମେଣ୍ଟ (%)",
    "rvb.loanRate": "ଋଣ ସୁଧ ହାର (%)",
    "rvb.loanTenure": "ଋଣ ଅବଧି (ବର୍ଷ)",
    "rvb.interiors": "ଇଣ୍ଟେରିଅର ଖର୍ଚ୍ଚ",
    "rvb.monthlyMaintenance": "ମାସିକ ରକ୍ଷଣାବେକ୍ଷଣ",
    "rvb.yearlyPropertyTax": "ବାର୍ଷିକ ସମ୍ପତ୍ତି କର",
    "rvb.appreciationRate": "ସମ୍ପତ୍ତି ମୂଲ୍ୟ ବୃଦ୍ଧି (%)",
    "rvb.monthlyRent": "ମାସିକ ଭଡ଼ା",
    "rvb.rentIncrease": "ବାର୍ଷିକ ଭଡ଼ା ବୃଦ୍ଧି (%)",
    "rvb.mfReturn": "ମ୍ୟୁଚୁଆଲ ଫଣ୍ଡ ରିଟର୍ନ (%)",
    "rvb.opportunityCostNote": "ସୁଯୋଗ ଖର୍ଚ୍ଚ: ଡାଉନ ପେମେଣ୍ଟ ବିନିଯୋଗ ହୋଇଥିଲେ",
    "rvb.investedInMF": "ମ୍ୟୁଚୁଆଲ ଫଣ୍ଡରେ ବିନିଯୋଗ",
    "rvb.hraNote": "ମନେ ରଖନ୍ତୁ: HRA ଛାଡ଼ କେବଳ ପୁରୁଣା ବ୍ୟବସ୍ଥାରେ",
    "rvb.monthlyEmi": "ମାସିକ EMI",
    "rvb.recommendation": "ସୁପାରିଶ",
    "rvb.buyingBetter": "କ୍ରୟ ଅଧିକ ସମ୍ପଦ ନିର୍ମାଣ କରେ",
    "rvb.rentingBetter": "ଭଡ଼ା ଆପଣଙ୍କ ସମ୍ପଦ ପାଇଁ ଭଲ",
    "rvb.wealthDiff": "ସମ୍ପଦ ପାର୍ଥକ୍ୟ",
    "rvb.breakeven": "ବ୍ରେକଇଭନ ବର୍ଷ",
    "rvb.year": "ବର୍ଷ",
    "rvb.neverBreakeven": "କ୍ରୟ ଆଉ ଧରିପାରେ ନାହିଁ",
    "rvb.wealthComparison": "ନିଟ ସମ୍ପଦ ତୁଳନା",
    "rvb.years": "ବର୍ଷ",
    "rvb.buyWealth": "କ୍ରୟ: ନିଟ ସମ୍ପଦ",
    "rvb.rentWealth": "ଭଡ଼ା + ବିନିଯୋଗ: ନିଟ ସମ୍ପଦ",
    "rvb.taxRulesTitle": "2026 କର ନିୟମ ଲାଗୁ",
    "rvb.taxRule1": "ପୁରୁଣା: ଧାରା 24b ସୁଧ କଟାଣି ₹2L ପର୍ଯ୍ୟନ୍ତ",
    "rvb.taxRule2": "ପୁରୁଣା: ଧାରା 80C ମୂଳ କଟାଣି ₹1.5L ପର୍ଯ୍ୟନ୍ତ",
    "rvb.taxRule3": "ନୂଆ: ସ୍ୱ-ନିବାସ ସମ୍ପତ୍ତି ପାଇଁ କୌଣସି କଟାଣି ନାହିଁ",
    "rvb.assumptions": "ବ୍ୟବହୃତ ଅନୁମାନ",
    "rvb.assumption1": "ସମ୍ପତ୍ତି ମୂଲ୍ୟ ବୃଦ୍ଧି",
    "rvb.assumption2": "ଭଡ଼ା ବୃଦ୍ଧି",
    "rvb.assumption3": "MF ରିଟର୍ନ (CAGR)",
    "rvb.purposeTitle": "ଏହି ଟୁଲ୍ କ'ଣ କରେ?",
    "rvb.purposeDesc": "ଏହି ଟୁଲ୍ ଘର କିଣିବାର ପ୍ରକୃତ ଖର୍ଚ୍ଚ (EMI, ଷ୍ଟାମ୍ପ ଡ୍ୟୁଟି, ରକ୍ଷଣାବେକ୍ଷଣ) କୁ ଭଡ଼ାରେ ରହି ପାର୍ଥକ୍ୟ ମ୍ୟୁଚୁଆଲ ଫଣ୍ଡରେ ନିବେଶ କରିବା ସହ ତୁଳନା କରେ।",
    "rvb.hiddenCosts": "ଲୁକ୍କାୟିତ ଖର୍ଚ୍ଚ (ଷ୍ଟାମ୍ପ ଡ୍ୟୁଟି, ରକ୍ଷଣାବେକ୍ଷଣ, କର)",
    "rvb.stampDuty": "ଷ୍ଟାମ୍ପ ଡ୍ୟୁଟି",
    "rvb.registration": "ନିବନ୍ଧନ",
    "rvb.wealthGapChart": "ସମ୍ପତ୍ତି ଅନ୍ତର: କିଣା vs ଭଡ଼ା + ନିବେଶ",
    "rvb.sathiVerdict": "ସାଥୀଙ୍କ ରାୟ",
    "rvb.sathiRentAdvice": "ସାଥୀ କହୁଛନ୍ତି: ଭଡ଼ା ଆପଣଙ୍କୁ ମାସରେ {AMT} ସଞ୍ଚୟ କରୁଛି। ଏହାକୁ SIPରେ ନିବେଶ କରନ୍ତୁ!",
    "rvb.sathiBuyAdvice": "ସାଥୀ କହୁଛନ୍ତି: ଏହି ମୂଲ୍ୟରେ କିଣିବା ଉତ୍ତମ ଦୀର୍ଘକାଳୀନ ସମ୍ପତ୍ତି!",
    "rvb.totalCostBuy": "ମାଲିକାନାର ମୋଟ ଖର୍ଚ୍ଚ (କିଣା)",
    "rvb.rentInvestPath": "ଭଡ଼ା + ନିବେଶ ପଥ",
    "rvb.downPaymentStampInteriors": "ଡାଉନ ପେମେଣ୍ଟ + ଷ୍ଟାମ୍ପ ଡ୍ୟୁଟି + ଇଣ୍ଟେରିଅର",
    "rvb.totalEmiPaid": "ମୋଟ EMI ଦେୟ",
    "rvb.interestLabel": "ସୁଧ",
    "rvb.taxSavings": "କର ସଞ୍ଚୟ (ଧାରା 24b + 80C)",
    "rvb.totalOutOfPocket": "ମୋଟ ପକେଟରୁ ଖର୍ଚ୍ଚ",
    "rvb.propertyValueAtYear": "ବର୍ଷରେ ସମ୍ପତ୍ତି ମୂଲ୍ୟ",
    "rvb.totalRentPaid": "ମୋଟ ଭଡ଼ା ଦେୟ",
    "rvb.downPaymentInvested": "ଡାଉନ ପେମେଣ୍ଟ ନିବେଶ",
    "rvb.indexFund": "ଇଣ୍ଡେକ୍ସ ଫଣ୍ଡ",
    "rvb.monthlySavingsInvested": "ମାସିକ ସଞ୍ଚୟ ନିବେଶ (EMI ପାର୍ଥକ୍ୟ)",
    "rvb.autoCompounded": "ଅଟୋ-କମ୍ପାଉଣ୍ଡ",
    "rvb.investmentCorpus": "ବର୍ଷରେ ନିବେଶ କୋଷ",
    "rvb.netWealth": "ନିଭ୍ୱଳ ସମ୍ପତ୍ତି (କୋଷ − ଭଡ଼ା)",
    "rvb.shareWhatsApp": "WhatsAppରେ ଶେୟାର କରନ୍ତୁ",
    "rvb.saveResult": "ଫଳାଫଳ ସଞ୍ଚୟ କରନ୍ତୁ",
    "mac.investmentAmount": "ନିବେଶ ରାଶି (₹)",
    "mac.frequency": "ନିବେଶ ଫ୍ରିକ୍ୱେନ୍ସି",
    "mac.freqMonthly": "ମାସିକ (SIP)",
    "mac.freqQuarterly": "ତ୍ରୈମାସିକ",
    "mac.freqHalfYearly": "ଅର୍ଦ୍ଧ-ବାର୍ଷିକ",
    "mac.freqYearly": "ବାର୍ଷିକ",
    "mac.chartTitle": "{amount} {frequency} ନିବେଶର {years} ବର୍ଷର ବୃଦ୍ଧି",
    "mac.yearlyTip": "ବର୍ଷରେ ଥରେ ବଡ଼ ରାଶି ନିବେଶ ବନାମ ପ୍ରତି ମାସ ଛୋଟ ରାଶି — 'ରୁପୀ କଷ୍ଟ ଏଭରେଜିଂ' କାରଣରୁ ଫଳାଫଳ ଭିନ୍ନ ହୋଇପାରେ!",
  },
  as: {
    "sidebar.dashboard": "ডেশ্বব'ৰ্ড",
    "sidebar.calculators": "বিনিয়োগ কেলকুলেটৰ",
    "sidebar.compare": "সামগ্ৰী তুলনা",
    "sidebar.realEstate": "ৰিয়েল এষ্টেট প্লেনাৰ",
    "sidebar.tax": "কৰ কেন্দ্ৰ",
    "sidebar.tagline": "স্মাৰ্ট বিত্তীয় সঁজুলি",
    "sidebar.disclaimer": "কেৱল শিক্ষামূলক উদ্দেশ্যে। বিত্তীয় পৰামৰ্শ নহয়।",
    "sidebar.catDashboard": "ডেশ্বব'ৰ্ড",
    "sidebar.catHomeLife": "🏠 ঘৰ আৰু জীৱন",
    "sidebar.catFuture": "🔮 ভৱিষ্যত",
    "sidebar.catSafety": "🛡️ সুৰক্ষা",
    "sidebar.catInvest": "📈 বিনিয়োগ",
    "sidebar.catBanking": "🏦 বেংকিং",
    "sidebar.catAccount": "একাউণ্ট",
    "sidebar.desc.dashboard": "এক চাৱনিত আপোনাৰ আৰ্থিক সাৰাংশ",
    "sidebar.desc.calculators": "SIP, একমুঠ আৰু চক্ৰবৃদ্ধি সুদৰ সঁজুলি",
    "sidebar.desc.compare": "আৰ্থিক সামগ্ৰীসমূহ কাষে কাষে তুলনা কৰক",
    "sidebar.desc.realEstate": "সম্পত্তি ক্ৰয় আৰু ROI পৰিকল্পনা",
    "sidebar.desc.tax": "পুৰণি আৰু নতুন কৰ ব্যৱস্থাত সঞ্চয় অপ্টিমাইজ কৰক",
    "sidebar.desc.retirement": "অৱসৰ নিধি আৰু SIP গণনা কৰক",
    "sidebar.desc.rentVsBuy": "ভাড়া নে ক্ৰয় — কোনটো ভাল জানক",
    "sidebar.desc.debtKiller": "সোনকালে ঋণমুক্ত হোৱাৰ পৰিকল্পনা কৰক",
    "sidebar.desc.stockResearch": "লাইভ বজাৰ ডাটা আৰু ষ্টক বিশ্লেষণ",
    "sidebar.desc.emiMentor": "আপুনি সঁচাকৈ কিনিব পাৰিব নে নাই পৰীক্ষা কৰক",
    "sidebar.desc.childFuture": "সন্তানৰ শিক্ষা আৰু ভৱিষ্যত খৰচৰ পৰিকল্পনা",
    "sidebar.desc.creditCard": "ক্ৰেডিট কাৰ্ড ঋণৰ প্ৰকৃত খৰচ বুজক",
    "sidebar.desc.assetComparison": "বিভিন্ন সম্পদ শ্ৰেণীত ৰিটাৰ্ণ তুলনা",
    "sidebar.desc.profile": "আপোনাৰ আৰ্থিক প্ৰ'ফাইল পৰিচালনা কৰক",
    "sidebar.desc.dataRights": "আপোনাৰ ডাটা গোপনীয়তা আৰু ডাউনলোড বিকল্প",
    "dash.title": "ডেশ্বব'ৰ্ড",
    "dash.subtitle": "আপোনাৰ বিত্তীয় স্থিতি এটা চকুতে",
    "dash.portfolioValue": "প'ৰ্টফলিঅ' মূল্য",
    "dash.monthlySip": "মাহিলী SIP",
    "dash.taxSaved": "কৰ ৰাহি (FY25)",
    "dash.returns1y": "লাভ (1 বছৰ)",
    "dash.portfolioGrowth": "প'ৰ্টফলিঅ' বৃদ্ধি",
    "dash.last9months": "যোৱা 9 মাহ",
    "dash.quickActions": "দ্ৰুত কাৰ্য",
    "dash.sipCalc": "SIP কেলকুলেটৰ",
    "dash.compareMF": "মিউচুৱেল ফাণ্ড তুলনা",
    "dash.emiEstimator": "EMI অনুমান",
    "dash.taxOptimizer": "কৰ অপ্টিমাইজাৰ",
    "dash.marketSnapshot": "বজাৰ স্নাপশ্বট",
    "dash.active": "সক্ৰিয়",
    "dash.vikasScore": "বিকাশ যাত্ৰা",
    "dash.totalAssets": "মুঠ সম্পত্তি",
    "dash.totalLiabilities": "মুঠ দায়",
    "dash.netInvestment": "নিট বিনিয়োগ",
    "dash.dtiRatio": "DTI অনুপাত",
    "dash.savingsInvestments": "সঞ্চয় + বিনিয়োগ",
    "dash.annualEmi": "বাৰ্ষিক EMI দায়",
    "dash.monthlyWealth": "মাহেকীয়া সম্পদ নিৰ্মাণ",
    "dash.financialPulse": "বিত্তীয় পালচ",
    "dash.assetsVsLiabilities": "সম্পত্তি vs দায় — ১২ মাহৰ অনুমান",
    "dash.dailySathiTip": "আজিৰ সাথী টিপ",
    "dash.goalTracker": "লক্ষ্য ট্ৰেকাৰ",
    "dash.miniTools": "মিনি টুলচ — দ্ৰুত ধন হেক",
    "dash.welcome": "স্বাগতম",
    "dash.welcomeSub": "আজিৰ আপোনাৰ বিত্তীয় সাৰাংশ ইয়াত আছে।",
    "dash.editProfile": "প্ৰ'ফাইল সম্পাদনা",
    "dash.user": "ব্যৱহাৰকাৰী",
    "ai.insightTitle": "AI অন্তৰ্দৃষ্টি",
    "ai.sipAdvice": "আপোনাৰ {income} আয়ৰ ভিত্তিত, আপোনাৰ {goal} দ্ৰুত কৰিবলৈ {sip}/মাহ SIP আৰম্ভ কৰিবলৈ পৰামৰ্শ দিওঁ.",
    "ai.dtiWarning": "আপোনাৰ EMI বোজা {dti}% — সুদ কমাবলৈ বেলেন্স ট্ৰেন্সফাৰ বিবেচনা কৰক.",
    "ai.financialGoal": "আৰ্থিক লক্ষ্য",
    "action.title": "কাৰ্য তালিকা",
    "action.completeProfile": "আপোনাৰ আৰ্থিক প্ৰ'ফাইল সম্পূৰ্ণ কৰক",
    "action.setup": "ছেটআপ",
    "action.runTax": "কৰ অপ্টিমাইজেচন চলাওক",
    "action.open": "খোলক",
    "action.rentVsBuy": "ভাড়া বনাম ক্ৰয় বিশ্লেষণ",
    "action.analyze": "বিশ্লেষণ",
    "goal.buyHome": "ঘৰ কিনক",
    "goal.payDebt": "ঋণ পৰিশোধ",
    "goal.retireEarly": "সোনকালে অৱসৰ",
    "goal.emergency": "জৰুৰী পুঁজি",
    "goal.childEdu": "সন্তানৰ শিক্ষা",
    "goal.wealthGrowth": "সম্পদ বৃদ্ধি",
    "goal.eta50plus": "50+ বছৰ",
    "goal.achieved": "অৰ্জিত",
    "goal.basedOn": "ভিত্তিত",
    "goal.moSIP": "মাহেকীয়া SIP",
    "goal.returns": "ৰিটাৰ্ণ",
    "goal.saved": "সঞ্চয়",
    "goal.target": "লক্ষ্য",
    "goal.custom": "কাষ্টম",
    "goal.complete": "সম্পূৰ্ণ",
    "goal.yrsToGo": "বছৰ বাকী",
    "goal.sipNeeded": "লক্ষ্যত উপনীত হ'বলৈ SIP",
    "goal.yrs": "বছৰ",
    "goal.month": "মাহ",
    "goal.save": "ছেভ",
    "goal.reset": "ৰিছেট",
    "goal.setCustomTarget": "কাষ্টম লক্ষ্য ছেট কৰক",
    "goal.noGoals": "কোনো লক্ষ্য ছেট নাই। ট্ৰেক কৰিবলৈ প্ৰ'ফাইল আপডেট কৰক।",
    "goal.enterValid": "সঠিক পৰিমাণ দিয়ক",
    "goal.targetUpdated": "লক্ষ্য আপডেট হৈছে",
    "goal.resetDefault": "ডিফল্টলৈ ৰিছেট",
    "calc.title": "বিনিয়োগ কেলকুলেটৰ",
    "calc.subtitle": "সঠিকভাৱে আপোনাৰ সম্পত্তিৰ পৰিকল্পনা কৰক",
    "calc.sipCalc": "SIP কেলকুলেটৰ",
    "calc.lumpSum": "একেলগে",
    "calc.monthlyInvestment": "মাহিলী বিনিয়োগ",
    "calc.investmentAmount": "বিনিয়োগ পৰিমাণ",
    "calc.expectedReturn": "আশানুৰূপ লাভ (%)",
    "calc.timePeriod": "সময় কাল (বছৰ)",
    "calc.yrs": "বছৰ",
    "calc.totalValue": "মুঠ মূল্য",
    "calc.invested": "বিনিয়োগ",
    "calc.gains": "লাভ",
    "compare.title": "সামগ্ৰী তুলনা",
    "compare.subtitle": "মিউচুৱেল ফাণ্ড, FD আৰু বীমা পৰিকল্পনা তুলনা কৰক",
    "compare.fundName": "ফাণ্ড নাম",
    "compare.1yReturn": "1 বছৰ লাভ",
    "compare.3yReturn": "3 বছৰ লাভ",
    "compare.5yReturn": "5 বছৰ লাভ",
    "compare.risk": "বিপদ",
    "compare.expense": "খৰচ",
    "compare.disclaimer": "* অতীত কাৰ্যদক্ষতা ভৱিষ্যতৰ লাভৰ নিশ্চয়তা নিদিয়ে।",
    "compare.low": "কম",
    "compare.moderate": "মধ্যমীয়া",
    "compare.high": "বেছি",
    "compare.moderateHigh": "মধ্যমীয়া-বেছি",
    "re.title": "ৰিয়েল এষ্টেট প্লেনাৰ",
    "re.subtitle": "EMI কেলকুলেটৰ আৰু সম্পত্তি পৰিকল্পনা সঁজুলি",
    "re.propertyValue": "সম্পত্তি মূল্য",
    "re.downPayment": "ডাউন পেমেণ্ট (%)",
    "re.interestRate": "সুদৰ হাৰ (%)",
    "re.loanTenure": "ঋণ অৱধি (বছৰ)",
    "re.monthlyEmi": "মাহিলী EMI",
    "re.loanAmount": "ঋণৰ পৰিমাণ",
    "re.totalInterest": "মুঠ সুদ",
    "re.totalPayment": "মুঠ পৰিশোধ",
    "tax.title": "কৰ কেন্দ্ৰ",
    "tax.subtitle": "ভাৰতীয় কৰ আইনৰ অধীনত কৰ ৰাহি পৰিকল্পনা কৰক",
    "tax.recommended": "পৰামৰ্শিত",
    "tax.alternative": "বিকল্প",
    "tax.oldRegime": "পুৰণি কৰ ব্যৱস্থা",
    "tax.newRegime": "নতুন কৰ ব্যৱস্থা",
    "tax.estimatedTax": "আনুমানিক কৰ",
    "tax.withDeductions": "₹2,41,000 কৰ্তনসহ",
    "tax.standardDeduction": "মানক কৰ্তন ₹75,000",
    "tax.section80c": "ধাৰা 80C – ₹1.5 লাখ",
    "tax.section80d": "ধাৰা 80D – স্বাস্থ্য বীমা",
    "tax.hraExemption": "HRA ছাড়",
    "tax.elss": "ELSS মিউচুৱেল ফাণ্ড",
    "tax.epf": "EPF অৱদান",
    "tax.lifeInsurance": "জীৱন বীমা প্ৰিমিয়াম",
    "tax.selfFamily": "নিজ আৰু পৰিয়াল প্ৰিমিয়াম",
    "tax.parentsPremium": "পিতৃ-মাতৃ প্ৰিমিয়াম",
    "tax.rentPaid": "দিয়া ভাড়া (বাৰ্ষিক)",
    "tax.exemptedAmount": "ছাড় পোৱা পৰিমাণ",
    "tax.invested": "বিনিয়োগ",
    "tax.autoDeducted": "স্বয়ংক্ৰিয় কৰ্তন",
    "tax.paid": "পৰিশোধ",
    "tax.claimable": "দাবীযোগ্য",
    "tax.calculated": "গণনা",
    "sidebar.retirement": "অৱসৰ ঘড়ী",
    "re.step1Title": "বাজেট",
    "re.step2Title": "ROI চিমুলেটৰ",
    "re.step3Title": "সামৰ্থ্য",
    "re.stampDuty": "ষ্টাম্প ডিউটি",
    "re.registration": "পঞ্জীয়ন",
    "re.gst": "GST",
    "re.totalCost": "মুঠ সম্পত্তি খৰচ",
    "re.nextStep": "পৰৱৰ্তী পদক্ষেপ",
    "re.back": "পিছলৈ",
    "re.roiDesc": "তুলনা: একেই পৰিমাণ মিউচুৱেল ফাণ্ডত বিনিয়োগ কৰিলে কি হ'লহেঁতেন?",
    "re.realEstateInv": "ৰিয়েল এষ্টেট বিনিয়োগ",
    "re.mutualFundInv": "মিউচুৱেল ফাণ্ড বিনিয়োগ",
    "re.rentalYield": "ভাড়া উপাৰ্জন",
    "re.appreciation": "মূল্য বৃদ্ধি",
    "re.propertyAfter20": "সম্পত্তি মূল্য (20 বছৰ)",
    "re.totalRentalIncome": "মুঠ ভাড়া আয়",
    "re.costDeductions": "খৰচ কৰ্তন",
    "re.netGain": "নিকা লাভ",
    "re.expectedReturn": "প্ৰত্যাশিত ৰিটাৰ্ন",
    "re.amountInvested": "বিনিয়োগ কৰা পৰিমাণ",
    "re.valueAfter20": "20 বছৰ পিছত মূল্য",
    "re.mfWins": "মিউচুৱেল ফাণ্ড জয়ী হয়",
    "re.reWins": "ৰিয়েল এষ্টেট জয়ী হয়",
    "re.summary": "সাৰাংশ",
    "re.minIncome": "নূন্যতম মাহিলী আয়",
    "re.affordable": "এই সম্পত্তি আপোনাৰ বাবে সাধ্যৰ ভিতৰত!",
    "re.notAffordable": "এই সম্পত্তি আপোনাৰ বাজেটৰ বাহিৰত হ'ব পাৰে.",
    "re.emiShouldBe": "EMI আপোনাৰ মাহিলী আয়ৰ 40% তকৈ কম হোৱা উচিত.",
    "re.nextSteps": "পৰৱৰ্তী পদক্ষেপ গাইড",
    "re.step1Tip": "2-3 বেংকৰ পৰা হোম লোন প্ৰি-এপ্ৰুভেল লওক",
    "re.step2Tip": "সম্পত্তিৰ RERA পঞ্জীয়ন পৰীক্ষা কৰক",
    "re.step3Tip": "ইণ্টেৰিয়ৰ আৰু স্থানান্তৰ খৰচ (5-10%) বাজেট কৰক",
    "re.step4Tip": "দখলৰ আগতে হোম ইনচিউৰেঞ্চ বিবেচনা কৰক",
    "re.startOver": "পুনৰ আৰম্ভ কৰক",
    "re.step4Title": "ঋণ যোগ্যতা",
    "re.empType": "নিয়োগৰ ধৰণ",
    "re.empSalaried": "দৰমহা পোৱা",
    "re.empBusiness": "ব্যৱসায়ৰ মালিক",
    "re.empFreelancer": "ফ্ৰিলেন্সাৰ",
    "re.netSalary": "নিকা মাহিলী দৰমহা (₹)",
    "re.existingEmis": "বিদ্যমান EMI (₹)",
    "re.workExp": "কামৰ অভিজ্ঞতা",
    "re.maxAllowableEmi": "সৰ্বাধিক অনুমোদিত EMI",
    "re.maxLoanAmount": "সৰ্বাধিক ঋণৰ পৰিমাণ",
    "re.requiredLoan": "আৱশ্যকীয় ঋণৰ পৰিমাণ",
    "re.calcBasis": "গণনাৰ ভিত্তি",
    "re.eligible": "যোগ্য — আপুনি যোগ্য!",
    "re.borderline": "সীমাৰেখা — সীমাৰ ওচৰত",
    "re.notEligible": "অযোগ্য — ব্যৱধান বহুত বেছি",
    "re.eligibleTip": "আপোনাৰ আৱশ্যক ঋণ আপোনাৰ যোগ্যতাৰ ভিতৰত। আৱেদন কৰিবলৈ সাজু!",
    "re.borderlineTip": "সীমা বঢ়াবলৈ স্বামী/পত্নীক সহ-আৱেদকাৰী হিচাপে যোগ কৰক.",
    "re.notEligibleTip": "ডাউন পেমেণ্ট বঢ়াওক বা আগতে বিদ্যমান ঋণ পৰিশোধ কৰক.",
    "re.sathiLoanTip": "সাথী টিপ: বেংকে স্থিৰতা ভাল পায়। যোৱা 3 মাহত চাকৰি সলনি কৰিছে যদি, আৱেদনৰ আগতে 3 দৰমহা স্লিপৰ বাবে অপেক্ষা কৰক!",
    "re.addCoApplicant": "সহ-আৱেদকাৰীৰ আয় যোগ কৰক?",
    "re.coApplicantSalary": "সহ-আৱেদকাৰীৰ মাহিলী দৰমহা (₹)",
    "re.coApplicantNote": "স্বামী/পত্নী বা পিতৃ-মাতৃৰ আয় যোগ কৰিলে আপোনাৰ ঋণ যোগ্যতা যথেষ্ট বাঢ়িব পাৰে!",
    "ret.title": "অৱসৰ ঘড়ী",
    "ret.subtitle": "মুদ্ৰাস্ফীতি-সমন্বিত গণনাৰে অৱসৰ পৰিকল্পনা",
    "ret.monthlyExpense": "বৰ্তমান মাহিলী খৰচ",
    "ret.currentAge": "বৰ্তমান বয়স",
    "ret.retireAge": "অৱসৰৰ বয়স",
    "ret.inflationRate": "মুদ্ৰাস্ফীতি হাৰ (%)",
    "ret.sipReturn": "প্ৰত্যাশিত SIP ৰিটাৰ্ন (%)",
    "ret.yearsLeft": "অৱসৰলৈ বাকী বছৰ",
    "ret.futureCost": "ভৱিষ্যতৰ মাহিলী খৰচ",
    "ret.month": "মাহ",
    "ret.inflationNote": "মুদ্ৰাস্ফীতিৰ বাবে সমন্বিত জীৱনশৈলী খৰচ",
    "ret.corpusNeeded": "অৱসৰ কৰ্পাছ প্ৰয়োজন",
    "ret.sipNeeded": "মাহিলী SIP প্ৰয়োজন",
    "ret.sipNote": "আৰামদায়ক অৱসৰৰ বাবে আজিয়েই এই SIP আৰম্ভ কৰক",
    "sidebar.rentVsBuy": "ভাড়া vs ক্ৰয়",
    "sidebar.debtKiller": "ঋণ-মুক্তি পথ",
    "sidebar.stockResearch": "ষ্টক গৱেষণা",
    "sidebar.emiMentor": "মই এইটো কিনিব পাৰিম নে?",
    "sidebar.lifeStages": "জীৱন পৰ্যায়",
    "sidebar.creditCard": "ক্ৰেডিট কাৰ্ড মাষ্টাৰ",
    "sidebar.profile": "মোৰ প্ৰফাইল",
    "sidebar.childFuture": "সন্তানৰ ভৱিষ্যত পৰিকল্পক",
    "sidebar.assetComparison": "সম্পদ তুলনা",
    "sidebar.dataRights": "ডেটা অধিকাৰ",
    "rvb.title": "ভাড়া vs ক্ৰয় বিশ্লেষক",
    "rvb.subtitle": "ভাড়াত থাকিব নে কিনিব? সময়ৰ লগত প্ৰকৃত খৰচ তুলনা কৰক।",
    "rvb.cityPreset": "চহৰ প্ৰিছেট",
    "rvb.custom": "কাষ্টম",
    "rvb.taxRegime": "কৰ ব্যৱস্থা",
    "rvb.oldRegime": "পুৰণি ব্যৱস্থা",
    "rvb.newRegime": "নতুন ব্যৱস্থা",
    "rvb.horizon": "বিশ্লেষণ সময়",
    "rvb.yrs": "বছৰ",
    "rvb.buyPath": "ক্ৰয় পথ",
    "rvb.rentPath": "ভাড়া + বিনিয়োগ পথ",
    "rvb.propertyPrice": "সম্পত্তি মূল্য",
    "rvb.downPayment": "ডাউন পেমেণ্ট (%)",
    "rvb.loanRate": "ঋণ সুদৰ হাৰ (%)",
    "rvb.loanTenure": "ঋণৰ মেয়াদ (বছৰ)",
    "rvb.interiors": "ইণ্টেৰিয়ৰ খৰচ",
    "rvb.monthlyMaintenance": "মাহিলী ৰক্ষণাবেক্ষণ",
    "rvb.yearlyPropertyTax": "বাৰ্ষিক সম্পত্তি কৰ",
    "rvb.appreciationRate": "সম্পত্তি মূল্যবৃদ্ধি (%)",
    "rvb.monthlyRent": "মাহিলী ভাড়া",
    "rvb.rentIncrease": "বাৰ্ষিক ভাড়া বৃদ্ধি (%)",
    "rvb.mfReturn": "মিউচুৱেল ফাণ্ড ৰিটাৰ্ন (%)",
    "rvb.opportunityCostNote": "সুযোগ খৰচ: ডাউন পেমেণ্ট বিনিয়োগ কৰিলে",
    "rvb.investedInMF": "মিউচুৱেল ফাণ্ডত বিনিয়োগ",
    "rvb.hraNote": "মনত ৰাখক: HRA ৰেহাই কেৱল পুৰণি ব্যৱস্থাত",
    "rvb.monthlyEmi": "মাহিলী EMI",
    "rvb.recommendation": "পৰামৰ্শ",
    "rvb.buyingBetter": "ক্ৰয়ে অধিক সম্পদ নিৰ্মাণ কৰে",
    "rvb.rentingBetter": "ভাড়া আপোনাৰ সম্পদৰ বাবে ভাল",
    "rvb.wealthDiff": "সম্পদ পাৰ্থক্য",
    "rvb.breakeven": "ব্ৰেকইভেন বছৰ",
    "rvb.year": "বছৰ",
    "rvb.neverBreakeven": "ক্ৰয়ে কেতিয়াও ধৰিব নোৱাৰে",
    "rvb.wealthComparison": "নিট সম্পদ তুলনা",
    "rvb.years": "বছৰ",
    "rvb.buyWealth": "ক্ৰয়: নিট সম্পদ",
    "rvb.rentWealth": "ভাড়া + বিনিয়োগ: নিট সম্পদ",
    "rvb.taxRulesTitle": "2026 কৰ নিয়ম প্ৰয়োগ",
    "rvb.taxRule1": "পুৰণি: ধাৰা 24b সুদ কৰ্তন ₹2L লৈকে",
    "rvb.taxRule2": "পুৰণি: ধাৰা 80C মূলধন কৰ্তন ₹1.5L লৈকে",
    "rvb.taxRule3": "নতুন: স্ব-আবাসিক সম্পত্তিৰ বাবে কোনো কৰ্তন নাই",
    "rvb.assumptions": "ব্যৱহৃত অনুমান",
    "rvb.assumption1": "সম্পত্তি মূল্যবৃদ্ধি",
    "rvb.assumption2": "ভাড়া বৃদ্ধি",
    "rvb.assumption3": "MF ৰিটাৰ্ন (CAGR)",
    "rvb.purposeTitle": "এই টুলটোৱে কি কৰে?",
    "rvb.purposeDesc": "এই টুলে ঘৰ কিনাৰ প্ৰকৃত খৰচ (EMI, ষ্টাম্প ডিউটি, ৰক্ষণাবেক্ষণ) ভাড়াত থাকি পাৰ্থক্য মিউচুৱেল ফাণ্ডত বিনিয়োগ কৰাৰ সৈতে তুলনা কৰে।",
    "rvb.hiddenCosts": "লুকাই থকা খৰচ (ষ্টাম্প ডিউটি, ৰক্ষণাবেক্ষণ, কৰ)",
    "rvb.stampDuty": "ষ্টাম্প ডিউটি",
    "rvb.registration": "পঞ্জীয়ন",
    "rvb.wealthGapChart": "সম্পদ ব্যৱধান: কিনা vs ভাড়া + বিনিয়োগ",
    "rvb.sathiVerdict": "সাথীৰ ৰায়",
    "rvb.sathiRentAdvice": "সাথীয়ে কয়: ভাড়া আপোনাক মাহে {AMT} সঞ্চয় কৰিছে। ইয়াক SIPত বিনিয়োগ কৰক!",
    "rvb.sathiBuyAdvice": "সাথীয়ে কয়: এই দামত কিনাটো উৎকৃষ্ট দীৰ্ঘম্যাদী সম্পদ!",
    "rvb.totalCostBuy": "মালিকানাৰ মুঠ খৰচ (কিনা)",
    "rvb.rentInvestPath": "ভাড়া + বিনিয়োগ পথ",
    "rvb.downPaymentStampInteriors": "ডাউন পেমেণ্ট + ষ্টাম্প ডিউটি + ইণ্টেৰিয়ৰ",
    "rvb.totalEmiPaid": "মুঠ EMI পৰিশোধ",
    "rvb.interestLabel": "সুদ",
    "rvb.taxSavings": "কৰ সঞ্চয় (ধাৰা 24b + 80C)",
    "rvb.totalOutOfPocket": "মুঠ পকেটৰ পৰা খৰচ",
    "rvb.propertyValueAtYear": "বছৰত সম্পত্তিৰ মূল্য",
    "rvb.totalRentPaid": "মুঠ ভাড়া পৰিশোধ",
    "rvb.downPaymentInvested": "ডাউন পেমেণ্ট বিনিয়োগ",
    "rvb.indexFund": "ইণ্ডেক্স ফাণ্ড",
    "rvb.monthlySavingsInvested": "মাহিলী সঞ্চয় বিনিয়োগ (EMI পাৰ্থক্য)",
    "rvb.autoCompounded": "অটো-কম্পাউণ্ড",
    "rvb.investmentCorpus": "বছৰত বিনিয়োগ কোষ",
    "rvb.netWealth": "নিট সম্পদ (কোষ − ভাড়া)",
    "rvb.shareWhatsApp": "WhatsAppত শ্বেয়াৰ কৰক",
    "rvb.saveResult": "ফলাফল সংৰক্ষণ কৰক",
    "mac.investmentAmount": "বিনিয়োগ ৰাশি (₹)",
    "mac.frequency": "বিনিয়োগ কম্পাঙ্ক",
    "mac.freqMonthly": "মাহেকীয়া (SIP)",
    "mac.freqQuarterly": "ত্ৰৈমাসিক",
    "mac.freqHalfYearly": "অৰ্ধ-বাৰ্ষিক",
    "mac.freqYearly": "বাৰ্ষিক",
    "mac.chartTitle": "{amount} {frequency} বিনিয়োগৰ {years} বছৰৰ বৃদ্ধি",
    "mac.yearlyTip": "বছৰত এবাৰ ডাঙৰ ৰাশি বিনিয়োগ বনাম প্ৰতি মাহে সৰু ৰাশি — 'ৰুপী কষ্ট এভাৰেজিং'-ৰ কাৰণে ফলাফল বেলেগ হ'ব পাৰে!",
  },
  ur: {
    "sidebar.dashboard": "ڈیش بورڈ",
    "sidebar.calculators": "سرمایہ کاری کیلکولیٹر",
    "sidebar.compare": "مصنوعات کا موازنہ",
    "sidebar.realEstate": "ریئل اسٹیٹ پلانر",
    "sidebar.tax": "ٹیکس سینٹر",
    "sidebar.tagline": "سمارٹ مالی ٹولز",
    "sidebar.disclaimer": "صرف تعلیمی مقاصد کے لیے۔ مالی مشورہ نہیں۔",
    "sidebar.catDashboard": "ڈیش بورڈ",
    "sidebar.catHomeLife": "🏠 گھر اور زندگی",
    "sidebar.catFuture": "🔮 مستقبل",
    "sidebar.catSafety": "🛡️ تحفظ",
    "sidebar.catInvest": "📈 سرمایہ کاری",
    "sidebar.catBanking": "🏦 بینکنگ",
    "sidebar.catAccount": "اکاؤنٹ",
    "sidebar.desc.dashboard": "ایک نظر میں آپ کا مالی جائزہ",
    "sidebar.desc.calculators": "SIP، یکمشت اور مرکب سود کے ٹولز",
    "sidebar.desc.compare": "مالی مصنوعات کا ساتھ ساتھ موازنہ کریں",
    "sidebar.desc.realEstate": "جائیداد خریداری اور ROI منصوبہ بندی",
    "sidebar.desc.tax": "پرانے اور نئے ٹیکس نظام میں بچت بہتر بنائیں",
    "sidebar.desc.retirement": "ریٹائرمنٹ فنڈ اور SIP کا حساب لگائیں",
    "sidebar.desc.rentVsBuy": "کرایہ یا خریداری — کیا بہتر ہے جانیں",
    "sidebar.desc.debtKiller": "تیزی سے قرض سے آزاد ہونے کا منصوبہ بنائیں",
    "sidebar.desc.stockResearch": "لائیو مارکیٹ ڈیٹا اور اسٹاک تجزیہ",
    "sidebar.desc.emiMentor": "چیک کریں کہ کیا آپ واقعی خرید سکتے ہیں",
    "sidebar.desc.childFuture": "بچے کی تعلیم اور مستقبل کے اخراجات کی منصوبہ بندی",
    "sidebar.desc.creditCard": "کریڈٹ کارڈ قرض کی اصل لاگت سمجھیں",
    "sidebar.desc.assetComparison": "مختلف اثاثہ زمروں میں منافع کا موازنہ",
    "sidebar.desc.profile": "اپنا مالی پروفائل منظم کریں",
    "sidebar.desc.dataRights": "آپ کی ڈیٹا رازداری اور ڈاؤن لوڈ آپشنز",
    "dash.title": "ڈیش بورڈ",
    "dash.subtitle": "آپ کی مالی صورتحال ایک نظر میں",
    "dash.portfolioValue": "پورٹفولیو ویلیو",
    "dash.monthlySip": "ماہانہ SIP",
    "dash.taxSaved": "ٹیکس بچت (FY25)",
    "dash.returns1y": "منافع (1 سال)",
    "dash.portfolioGrowth": "پورٹفولیو اضافہ",
    "dash.last9months": "پچھلے 9 مہینے",
    "dash.quickActions": "فوری اقدامات",
    "dash.sipCalc": "SIP کیلکولیٹر",
    "dash.compareMF": "میوچوئل فنڈ موازنہ",
    "dash.emiEstimator": "EMI تخمینہ",
    "dash.taxOptimizer": "ٹیکس آپٹیمائزر",
    "dash.marketSnapshot": "مارکیٹ سنیپ شاٹ",
    "dash.active": "فعال",
    "dash.vikasScore": "وکاس سفر",
    "dash.totalAssets": "کل اثاثے",
    "dash.totalLiabilities": "کل واجبات",
    "dash.netInvestment": "خالص سرمایہ کاری",
    "dash.dtiRatio": "DTI تناسب",
    "dash.savingsInvestments": "بچت + سرمایہ کاری",
    "dash.annualEmi": "سالانہ EMI ذمہ داریاں",
    "dash.monthlyWealth": "ماہانہ دولت کی تعمیر",
    "dash.financialPulse": "مالی نبض",
    "dash.assetsVsLiabilities": "اثاثے بمقابلہ واجبات — 12 ماہ کا تخمینہ",
    "dash.dailySathiTip": "آج کی ساتھی ٹپ",
    "dash.goalTracker": "ہدف ٹریکر",
    "dash.miniTools": "منی ٹولز — فوری پیسے ہیکس",
    "dash.welcome": "خوش آمدید",
    "dash.welcomeSub": "آج کا آپ کا مالی جائزہ یہاں ہے۔",
    "dash.editProfile": "پروفائل ترمیم",
    "dash.user": "صارف",
    "ai.insightTitle": "AI بصیرت",
    "ai.sipAdvice": "آپ کی {income} آمدنی کی بنیاد پر، آپ کے {goal} کو تیز کرنے کے لیے {sip}/ماہ SIP شروع کرنے کی سفارش کرتے ہیں۔",
    "ai.dtiWarning": "آپ کا EMI بوجھ {dti}% ہے — سود کم کرنے کے لیے بیلنس ٹرانسفر پر غور کریں۔",
    "ai.financialGoal": "مالی ہدف",
    "action.title": "عمل کی فہرست",
    "action.completeProfile": "اپنا مالی پروفائل مکمل کریں",
    "action.setup": "سیٹ اپ",
    "action.runTax": "ٹیکس آپٹیمائزیشن چلائیں",
    "action.open": "کھولیں",
    "action.rentVsBuy": "کرایہ بمقابلہ خریداری تجزیہ",
    "action.analyze": "تجزیہ",
    "goal.buyHome": "گھر خریدیں",
    "goal.payDebt": "قرض ادا کریں",
    "goal.retireEarly": "جلدی ریٹائر",
    "goal.emergency": "ایمرجنسی فنڈ",
    "goal.childEdu": "بچے کی تعلیم",
    "goal.wealthGrowth": "دولت بڑھائیں",
    "goal.eta50plus": "50+ سال",
    "goal.achieved": "حاصل",
    "goal.basedOn": "کی بنیاد پر",
    "goal.moSIP": "ماہانہ SIP",
    "goal.returns": "منافع",
    "goal.saved": "بچت",
    "goal.target": "ہدف",
    "goal.custom": "کسٹم",
    "goal.complete": "مکمل",
    "goal.yrsToGo": "سال باقی",
    "goal.sipNeeded": "ہدف تک پہنچنے کے لیے SIP",
    "goal.yrs": "سال",
    "goal.month": "مہینہ",
    "goal.save": "محفوظ",
    "goal.reset": "ری سیٹ",
    "goal.setCustomTarget": "کسٹم ہدف سیٹ کریں",
    "goal.noGoals": "کوئی ہدف سیٹ نہیں۔ ٹریک کرنے کے لیے پروفائل اپ ڈیٹ کریں۔",
    "goal.enterValid": "درست رقم درج کریں",
    "goal.targetUpdated": "ہدف اپ ڈیٹ ہوا",
    "goal.resetDefault": "ڈیفالٹ پر ری سیٹ",
    "calc.title": "سرمایہ کاری کیلکولیٹر",
    "calc.subtitle": "درستگی کے ساتھ اپنی دولت کی منصوبہ بندی کریں",
    "calc.sipCalc": "SIP کیلکولیٹر",
    "calc.lumpSum": "یکمشت",
    "calc.monthlyInvestment": "ماہانہ سرمایہ کاری",
    "calc.investmentAmount": "سرمایہ کاری کی رقم",
    "calc.expectedReturn": "متوقع منافع (%)",
    "calc.timePeriod": "مدت (سال)",
    "calc.yrs": "سال",
    "calc.totalValue": "کل قیمت",
    "calc.invested": "سرمایہ کاری",
    "calc.gains": "منافع",
    "compare.title": "مصنوعات کا موازنہ",
    "compare.subtitle": "میوچوئل فنڈز، FD اور انشورنس پلانز کا موازنہ کریں",
    "compare.fundName": "فنڈ کا نام",
    "compare.1yReturn": "1 سال منافع",
    "compare.3yReturn": "3 سال منافع",
    "compare.5yReturn": "5 سال منافع",
    "compare.risk": "خطرہ",
    "compare.expense": "خرچ",
    "compare.disclaimer": "* ماضی کی کارکردگی مستقبل کے منافع کی ضمانت نہیں دیتی۔",
    "compare.low": "کم",
    "compare.moderate": "درمیانہ",
    "compare.high": "زیادہ",
    "compare.moderateHigh": "درمیانہ-زیادہ",
    "re.title": "ریئل اسٹیٹ پلانر",
    "re.subtitle": "EMI کیلکولیٹر اور پراپرٹی پلاننگ ٹولز",
    "re.propertyValue": "پراپرٹی قیمت",
    "re.downPayment": "ڈاؤن پیمنٹ (%)",
    "re.interestRate": "سود کی شرح (%)",
    "re.loanTenure": "قرض کی مدت (سال)",
    "re.monthlyEmi": "ماہانہ EMI",
    "re.loanAmount": "قرض کی رقم",
    "re.totalInterest": "کل سود",
    "re.totalPayment": "کل ادائیگی",
    "tax.title": "ٹیکس سینٹر",
    "tax.subtitle": "ہندوستانی ٹیکس قانون کے تحت ٹیکس بچت کی منصوبہ بندی کریں",
    "tax.recommended": "تجویز کردہ",
    "tax.alternative": "متبادل",
    "tax.oldRegime": "پرانی ٹیکس ریجیم",
    "tax.newRegime": "نئی ٹیکس ریجیم",
    "tax.estimatedTax": "تخمینی ٹیکس",
    "tax.withDeductions": "₹2,41,000 کٹوتیوں کے ساتھ",
    "tax.standardDeduction": "معیاری کٹوتی ₹75,000",
    "tax.section80c": "سیکشن 80C – ₹1.5 لاکھ",
    "tax.section80d": "سیکشن 80D – صحت انشورنس",
    "tax.hraExemption": "HRA چھوٹ",
    "tax.elss": "ELSS میوچوئل فنڈز",
    "tax.epf": "EPF شراکت",
    "tax.lifeInsurance": "لائف انشورنس پریمیم",
    "tax.selfFamily": "خود اور خاندان پریمیم",
    "tax.parentsPremium": "والدین پریمیم",
    "tax.rentPaid": "ادا شدہ کرایہ (سالانہ)",
    "tax.exemptedAmount": "مستثنیٰ رقم",
    "tax.invested": "سرمایہ کاری",
    "tax.autoDeducted": "خودکار کٹوتی",
    "tax.paid": "ادائیگی",
    "tax.claimable": "دعوے کے قابل",
    "tax.calculated": "حساب شدہ",
    "sidebar.retirement": "ریٹائرمنٹ کلاک",
    "re.step1Title": "بجٹ",
    "re.step2Title": "ROI سمیولیٹر",
    "re.step3Title": "استطاعت",
    "re.stampDuty": "اسٹامپ ڈیوٹی",
    "re.registration": "رجسٹریشن",
    "re.gst": "GST",
    "re.totalCost": "کل جائیداد لاگت",
    "re.nextStep": "اگلا قدم",
    "re.back": "واپس",
    "re.roiDesc": "موازنہ: وہی رقم میوچوئل فنڈ میں لگائی ہوتی تو؟",
    "re.realEstateInv": "ریئل اسٹیٹ سرمایہ کاری",
    "re.mutualFundInv": "میوچوئل فنڈ سرمایہ کاری",
    "re.rentalYield": "کرایہ کی آمدنی",
    "re.appreciation": "قدر میں اضافہ",
    "re.propertyAfter20": "جائیداد کی قیمت (20 سال)",
    "re.totalRentalIncome": "کل کرایہ کی آمدنی",
    "re.costDeductions": "لاگت کٹوتیاں",
    "re.netGain": "خالص منافع",
    "re.expectedReturn": "متوقع منافع",
    "re.amountInvested": "لگائی گئی رقم",
    "re.valueAfter20": "20 سال بعد قیمت",
    "re.mfWins": "میوچوئل فنڈ جیتتا ہے",
    "re.reWins": "ریئل اسٹیٹ جیتتی ہے",
    "re.summary": "خلاصہ",
    "re.minIncome": "کم از کم ماہانہ آمدنی",
    "re.affordable": "یہ جائیداد آپ کے لیے قابل استطاعت ہے!",
    "re.notAffordable": "یہ جائیداد آپ کے بجٹ سے باہر ہو سکتی ہے۔",
    "re.emiShouldBe": "EMI آپ کی ماہانہ آمدنی کے 40% سے کم ہونی چاہیے۔",
    "re.nextSteps": "اگلے اقدامات گائیڈ",
    "re.step1Tip": "2-3 بینکوں سے ہوم لون پری اپروول حاصل کریں",
    "re.step2Tip": "جائیداد کی RERA رجسٹریشن تصدیق کریں",
    "re.step3Tip": "انٹیریئر اور منتقلی کے اخراجات (5-10%) کا بجٹ بنائیں",
    "re.step4Tip": "قبضے سے پہلے ہوم انشورنس پر غور کریں",
    "re.startOver": "دوبارہ شروع کریں",
    "re.step4Title": "قرض کی اہلیت",
    "re.empType": "ملازمت کی قسم",
    "re.empSalaried": "تنخواہ دار",
    "re.empBusiness": "کاروبار کا مالک",
    "re.empFreelancer": "فری لانسر",
    "re.netSalary": "خالص ماہانہ تنخواہ (₹)",
    "re.existingEmis": "موجودہ EMI (₹)",
    "re.workExp": "کام کا تجربہ",
    "re.maxAllowableEmi": "زیادہ سے زیادہ مجاز EMI",
    "re.maxLoanAmount": "زیادہ سے زیادہ قرض کی رقم",
    "re.requiredLoan": "مطلوبہ قرض کی رقم",
    "re.calcBasis": "حساب کی بنیاد",
    "re.eligible": "اہل — آپ اہل ہیں!",
    "re.borderline": "سرحد پر — حد کے قریب",
    "re.notEligible": "نااہل — فرق بہت زیادہ",
    "re.eligibleTip": "آپ کا مطلوبہ قرض آپ کی اہلیت میں ہے۔ درخواست دینے کے لیے تیار!",
    "re.borderlineTip": "حد بڑھانے کے لیے شوہر/بیوی کو شریک درخواست دہندہ کے طور پر شامل کریں۔",
    "re.notEligibleTip": "اپنی ڈاؤن پیمنٹ بڑھائیں یا پہلے موجودہ قرض ادا کریں۔",
    "re.sathiLoanTip": "ساتھی ٹپ: بینک استحکام پسند کرتے ہیں۔ پچھلے 3 مہینوں میں نوکری بدلی ہے تو، درخواست سے پہلے 3 تنخواہ سلپ کا انتظار کریں!",
    "re.addCoApplicant": "شریک درخواست دہندہ کی آمدنی شامل کریں؟",
    "re.coApplicantSalary": "شریک درخواست دہندہ ماہانہ تنخواہ (₹)",
    "re.coApplicantNote": "شوہر/بیوی یا والدین کی آمدنی شامل کرنے سے آپ کی قرض کی اہلیت میں نمایاں اضافہ ہو سکتا ہے!",
    "ret.title": "ریٹائرمنٹ کلاک",
    "ret.subtitle": "مہنگائی سے ایڈجسٹ حسابات کے ساتھ ریٹائرمنٹ کی منصوبہ بندی",
    "ret.monthlyExpense": "موجودہ ماہانہ اخراجات",
    "ret.currentAge": "موجودہ عمر",
    "ret.retireAge": "ریٹائرمنٹ کی عمر",
    "ret.inflationRate": "مہنگائی کی شرح (%)",
    "ret.sipReturn": "متوقع SIP منافع (%)",
    "ret.yearsLeft": "ریٹائرمنٹ تک باقی سال",
    "ret.futureCost": "مستقبل کا ماہانہ خرچ",
    "ret.month": "ماہ",
    "ret.inflationNote": "مہنگائی کے لیے ایڈجسٹ طرز زندگی کا خرچ",
    "ret.corpusNeeded": "ریٹائرمنٹ کارپس درکار",
    "ret.sipNeeded": "ماہانہ SIP درکار",
    "ret.sipNote": "آرام دہ ریٹائرمنٹ کے لیے آج ہی یہ SIP شروع کریں",
    "sidebar.rentVsBuy": "کرایہ vs خرید",
    "sidebar.debtKiller": "قرض سے آزادی",
    "sidebar.emiMentor": "کیا میں یہ خرید سکتا ہوں؟",
    "sidebar.lifeStages": "زندگی کے مراحل",
    "rvb.title": "کرایہ vs خرید تجزیہ کار",
    "sidebar.creditCard": "کریڈٹ کارڈ ماسٹر",
    "sidebar.profile": "میری پروفائل",
    "sidebar.childFuture": "بچے کا مستقبل پلانر",
    "sidebar.assetComparison": "اثاثوں کا موازنہ",
    "sidebar.dataRights": "ڈیٹا حقوق",
    "sidebar.stockResearch": "اسٹاک ریسرچ",
    "rvb.subtitle": "کرایے پر رہیں یا خریدیں؟ وقت کے ساتھ اصل لاگت کا موازنہ کریں۔",
    "rvb.cityPreset": "شہر پری سیٹ",
    "rvb.custom": "حسب ضرورت",
    "rvb.taxRegime": "ٹیکس نظام",
    "rvb.oldRegime": "پرانا نظام",
    "rvb.newRegime": "نیا نظام",
    "rvb.horizon": "تجزیے کی مدت",
    "rvb.yrs": "سال",
    "rvb.buyPath": "خرید کا راستہ",
    "rvb.rentPath": "کرایہ + سرمایہ کاری کا راستہ",
    "rvb.propertyPrice": "جائیداد کی قیمت",
    "rvb.downPayment": "ڈاؤن پیمنٹ (%)",
    "rvb.loanRate": "قرض سود کی شرح (%)",
    "rvb.loanTenure": "قرض کی مدت (سال)",
    "rvb.interiors": "انٹیریئر لاگت",
    "rvb.monthlyMaintenance": "ماہانہ دیکھ بھال",
    "rvb.yearlyPropertyTax": "سالانہ جائیداد ٹیکس",
    "rvb.appreciationRate": "جائیداد قدر میں اضافہ (%)",
    "rvb.monthlyRent": "ماہانہ کرایہ",
    "rvb.rentIncrease": "سالانہ کرایہ اضافہ (%)",
    "rvb.mfReturn": "میوچوئل فنڈ ریٹرن (%)",
    "rvb.opportunityCostNote": "موقع کی لاگت: ڈاؤن پیمنٹ سرمایہ کاری کی جاتی تو",
    "rvb.investedInMF": "میوچوئل فنڈ میں سرمایہ کاری",
    "rvb.hraNote": "یاد رکھیں: HRA چھوٹ صرف پرانے نظام میں دستیاب",
    "rvb.monthlyEmi": "ماہانہ EMI",
    "rvb.recommendation": "سفارش",
    "rvb.buyingBetter": "خرید زیادہ دولت بناتی ہے",
    "rvb.rentingBetter": "کرایہ آپ کی دولت کے لیے بہتر ہے",
    "rvb.wealthDiff": "دولت کا فرق",
    "rvb.breakeven": "بریک ایون سال",
    "rvb.year": "سال",
    "rvb.neverBreakeven": "خرید کبھی نہیں پکڑ سکتی",
    "rvb.wealthComparison": "خالص دولت کا موازنہ",
    "rvb.years": "سال",
    "rvb.buyWealth": "خرید: خالص دولت",
    "rvb.rentWealth": "کرایہ + سرمایہ کاری: خالص دولت",
    "rvb.taxRulesTitle": "2026 ٹیکس قوانین لاگو",
    "rvb.taxRule1": "پرانا: سیکشن 24b سود کٹوتی ₹2L تک",
    "rvb.taxRule2": "پرانا: سیکشن 80C اصل کٹوتی ₹1.5L تک",
    "rvb.taxRule3": "نیا: ذاتی جائیداد کے لیے کوئی کٹوتی نہیں",
    "rvb.assumptions": "استعمال شدہ مفروضے",
    "rvb.assumption1": "جائیداد قدر میں اضافہ",
    "rvb.assumption2": "کرایہ اضافہ",
    "rvb.assumption3": "MF ریٹرن (CAGR)",
    "rvb.purposeTitle": "یہ ٹول کیا کرتا ہے؟",
    "rvb.purposeDesc": "یہ ٹول گھر خریدنے کی اصل لاگت (EMI، سٹامپ ڈیوٹی، دیکھ بھال) کا کرایے پر رہ کر فرق میوچل فنڈ میں سرمایہ کاری سے موازنہ کرتا ہے۔",
    "rvb.hiddenCosts": "چھپے ہوئے اخراجات (سٹامپ ڈیوٹی، دیکھ بھال، ٹیکس)",
    "rvb.stampDuty": "سٹامپ ڈیوٹی",
    "rvb.registration": "رجسٹریشن",
    "rvb.wealthGapChart": "دولت کا فرق: خرید vs کرایہ + سرمایہ کاری",
    "rvb.sathiVerdict": "ساتھی کا فیصلہ",
    "rvb.sathiRentAdvice": "ساتھی کہتا ہے: کرایہ آپ کو ماہانہ {AMT} بچا رہا ہے۔ اسے SIP میں لگائیں!",
    "rvb.sathiBuyAdvice": "ساتھی کہتا ہے: اس قیمت پر خریدنا بہترین طویل مدتی اثاثہ ہے!",
    "rvb.totalCostBuy": "ملکیت کی کل لاگت (خرید)",
    "rvb.rentInvestPath": "کرایہ + سرمایہ کاری راستہ",
    "rvb.downPaymentStampInteriors": "ڈاؤن پیمنٹ + سٹامپ ڈیوٹی + انٹیریئر",
    "rvb.totalEmiPaid": "کل EMI ادائیگی",
    "rvb.interestLabel": "سود",
    "rvb.taxSavings": "ٹیکس بچت (سیکشن 24b + 80C)",
    "rvb.totalOutOfPocket": "کل جیب سے خرچ",
    "rvb.propertyValueAtYear": "سال میں جائیداد کی قیمت",
    "rvb.totalRentPaid": "کل کرایہ ادائیگی",
    "rvb.downPaymentInvested": "ڈاؤن پیمنٹ سرمایہ کاری",
    "rvb.indexFund": "انڈیکس فنڈ",
    "rvb.monthlySavingsInvested": "ماہانہ بچت سرمایہ کاری (EMI فرق)",
    "rvb.autoCompounded": "آٹو-کمپاؤنڈ",
    "rvb.investmentCorpus": "سال میں سرمایہ کاری کارپس",
    "rvb.netWealth": "خالص دولت (کارپس − کرایہ)",
    "rvb.shareWhatsApp": "WhatsApp پر شیئر کریں",
    "rvb.saveResult": "نتیجہ محفوظ کریں",
    "mac.investmentAmount": "سرمایہ کاری کی رقم (₹)",
    "mac.frequency": "سرمایہ کاری کی فریکوئنسی",
    "mac.freqMonthly": "ماہانہ (SIP)",
    "mac.freqQuarterly": "سہ ماہی",
    "mac.freqHalfYearly": "نصف سالانہ",
    "mac.freqYearly": "سالانہ",
    "mac.chartTitle": "{amount} {frequency} سرمایہ کاری کی {years} سالوں میں ترقی",
    "mac.yearlyTip": "سال میں ایک بار بڑی رقم سرمایہ کاری بمقابلہ ہر مہینے چھوٹی رقم — 'روپی کاسٹ ایوریجنگ' کی وجہ سے نتائج مختلف ہو سکتے ہیں!",
  },
};
